// src/components/MapView.tsx
// ✅ FishIQ Overlay v2 (user-anchored, viewport-relative)
// - User GPS is the anchor for Fish + Shop spawning
// - Fish pings describe the general area around the user (not glued to the user icon)
// - Fish snap-to-water is best-effort + spread + "stay on-screen" guard
// - Shop ping is map-locked (Leaflet marker) but NOT water-snapped (prevents “glued to centroid”)
// - FishIQ refreshes once from the user GPS anchor on boot/recenter; manual map exploration still uses "Search this area"
// - Keeps invalidateSize, geolocation 2-pass locate, redo auto-hide
// - Keeps tournament button pop
//
// ✅ Fixes in this file swap:
// - Removed undefined helpers (clampLatLngToBounds / metersToLat / metersToLng) + any pseudo-code fragments.
// - Shop spawn is now computed from the TWO LEFT-MOST FISH pings (viewport-left), midpoint in screen-space,
//   gently pulled toward a middle-left safe target, then clamped into safe box + repelled from user/fish.
// - Fish snap-to-water is accepted ONLY if snapped point remains inside the safe box (otherwise keep original).
// - No styling refactors beyond necessary logic fixes.
//
// ✅ NEW (this patch):
// - Dark mode tint now GUARANTEED by applying filter directly to Leaflet tilePane (inline style).
// - Offline download tile keys no longer fork by theme (matches runtime cache key).
//
// ✅ HUD Fitment (this patch):
// - Fixes broken HUD overlay nesting (top HUD “got worse” because it fell out of overlay).
// - Removes dock underlay extension (it didn’t move buttons; it just extended visuals).
// - Bottom HUD sits on a clean shared baseline just above the BottomNav boundary.
// - Uses better header height fallback for iOS.
//
// ✅ WINDOW FIT (this patch):
// - Hard clamp MapView to viewport height (100dvh) + overflow-hidden so page never scrolls.
// - Map fills container via absolute inset-0.
// - HUD elements are absolute within overlay (not fixed) so they cannot "fall off" due to document scroll.
//
// ✅ SEARCHED FISH HYDRATION:
// - Successful FishIQ area results persist across app navigation / MapView remounts.
// - Returning to Big Blue repaints the existing fish instead of forcing another paid search.
// - Manual Search Lure charging remains server-authoritative; this cache never spends/refunds currency.
//
// ✅ UNIFIED MAP DECLUTTER:
// - User/FishIQ/Tournaments/Crew/Saved Spots share one screen-space occupancy model.
// - Higher-value markers keep breathing room; lower-priority markers shift first.
// - Future Bazaar/shop markers can join the same resolver without another layout rewrite.
//
// ✅ TOURNAMENT MAP-LOCK:
// - Tournament Leaflet markers are no longer destroyed/recreated on normal map pans.
// - Geographic tournament pins stay attached to their map position while dragging.
// - Collision/layout recalculation runs on zoom/data/theme changes, where it is actually needed.

//
// ✅ FISHIQ → LOG PREFILL:
// - "Log this fish" now carries the selected FishIQ species into LogCatchView.
// - Coordinates are preserved and the species field opens prefilled.
//
// ✅ HUD SEARCH LURE PLACEMENT:
// - Search Lure wallet now lives on the left rail under Today’s Bite Plan.
// - Right-side map tools stay dedicated to recenter / crew / globe controls.
//
// ✅ PRIVATE CATCH MAP MEMORY:
// - Signed-in anglers see their own coordinate-bearing catchDiary/catchLog entries as persistent private map pins.
// - Previous-catches FishIQ hub only exists when real nearby catches exist.
// - FishIQ area hub is map-locked to the actual read/search anchor instead of floating decoratively near fish pings.

import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";
// @ts-ignore - local editor type noise for leaflet; runtime import is valid in this project
import L from "leaflet";

import { DownloadCloudIcon, XIcon, TrashIcon } from "./icons";
import { share, buildSavedSpotShareUrl } from "../utils/share";

import type {
  Hotspot,
  RestrictedZone,
  LogEntry,
  UserSpot,
  UserProfile,
  Rating,
  Comment,
  WeatherData,
  ToastType,
  CrewPresence,
  FishIQAreaRead,
} from "../types";

import { restrictedZones } from "../../functions/scripts/fishIQ";
import roddoFish from "../assets/roddo-fish.png";
import { Capacitor } from "@capacitor/core";
import { App as CapacitorApp } from "@capacitor/app";
import {
  getMapAvatarIconMetrics,
  getMapAvatarScaleForZoom,
  renderMapAvatarMarkerHtml,
} from "./avatar/MapAvatarMarker";
import { getCachedWaterIntelRead, getWaterIntelRead } from "../services/waterIntel/waterIntelClient";
import { buildWaterIntelEnvironment } from "../services/waterIntelEnvironment";
import ConfirmationModal from "./ConfirmationModal";
import { createBazaarLayerController, type BazaarLayerController } from "./map/layers/BazaarLayer";
import { createTournamentLayerController, type TournamentLayerController } from "./map/layers/TournamentLayer";
import { createSavedSpotsLayerController, type SavedSpotsLayerController } from "./map/layers/SavedSpotsLayer";
import { createCrewPresenceLayerController, type CrewPresenceLayerController } from "./map/layers/CrewPresenceLayer";
import { createFishIQLayerController, type FishIQLayerController } from "./map/layers/FishIQLayer";
import {
  FishIQThinkingState,
  FishSpeciesRow,
  MapFishQuickCard,
  getAreaActivityBadgeLabel,
  getFishAreaEmoji,
  getFishRulesBadgeLabel,
  getHotspotLogCatchCoords,
} from "./map/FishIQMapPresentation";
import SavedSpotsMapOverlay from "./map/SavedSpotsMapOverlay";
import BigBlueTournamentOverlays from "./map/BigBlueTournamentOverlays";
import BigBlueQuickOverlays from "./map/BigBlueQuickOverlays";
import BigBlueWaterTools from "./map/BigBlueWaterTools";
import BigBlueMapHud from "./map/BigBlueMapHud";
import {
  loadBigBlueMapLayerVisibility,
  saveBigBlueMapLayerVisibility,
  type BigBlueMapLayerVisibility,
} from "./map/MapLayerControl";
import {
  clampMapPointToSafeBox,
  getBigBlueSafeInsets,
  isMapPointInsideSafeBox,
  resolveScreenMarkerCollision,
} from "./map/mapCollision";
import {
  focusMapActionAnchor as focusMapActionAnchorInViewport,
  focusMapCardNearMarker as focusMapCardNearMarkerInViewport,
} from "./map/mapPopupPositioning";
import {
  BIG_BLUE_MAP_PANES,
  ensureBigBlueMapPanes,
  getBigBlueMapPane,
  isBigBlueMapPaneConnected,
} from "./map/mapPanes";
import {
  createBigBlueDivIcon,
  createBigBlueMapPinIcon,
} from "./map/mapIcons";
import FishIQDetailView from "./FishIQDetailView";
import {
  forwardGeocodeServer,
  reverseGeocodeServer,
} from "../services/geocodeClient";
import {
  deleteSavedSpot,
  migrateLegacyProfileSavedSpots,
  saveSavedSpot,
  subscribeToSavedSpots,
} from "../services/savedSpots";
import {
  isCrewPresenceOptedIn,
  markOwnCrewPresenceOffline,
  subscribeToAcceptedCrewPresence,
  updateOwnCrewPresence,
} from "../services/crewPresence";
import { getCrewMemberProfiles, sendFriendRequest, subscribeToCrewRelationships, updateUserProfile } from "../services/users";
import { subscribeGroupCrews, type CrewChannel } from "../services/crews";
import { subscribeCrewDMInbox, type CrewDMInboxRow } from "../services/crewDMs";
import { challenges as canonicalChallenges } from "../constants/challenges";
import {
  subscribeToChallengeCompletions,
  subscribeToChallengeProgress,
  type BackendChallengeCompletion,
  type BackendChallengeProgress,
} from "../services/challengeEngine";

// ✅ Overpass water snap helpers (your v3)
import {
  getWaterFeaturesCached,
  snapHotspotsToWaterSpread,
} from "../services/waterOverpass";
import { decorateHotspotsWithUniqueSpecies } from "../services/fishIQWaterSpecies";
import {
  applyFishIQEnvironmentWaterContext,
  buildFishIQLocationContext,
  getFishIQEnvironmentCompatibility,
  rankFishIQSpeciesForLocation,
} from "../services/fishIQLocationRanker";
import { buildFishIQAreaRead } from "../lib/fishiq";
import { getFishIQPresentationIdentityKey } from "../data/fishIQCanonicalSpecies";
import { fishIQGetSignalsNearMe } from "../services/fishIQClient";
import { recordFishIQPredictionRead } from "../services/fishIQValidation";
import {
  completeDailyTaskAction,
  getDailyRewardPreview,
  subscribeToDailyActivity,
  type DailyActivityState,
} from "../services/dailyRewards";
import {
  consumeSearchLure,
  createSearchLureRequestId,
  getSearchLureWalletPreview,
  getSearchLureRefillLabel,
  SEARCH_LURE_FREE_REGEN_CAP,
} from "../services/searchLures";
import { trackRoddoEvent } from '../services/analytics';
import {
  hasSeenTournamentResults,
  joinTournamentRegistration,
  leaveTournamentRegistration,
  markTournamentResultsSeen,
  subscribeMyTournamentParticipationStates,
  type MyTournamentParticipationState,
} from "../services/tournaments";
import {
  DEFAULT_TOURNAMENT_DISCOVERY_FILTERS,
  TOURNAMENT_DISCOVERY_PREFS_EVENT,
  getTournamentDiscoveryPriority,
  getTournamentMapLatLng as getSharedTournamentMapLatLng,
  loadTournamentDiscoveryPreferences,
  saveTournamentDiscoveryPreferences,
  tournamentPassesDiscoveryFilters,
  type TournamentDiscoveryFilters,
  type TournamentDiscoveryPreferences,
} from "../utils/tournamentDiscovery";

// ---------------------------------------------------------------------------
// OVERLAY TUNING
// ---------------------------------------------------------------------------
const OVERLAY_MAX_FISH_DEFAULT = 5;
const FISH_AREA_DEFAULT_LIST_LIMIT = 12;
const FISH_AREA_EXPANDED_LIST_LIMIT = 25;

// Locked Big Blue behavior: map can zoom out this far, but the globe only
// opens from the explicit HUD button. Never auto-open from zoomend.
const MAP_MIN_ZOOM = 4;
// Cap zoom before raster tiles and overlay markers enter unstable over-zoom behavior.
const MAP_MAX_ZOOM = 18;

const getFishWidgetCountForZoom = (_zoom: number, overlayMaxFish: number) => {
  // Keep regular viewport reads compact. Selected-spot reads use the dedicated
  // top-12/show-more limits below instead of this preference cap.
  const cappedMax = clampInt(overlayMaxFish, 1, 10, OVERLAY_MAX_FISH_DEFAULT);
  return Math.min(cappedMax, 5);
};

// ---------------------------------------------------------------------------
// FishIQ Overlay Preferences (localStorage)
// ---------------------------------------------------------------------------
type FishIQOverlayPrefs = {
  enabled: boolean;
  maxFish: number; // 1..10
  computeOnlyOnSearch: boolean; // always true here
};

const FISHIQ_PREF_KEY = "roddo.fishiQ.overlayPrefs.v2";

const SEARCH_LURE_PENDING_PREFIX = 'roddo.searchLure.pending.v1';
const SEARCH_LURE_PENDING_MAX_AGE_MS = 30 * 60 * 1000;

type PendingSearchLureRequest = {
  areaKey: string;
  requestId: string;
  createdAt: number;
};

const searchLurePendingStorageKey = (uid?: string | null) =>
  `${SEARCH_LURE_PENDING_PREFIX}.${String(uid || 'guest').trim() || 'guest'}`;

const normalizePendingAreaKey = (lat: number, lng: number) => {
  const wrappedLng = ((((Number(lng) + 180) % 360) + 360) % 360) - 180;
  return `${Number(lat).toFixed(2)}:${wrappedLng.toFixed(2)}`;
};

const getOrCreatePendingSearchLureRequest = (
  uid: string | null | undefined,
  lat: number,
  lng: number,
  prefix: string,
): PendingSearchLureRequest => {
  const storageKey = searchLurePendingStorageKey(uid);
  const areaKey = normalizePendingAreaKey(lat, lng);
  try {
    const raw = window.localStorage.getItem(storageKey);
    const existing = raw ? JSON.parse(raw) : null;
    if (
      existing?.areaKey === areaKey &&
      typeof existing?.requestId === 'string' &&
      existing.requestId &&
      Date.now() - Number(existing?.createdAt || 0) <= SEARCH_LURE_PENDING_MAX_AGE_MS
    ) {
      return existing as PendingSearchLureRequest;
    }
  } catch {}

  const next: PendingSearchLureRequest = {
    areaKey,
    requestId: createSearchLureRequestId(prefix),
    createdAt: Date.now(),
  };
  try {
    window.localStorage.setItem(storageKey, JSON.stringify(next));
  } catch {}
  return next;
};

const clearPendingSearchLureRequest = (
  uid: string | null | undefined,
  requestId?: string | null,
) => {
  const storageKey = searchLurePendingStorageKey(uid);
  try {
    if (requestId) {
      const raw = window.localStorage.getItem(storageKey);
      const existing = raw ? JSON.parse(raw) : null;
      if (existing?.requestId && existing.requestId !== requestId) return;
    }
    window.localStorage.removeItem(storageKey);
  } catch {}
};

const isDefinitiveSearchLureFailure = (err: any) => {
  const code = String(err?.code || '').toLowerCase();
  const message = String(err?.message || '').toLowerCase();
  return (
    code.includes('resource-exhausted') ||
    code.includes('invalid-argument') ||
    code.includes('permission-denied') ||
    code.includes('unauthenticated') ||
    code.includes('failed-precondition') ||
    /no search lures|invalid search lure coordinates|access must be active/.test(message)
  );
};

const clampInt = (n: any, min: number, max: number, fallback: number) => {
  const x = Math.floor(Number(n));
  if (!Number.isFinite(x)) return fallback;
  return Math.max(min, Math.min(max, x));
};

const loadFishIQPrefs = (): FishIQOverlayPrefs => {
  try {
    const raw = localStorage.getItem(FISHIQ_PREF_KEY);
    if (!raw) throw new Error("no prefs");
    const p = JSON.parse(raw);
    return {
      enabled: p.enabled !== false,
      maxFish: clampInt(p.maxFish, 1, 10, OVERLAY_MAX_FISH_DEFAULT),
      computeOnlyOnSearch: p.computeOnlyOnSearch !== false,
    };
  } catch {
    return {
      enabled: true,
      maxFish: OVERLAY_MAX_FISH_DEFAULT,
      computeOnlyOnSearch: true,
    };
  }
};

const saveFishIQPrefs = (prefs: FishIQOverlayPrefs) => {
  try {
    localStorage.setItem(FISHIQ_PREF_KEY, JSON.stringify(prefs));
  } catch {}
};

// ---------------------------------------------------------------------------
// BASEMAPS
// ---------------------------------------------------------------------------
type BasemapId = "hot" | "esri_topo";

const BASEMAPS: Record<
  BasemapId,
  { label: string; lightUrl: string; lightAttr: string }
> = {
  hot: {
    label: "Humanitarian",
    lightUrl: "https://{s}.tile.openstreetmap.fr/hot/{z}/{x}/{y}.png",
    lightAttr:
      '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors, Tiles courtesy of <a href="https://www.hotosm.org/">HOT</a>',
  },
  esri_topo: {
    label: "Topo",
    lightUrl:
      "https://server.arcgisonline.com/ArcGIS/rest/services/World_Topo_Map/MapServer/tile/{z}/{y}/{x}",
    lightAttr:
      "Tiles &copy; Esri &mdash; Esri, DeLorme, NAVTEQ, TomTom, Intermap, iPC, USGS, FAO, NPS, NRCAN, GeoBase, Kadaster NL, Ordnance Survey, METI, Esri Japan, Esri China (Hong Kong), and the GIS User Community",
  },
};

// ✅ Geolocation helper (native uses Capacitor Geolocation; web uses navigator.geolocation first)
const BROWSER_GEO_CACHE_KEY = "roddo.browserGeo.lastKnown.v1";
const BROWSER_GEO_CACHE_MAX_AGE_MS = 1000 * 60 * 60 * 12;
const BROWSER_GEO_SOFT_STALE_MS = 1000 * 60 * 3;
let geoRequestInFlight: Promise<void> | null = null;
let browserGeoWatchId: number | null = null;
let browserGeoWatchStartedAt = 0;

const isLikelySecureBrowserContext = () => {
  try {
    if ((window as any)?.isSecureContext) return true;
    const host = String(window.location?.hostname || "").toLowerCase();
    return host === "localhost" || host === "127.0.0.1" || host === "::1";
  } catch {
    return false;
  }
};

const isLikelyIOSSafari = () => {
  try {
    const ua = String(window.navigator?.userAgent || "").toLowerCase();
    const isIOS =
      /iphone|ipad|ipod/.test(ua) ||
      ((navigator as any)?.platform === "MacIntel" &&
        (navigator as any)?.maxTouchPoints > 1);
    const isWebKit = /applewebkit/.test(ua);
    const isExcluded = /crios|fxios|edgios|opios|mercury/.test(ua);
    return isIOS && isWebKit && !isExcluded;
  } catch {
    return false;
  }
};

const readCachedBrowserLocation = (): {
  lat: number;
  lng: number;
  accuracy?: number;
  ts: number;
} | null => {
  try {
    const raw = window.localStorage.getItem(BROWSER_GEO_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const lat = Number(parsed?.lat);
    const lng = Number(parsed?.lng);
    const ts = Number(parsed?.ts);
    const accuracy = Number(parsed?.accuracy);
    if (!Number.isFinite(lat) || !Number.isFinite(lng) || !Number.isFinite(ts))
      return null;
    if (Date.now() - ts > BROWSER_GEO_CACHE_MAX_AGE_MS) return null;
    return {
      lat,
      lng,
      ts,
      accuracy: Number.isFinite(accuracy) ? accuracy : undefined,
    };
  } catch {
    return null;
  }
};

const writeCachedBrowserLocation = (
  lat: number,
  lng: number,
  accuracy?: number,
) => {
  try {
    window.localStorage.setItem(
      BROWSER_GEO_CACHE_KEY,
      JSON.stringify({
        lat,
        lng,
        ts: Date.now(),
        ...(Number.isFinite(Number(accuracy))
          ? { accuracy: Number(accuracy) }
          : {}),
      }),
    );
  } catch {}
};

const requestLocation = async (
  map: any,
  opts?: {
    setView?: boolean;
    warmWatch?: boolean;
    forceFresh?: boolean;
    allowLivePrompt?: boolean;
  },
) => {
  const setView = !!opts?.setView;
  const warmWatch = opts?.warmWatch !== false;
  const forceFresh = !!opts?.forceFresh;
  const allowLivePrompt = opts?.allowLivePrompt !== false;

  // iOS Safari FIX:
  // Never let a passive/background request block a real user tap request.
  // Safari ONLY shows the permission prompt from a direct gesture.
  if (geoRequestInFlight && !allowLivePrompt) return geoRequestInFlight;

  if (geoRequestInFlight && allowLivePrompt) {
    try {
      console.log("[geo] forcing live GPS prompt (bypassing passive request)");
    } catch {}
    geoRequestInFlight = null;
  }

  geoRequestInFlight = (async () => {
    const isNative = (() => {
      try {
        return Capacitor.isNativePlatform();
      } catch {
        return false;
      }
    })();
    const isIOSSafariWeb = !isNative && isLikelyIOSSafari();

    try {
      console.log("[geo] requestLocation:start", {
        isNative,
        isIOSSafariWeb,
        setView,
        warmWatch,
        forceFresh,
        allowLivePrompt,
        secure: isLikelySecureBrowserContext(),
        hasCached: !!readCachedBrowserLocation(),
      });
    } catch {}

    const emitFound = (
      lat: number,
      lng: number,
      meta?: Record<string, any>,
    ) => {
      const source = String(meta?.source || "gps");
      const usedFallback = !!meta?.usedFallback;

      writeCachedBrowserLocation(lat, lng, meta?.accuracy);
      map.fire("locationfound", {
        latlng: { lat, lng },
        latitude: lat,
        longitude: lng,
        accuracy: meta?.accuracy,
        source,
        usedFallback,
      });

      try {
        window.dispatchEvent(
          new CustomEvent("roddo:anchor:ready", {
            detail: {
              lat,
              lon: lng,
              lng,
              source,
              usedFallback,
              accuracy: meta?.accuracy,
              ts: Date.now(),
            },
          }),
        );
      } catch {}

      if (setView) {
        try {
          map.setView([lat, lng], 16, { animate: true });
        } catch {}
      }
    };

    const emitError = (message: string, extra?: Record<string, any>) => {
      map.fire("locationerror", {
        message,
        ...(extra || {}),
      });
    };

    const maybeStartBrowserWatch = () => {
      if (isNative || isIOSSafariWeb) return;
      const navGeo = window.navigator?.geolocation;
      if (!navGeo || !warmWatch) return;
      if (browserGeoWatchId != null) return;

      try {
        browserGeoWatchStartedAt = Date.now();
        browserGeoWatchId = navGeo.watchPosition(
          (pos) => {
            emitFound(pos.coords.latitude, pos.coords.longitude, {
              accuracy: pos.coords.accuracy,
              source: "browser_watch",
              usedFallback: false,
            });
          },
          (err) => {
            const code = Number(err?.code ?? 0);
            console.warn("[geo] browser watch error", err);

            if (code === 1) {
              emitError("permission_denied", { source: "web_watch", code });
              try {
                if (browserGeoWatchId != null)
                  navGeo.clearWatch(browserGeoWatchId);
              } catch {}
              browserGeoWatchId = null;
            } else if (code === 2) {
              if (Date.now() - browserGeoWatchStartedAt > 20000) {
                try {
                  if (browserGeoWatchId != null)
                    navGeo.clearWatch(browserGeoWatchId);
                } catch {}
                browserGeoWatchId = null;
              }
            }
          },
          {
            enableHighAccuracy: isLikelyIOSSafari(),
            timeout: 20000,
            maximumAge: 15000,
          },
        );
      } catch (watchErr) {
        console.warn("[geo] could not start browser watch", watchErr);
        browserGeoWatchId = null;
      }
    };

    const tryDirectIOSSafariGestureRequest = () => {
      if (!allowLivePrompt || !isIOSSafariWeb) return false;

      const navGeo = window.navigator?.geolocation;
      if (!navGeo) return false;

      try {
        console.log("[geo] ios safari direct gesture request:start");
      } catch {}

      navGeo.getCurrentPosition(
        (pos) => {
          emitFound(pos.coords.latitude, pos.coords.longitude, {
            accuracy: pos.coords.accuracy,
            source: "ios_safari_direct",
            usedFallback: false,
          });
          maybeStartBrowserWatch();
        },
        (err) => {
          const code = Number(err?.code ?? 0);
          console.warn("[geo] ios safari direct gesture request failed", err);

          const cached = readCachedBrowserLocation();
          if (cached && code !== 1) {
            emitFound(cached.lat, cached.lng, {
              accuracy: cached.accuracy,
              source: "cache",
              usedFallback: true,
            });
            return;
          }

          if (code === 1) {
            emitError("interactive_required", {
              source: "web_ios_direct",
              code,
              requiresUserGesture: true,
              softDenied: true,
            });
          } else if (code === 2)
            emitError("position_unavailable", {
              source: "web_ios_direct",
              code,
            });
          else if (code === 3)
            emitError("timeout", { source: "web_ios_direct", code });
          else
            emitError("unknown", {
              source: "web_ios_direct",
              code,
              raw: err?.message || null,
            });
        },
        {
          enableHighAccuracy: true,
          timeout: 15000,
          maximumAge: forceFresh ? 0 : 5000,
        },
      );

      return true;
    };

    if (tryDirectIOSSafariGestureRequest()) {
      return;
    }

    if (isNative) {
      try {
        const mod = await import("@capacitor/geolocation");
        const { Geolocation } = mod;

        const perms = await Geolocation.checkPermissions();
        if (perms.location !== "granted") {
          const req = await Geolocation.requestPermissions({
            permissions: ["location"],
          });
          if (req.location !== "granted") {
            emitError("permission_denied", { source: "native" });
            return;
          }
        }

        const pos = await Geolocation.getCurrentPosition({
          enableHighAccuracy: true,
          timeout: 18000,
          maximumAge: forceFresh ? 0 : 10000,
        });

        emitFound(pos.coords.latitude, pos.coords.longitude, {
          accuracy: pos.coords.accuracy,
          source: "native",
        });
        return;
      } catch (e) {
        console.warn(
          "[map] native geolocation failed; falling back to browser path",
          e,
        );
      }
    }

    const cached = readCachedBrowserLocation();

    if (!isLikelySecureBrowserContext()) {
      if (cached) {
        emitFound(cached.lat, cached.lng, {
          accuracy: cached.accuracy,
          source: "cache",
          usedFallback: true,
        });
        return;
      }

      emitError("insecure_context", { source: "web" });
      return;
    }

    const navGeo = window.navigator?.geolocation;
    if (!navGeo) {
      if (cached) {
        emitFound(cached.lat, cached.lng, {
          accuracy: cached.accuracy,
          source: "cache",
          usedFallback: true,
        });
        return;
      }

      emitError("unsupported", { source: "web" });
      return;
    }

    const getPosition = (options: PositionOptions) =>
      new Promise<GeolocationPosition>((resolve, reject) => {
        navGeo.getCurrentPosition(resolve, reject, options);
      });

    // iPhone Safari requires a direct user gesture before it will reliably
    // expose live GPS. Other web browsers can resolve location during boot,
    // so do not block their automatic first-area search.
    if (!allowLivePrompt && isIOSSafariWeb) {
      if (cached) {
        emitFound(cached.lat, cached.lng, {
          accuracy: cached.accuracy,
          source: "cache_passive",
          usedFallback: true,
        });
        return;
      }

      emitError("interactive_required", {
        source: "web_ios_passive",
        requiresUserGesture: true,
      });
      return;
    }

    const cachedIsFreshEnough = !!(
      cached &&
      !forceFresh &&
      !isIOSSafariWeb &&
      Date.now() - cached.ts <= BROWSER_GEO_SOFT_STALE_MS
    );

    if (cachedIsFreshEnough) {
      emitFound(cached!.lat, cached!.lng, {
        accuracy: cached!.accuracy,
        source: "cache_recent",
        usedFallback: true,
      });
      maybeStartBrowserWatch();
      return;
    }

    let resolved = false;
    let watchIdForWarmup: number | null = null;
    let firstError: any = null;
    let secondError: any = null;

    const settleFromPosition = (
      pos: GeolocationPosition,
      source: string,
      usedFallback = false,
    ) => {
      if (resolved) return;
      resolved = true;
      try {
        if (watchIdForWarmup != null) navGeo.clearWatch(watchIdForWarmup);
      } catch {}
      emitFound(pos.coords.latitude, pos.coords.longitude, {
        accuracy: pos.coords.accuracy,
        source,
        usedFallback,
      });
      maybeStartBrowserWatch();
    };

    const settleFromError = (err: any) => {
      if (resolved) return;
      resolved = true;
      try {
        if (watchIdForWarmup != null) navGeo.clearWatch(watchIdForWarmup);
      } catch {}

      if (cached) {
        emitFound(cached.lat, cached.lng, {
          accuracy: cached.accuracy,
          source: "cache",
          usedFallback: true,
        });
        maybeStartBrowserWatch();
        return;
      }

      const code = Number(
        err?.code ?? secondError?.code ?? firstError?.code ?? 0,
      );
      if (code === 1) emitError("permission_denied", { source: "web", code });
      else if (code === 2)
        emitError("position_unavailable", { source: "web", code });
      else if (code === 3) emitError("timeout", { source: "web", code });
      else
        emitError("unknown", {
          source: "web",
          code,
          raw:
            err?.message || secondError?.message || firstError?.message || null,
        });
    };

    try {
      if (warmWatch && !isIOSSafariWeb) {
        watchIdForWarmup = navGeo.watchPosition(
          (pos) => settleFromPosition(pos, "gps_watch_warmup", false),
          (err) => {
            if (!firstError) firstError = err;
          },
          {
            enableHighAccuracy: isLikelyIOSSafari(),
            timeout: 18000,
            maximumAge: 0,
          },
        );
      }
    } catch (watchErr) {
      console.warn("[geo] warmup watch unavailable", watchErr);
      watchIdForWarmup = null;
    }

    try {
      const precise = await getPosition({
        enableHighAccuracy: true,
        timeout: isLikelyIOSSafari() ? 18000 : 12000,
        maximumAge: forceFresh ? 0 : 5000,
      });
      settleFromPosition(precise, "gps", false);
      return;
    } catch (primaryErr: any) {
      firstError = primaryErr;
      console.warn("[geo] high-accuracy attempt failed", primaryErr);
    }

    try {
      const relaxed = await getPosition({
        enableHighAccuracy: false,
        timeout: 12000,
        maximumAge: forceFresh ? 0 : 120000,
      });
      settleFromPosition(relaxed, "gps_relaxed", true);
      return;
    } catch (fallbackErr: any) {
      secondError = fallbackErr;
      console.warn("[geo] relaxed attempt failed", fallbackErr);
    }

    window.setTimeout(
      () => {
        if (!resolved) settleFromError(secondError || firstError || null);
      },
      isLikelyIOSSafari() ? 3500 : 1200,
    );
  })();

  try {
    await geoRequestInFlight;
  } finally {
    geoRequestInFlight = null;
  }
};

// ---------------------------------------------------------------------------
// OFFLINE MAPS SETUP (IndexedDB)
// ---------------------------------------------------------------------------
const DB_NAME = "roddo-offline-maps";
const DB_VERSION = 1;
const TILE_STORE = "tiles";
const REGION_STORE = "regions";

const isIndexedDBAvailable = () => {
  try {
    return typeof indexedDB !== "undefined" && !!indexedDB.open;
  } catch {
    return false;
  }
};

const isIdbClosingError = (err: unknown) => {
  const name = String((err as any)?.name || "");
  const msg = String((err as any)?.message || err || "");
  return (
    name === "InvalidStateError" ||
    msg.includes("database connection is closing") ||
    msg.includes("IDBDatabase") ||
    msg.includes("InvalidStateError")
  );
};

const idb = {
  _dbPromise: null as Promise<IDBDatabase> | null,
  _disabled: false,

  reset(err?: unknown) {
    if (err && isIdbClosingError(err)) {
      console.warn(
        "[offline-maps] IndexedDB connection was closing; reopening next use.",
        err,
      );
    }
    this._dbPromise = null;
  },

  openDB(): Promise<IDBDatabase> {
    if (this._disabled || !isIndexedDBAvailable()) {
      return Promise.reject(new Error("IndexedDB unavailable"));
    }

    if (this._dbPromise) return this._dbPromise;

    this._dbPromise = new Promise((resolve, reject) => {
      let request: IDBOpenDBRequest;

      try {
        request = indexedDB.open(DB_NAME, DB_VERSION);
      } catch (err) {
        this.reset(err);
        reject(err);
        return;
      }

      request.onerror = () => {
        const err = request.error || new Error("Error opening offline maps DB");
        this.reset(err);
        reject(err);
      };

      request.onblocked = () => {
        console.warn(
          "[offline-maps] IndexedDB open blocked by another connection.",
        );
      };

      request.onsuccess = () => {
        const dbi = request.result;
        dbi.onversionchange = () => {
          try {
            dbi.close();
          } catch {}
          this.reset();
        };
        resolve(dbi);
      };

      request.onupgradeneeded = (event) => {
        const dbi = (event.target as any).result as IDBDatabase;
        if (!dbi.objectStoreNames.contains(TILE_STORE)) {
          dbi.createObjectStore(TILE_STORE);
        }
        if (!dbi.objectStoreNames.contains(REGION_STORE)) {
          dbi.createObjectStore(REGION_STORE, { keyPath: "name" });
        }
      };
    });

    return this._dbPromise;
  },

  async withStore<T>(
    storeName: string,
    mode: IDBTransactionMode,
    run: (store: IDBObjectStore) => IDBRequest<T>,
  ): Promise<T> {
    const dbi = await this.openDB();

    return new Promise<T>((resolve, reject) => {
      let tx: IDBTransaction;
      let request: IDBRequest<T>;

      try {
        tx = dbi.transaction(storeName, mode);
        const store = tx.objectStore(storeName);
        request = run(store);
      } catch (err) {
        this.reset(err);
        reject(err);
        return;
      }

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
      tx.onabort = () =>
        reject(
          tx.error ||
            request.error ||
            new Error("IndexedDB transaction aborted"),
        );
      tx.onerror = () =>
        reject(
          tx.error ||
            request.error ||
            new Error("IndexedDB transaction failed"),
        );
    }).catch((err) => {
      if (isIdbClosingError(err)) this.reset(err);
      throw err;
    });
  },

  async get(storeName: string, key: IDBValidKey): Promise<any> {
    return this.withStore(storeName, "readonly", (store) => store.get(key));
  },

  async set(storeName: string, key: IDBValidKey, value: any): Promise<void> {
    await this.withStore(storeName, "readwrite", (store) =>
      value?.name && storeName === REGION_STORE
        ? store.put(value)
        : store.put(value, key),
    );
  },

  async getAll(storeName: string): Promise<any[]> {
    return this.withStore(storeName, "readonly", (store) => store.getAll());
  },

  async delete(storeName: string, key: IDBValidKey): Promise<void> {
    await this.withStore(storeName, "readwrite", (store) => store.delete(key));
  },
};

let OfflineTileLayer: any = null;

const initOfflineTileLayer = () => {
  OfflineTileLayer = (L as any).TileLayer.extend({
    createTile(this: any, coords: any, done: any) {
      const tile = document.createElement("img");

      tile.alt = "";
      tile.setAttribute("role", "presentation");

      // Do not tell Leaflet a tile is ready until the browser has actually
      // loaded/decoded it. Calling `done()` immediately after assigning src
      // lets Leaflet prune the previous zoom's tiles too early, which exposes
      // the bare map background during fast pan/zoom.
      let completed = false;
      let objectUrl: string | null = null;
      const finish = (err?: Error | null) => {
        if (completed) return;
        completed = true;
        if (objectUrl) {
          const revoke = objectUrl;
          objectUrl = null;
          window.setTimeout(() => {
            try { URL.revokeObjectURL(revoke); } catch {}
          }, 0);
        }
        done(err || null, tile);
      };
      tile.onload = () => finish(null);
      tile.onerror = () => finish(new Error("Tile error"));

      const tileUrl = this.getTileUrl(coords);

      const basemapKey = this.options.basemapId || "hot";
      const tileKey = `tile-${basemapKey}-${coords.z}-${coords.x}-${coords.y}`;

      idb
        .get(TILE_STORE, tileKey)
        .then((blob: Blob) => {
          if (blob) {
            objectUrl = URL.createObjectURL(blob);
            tile.src = objectUrl;
          } else {
            fetch(tileUrl)
              .then((response) => {
                if (!response.ok)
                  throw new Error(`Tile fetch failed: ${response.statusText}`);
                return response.blob();
              })
              .then((blob2) => {
                idb
                  .set(TILE_STORE, tileKey, blob2)
                  .catch((err) =>
                    console.warn(
                      "[offline-maps] Tile cache write skipped.",
                      err,
                    ),
                  );
                objectUrl = URL.createObjectURL(blob2);
                tile.src = objectUrl;
              })
              .catch((err) => {
                console.warn(`Could not fetch/save tile: ${tileUrl}`, err);
                if (this.options.errorTileUrl) {
                  tile.src = this.options.errorTileUrl;
                } else {
                  finish(err instanceof Error ? err : new Error(String(err || "Tile error")));
                }
              });
          }
        })
        .catch((err) => {
          console.warn(
            "[offline-maps] DB unavailable for tile; falling back to network tile.",
            err,
          );
          tile.src = tileUrl;
        });

      return tile;
    },
  });
};

type BigBlueFishIQReturnSession = {
  key: string;
  center: LatLng;
  zoom: number;
  anchor: LatLng | null;
  fishWidgetOpen: boolean;
  fishWidgetMode: "history" | "area";
  fishWidgetAreaSource: "viewport" | "selected";
  fishAreaExpanded: boolean;
  selectedFishId: string | null;
  selectedSpeciesKey: string | null;
  fullDetailOpen: boolean;
};

// ---------------------------------------------------------------------------
// PROPS
// ---------------------------------------------------------------------------
interface MapViewProps {
  currentUser?: UserProfile;
  onEnterRestrictedZone?: (zone: RestrictedZone) => void;
  isDarkMode: boolean;
  isActive?: boolean;
  catchLog?: LogEntry[];
  userSpots?: Record<string, UserSpot>;
  onSpotAdded?: (spot: Omit<UserSpot, "id">) => void;
  onSpotRated?: (spotId: string, rating: Rating) => void;
  onSpotCommentAdded?: (spotId: string, comment: Omit<Comment, "id">) => void;
  onUpgradeToPro?: () => void;
  onProfileClick?: () => void;
  onCommunityClick?: () => void;
  onCrewClick?: (payload?: { crewId?: string; from?: string }) => void;

  onLogCatchClick?: (payload?: {
    coords?: { lat: number; lon: number } | null;
    species?: string;
    prefilledData?: {
      species?: string;
      [key: string]: any;
    } | null;
    [key: string]: any;
  }) => void;
  onOpenCatchHistory?: (payload?: {
    targetId?: string | null;
    catchId?: string | null;
    source?: "log" | "diary" | "all";
    species?: string | null;
  }) => void;
  onTackleBoxClick?: () => void;
  onEventsClick?: (payload?: { from?: string }) => void;
  onTournamentClick?: (tournamentId: string) => void;
  onChallengesClick?: () => void;
  marketplaceItems?: Record<string, any> | Record<number, any>;
  onBazaarClick?: (payload?: {
    itemId?: string;
    locationQuery?: string;
    locationKey?: string;
    itemIds?: string[];
    from?: string;
  }) => void;
  onFishIQClick?: (payload?: any) => void;
  onBitePlanClick?: () => void;
  onToggleNotifications?: () => void;
  onOpenGlobe?: (payload?: { lat: number; lng: number; zoom?: number }) => void;

  viewState?: {
    targetId?: string;
    restoreFishIQSession?: BigBlueFishIQReturnSession | null;
    globeTarget?: {
      lat: number;
      lng?: number;
      lon?: number;
      zoom?: number;
      label?: string;
    };
  };
  locationName?: string | null;

  weatherData: WeatherData | null;
  isLoadingWeather: boolean;
  tempUnit: "Celsius" | "Fahrenheit";

  isOfflinePanelOpen?: boolean;
  onCloseOfflinePanel?: () => void;
  onAchievementsClick?: () => void;

  // OLD wiring
  userProfile?: UserProfile;
  coords?: { lat: number; lon: number } | null;
  setCoords?: (c: { lat: number; lon: number }) => void;

  addToast?: (message: string, type?: ToastType) => void;

  tournaments?: any[];
  lastLoggedCatchCoords?: { lat: number; lng: number } | null;
  onBootProgress?: (state: { progress: number; detail: string; ready: boolean }) => void;
  bootDependenciesReady?: {
    weather?: boolean;
    tournaments?: boolean;
  };

  [key: string]: any;
}

// ---------------------------------------------------------------------------
// helpers
// ---------------------------------------------------------------------------
type LatLng = { lat: number; lng: number };

type MyCatchMapEntry = {
  id: string;
  species: string;
  location: string;
  timestamp: number;
  lat: number;
  lng: number;
  weight?: string;
  note?: string;
  imageUrl?: string | null;
  raw?: any;
};

const toMillisSafe = (value: any): number => {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (value && typeof value.toMillis === "function") {
    try {
      const millis = value.toMillis();
      if (typeof millis === "number" && Number.isFinite(millis)) return millis;
    } catch {}
  }
  if (value && typeof value.toDate === "function") {
    try {
      const dt = value.toDate();
      if (dt instanceof Date && Number.isFinite(dt.getTime()))
        return dt.getTime();
    } catch {}
  }
  if (value instanceof Date && Number.isFinite(value.getTime()))
    return value.getTime();
  if (typeof value === "string" && value.trim()) {
    const parsed = Date.parse(value);
    if (!Number.isNaN(parsed)) return parsed;
  }
  return 0;
};

const toCatchTimestamp = (entry: any): number => {
  const direct =
    toMillisSafe(entry?.timestamp) ||
    toMillisSafe(entry?.createdAt) ||
    toMillisSafe(entry?.updatedAt);
  if (direct > 0) return direct;

  const datePart = typeof entry?.date === "string" ? entry.date.trim() : "";
  const timePart = typeof entry?.time === "string" ? entry.time.trim() : "";
  if (datePart) {
    const parsed = Date.parse(`${datePart}T${timePart || "00:00"}`);
    if (!Number.isNaN(parsed)) return parsed;
  }

  return 0;
};

const getCatchImageUrl = (entry: any): string | null => {
  const first = [
    entry?.imageUrl,
    entry?.image,
    Array.isArray(entry?.imageUrls) ? entry.imageUrls[0] : null,
    Array.isArray(entry?.images) ? entry.images[0] : null,
  ].find((value) => typeof value === "string" && value.trim());

  return typeof first === "string" ? first : null;
};

const USER_AVATAR_FALLBACK =
  "data:image/svg+xml;utf8," +
  encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="96" height="96">
       <rect width="96" height="96" rx="24" fill="#cbd5e1"/>
       <circle cx="48" cy="38" r="16" fill="#94a3b8"/>
       <path d="M20 86c6-18 20-26 28-26s22 8 28 26" fill="#94a3b8"/>
     </svg>`,
  );

const getProfileAvatarBaseUrl = (profile: any): string => {
  const first = [
    profile?.avatarUrl,
    profile?.avatar,
    profile?.photoURL,
    profile?.photoUrl,
    profile?.profilePhotoUrl,
    profile?.imageUrl,
  ].find((value) => typeof value === "string" && value.trim());

  return typeof first === "string" ? first.trim() : "";
};

const getProfileAvatarVersion = (profile: any): string => {
  const versionSource =
    profile?.avatarUpdatedAt ??
    profile?.photoUpdatedAt ??
    profile?.profilePhotoUpdatedAt ??
    profile?.updatedAt ??
    profile?.profileUpdatedAt;

  const millis = toMillisSafe(versionSource);
  if (millis > 0) return String(millis);

  const url = getProfileAvatarBaseUrl(profile);
  const pathMatch = url.match(/avatar[-_](\d+)/i);
  if (pathMatch?.[1]) return pathMatch[1];

  return "";
};

const withAvatarCacheBust = (url: string, version: string): string => {
  const clean = String(url || "").trim();
  if (!clean) return "";
  if (!version || clean.startsWith("data:")) return clean;
  return `${clean}${clean.includes("?") ? "&" : "?"}v=${encodeURIComponent(version)}`;
};

const getProfileAvatarUrl = (profile: any): string => {
  const base = getProfileAvatarBaseUrl(profile);
  if (!base) return USER_AVATAR_FALLBACK;
  return withAvatarCacheBust(base, getProfileAvatarVersion(profile));
};

const toCatchLatLng = (entry: any): LatLng | null => {
  const lat = Number(entry?.lat ?? entry?.coords?.lat ?? entry?.position?.lat);
  const lng = Number(
    entry?.lng ??
      entry?.lon ??
      entry?.coords?.lng ??
      entry?.coords?.lon ??
      entry?.position?.lng ??
      entry?.position?.lon,
  );
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  return { lat, lng };
};

const normalizeCatchMapEntries = (entries: any[]): MyCatchMapEntry[] => {
  return (Array.isArray(entries) ? entries : [])
    .map((entry: any) => {
      const latLng = toCatchLatLng(entry);
      if (!latLng) return null;

      const timestamp = toCatchTimestamp(entry);
      return {
        id: String(entry?.id ?? `catch_${timestamp || Date.now()}`),
        species:
          String(entry?.species ?? entry?.speciesName ?? "Catch").trim() ||
          "Catch",
        location:
          String(
            entry?.location ?? entry?.locationName ?? "Logged catch",
          ).trim() || "Logged catch",
        timestamp,
        lat: latLng.lat,
        lng: latLng.lng,
        weight: typeof entry?.weight === "string" ? entry.weight.trim() : "",
        note:
          (typeof entry?.notes === "string" && entry.notes.trim()) ||
          (typeof entry?.note === "string" && entry.note.trim()) ||
          (typeof entry?.caption === "string" && entry.caption.trim()) ||
          undefined,
        imageUrl: getCatchImageUrl(entry),
        raw: entry,
      } as MyCatchMapEntry;
    })
    .filter(Boolean)
    .sort(
      (a: any, b: any) => Number(b?.timestamp || 0) - Number(a?.timestamp || 0),
    ) as MyCatchMapEntry[];
};

const HudIconBase: React.FC<{
  className?: string;
  style?: React.CSSProperties;
  children: React.ReactNode;
  viewBox?: string;
}> = ({ className = "", style, children, viewBox = "0 0 24 24" }) => (
  <svg
    viewBox={viewBox}
    aria-hidden="true"
    className={className}
    style={{
      display: "block",
      color: "currentColor",
      transition:
        "color 180ms ease, filter 180ms ease, opacity 180ms ease, transform 180ms ease",
      ...style,
    }}
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    {children}
  </svg>
);

const HudTrophyIcon: React.FC<{
  className?: string;
  style?: React.CSSProperties;
}> = ({ className, style }) => (
  <HudIconBase className={className} style={style}>
    <path
      d="M8 4.75H16V8.5C16 10.7091 14.2091 12.5 12 12.5C9.79086 12.5 8 10.7091 8 8.5V4.75Z"
      fill="currentColor"
      fillOpacity="0.18"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinejoin="round"
    />
    <path
      d="M8 6H5.75C5.33579 6 5 6.33579 5 6.75V7.5C5 9.433 6.567 11 8.5 11"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M16 6H18.25C18.6642 6 19 6.33579 19 6.75V7.5C19 9.433 17.433 11 15.5 11"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M12 12.5V16"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
    />
    <path
      d="M9 19H15"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
    />
    <path
      d="M9.5 16H14.5"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
    />
  </HudIconBase>
);

const HudFlagIcon: React.FC<{
  className?: string;
  style?: React.CSSProperties;
}> = ({ className, style }) => (
  <HudIconBase className={className} style={style}>
    <path
      d="M6.25 20V4.75"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
    />
    <path
      d="M7.25 5.5H16.6728C17.5474 5.5 18.0947 6.44723 17.6564 7.20408L16.0027 10.0605C15.8208 10.3747 15.8208 10.7613 16.0027 11.0755L17.6564 13.9319C18.0947 14.6888 17.5474 15.636 16.6728 15.636H7.25V5.5Z"
      fill="currentColor"
      fillOpacity="0.18"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinejoin="round"
    />
    <path
      d="M9.75 8.75H14.25"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
      opacity="0.9"
    />
  </HudIconBase>
);

type SavedSpotKind = "favorite" | "fish" | "food" | "launch" | "sunset";
type SavedSpotFilter = "all" | SavedSpotKind;

type SavedSpotRecord = {
  id: string;
  name: string;
  kind: SavedSpotKind;
  lat: number;
  lng: number;
  createdAt: number;
  clientCreatedAt?: number;
  note?: string;
};

const SAVED_SPOT_KIND_META: Record<
  SavedSpotKind,
  { label: string; emoji: string; accent: string; accentDark: string }
> = {
  favorite: {
    label: "Favorite",
    emoji: "⭐",
    accent: "#ffb84d",
    accentDark: "#ffd37a",
  },
  fish: {
    label: "Fish Spot",
    emoji: "🐟",
    accent: "#4f9cff",
    accentDark: "#8fc7ff",
  },
  food: {
    label: "Food Spot",
    emoji: "🍜",
    accent: "#ff8a4c",
    accentDark: "#ffb07a",
  },
  launch: {
    label: "Launch",
    emoji: "🛶",
    accent: "#6ac7ff",
    accentDark: "#8ed8ff",
  },
  sunset: {
    label: "Sunset",
    emoji: "🌅",
    accent: "#ff8f70",
    accentDark: "#ffb198",
  },
};

const getSavedSpotDefaultName = (kind: SavedSpotKind) => {
  const label =
    SAVED_SPOT_KIND_META[kind]?.label || SAVED_SPOT_KIND_META.favorite.label;
  return label.toLowerCase().includes("spot") ? label : `${label} spot`;
};

const SAVED_SPOT_FILTER_STORAGE_KEY = "roddo.savedSpots.filter.v1";

const normalizeSavedSpotKind = (value: any): SavedSpotKind => {
  const v = String(value || "favorite")
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, "_");

  if (
    v === "fish" ||
    v === "fishing" ||
    v === "fishspot" ||
    v === "fish_spot" ||
    v === "fishing_spot"
  )
    return "fish";
  if (
    v === "food" ||
    v === "restaurant" ||
    v === "eat" ||
    v === "meal" ||
    v === "foodspot" ||
    v === "food_spot"
  )
    return "food";
  if (
    v === "launch" ||
    v === "boat" ||
    v === "ramp" ||
    v === "boat_launch" ||
    v === "boatlaunch"
  )
    return "launch";
  if (
    v === "sunset" ||
    v === "view" ||
    v === "scenic" ||
    v === "photo" ||
    v === "picture"
  )
    return "sunset";
  return "favorite";
};

const inferSavedSpotKindFromLabel = (value: any): SavedSpotKind | null => {
  const v = String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, "_");

  if (!v) return null;
  if (
    v === "fish" ||
    v === "fish_spot" ||
    v === "fishing_spot" ||
    v.includes("fish_spot")
  )
    return "fish";
  if (
    v === "food" ||
    v === "food_spot" ||
    v === "restaurant" ||
    v.includes("food_spot")
  )
    return "food";
  if (v === "launch" || v === "boat_launch" || v.includes("launch"))
    return "launch";
  if (v === "sunset" || v === "scenic" || v.includes("sunset")) return "sunset";
  return null;
};

const resolveSavedSpotKind = (raw: any): SavedSpotKind => {
  const explicit = normalizeSavedSpotKind(
    raw?.kind ?? raw?.type ?? raw?.category ?? raw?.spotType,
  );
  if (explicit !== "favorite") return explicit;

  return (
    inferSavedSpotKindFromLabel(raw?.kindLabel) ??
    inferSavedSpotKindFromLabel(raw?.label) ??
    inferSavedSpotKindFromLabel(raw?.name) ??
    inferSavedSpotKindFromLabel(raw?.title) ??
    explicit
  );
};

const toStableSavedSpotTime = (value: any): number | null => {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") {
    const parsed = Date.parse(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  if (value && typeof value.toMillis === "function") {
    const millis = Number(value.toMillis());
    return Number.isFinite(millis) ? millis : null;
  }
  if (value && typeof value.seconds === "number") {
    const millis =
      Number(value.seconds) * 1000 +
      Math.floor(Number(value.nanoseconds || 0) / 1000000);
    return Number.isFinite(millis) ? millis : null;
  }
  return null;
};

const normalizeSavedSpotRecord = (
  raw: any,
  fallbackId?: string,
): SavedSpotRecord | null => {
  const lat = Number(raw?.lat ?? raw?.position?.lat);
  const lng = Number(
    raw?.lng ?? raw?.lon ?? raw?.position?.lng ?? raw?.position?.lon,
  );
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;

  const id = String(raw?.id ?? fallbackId ?? "").trim();
  if (!id) return null;

  const kind = resolveSavedSpotKind(raw);
  const meta = SAVED_SPOT_KIND_META[kind];
  const name =
    String(raw?.name || raw?.title || meta.label).trim() || meta.label;
  const clientCreatedAt =
    toStableSavedSpotTime(raw?.clientCreatedAt) ??
    toStableSavedSpotTime(raw?.createdAt) ??
    toStableSavedSpotTime(raw?.savedAt) ??
    0;

  return {
    id,
    name,
    kind,
    lat,
    lng,
    note: typeof raw?.note === "string" ? raw.note : "",
    createdAt: clientCreatedAt,
    clientCreatedAt,
  };
};

const getSavedSpotCollectionSignature = (spots: SavedSpotRecord[]) =>
  spots
    .map((spot) =>
      [
        spot.id,
        spot.name,
        spot.kind,
        Number(spot.lat).toFixed(6),
        Number(spot.lng).toFixed(6),
        String(spot.note || ""),
        String(Number(spot.clientCreatedAt ?? spot.createdAt ?? 0) || 0),
      ].join("~"),
    )
    .join("|");

const normalizeSavedSpotsCollection = (input: any): SavedSpotRecord[] => {
  if (!Array.isArray(input)) return [];
  return input
    .map((item, idx) =>
      normalizeSavedSpotRecord(item, item?.id ?? `saved_${idx}`),
    )
    .filter(Boolean) as SavedSpotRecord[];
};

const getDistanceInMeters = (
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number,
) => {
  const R = 6371e3;
  const φ1 = (lat1 * Math.PI) / 180;
  const φ2 = (lat2 * Math.PI) / 180;
  const Δφ = ((lat2 - lat1) * Math.PI) / 180;
  const Δλ = ((lon2 - lon1) * Math.PI) / 180;

  const a =
    Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
    Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return R * c;
};

const distanceMeters = (a: LatLng, b: LatLng) =>
  getDistanceInMeters(a.lat, a.lng, b.lat, b.lng);

const uniqueFishListCountSafe = (spots: Hotspot[]): number => {
  const seen = new Set<string>();
  for (const spot of Array.isArray(spots) ? spots : []) {
    const speciesData = (spot as any)?.fishIQSpeciesData || {
      id: (spot as any)?.id,
      commonName: (spot as any)?.species || (spot as any)?.name,
    };
    const key = getFishIQPresentationIdentityKey(speciesData);
    if (key) seen.add(key);
  }
  return seen.size;
};

const normalizeWrappedLongitude = (value: any): number | null => {
  const raw = Number(value);
  if (!Number.isFinite(raw)) return null;

  // Leaflet allows horizontal world wrapping, so panning across a world copy
  // can legitimately produce longitudes such as -287.4 or 432.6. Normalize
  // those display coordinates back to the canonical [-180, 180] range before
  // sending them to Search Lures / FishIQ / persistence.
  const wrapped = ((((raw + 180) % 360) + 360) % 360) - 180;
  return Object.is(wrapped, -0) ? 0 : wrapped;
};

const normalizeSearchLureLatLng = (value: any): LatLng | null => {
  const lat = Number(value?.lat);
  const lng = normalizeWrappedLongitude(value?.lng ?? value?.lon);

  if (
    !Number.isFinite(lat) ||
    lng == null ||
    lat < -90 ||
    lat > 90
  ) {
    return null;
  }

  return { lat, lng };
};

const MAP_ACTION_ANCHOR_STORAGE_VERSION = 1;
const mapActionAnchorStorageKey = (uid?: string | null) =>
  `roddo.bigBlue.mapActionAnchor.v${MAP_ACTION_ANCHOR_STORAGE_VERSION}.${String(uid || 'guest').trim() || 'guest'}`;

const readMapActionAnchor = (uid?: string | null): LatLng | null => {
  try {
    const raw = window.localStorage.getItem(mapActionAnchorStorageKey(uid));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const lat = Number(parsed?.lat);
    const lng = normalizeWrappedLongitude(parsed?.lng ?? parsed?.lon);
    if (!Number.isFinite(lat) || lng == null || lat < -90 || lat > 90) return null;
    return { lat, lng };
  } catch {
    return null;
  }
};

const writeMapActionAnchor = (uid: string | null | undefined, anchor: LatLng | null) => {
  try {
    const key = mapActionAnchorStorageKey(uid);
    if (!anchor) {
      window.localStorage.removeItem(key);
      return;
    }
    window.localStorage.setItem(key, JSON.stringify({ lat: anchor.lat, lng: anchor.lng }));
  } catch {}
};

// ---------------------------------------------------------------------------
// SEARCHED FISH HYDRATION
// ---------------------------------------------------------------------------
// Paid/manual area reads should survive navigation away from Big Blue.
// This cache is presentation/state hydration only: it never spends or refunds
// Search Lures. The server remains authoritative for whether a later manual
// search is a reused area.
const FISH_HYDRATION_CACHE_VERSION = 1;
const FISH_HYDRATION_CACHE_MAX_AGE_MS = 12 * 60 * 60 * 1000;
const FISH_HYDRATION_MAX_HOTSPOTS = FISH_AREA_EXPANDED_LIST_LIMIT;

type FishHydrationCache = {
  version: number;
  savedAt: number;
  uid: string;
  center: LatLng;
  zoom: number;
  anchor: LatLng | null;
  hotspots: Hotspot[];
};

const fishHydrationStorageKey = (uid: string) =>
  `roddo.bigBlue.fishHydration.v${FISH_HYDRATION_CACHE_VERSION}.${uid || "guest"}`;

const isFiniteLatLng = (value: any): value is LatLng =>
  Number.isFinite(Number(value?.lat)) && Number.isFinite(Number(value?.lng));

const readFishHydrationCache = (uid: string): FishHydrationCache | null => {
  const cleanUid = String(uid || "").trim();
  if (!cleanUid) return null;

  try {
    const raw = window.localStorage.getItem(fishHydrationStorageKey(cleanUid));
    if (!raw) return null;

    const parsed = JSON.parse(raw) as FishHydrationCache;
    const age = Date.now() - Number(parsed?.savedAt || 0);
    const hotspots = Array.isArray(parsed?.hotspots)
      ? parsed.hotspots.filter((spot: any) => isFiniteLatLng(spot?.position))
      : [];

    if (
      parsed?.version !== FISH_HYDRATION_CACHE_VERSION ||
      parsed?.uid !== cleanUid ||
      age < 0 ||
      age > FISH_HYDRATION_CACHE_MAX_AGE_MS ||
      !isFiniteLatLng(parsed?.center) ||
      !hotspots.length
    ) {
      return null;
    }

    return {
      version: FISH_HYDRATION_CACHE_VERSION,
      savedAt: Number(parsed.savedAt),
      uid: cleanUid,
      center: {
        lat: Number(parsed.center.lat),
        lng: Number(parsed.center.lng),
      },
      zoom: Math.max(MAP_MIN_ZOOM, Math.min(MAP_MAX_ZOOM, Number(parsed.zoom || 13))),
      anchor: isFiniteLatLng(parsed?.anchor)
        ? { lat: Number(parsed.anchor.lat), lng: Number(parsed.anchor.lng) }
        : null,
      hotspots: hotspots.slice(0, FISH_HYDRATION_MAX_HOTSPOTS),
    };
  } catch {
    return null;
  }
};

const writeFishHydrationCache = (cache: FishHydrationCache) => {
  try {
    window.localStorage.setItem(
      fishHydrationStorageKey(cache.uid),
      JSON.stringify({
        ...cache,
        hotspots: cache.hotspots.slice(0, FISH_HYDRATION_MAX_HOTSPOTS),
      }),
    );
  } catch {
    // Storage can be unavailable in private/restricted webviews. In-memory
    // refs still keep fish hydrated while MapView remains mounted.
  }
};

const hashString = (input: string) => {
  let h = 2166136261;
  for (let i = 0; i < input.length; i += 1) {
    h ^= input.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
};

const mulberry32 = (seed: number) => {
  let t = seed >>> 0;
  return () => {
    t += 0x6d2b79f5;
    let x = Math.imul(t ^ (t >>> 15), 1 | t);
    x ^= x + Math.imul(x ^ (x >>> 7), 61 | x);
    return ((x ^ (x >>> 14)) >>> 0) / 4294967296;
  };
};

const makeSeededRandom = (...parts: Array<string | number>) =>
  mulberry32(hashString(parts.map((p) => String(p)).join("|")));

const pickFromPool = (
  pool: string[],
  rand: () => number,
  used: Set<string>,
) => {
  if (!pool.length) return "Fish";
  const fresh = pool.filter((item) => !used.has(item));
  const source = fresh.length ? fresh : pool;
  const next = source[Math.floor(rand() * source.length)] || pool[0] || "Fish";
  used.add(next);
  return next;
};

const inferGeoProfile = (anchor: LatLng, locationName?: string | null) => {
  const hint = String(locationName || "").toLowerCase();
  const freshwaterByName =
    /(lake|reservoir|pond|river|creek|stream|fork|branch|run|brook|slough|tailwater|canal|ditch|drain|freshwater|fresh water|waterway|inland|dam|basin|wetland)/i.test(
      hint,
    );
  const coastalByName =
    /(bay|harbor|harbour|ocean|sea|beach|surf|coast|coastal|pier|marina|inlet|sound|gulf|salt|keys|delta|estuary|atlantic|pacific|lagoon|strait|mangrove|port|nearshore)/i.test(
      hint,
    );
  const riverByName =
    /(river|creek|stream|fork|branch|run|brook|slough|tailwater)/i.test(hint);
  const lakeByName = /(lake|reservoir|pond|dam|basin|wetland)/i.test(hint);

  const lat = Number(anchor.lat || 0);
  const lng = Number(anchor.lng || 0);

  type RoddoUSMapRegion =
    | "us_socal"
    | "california_coast"
    | "pacific_northwest"
    | "mountain_west"
    | "southwest_desert"
    | "great_lakes"
    | "midwest_inland"
    | "texas_reservoirs"
    | "gulf_coast"
    | "florida_inshore"
    | "southeast_atlantic"
    | "southeast_inland"
    | "northeast_us"
    | "alaska"
    | "hawaii"
    | "vietnam"
    | "singapore"
    | "malaysia"
    | "thailand"
    | "philippines"
    | "indonesia"
    | "india_inland"
    | "india_coastal"
    | "japan_inshore"
    | "generic_us";

  const inBox = (
    minLat: number,
    maxLat: number,
    minLng: number,
    maxLng: number,
  ) => lat >= minLat && lat <= maxLat && lng >= minLng && lng <= maxLng;

  let region: RoddoUSMapRegion = "generic_us";

  // Country precedence matters in Southeast Asia: broad Thailand/Indonesia
  // boxes overlap nearby neighbors. Check compact countries first so Vietnam
  // and Singapore do not get swallowed by adjacent regional packs.
  if (inBox(1.12, 1.55, 103.55, 104.15)) region = "singapore";
  else if (inBox(8.0, 23.7, 102.0, 109.8)) region = "vietnam";
  else if (inBox(0.8, 7.8, 99.0, 119.5)) region = "malaysia";
  else if (inBox(5.4, 20.6, 97.0, 106.2)) region = "thailand";
  else if (inBox(4.0, 21.5, 116.0, 127.5)) region = "philippines";
  else if (inBox(-11.5, 6.5, 94.0, 142.5)) region = "indonesia";
  else if (inBox(24.0, 46.5, 122.0, 146.5)) region = "japan_inshore";
  else if (
    inBox(6.0, 24.5, 68.0, 90.5) &&
    (coastalByName || lng <= 78.4 || lng >= 80.0 || lat <= 11.5)
  )
    region = "india_coastal";
  else if (inBox(6.0, 35.8, 68.0, 97.5)) region = "india_inland";
  else if (inBox(51.0, 72.5, -170.5, -129.0)) region = "alaska";
  else if (inBox(18.5, 22.8, -161.0, -154.5)) region = "hawaii";
  else if (inBox(32.0, 35.95, -121.0, -114.0)) region = "us_socal";
  else if (inBox(35.4, 42.2, -124.8, -118.0)) region = "california_coast";
  else if (inBox(42.0, 49.3, -125.5, -116.0)) region = "pacific_northwest";
  else if (inBox(24.0, 31.2, -87.8, -79.6)) region = "florida_inshore";
  else if (inBox(25.0, 31.6, -98.8, -81.0)) region = "gulf_coast";
  else if (inBox(30.0, 36.8, -82.2, -75.0)) region = "southeast_atlantic";
  else if (inBox(41.0, 49.2, -93.5, -74.0)) region = "great_lakes";
  else if (inBox(38.5, 47.8, -80.5, -66.0)) region = "northeast_us";
  else if (inBox(25.5, 36.7, -106.8, -93.3)) region = "texas_reservoirs";
  else if (inBox(31.0, 38.5, -118.8, -106.0)) region = "southwest_desert";
  else if (inBox(31.0, 49.3, -116.5, -102.0)) region = "mountain_west";
  else if (inBox(29.0, 37.2, -94.5, -75.0)) region = "southeast_inland";
  else if (inBox(36.0, 49.3, -104.2, -82.0)) region = "midwest_inland";

  const roughCoastAnchors: Array<{
    lat: number;
    lng: number;
    radiusKm: number;
  }> = [
    { lat: 34.02, lng: -118.5, radiusKm: 38 }, // LA coast / Santa Monica
    { lat: 33.77, lng: -118.19, radiusKm: 38 }, // Long Beach / LA harbor
    { lat: 33.61, lng: -117.9, radiusKm: 34 }, // Newport / Orange coast
    { lat: 32.72, lng: -117.17, radiusKm: 40 }, // San Diego Bay
    { lat: 37.78, lng: -122.42, radiusKm: 48 }, // SF Bay
    { lat: 36.6, lng: -121.9, radiusKm: 42 }, // Monterey Bay
    { lat: 45.55, lng: -122.72, radiusKm: 34 }, // Lower Columbia / Portland-ish
    { lat: 47.6, lng: -122.33, radiusKm: 52 }, // Puget Sound / Seattle
    { lat: 27.78, lng: -82.66, radiusKm: 56 }, // Tampa Bay
    { lat: 25.77, lng: -80.19, radiusKm: 50 }, // Miami / Biscayne
    { lat: 29.76, lng: -95.0, radiusKm: 62 }, // Galveston / Houston coast
    { lat: 29.95, lng: -90.06, radiusKm: 62 }, // Louisiana delta / NOLA
    { lat: 30.69, lng: -88.04, radiusKm: 52 }, // Mobile Bay
    { lat: 32.78, lng: -79.93, radiusKm: 48 }, // Charleston
    { lat: 34.2, lng: -77.8, radiusKm: 48 }, // NC coast
    { lat: 40.7, lng: -74.01, radiusKm: 48 }, // NYC harbor
    { lat: 42.36, lng: -71.05, radiusKm: 42 }, // Boston harbor
    { lat: 21.31, lng: -157.86, radiusKm: 60 }, // Oahu
  ];

  const nearKnownCoast = roughCoastAnchors.some(
    (c) => getDistanceInMeters(lat, lng, c.lat, c.lng) <= c.radiusKm * 1000,
  );

  const explicitFreshwaterByName =
    freshwaterByName || riverByName || lakeByName;

  // Asia fallback should be ordered after explicit water context. Island
  // countries can still default coastal when no freshwater signal exists, while
  // mainland SEA/India only become coastal from clear name or coarse near-coast
  // coordinates. This keeps inland lakes/rivers/reservoirs from becoming inshore.
  const islandAsiaDefaultCoastal =
    region === "philippines" ||
    region === "indonesia" ||
    region === "singapore" ||
    region === "japan_inshore";

  const mainlandAsiaLikelyCoastal =
    region === "india_coastal" ||
    (region === "vietnam" && (coastalByName || lng >= 106.6 || lat <= 11.2)) ||
    (region === "malaysia" &&
      (coastalByName ||
        lng <= 100.8 ||
        (lng >= 102.4 && lng <= 104.8) ||
        lng >= 109.0 ||
        lat <= 2.1)) ||
    (region === "thailand" &&
      (coastalByName || lat <= 10.5 || lng <= 99.3 || lng >= 100.7));

  const asiaCoastalByGeo =
    !explicitFreshwaterByName &&
    (coastalByName || islandAsiaDefaultCoastal || mainlandAsiaLikelyCoastal);

  const islandOrAlaskaCoast =
    region === "hawaii" ||
    (region === "alaska" && (coastalByName || nearKnownCoast));

  // Coastal proximity is only a weak mainland hint. A 30-60 km radius around
  // ports/bays was classifying inland rivers and reservoirs as saltwater (for
  // example LA/Pico Rivera and inland San Diego County). Mainland reads now
  // need explicit coastal wording here; exact marine Water Intel may still
  // promote the context later when it has real bathymetry coverage.
  const maybeCoastalByGeo =
    !explicitFreshwaterByName &&
    (coastalByName ||
      islandOrAlaskaCoast ||
      asiaCoastalByGeo);

  const waterKind: "saltwater" | "river" | "lake" | "freshwater" = riverByName
    ? "river"
    : lakeByName
      ? "lake"
      : maybeCoastalByGeo
        ? "saltwater"
        : region === "great_lakes"
          ? "lake"
          : "freshwater";

  return { region, waterKind, hint };
};

const getSpeciesPoolForProfile = (
  profile: ReturnType<typeof inferGeoProfile>,
) => {
  const freshwaterRegional: Record<string, string[]> = {
    us_socal: [
      "Largemouth Bass",
      "Rainbow Trout",
      "Channel Catfish",
      "Bluegill",
      "Common Carp",
      "Striped Bass",
    ],
    california_coast: [
      "Rainbow Trout",
      "Largemouth Bass",
      "Smallmouth Bass",
      "Channel Catfish",
      "Common Carp",
      "Bluegill",
    ],
    pacific_northwest: [
      "Rainbow Trout",
      "Cutthroat Trout",
      "Smallmouth Bass",
      "Kokanee Salmon",
      "Chinook Salmon",
      "Steelhead",
    ],
    mountain_west: [
      "Cutthroat Trout",
      "Rainbow Trout",
      "Brown Trout",
      "Kokanee Salmon",
      "Mountain Whitefish",
      "Walleye",
    ],
    southwest_desert: [
      "Striped Bass",
      "Largemouth Bass",
      "Channel Catfish",
      "Flathead Catfish",
      "Common Carp",
      "Bluegill",
    ],
    great_lakes: [
      "Walleye",
      "Yellow Perch",
      "Smallmouth Bass",
      "Lake Trout",
      "Northern Pike",
      "Chinook Salmon",
    ],
    midwest_inland: [
      "Walleye",
      "Largemouth Bass",
      "Smallmouth Bass",
      "Crappie",
      "Bluegill",
      "Channel Catfish",
    ],
    texas_reservoirs: [
      "Largemouth Bass",
      "Guadalupe Bass",
      "White Bass",
      "Striped Bass",
      "Blue Catfish",
      "Crappie",
    ],
    southeast_inland: [
      "Largemouth Bass",
      "Spotted Bass",
      "Shoal Bass",
      "Crappie",
      "Bluegill",
      "Blue Catfish",
    ],
    northeast_us: [
      "Smallmouth Bass",
      "Largemouth Bass",
      "Chain Pickerel",
      "Yellow Perch",
      "Rainbow Trout",
      "Walleye",
    ],
    alaska: [
      "Chinook Salmon",
      "Coho Salmon",
      "Sockeye Salmon",
      "Rainbow Trout",
      "Arctic Grayling",
      "Arctic Char",
    ],
    hawaii: [
      "Peacock Bass",
      "Tilapia",
      "Common Carp",
      "Catfish",
      "Oscar",
      "Bluegill",
    ],
    vietnam: [
      "Striped Snakehead",
      "Giant Snakehead",
      "Barramundi",
      "Hampala Barb",
      "Rohu",
      "Nile Tilapia",
    ],
    singapore: [
      "Barramundi",
      "Mangrove Jack",
      "Giant Trevally",
      "Rabbitfish",
      "Tilapia",
      "Common Carp",
    ],
    malaysia: [
      "Giant Snakehead",
      "Hampala Barb",
      "Mahseer",
      "Barramundi",
      "Mangrove Jack",
      "Giant Trevally",
    ],
    thailand: [
      "Giant Snakehead",
      "Striped Snakehead",
      "Barramundi",
      "Hampala Barb",
      "Rohu",
      "Nile Tilapia",
    ],
    philippines: [
      "Striped Snakehead",
      "Barramundi",
      "Tilapia",
      "Common Carp",
      "Freshwater Catfish",
      "Mudfish",
    ],
    indonesia: [
      "Hampala Barb",
      "Mahseer",
      "Giant Snakehead",
      "Patin Catfish",
      "Nile Tilapia",
    ],
    india_inland: [
      "Mahseer",
      "Rohu",
      "Mrigal Carp",
      "Snakehead",
      "Common Carp",
      "Freshwater Catfish",
    ],
    india_coastal: [
      "Barramundi",
      "Mangrove Jack",
      "Giant Trevally",
      "Queenfish",
      "Cobia",
      "Narrow-barred Spanish Mackerel",
    ],
    japan_inshore: [
      "Japanese Seabass",
      "Black Porgy",
      "Red Seabream",
      "Japanese Flounder",
      "Black Rockfish",
      "Chub Mackerel",
    ],
    generic_us: [
      "Largemouth Bass",
      "Smallmouth Bass",
      "Bluegill",
      "Channel Catfish",
      "Crappie",
      "Common Carp",
    ],
  };

  const riverRegional: Record<string, string[]> = {
    us_socal: [
      "Rainbow Trout",
      "Largemouth Bass",
      "Channel Catfish",
      "Common Carp",
      "Bluegill",
      "Striped Bass",
    ],
    california_coast: [
      "Steelhead",
      "Rainbow Trout",
      "Smallmouth Bass",
      "Channel Catfish",
      "Common Carp",
      "Striped Bass",
    ],
    pacific_northwest: [
      "Steelhead",
      "Chinook Salmon",
      "Coho Salmon",
      "Cutthroat Trout",
      "Smallmouth Bass",
      "Walleye",
    ],
    mountain_west: [
      "Cutthroat Trout",
      "Brown Trout",
      "Rainbow Trout",
      "Mountain Whitefish",
      "Arctic Grayling",
      "Smallmouth Bass",
    ],
    southwest_desert: [
      "Striped Bass",
      "Channel Catfish",
      "Flathead Catfish",
      "Largemouth Bass",
      "Common Carp",
      "Rainbow Trout",
    ],
    great_lakes: [
      "Walleye",
      "Smallmouth Bass",
      "Yellow Perch",
      "Northern Pike",
      "Freshwater Drum",
      "Channel Catfish",
    ],
    midwest_inland: [
      "Walleye",
      "Smallmouth Bass",
      "Channel Catfish",
      "Flathead Catfish",
      "Freshwater Drum",
      "Common Carp",
    ],
    texas_reservoirs: [
      "Guadalupe Bass",
      "Striped Bass",
      "White Bass",
      "Blue Catfish",
      "Flathead Catfish",
      "Largemouth Bass",
    ],
    southeast_inland: [
      "Shoal Bass",
      "Redeye Bass",
      "Largemouth Bass",
      "Blue Catfish",
      "Flathead Catfish",
      "Bowfin",
    ],
    northeast_us: [
      "Striped Bass",
      "Smallmouth Bass",
      "Walleye",
      "Chain Pickerel",
      "Yellow Perch",
      "Rainbow Trout",
    ],
    alaska: [
      "Chinook Salmon",
      "Coho Salmon",
      "Sockeye Salmon",
      "Rainbow Trout",
      "Arctic Grayling",
      "Dolly Varden",
    ],
    hawaii: [
      "Peacock Bass",
      "Tilapia",
      "Catfish",
      "Oscar",
      "Common Carp",
      "Bluegill",
    ],
    vietnam: [
      "Striped Snakehead",
      "Giant Snakehead",
      "Hampala Barb",
      "Rohu",
      "Mrigal Carp",
      "Nile Tilapia",
    ],
    singapore: [
      "Barramundi",
      "Mangrove Jack",
      "Tilapia",
      "Common Carp",
      "Freshwater Catfish",
      "Rabbitfish",
    ],
    malaysia: [
      "Giant Snakehead",
      "Hampala Barb",
      "Mahseer",
      "Barramundi",
      "Patin Catfish",
      "Nile Tilapia",
    ],
    thailand: [
      "Giant Snakehead",
      "Striped Snakehead",
      "Hampala Barb",
      "Mahseer",
      "Rohu",
      "Mrigal Carp",
    ],
    philippines: [
      "Striped Snakehead",
      "Barramundi",
      "Freshwater Catfish",
      "Tilapia",
      "Common Carp",
      "Mudfish",
    ],
    indonesia: [
      "Hampala Barb",
      "Mahseer",
      "Giant Snakehead",
      "Patin Catfish",
      "Barramundi",
    ],
    india_inland: [
      "Mahseer",
      "Rohu",
      "Mrigal Carp",
      "Snakehead",
      "Freshwater Catfish",
      "Common Carp",
    ],
    india_coastal: [
      "Barramundi",
      "Threadfin",
      "Mangrove Jack",
      "Giant Trevally",
      "Queenfish",
      "Snapper",
    ],
    japan_inshore: [
      "Japanese Seabass",
      "Japanese Flounder",
      "Black Porgy",
      "Red Seabream",
      "Black Rockfish",
      "Chub Mackerel",
    ],
    generic_us: [
      "Smallmouth Bass",
      "Channel Catfish",
      "Flathead Catfish",
      "Common Carp",
      "Walleye",
      "Largemouth Bass",
    ],
  };

  const saltRegional: Record<string, string[]> = {
    us_socal: [
      "California Halibut",
      "Spotted Bay Bass",
      "Kelp Bass",
      "Barred Sand Bass",
      "Surfperch",
      "White Seabass",
    ],
    california_coast: [
      "California Halibut",
      "Surfperch",
      "Lingcod",
      "Rockfish",
      "Cabezon",
      "White Seabass",
    ],
    pacific_northwest: [
      "Chinook Salmon",
      "Coho Salmon",
      "Lingcod",
      "Rockfish",
      "Cabezon",
      "Surfperch",
    ],
    gulf_coast: [
      "Redfish",
      "Speckled Trout",
      "Flounder",
      "Red Snapper",
      "Black Drum",
      "Sheepshead",
    ],
    florida_inshore: [
      "Snook",
      "Tarpon",
      "Redfish",
      "Speckled Trout",
      "Mangrove Snapper",
      "Pompano",
    ],
    southeast_atlantic: [
      "Redfish",
      "Speckled Trout",
      "Flounder",
      "Black Drum",
      "Spanish Mackerel",
      "Cobia",
    ],
    northeast_us: [
      "Striped Bass",
      "Bluefish",
      "Summer Flounder",
      "Black Sea Bass",
      "Tautog",
      "Scup",
    ],
    alaska: [
      "Pacific Halibut",
      "Lingcod",
      "Rockfish",
      "Chinook Salmon",
      "Coho Salmon",
      "Cod",
    ],
    hawaii: [
      "Mahi-mahi",
      "Yellowfin Tuna",
      "Skipjack Tuna",
      "Wahoo",
      "Giant Trevally",
      "Blue Marlin",
    ],
    vietnam: [
      "Barramundi",
      "Mangrove Jack",
      "Giant Trevally",
      "Queenfish",
      "Cobia",
      "Spanish Mackerel",
    ],
    singapore: [
      "Barramundi",
      "Mangrove Jack",
      "Giant Trevally",
      "Rabbitfish",
      "Grouper",
      "Snapper",
    ],
    malaysia: [
      "Barramundi",
      "Mangrove Jack",
      "Giant Trevally",
      "Queenfish",
      "Dogtooth Tuna",
      "Grouper",
    ],
    thailand: [
      "Barramundi",
      "Mangrove Jack",
      "Cobia",
      "Giant Trevally",
      "Queenfish",
      "Spanish Mackerel",
    ],
    philippines: [
      "Giant Trevally",
      "Dogtooth Tuna",
      "Yellowfin Tuna",
      "Wahoo",
      "Mangrove Jack",
      "Rabbitfish",
    ],
    indonesia: [
      "Dogtooth Tuna",
      "Giant Trevally",
      "Yellowfin Tuna",
      "Wahoo",
      "Mangrove Jack",
      "Grouper",
    ],
    india_inland: [
      "Mahseer",
      "Rohu",
      "Mrigal Carp",
      "Snakehead",
      "Common Carp",
      "Freshwater Catfish",
    ],
    india_coastal: [
      "Barramundi",
      "Mangrove Jack",
      "Giant Trevally",
      "Queenfish",
      "Cobia",
      "Spanish Mackerel",
    ],
    japan_inshore: [
      "Japanese Seabass",
      "Black Porgy",
      "Red Seabream",
      "Japanese Flounder",
      "Chub Mackerel",
      "Yellowtail Amberjack",
    ],
    generic_us: [
      "Striped Bass",
      "Bluefish",
      "Flounder",
      "Redfish",
      "Spanish Mackerel",
      "Black Sea Bass",
    ],
  };

  if (profile.waterKind === "saltwater")
    return saltRegional[profile.region] || saltRegional.generic_us;
  if (profile.waterKind === "river")
    return riverRegional[profile.region] || riverRegional.generic_us;
  return freshwaterRegional[profile.region] || freshwaterRegional.generic_us;
};

const uniqueSpeciesPool = (...groups: Array<string[] | undefined | null>) => {
  const seen = new Set<string>();
  const out: string[] = [];

  for (const group of groups) {
    for (const raw of group || []) {
      const name = String(raw || "").trim();
      const key = name.toLowerCase();
      if (!name || seen.has(key)) continue;
      seen.add(key);
      out.push(name);
    }
  }

  return out;
};

const getExpandedSpeciesPoolForProfile = (
  profile: ReturnType<typeof inferGeoProfile>,
) => {
  const base = getSpeciesPoolForProfile(profile);

  const freshwaterExtras = [
    "Largemouth Bass",
    "Smallmouth Bass",
    "Spotted Bass",
    "Striped Bass",
    "White Bass",
    "Rainbow Trout",
    "Brown Trout",
    "Channel Catfish",
    "Blue Catfish",
    "Flathead Catfish",
    "Bluegill",
    "Redear Sunfish",
    "Black Crappie",
    "White Crappie",
    "Common Carp",
    "Walleye",
    "Yellow Perch",
    "Northern Pike",
    "Freshwater Drum",
    "Tilapia",
  ];

  const riverExtras = [
    "Smallmouth Bass",
    "Rainbow Trout",
    "Brown Trout",
    "Steelhead",
    "Chinook Salmon",
    "Coho Salmon",
    "Channel Catfish",
    "Flathead Catfish",
    "Common Carp",
    "Striped Bass",
    "White Bass",
    "Walleye",
    "Freshwater Drum",
    "Northern Pike",
    "Bluegill",
    "Crappie",
    "Mountain Whitefish",
    "Cutthroat Trout",
    "Arctic Grayling",
    "Bowfin",
  ];

  const saltwaterExtras = [
    "Barred Sand Bass",
    "Spotted Bay Bass",
    "Kelp Bass",
    "California Halibut",
    "Surfperch",
    "White Seabass",
    "Yellowfin Croaker",
    "California Corbina",
    "Leopard Shark",
    "Bat Ray",
    "Opaleye",
    "California Sheephead",
    "Rockfish",
    "Lingcod",
    "Cabezon",
    "Pacific Bonito",
    "Pacific Mackerel",
    "Jacksmelt",
    "Sculpin",
    "Shovelnose Guitarfish",
    "Pacific Barracuda",
    "Queenfish",
    "Cobia",
    "Spanish Mackerel",
    "Giant Trevally",
  ];

  const southeastAsiaFreshwaterExtras = [
    "Giant Snakehead",
    "Striped Snakehead",
    "Hampala Barb",
    "Mahseer",
    "Rohu",
    "Mrigal Carp",
    "Patin Catfish",
    "Nile Tilapia",
    "Common Carp",
    "Freshwater Catfish",
    "Mudfish",
  ];

  const southeastAsiaSaltwaterExtras = [
    "Barramundi",
    "Mangrove Jack",
    "Giant Trevally",
    "Queenfish",
    "Cobia",
    "Narrow-barred Spanish Mackerel",
    "Grouper",
    "Snapper",
    "Rabbitfish",
    "Dogtooth Tuna",
    "Yellowfin Tuna",
    "Wahoo",
    "Threadfin",
  ];

  const asiaRegion =
    /vietnam|singapore|malaysia|thailand|philippines|indonesia|india|japan/.test(
      profile.region,
    );

  if (profile.waterKind === "saltwater") {
    return uniqueSpeciesPool(
      base,
      asiaRegion ? southeastAsiaSaltwaterExtras : null,
      saltwaterExtras,
    );
  }

  if (profile.waterKind === "river") {
    return uniqueSpeciesPool(
      base,
      asiaRegion ? southeastAsiaFreshwaterExtras : null,
      riverExtras,
      freshwaterExtras,
    );
  }

  return uniqueSpeciesPool(
    base,
    asiaRegion ? southeastAsiaFreshwaterExtras : null,
    freshwaterExtras,
  );
};

// ---------------------------------------------------------------------------
// component
// ---------------------------------------------------------------------------
const MapView: React.FC<MapViewProps> = (props) => {
  const {
    currentUser,
    userProfile,
    onEnterRestrictedZone,
    isDarkMode,
    weatherData,
    isLoadingWeather,
    tempUnit,
    isOfflinePanelOpen,
    onCloseOfflinePanel,
    viewState,
  } = props;

  const profile: UserProfile | undefined = currentUser ?? userProfile;
  const [searchLureClockMs, setSearchLureClockMs] = useState(() => Date.now());
  const [localSearchLureBalance, setLocalSearchLureBalance] = useState<number | null>(null);
  const searchLureWallet = getSearchLureWalletPreview(profile as any, searchLureClockMs);
  const displayedSearchLureBalance =
    localSearchLureBalance ?? searchLureWallet.balance;
  const searchLureRefillLabel = getSearchLureRefillLabel(
    {
      ...searchLureWallet,
      balance: displayedSearchLureBalance,
      storedBalance: localSearchLureBalance ?? searchLureWallet.storedBalance,
      isAtOrAboveFreeCap:
        displayedSearchLureBalance >= SEARCH_LURE_FREE_REGEN_CAP,
    },
    searchLureClockMs,
  );
  const compactSearchLureRefillLabel = useMemo(() => {
    if (displayedSearchLureBalance >= SEARCH_LURE_FREE_REGEN_CAP) return "";
    return String(searchLureRefillLabel || "")
      .replace(/^\+1 free lure in\s*/i, "")
      .replace(/^\+1 every\s*/i, "")
      .trim();
  }, [displayedSearchLureBalance, searchLureRefillLabel]);
  const { t } = useTranslation();

  useEffect(() => {
    const timer = window.setInterval(
      () => setSearchLureClockMs(Date.now()),
      60_000,
    );
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    setLocalSearchLureBalance(null);
  }, [
    (profile as any)?.searchLures,
    (profile as any)?.searchLureRegenAnchorAtMs,
  ]);

  const userAvatarUrl = useMemo(
    () => getProfileAvatarUrl(profile as any),
    [profile],
  );
  const userCosmeticSignature = useMemo(() => {
    const loadout = (profile as any)?.cosmeticLoadout || {};
    const updatedAt = Number((profile as any)?.cosmeticLoadoutUpdatedAt || 0);
    try {
      return `${updatedAt}:${JSON.stringify(loadout)}`;
    } catch {
      return `${updatedAt}`;
    }
  }, [(profile as any)?.cosmeticLoadout, (profile as any)?.cosmeticLoadoutUpdatedAt]);

  const userAvatarSignature = useMemo(() => {
    const uid = String((profile as any)?.uid || "self");
    return `${uid}:${userAvatarUrl}:${userCosmeticSignature}`;
  }, [profile?.uid, userAvatarUrl, userCosmeticSignature]);

  const [searchQuery, setSearchQuery] = useState("");
  const [hotspots, setHotspots] = useState<Hotspot[]>([]);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [isSearchingArea, setIsSearchingArea] = useState(false);
  const [isSearchingSavedArea, setIsSearchingSavedArea] = useState(false);
  const [fishIQThinkingError, setFishIQThinkingError] = useState<string | null>(null);
  const [fishIQRetryAnchor, setFishIQRetryAnchor] = useState<LatLng | null>(null);
  const [fishIQRetryMode, setFishIQRetryMode] = useState<"selected" | "viewport" | null>(null);
  const [currentMapZoom, setCurrentMapZoom] = useState(13);
  const [crewPresence, setCrewPresence] = useState<CrewPresence[]>([]);
  const initialTournamentDiscoveryPrefsRef = useRef<TournamentDiscoveryPreferences | null>(null);
  if (!initialTournamentDiscoveryPrefsRef.current) {
    initialTournamentDiscoveryPrefsRef.current = loadTournamentDiscoveryPreferences();
  }
  const [tournamentMapFilters, setTournamentMapFilters] = useState<TournamentDiscoveryFilters>(
    () => initialTournamentDiscoveryPrefsRef.current?.filters || DEFAULT_TOURNAMENT_DISCOVERY_FILTERS,
  );
  const [isTournamentMapFilterOpen, setIsTournamentMapFilterOpen] = useState(false);
  const [isWorldTournamentMapOpen, setIsWorldTournamentMapOpen] = useState(false);
  const [selectedTournamentMapDetail, setSelectedTournamentMapDetail] = useState<any | null>(null);
  useEffect(() => {
    if (!props.isActive) {
      setSelectedTournamentMapDetail(null);
    }
  }, [props.isActive]);

  const [hideAllTournamentMapMarkers, setHideAllTournamentMapMarkers] = useState(
    () => initialTournamentDiscoveryPrefsRef.current?.hideAllMapTournaments === true,
  );
  const updateTournamentMarkersRef = useRef<() => void>(() => {});
  const [myTournamentParticipation, setMyTournamentParticipation] = useState<MyTournamentParticipationState>({});
  const [tournamentMapRegistrationBusyId, setTournamentMapRegistrationBusyId] = useState<string | null>(null);
  const [tournamentResultsSeenRevision, setTournamentResultsSeenRevision] = useState(0);

  useEffect(() => {
    const uid = String((props.userProfile as any)?.uid || '').trim();
    const ids = (Array.isArray(props.tournaments) ? props.tournaments : [])
      .filter((tournament: any) => !['draft', 'rejected', 'cancelled', 'archived'].includes(String(tournament?.status || '').toLowerCase()))
      .map((tournament: any) => String(tournament?.id || '').trim())
      .filter(Boolean);

    return subscribeMyTournamentParticipationStates(ids, uid, setMyTournamentParticipation);
  }, [props.tournaments, (props.userProfile as any)?.uid]);

  useEffect(() => {
    saveTournamentDiscoveryPreferences({
      filters: tournamentMapFilters,
      hideAllMapTournaments: hideAllTournamentMapMarkers,
    });
  }, [tournamentMapFilters, hideAllTournamentMapMarkers]);

  useEffect(() => {
    const syncTournamentPrefs = (event: Event) => {
      const detail = (event as CustomEvent<TournamentDiscoveryPreferences>)?.detail;
      const next = detail || loadTournamentDiscoveryPreferences();
      setTournamentMapFilters(next.filters);
      setHideAllTournamentMapMarkers(next.hideAllMapTournaments === true);
    };

    window.addEventListener(TOURNAMENT_DISCOVERY_PREFS_EVENT, syncTournamentPrefs);
    return () =>
      window.removeEventListener(TOURNAMENT_DISCOVERY_PREFS_EVENT, syncTournamentPrefs);
  }, []);

  const [offlineRegions, setOfflineRegions] = useState<any[]>([]);
  const [downloadState, setDownloadState] = useState<{
    active: boolean;
    progress: number;
    total: number;
    name: string;
  }>({ active: false, progress: 0, total: 0, name: "" });
  const [regionToDelete, setRegionToDelete] = useState<string | null>(null);

  const [basemapId] = useState<BasemapId>("hot");
  const [isMapVisualReady, setIsMapVisualReady] = useState(false);
  const [mapLayerVisibility, setMapLayerVisibility] =
    useState<BigBlueMapLayerVisibility>(() => loadBigBlueMapLayerVisibility());
  const mapLayerVisibilityRef = useRef<BigBlueMapLayerVisibility>(mapLayerVisibility);
  mapLayerVisibilityRef.current = mapLayerVisibility;
  const previousMapLayerVisibilityRef = useRef<BigBlueMapLayerVisibility>(mapLayerVisibility);

  const mapShellRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<HTMLDivElement>(null);
  const resizeInvalidateRafRef = useRef<number | null>(null);
  const appShellSyncTimerRef = useRef<number | null>(null);
  const fontsReadySyncTimerRef = useRef<number | null>(null);
  const pendingMapUiSyncRafRef = useRef<number | null>(null);
  const mapInstanceRef = useRef<any>(null);
  const fishLayerRef = useRef<any>(null);
  const fishIQLayerControllerRef = useRef<FishIQLayerController | null>(null);
  if (!fishIQLayerControllerRef.current) {
    fishIQLayerControllerRef.current = createFishIQLayerController();
  }
  const fishMarkerHotspotsRef = useRef<Hotspot[]>([]);
  const searchedFishAreaMarkerRef = useRef<any>(null);
  const fishHydrationWriteTimerRef = useRef<number | null>(null);
  const fishHydrationRestoreSeqRef = useRef(0);
  const lastFishIQReturnRestoreKeyRef = useRef<string>("");
  const restoreHydratedFishRef = useRef<
    ((opts?: { restoreViewport?: boolean; settlePasses?: boolean }) => boolean) | null
  >(null);
  const shopLayerRef = useRef<any>(null);
  const bazaarLayerControllerRef = useRef<BazaarLayerController | null>(null);
  if (!bazaarLayerControllerRef.current) {
    bazaarLayerControllerRef.current = createBazaarLayerController();
  }
  const onBazaarClickRef = useRef(props.onBazaarClick);
  onBazaarClickRef.current = props.onBazaarClick;
  const [bazaarMapLayerReady, setBazaarMapLayerReady] = useState(0);
  const [selectedBazaarMapListing, setSelectedBazaarMapListing] = useState<{ id: string; item: any } | null>(null);
  const justLoggedCatchMarkerRef = useRef<any>(null);
  const justLoggedCatchPulseTimerRef = useRef<number | null>(null);
  const justLoggedCatchCleanupTimerRef = useRef<number | null>(null);
  const lastJustLoggedCatchKeyRef = useRef<string>("");
  const savedSpotLayerRef = useRef<any>(null);
  const savedSpotsLayerControllerRef = useRef<SavedSpotsLayerController | null>(null);
  if (!savedSpotsLayerControllerRef.current) {
    savedSpotsLayerControllerRef.current = createSavedSpotsLayerController();
  }
  const crewPresenceLayerRef = useRef<any>(null);
  const crewPresenceLayerControllerRef = useRef<CrewPresenceLayerController | null>(null);
  if (!crewPresenceLayerControllerRef.current) {
    crewPresenceLayerControllerRef.current = createCrewPresenceLayerController();
  }
  const crewPresenceRef = useRef<CrewPresence[]>([]);
  crewPresenceRef.current = crewPresence;
  const tournamentLayerRef = useRef<any>(null);
  const tournamentLayerControllerRef = useRef<TournamentLayerController | null>(null);
  if (!tournamentLayerControllerRef.current) {
    tournamentLayerControllerRef.current = createTournamentLayerController();
  }
  const crewPresenceSessionPulseUidRef = useRef<string | null>(null);
  const crewPresenceTransitionRef = useRef<{ uid: string; online: boolean; at: number } | null>(null);

  const restrictedLayerRef = useRef<any>(null);
  const userMarkerRef = useRef<any>(null);
  const tileLayerRef = useRef<any>(null);
  const mapVisualReadyTimerRef = useRef<number | null>(null);

  const latestUserLatLngRef = useRef<LatLng | null>(null);

  const isDarkModeRef = useRef(isDarkMode);
  isDarkModeRef.current = isDarkMode;

  const isActiveRef = useRef(!!props.isActive);
  isActiveRef.current = !!props.isActive;

  type BigBlueQuickWindowId = "daily" | "crew" | "challenges" | "fishiq";
  const [quickWindow, setQuickWindow] = useState<BigBlueQuickWindowId | null>(null);
  const [quickDailyActivity, setQuickDailyActivity] = useState<DailyActivityState | null>(null);
  const [quickCrewRelationships, setQuickCrewRelationships] = useState<any[]>([]);
  const [quickCrewProfiles, setQuickCrewProfiles] = useState<Record<string, any>>({});
  const [quickGroupCrews, setQuickGroupCrews] = useState<CrewChannel[]>([]);
  const [quickCrewMode, setQuickCrewMode] = useState<"summary" | "find" | "dm">("summary");
  const [quickCrewSearch, setQuickCrewSearch] = useState("");
  const [quickCrewSearchBusyUid, setQuickCrewSearchBusyUid] = useState<string | null>(null);
  const [quickCrewDmUid, setQuickCrewDmUid] = useState("");
  const [quickCrewDmInbox, setQuickCrewDmInbox] = useState<CrewDMInboxRow[]>([]);
  const [quickCrewPresenceOptIn, setQuickCrewPresenceOptIn] = useState(() =>
    isCrewPresenceOptedIn(((profile as any)?.notificationSettings || {}) as Record<string, any>),
  );
  const [quickCrewPresenceSaving, setQuickCrewPresenceSaving] = useState(false);
  const [quickChallengeProgress, setQuickChallengeProgress] = useState<Record<string, BackendChallengeProgress>>({});
  const [quickChallengeCompletions, setQuickChallengeCompletions] = useState<Record<string, BackendChallengeCompletion>>({});
  const [quickFishIQSearch, setQuickFishIQSearch] = useState("");

  const [isWeatherOpen, setIsWeatherOpen] = useState(false);
  const programmaticMoveRef = useRef(false);
  const onOpenGlobeRef = useRef<MapViewProps["onOpenGlobe"]>(props.onOpenGlobe);
  onOpenGlobeRef.current = props.onOpenGlobe;
  const initialOverlayDoneRef = useRef(false);
  const hasUserMovedMapRef = useRef(false);
  const lastRefreshViewportRef = useRef<{
    center: LatLng;
    zoom: number;
  } | null>(null);
  const pendingUserMoveRef = useRef(false);
  const initialUserAnchoredSearchDoneRef = useRef(false);
  const initialUserAnchoredSearchPendingRef = useRef(false);
  const hasHandledFirstActiveSyncRef = useRef(false);
  const bootRequestStartedRef = useRef(false);
  const [mapBootReady, setMapBootReady] = useState(false);
  const [mapBootProgress, setMapBootProgress] = useState(12);
  const [mapBootDetail, setMapBootDetail] = useState("Preparing Big Blue…");
  const [initialFishBootSettled, setInitialFishBootSettled] = useState(false);
  const [userAnchorBootReady, setUserAnchorBootReady] = useState(false);
  const userAnchorBootReadyRef = useRef(false);
  userAnchorBootReadyRef.current = userAnchorBootReady;
  const [crewRelationshipsBootReady, setCrewRelationshipsBootReady] = useState(false);
  const [crewProfilesBootReady, setCrewProfilesBootReady] = useState(false);
  const [crewGroupsBootReady, setCrewGroupsBootReady] = useState(false);
  const [crewInboxBootReady, setCrewInboxBootReady] = useState(false);
  const [crewPresenceBootReady, setCrewPresenceBootReady] = useState(false);
  const [dailyBootReady, setDailyBootReady] = useState(false);
  const [challengesBootReady, setChallengesBootReady] = useState(false);
  const mapBootReadyRef = useRef(false);
  mapBootReadyRef.current = mapBootReady;

  useEffect(() => {
    props.onBootProgress?.({
      progress: mapBootReady ? 100 : mapBootProgress,
      detail: mapBootDetail,
      ready: mapBootReady,
    });
  }, [mapBootDetail, mapBootProgress, mapBootReady, props.onBootProgress]);

  // GPS -> FishIQ bridge.
  // Boot performs one quiet user-anchored read when GPS/cache resolves.
  // Recenter requests another user-anchored read after the fresh location lands.
  const pendingRecenterFishRefreshRef = useRef(false);
  const handleUserAnchoredFishRefreshRef = useRef<
    ((options?: { isInitial?: boolean; spendSearchLure?: boolean }) => void | Promise<void>) | null
  >(null);
  const [userAnchoredFishSearchRequest, setUserAnchoredFishSearchRequest] = useState<{
    id: number;
    isInitial: boolean;
  } | null>(null);
  const userAnchoredFishSearchRequestIdRef = useRef(0);
  const processedUserAnchoredFishSearchRequestIdRef = useRef(0);


  const hasGeoErrorToastRef = useRef(false);
  const hasArmedSafariFirstTapGpsRef = useRef(false);
  const hasTriggeredSafariFirstTapGpsRef = useRef(false);
  const didInitialLayoutSettleRef = useRef(false);
  const observedMapSizeRef = useRef<{ width: number; height: number }>({
    width: 0,
    height: 0,
  });
  const initialViewportKickTimersRef = useRef<number[]>([]);

  const [fishIQPrefs] = useState<FishIQOverlayPrefs>(() =>
    loadFishIQPrefs(),
  );
  const [isFishWidgetOpen, setIsFishWidgetOpen] = useState(false);
  const isFishWidgetOpenRef = useRef(false);
  const fishWidgetAreaSourceRef = useRef<"viewport" | "selected">("viewport");
  const [selectedFishSpot, setSelectedFishSpot] = useState<Hotspot | null>(
    null,
  );
  const [isFishFullDetailOpen, setIsFishFullDetailOpen] = useState(false);
  const [fishWidgetMode, setFishWidgetMode] = useState<"history" | "area">(
    "area",
  );
  const [fishWidgetAreaSource, setFishWidgetAreaSource] = useState<
    "viewport" | "selected"
  >("viewport");

  useEffect(() => {
    isFishWidgetOpenRef.current = isFishWidgetOpen;
  }, [isFishWidgetOpen]);

  useEffect(() => {
    const uid = String(profile?.uid || "").trim();
    setDailyBootReady(false);

    if (!uid || !props.isActive) {
      setQuickDailyActivity(null);
      setDailyBootReady(!uid);
      return;
    }

    const preview = getDailyRewardPreview(profile as any);
    return subscribeToDailyActivity(
      uid,
      preview.today,
      (next) => {
        setQuickDailyActivity(next);
        setDailyBootReady(true);
      },
      () => {
        setQuickDailyActivity(null);
        setDailyBootReady(true);
      },
    );
  }, [profile?.uid, props.isActive, (profile as any)?.dailyRewards]);

  useEffect(() => {
    const uid = String(profile?.uid || "").trim();
    setCrewRelationshipsBootReady(false);

    if (!uid) {
      setQuickCrewRelationships([]);
      setCrewRelationshipsBootReady(true);
      return;
    }
    return subscribeToCrewRelationships(
      uid,
      (next) => {
        setQuickCrewRelationships(next || []);
        setCrewRelationshipsBootReady(true);
      },
      () => {
        setQuickCrewRelationships([]);
        setCrewRelationshipsBootReady(true);
      },
    );
  }, [profile?.uid]);

  useEffect(() => {
    const uid = String(profile?.uid || "").trim();
    setCrewGroupsBootReady(false);

    if (!uid) {
      setQuickGroupCrews([]);
      setCrewGroupsBootReady(true);
      return;
    }
    return subscribeGroupCrews(
      uid,
      (next) => {
        setQuickGroupCrews(next || []);
        setCrewGroupsBootReady(true);
      },
      () => {
        setQuickGroupCrews([]);
        setCrewGroupsBootReady(true);
      },
    );
  }, [profile?.uid]);

  useEffect(() => {
    const uid = String(profile?.uid || "").trim();
    setCrewProfilesBootReady(false);

    if (!uid) {
      setQuickCrewProfiles({});
      setCrewProfilesBootReady(true);
      return;
    }
    if (!crewRelationshipsBootReady) return;

    const accepted = quickCrewRelationships.filter(
      (rel: any) => String(rel?.status || "") === "accepted",
    );
    const targetUids = Array.from(
      new Set(
        accepted
          .map((rel: any) => {
            const ids = Array.isArray(rel?.participantIds)
              ? rel.participantIds
              : [rel?.requesterId, rel?.recipientId].filter(Boolean);
            return String(ids.find((id: any) => String(id || "").trim() && String(id) !== uid) || "").trim();
          })
          .filter(Boolean),
      ),
    );

    if (!targetUids.length) {
      setQuickCrewProfiles({});
      setCrewProfilesBootReady(true);
      return;
    }

    let cancelled = false;
    void getCrewMemberProfiles(targetUids)
      .then((profiles) => {
        if (cancelled) return;
        setQuickCrewProfiles(profiles || {});
        setCrewProfilesBootReady(true);
      })
      .catch(() => {
        if (cancelled) return;
        setQuickCrewProfiles({});
        setCrewProfilesBootReady(true);
      });

    return () => {
      cancelled = true;
    };
  }, [profile?.uid, crewRelationshipsBootReady, quickCrewRelationships]);

  useEffect(() => {
    const uid = String(profile?.uid || "").trim();
    setCrewInboxBootReady(false);

    if (!uid) {
      setQuickCrewDmInbox([]);
      setCrewInboxBootReady(true);
      return;
    }
    if (!crewRelationshipsBootReady) return;

    const acceptedDmIds = quickCrewRelationships
      .filter((rel: any) => String(rel?.status || "") === "accepted")
      .map((rel: any) => String(rel?.id || "").trim())
      .filter(Boolean);

    if (!acceptedDmIds.length) {
      setQuickCrewDmInbox([]);
      setCrewInboxBootReady(true);
      return;
    }

    return subscribeCrewDMInbox(
      uid,
      acceptedDmIds,
      (next) => {
        setQuickCrewDmInbox(next || []);
        setCrewInboxBootReady(true);
      },
      (err) => {
        console.warn("[MapView] Crew unread badge subscription warning", err);
        setQuickCrewDmInbox([]);
        setCrewInboxBootReady(true);
      },
    );
  }, [profile?.uid, crewRelationshipsBootReady, quickCrewRelationships]);

  useEffect(() => {
    const uid = String(profile?.uid || "").trim();
    setChallengesBootReady(false);

    if (!uid || !props.isActive) {
      setQuickChallengeProgress({});
      setQuickChallengeCompletions({});
      setChallengesBootReady(!uid);
      return;
    }

    let progressReady = false;
    let completionsReady = false;
    const markReady = () => {
      if (progressReady && completionsReady) setChallengesBootReady(true);
    };

    const unsubProgress = subscribeToChallengeProgress(uid, (next) => {
      setQuickChallengeProgress(next || {});
      progressReady = true;
      markReady();
    });
    const unsubCompletions = subscribeToChallengeCompletions(uid, (next) => {
      setQuickChallengeCompletions(next || {});
      completionsReady = true;
      markReady();
    });

    return () => {
      try { unsubProgress?.(); } catch {}
      try { unsubCompletions?.(); } catch {}
    };
  }, [profile?.uid, props.isActive]);

  useEffect(() => {
    fishWidgetAreaSourceRef.current = fishWidgetAreaSource;
  }, [fishWidgetAreaSource]);
  const [isFishAreaExpanded, setIsFishAreaExpanded] = useState(false);
  const [fishAreaFilter] = useState<"all" | "likely" | "possible">("all");
  const [isMapSearchFocused, setIsMapSearchFocused] = useState(false);
  const [isMapSearchOpen, setIsMapSearchOpen] = useState(false);
  const mapSearchInputRef = useRef<HTMLInputElement | null>(null);
  const [fishWidgetAnchor, setFishWidgetAnchor] = useState<LatLng | null>(null);

  const buildFishIQReturnSession = useCallback((): BigBlueFishIQReturnSession | null => {
    const map = mapInstanceRef.current;
    if (!map) return null;

    try {
      const center = map.getCenter?.();
      const zoom = Number(map.getZoom?.() ?? currentMapZoom);
      if (!center || !Number.isFinite(Number(center.lat)) || !Number.isFinite(Number(center.lng))) {
        return null;
      }

      const selected = selectedFishSpot as any;
      const selectedSpecies = String(
        selected?.fishIQSpeciesData?.scientificName ||
          selected?.fishIQSpeciesData?.commonName ||
          selected?.fishIQSpeciesData?.name ||
          selected?.species ||
          selected?.commonName ||
          selected?.name ||
          "",
      ).trim();

      return {
        key: `fishiq_log_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
        center: { lat: Number(center.lat), lng: Number(center.lng) },
        zoom: Number.isFinite(zoom) ? zoom : currentMapZoom,
        anchor: fishWidgetAnchor
          ? { lat: Number(fishWidgetAnchor.lat), lng: Number(fishWidgetAnchor.lng) }
          : null,
        fishWidgetOpen: isFishWidgetOpen,
        fishWidgetMode,
        fishWidgetAreaSource,
        fishAreaExpanded: isFishAreaExpanded,
        selectedFishId: selected?.id ? String(selected.id) : null,
        selectedSpeciesKey: selectedSpecies ? normalizeSpeciesKey(selectedSpecies) : null,
        fullDetailOpen: isFishFullDetailOpen,
      };
    } catch {
      return null;
    }
  }, [
    currentMapZoom,
    fishWidgetAnchor,
    fishWidgetAreaSource,
    fishWidgetMode,
    isFishAreaExpanded,
    isFishFullDetailOpen,
    isFishWidgetOpen,
    selectedFishSpot,
  ]);

  useEffect(() => {
    if (!isMapSearchOpen) return;
    const timer = window.setTimeout(() => {
      try {
        mapSearchInputRef.current?.focus();
        mapSearchInputRef.current?.select();
      } catch {}
    }, 40);
    return () => window.clearTimeout(timer);
  }, [isMapSearchOpen]);

  const closeMapSearch = useCallback((opts?: { clear?: boolean }) => {
    if (opts?.clear) setSearchQuery("");
    setIsMapSearchFocused(false);
    setIsMapSearchOpen(false);
    try { mapSearchInputRef.current?.blur(); } catch {}
  }, []);

  useEffect(() => {
    saveBigBlueMapLayerVisibility(mapLayerVisibility);
  }, [mapLayerVisibility]);

  const [fishIQAreaSignals, setFishIQAreaSignals] = useState<any[]>([]);
  const fishIQAreaSignalRequestSeqRef = useRef(0);
  const fishIQEnvironmentRequestSeqRef = useRef(0);
  const legacyProfileSavedSpots = useMemo(
    () => normalizeSavedSpotsCollection((profile as any)?.savedSpots ?? []),
    [profile?.uid, (profile as any)?.savedSpots],
  );
  const [savedSpots, setSavedSpots] = useState<SavedSpotRecord[]>([]);
  const [hasLoadedSavedSpots, setHasLoadedSavedSpots] = useState(false);
  const hasAttemptedSavedSpotsMigrationRef = useRef(false);
  const [isSavedSpotsOpen, setIsSavedSpotsOpen] = useState(false);
  const [showSavedSpotsOnMap, setShowSavedSpotsOnMap] = useState(
    () => mapLayerVisibilityRef.current.savedSpots,
  );
  const [isSavedSpotFilterMenuOpen, setIsSavedSpotFilterMenuOpen] =
    useState(false);
  const [savedSpotFilter, setSavedSpotFilter] = useState<SavedSpotFilter>(
    () => {
      try {
        const raw = String(
          window.localStorage.getItem(SAVED_SPOT_FILTER_STORAGE_KEY) || "all",
        )
          .trim()
          .toLowerCase();
        return raw === "favorite" ||
          raw === "fish" ||
          raw === "food" ||
          raw === "launch" ||
          raw === "sunset"
          ? (raw as SavedSpotFilter)
          : "all";
      } catch {
        return "all";
      }
    },
  );
  const [selectedSavedSpotId, setSelectedSavedSpotId] = useState<string | null>(
    null,
  );
  const savedSpotsRef = useRef<SavedSpotRecord[]>([]);
  const showSavedSpotsOnMapRef = useRef(true);
  const savedSpotFilterRef = useRef<SavedSpotFilter>("all");
  const selectedSavedSpotIdRef = useRef<string | null>(null);
  const savedSpotsPanelRef = useRef<HTMLDivElement | null>(null);
  const savedSpotsAnchorRef = useRef<HTMLButtonElement | null>(null);
  const [savedSpotsDesktopAnchor, setSavedSpotsDesktopAnchor] = useState<{
    left: number;
    bottom: number;
    height: number;
  } | null>(null);
  const addToastRef = useRef<MapViewProps["addToast"]>(props.addToast);
  addToastRef.current = props.addToast;
  const pendingSavedSpotSaveRef = useRef<{
    id: string;
    message: string;
    expected: { name: string; kind: SavedSpotKind; lat: number; lng: number; note: string };
  } | null>(null);
  const pendingSavedSpotDeleteRef = useRef<{ id: string; name: string } | null>(
    null,
  );
  const [savingSavedSpotId, setSavingSavedSpotId] = useState<string | null>(
    null,
  );
  const [deletingSavedSpotId, setDeletingSavedSpotId] = useState<string | null>(
    null,
  );
  const [pendingSavedSpotLatLng, setPendingSavedSpotLatLng] =
    useState<LatLng | null>(null);
  // One neutral map-selection anchor for the desktop double-click action card.
  // It is intentionally separate from Saved Spots and FishIQ state so choosing
  // Log Catch/Search FishIQ never leaves the Saved Spots drawer hanging around.
  const [mapActionAnchor, setMapActionAnchor] = useState<LatLng | null>(() =>
    readMapActionAnchor(profile?.uid),
  );
  const [isMapActionCardOpen, setIsMapActionCardOpen] = useState(false);
  const [mapActionWaterRead, setMapActionWaterRead] = useState<any | null>(null);
  const [mapActionWaterLoading, setMapActionWaterLoading] = useState(false);
  const mapActionWaterSeqRef = useRef(0);
  const mapActionMarkerRef = useRef<any>(null);
  // Synchronous marker-ownership refs close the one-render gap between a
  // successful selected-water FishIQ search and React committing the new
  // fishWidgetAnchor. This guarantees that the old standalone FishIQ hub can
  // never survive beside the promoted working-point marker.
  const mapActionAnchorRef = useRef<LatLng | null>(mapActionAnchor);
  mapActionAnchorRef.current = mapActionAnchor;
  const fishWidgetAnchorRef = useRef<LatLng | null>(fishWidgetAnchor);
  fishWidgetAnchorRef.current = fishWidgetAnchor;
  const promotedMapActionFishIQAnchorRef = useRef<LatLng | null>(null);

  // One working-point marker owns both POI and FishIQ presentation. Once the
  // selected POI has a FishIQ read at the same geographic anchor, the marker
  // promotes in place instead of rendering a second FishIQ hub beside it.
  const mapActionFishIQDistanceMeters =
    mapActionAnchor && fishWidgetAnchor
      ? getDistanceInMeters(
          mapActionAnchor.lat,
          mapActionAnchor.lng,
          fishWidgetAnchor.lat,
          fishWidgetAnchor.lng,
        )
      : Number.POSITIVE_INFINITY;
  const mapActionHasFishIQ = Boolean(
    mapActionAnchor &&
      fishWidgetAnchor &&
      Number.isFinite(mapActionFishIQDistanceMeters) &&
      mapActionFishIQDistanceMeters <= 80 &&
      hotspots.length > 0,
  );
  const mapActionFishIQCount = mapActionHasFishIQ
    ? Math.max(1, uniqueFishListCountSafe(hotspots))
    : 0;

  useEffect(() => {
    writeMapActionAnchor(profile?.uid, mapActionAnchor);
    if (!mapActionAnchor) {
      promotedMapActionFishIQAnchorRef.current = null;
      return;
    }

    const currentFishAnchor = fishWidgetAnchorRef.current;
    if (currentFishAnchor) {
      const distance = getDistanceInMeters(
        mapActionAnchor.lat,
        mapActionAnchor.lng,
        currentFishAnchor.lat,
        currentFishAnchor.lng,
      );
      if (Number.isFinite(distance) && distance <= 80) {
        promotedMapActionFishIQAnchorRef.current = currentFishAnchor;
      }
    }
  }, [profile?.uid, mapActionAnchor]);

  useEffect(() => {
    const restored = readMapActionAnchor(profile?.uid);
    setMapActionAnchor(restored);
    setIsMapActionCardOpen(false);
  }, [profile?.uid]);


  useEffect(() => {
    const anchor = mapActionAnchor;
    if (!anchor || !isMapActionCardOpen) {
      if (!anchor) setMapActionWaterRead(null);
      setMapActionWaterLoading(false);
      return;
    }

    const seq = ++mapActionWaterSeqRef.current;
    const request = {
      lat: anchor.lat,
      lng: anchor.lng,
      requestedCapabilities: ["basic_current", "basic_depth", "structure_analysis"] as any,
    };

    const cached = getCachedWaterIntelRead(request);
    if (cached) setMapActionWaterRead(cached);
    setMapActionWaterLoading(!cached);

    void getWaterIntelRead(request, { timeoutMs: 6500, quiet: true })
      .then((next) => {
        if (mapActionWaterSeqRef.current !== seq) return;
        setMapActionWaterRead(next || cached || null);
      })
      .catch(() => {
        if (mapActionWaterSeqRef.current !== seq) return;
        if (!cached) setMapActionWaterRead(null);
      })
      .finally(() => {
        if (mapActionWaterSeqRef.current === seq) setMapActionWaterLoading(false);
      });
  }, [isMapActionCardOpen, mapActionAnchor]);

  const focusMapActionAnchor = useCallback(
    (map: any, anchor: LatLng) => focusMapActionAnchorInViewport(map, anchor),
    [],
  );

  useEffect(() => {
    const map = mapInstanceRef.current;

    if (mapActionMarkerRef.current && map) {
      try { map.removeLayer(mapActionMarkerRef.current); } catch {}
      mapActionMarkerRef.current = null;
    }

    if (!map || !mapActionAnchor) return;

    const fishCountText = mapActionFishIQCount > 99 ? "99+" : String(mapActionFishIQCount || "");
    const promotedFishSize = 42;
    const promotedFishShell = isDarkMode ? "#33436F" : "#2176E8";
    const promotedFishShellDeep = isDarkMode ? "#1B2647" : "#0B5ED1";
    const pinIcon = mapActionHasFishIQ
      ? createBigBlueDivIcon({
          className: "roddo-map-fish-hub-icon roddo-map-action-fishiq",
          html: `<div class="roddo-fish-hub-entry" data-kind="area" style="position:relative;width:${promotedFishSize + 8}px;height:${promotedFishSize + 9}px;overflow:visible;">
            <div style="position:absolute;left:50%;top:1px;width:${promotedFishSize - 5}px;height:${promotedFishSize - 5}px;transform:translateX(-50%) rotate(-45deg);border-radius:50% 50% 50% 10%;background:linear-gradient(145deg,${promotedFishShell} 0%,${promotedFishShellDeep} 78%);border:2px solid ${isDarkMode ? 'rgba(190,211,244,.58)' : 'rgba(255,255,255,.96)'};box-shadow:0 5px 11px rgba(15,23,42,.22);">
              <div style="position:absolute;inset:5px;border-radius:999px;background:${isDarkMode ? '#0D1627' : '#FFFDF7'};border:1px solid ${isDarkMode ? 'rgba(176,202,244,.14)' : 'rgba(255,255,255,.86)'};display:grid;place-items:center;transform:rotate(45deg);overflow:hidden;">
                <img src="${roddoFish}" alt="" draggable="false" style="width:27px;height:27px;object-fit:contain;display:block;transform:translate(-3px,-4px);" />
              </div>
            </div>
            ${fishCountText ? `<div aria-label="${fishCountText} fish found" style="position:absolute;right:0;top:-2px;min-width:18px;height:18px;padding:0 4px;border-radius:999px;display:flex;align-items:center;justify-content:center;background:${isDarkMode ? '#0B1324' : '#FFFFFF'};border:2px solid ${promotedFishShell};color:${isDarkMode ? '#CFE2FF' : promotedFishShellDeep};font-size:9px;font-weight:950;line-height:1;box-sizing:border-box;box-shadow:0 3px 7px rgba(15,23,42,.18);">${fishCountText}</div>` : ""}
          </div>`,
          iconSize: [promotedFishSize + 8, promotedFishSize + 9],
          // Match the visible diamond tip rather than the wrapper's bottom edge.
          // This keeps the promoted FishIQ marker centered on the exact POI pixel.
          iconAnchor: [Math.round((promotedFishSize + 8) / 2), promotedFishSize + 2],
          popupAnchor: [0, -(promotedFishSize - 2)],
        })
      : createBigBlueMapPinIcon(isDarkMode);

    try {
      const marker = L.marker(
        [mapActionAnchor.lat, mapActionAnchor.lng],
        { icon: pinIcon, interactive: true, keyboard: false, zIndexOffset: 1800 },
      ).addTo(map);
      marker.on("click", (event: any) => {
        try { L.DomEvent.stopPropagation(event); } catch {}

        if (mapActionHasFishIQ) {
          // A promoted POI behaves exactly like the canonical FishIQ area hub.
          // The POI is still retained underneath as the working coordinate,
          // but the visible marker now opens the hydrated FishIQ read directly.
          setFishWidgetMode("area");
          setFishWidgetAreaSource("selected");
          setIsFishWidgetOpen(true);
          setSelectedFishSpot(null);
          setIsFishFullDetailOpen(false);
          setIsMapActionCardOpen(false);
          return;
        }

        setIsMapActionCardOpen(true);
        focusMapActionAnchor(map, mapActionAnchor);
      });
      mapActionMarkerRef.current = marker;
    } catch {}

    return () => {
      if (mapActionMarkerRef.current) {
        try { map.removeLayer(mapActionMarkerRef.current); } catch {}
        mapActionMarkerRef.current = null;
      }
    };
  }, [
    focusMapActionAnchor,
    isDarkMode,
    mapActionAnchor,
    mapActionHasFishIQ,
    mapActionFishIQCount,
  ]);

  useEffect(() => {
    if (!mapActionHasFishIQ || !mapActionAnchor) return;
    const map = mapInstanceRef.current;
    if (!map) return;
    const raf = window.requestAnimationFrame(() => {
      focusMapActionAnchor(map, mapActionAnchor);
    });
    return () => window.cancelAnimationFrame(raf);
  }, [focusMapActionAnchor, mapActionAnchor, mapActionHasFishIQ]);

  const [editingSavedSpotId, setEditingSavedSpotId] = useState<string | null>(null);
  const [savedSpotDeleteConfirm, setSavedSpotDeleteConfirm] = useState<SavedSpotRecord | null>(null);
  const [savedSpotDraftName, setSavedSpotDraftName] = useState("");
  const savedSpotNameInputRef = useRef<HTMLInputElement | null>(null);
  const savedSpotDraftNameLiveRef = useRef("");
  const savedSpotDraftNoteLiveRef = useRef("");
  const [savedSpotDraftKind, setSavedSpotDraftKind] =
    useState<SavedSpotKind>("favorite");
  const [savedSpotDraftNote, setSavedSpotDraftNote] = useState("");
  const [isSelectedSavedSpotMenuOpen, setIsSelectedSavedSpotMenuOpen] =
    useState(false);

  // Data stays warm while Big Blue is hidden, but Map-only presentation must be
  // gone before the next route paints. useLayoutEffect prevents body portals,
  // quick windows and HUD drawers from surviving for a single frame.
  useLayoutEffect(() => {
    if (props.isActive) return;

    setQuickWindow(null);
    setQuickCrewMode("summary");
    setQuickCrewSearch("");
    setQuickCrewDmUid("");
    setIsWeatherOpen(false);
    setIsFishWidgetOpen(false);
    setIsFishFullDetailOpen(false);
    setIsMapSearchOpen(false);
    setIsSavedSpotsOpen(false);
    setIsSavedSpotFilterMenuOpen(false);
    setIsSelectedSavedSpotMenuOpen(false);
    setIsTournamentMapFilterOpen(false);
    setIsWorldTournamentMapOpen(false);
    setIsMapActionCardOpen(false);
  }, [props.isActive]);
  const [isSelectedSavedSpotExpanded, setIsSelectedSavedSpotExpanded] =
    useState(true);
  const [isSelectedSavedSpotNoteExpanded, setIsSelectedSavedSpotNoteExpanded] =
    useState(false);
  const hotspotRequestSeqRef = useRef(0);
  const savedSpotLongPressTimerRef = useRef<number | null>(null);
  const savedSpotLongPressStartRef = useRef<{ x: number; y: number } | null>(
    null,
  );
  const uniqueFishListSpots = useMemo(() => {
    const seen = new Set<string>();
    const deduped: Hotspot[] = [];

    for (const spot of hotspots) {
      const speciesData = (spot as any)?.fishIQSpeciesData || {
        id: (spot as any)?.id,
        commonName: (spot as any)?.species || (spot as any)?.name,
      };
      const speciesKey = getFishIQPresentationIdentityKey(speciesData);

      if (!speciesKey || seen.has(speciesKey)) continue;
      seen.add(speciesKey);
      deduped.push(spot);
    }

    return deduped;
  }, [hotspots]);
  const overlayMaxFish = fishIQPrefs.maxFish;

  const fishWidgetVisibleCount = useMemo(
    () => getFishWidgetCountForZoom(currentMapZoom, overlayMaxFish),
    [currentMapZoom, overlayMaxFish],
  );

  const fishAreaListLimit = useMemo(() => {
    if (fishWidgetAreaSource === "selected") {
      return isFishAreaExpanded
        ? FISH_AREA_EXPANDED_LIST_LIMIT
        : FISH_AREA_DEFAULT_LIST_LIMIT;
    }

    // The map FishIQ hub count represents every unique species available in
    // the current area read, so the normal area window must expose that same
    // complete set. Do not silently cap it at the old 5-row widget preference.
    return uniqueFishListSpots.length;
  }, [fishWidgetAreaSource, isFishAreaExpanded, uniqueFishListSpots.length]);

  const visibleFishSpotsBase = useMemo(
    () => uniqueFishListSpots.slice(0, fishAreaListLimit),
    [uniqueFishListSpots, fishAreaListLimit],
  );

  const visibleFishSpots = useMemo(() => {
    const bucketOf = (spot: any) =>
      String(
        spot?.fishIQSpeciesData?.__fishiq?.locationRank?.bucket || "",
      ).trim();

    if (fishAreaFilter === "likely") {
      const likely = visibleFishSpotsBase.filter((spot: any) =>
        ["likely", "good_target"].includes(bucketOf(spot)),
      );
      return likely.length ? likely : visibleFishSpotsBase.slice(0, 3);
    }

    if (fishAreaFilter === "possible") {
      const possible = visibleFishSpotsBase.filter((spot: any) =>
        ["possible", "seasonal", "low_signal"].includes(bucketOf(spot)),
      );
      return possible.length ? possible : visibleFishSpotsBase.slice(3, 8);
    }

    return visibleFishSpotsBase;
  }, [fishAreaFilter, visibleFishSpotsBase]);

  const selectedFishIQAreaRead = useMemo<FishIQAreaRead | null>(() => {
    if (fishWidgetMode !== "area" || fishWidgetAreaSource !== "selected") {
      return null;
    }

    const read = visibleFishSpots.find(
      (spot: any) => spot?.fishIQAreaRead,
    )?.fishIQAreaRead;

    return read || null;
  }, [fishWidgetAreaSource, fishWidgetMode, visibleFishSpots]);

  const fishIQWaterReadLabel = useMemo(() => {
    if (!selectedFishIQAreaRead) return "";

    const waterType = String(selectedFishIQAreaRead.waterType || "unknown")
      .replace(/_/g, " ")
      .replace(/\b\w/g, (char) => char.toUpperCase());
    const bestWindow = String(selectedFishIQAreaRead.bestWindow || "")
      .replace(/\s+/g, " ")
      .trim();
    const confidence = Number(selectedFishIQAreaRead.confidence);
    const modelLabel = String(selectedFishIQAreaRead.confidenceLabel || "").trim();
    const fallbackLabel = Number.isFinite(confidence)
      ? confidence >= 82
        ? "Strong local match"
        : confidence >= 66
          ? "Likely match"
          : "Limited local data"
      : "Limited local data";
    const confidenceLabel = Number.isFinite(confidence)
      ? `${modelLabel || fallbackLabel} ${Math.round(confidence)}%`
      : modelLabel || fallbackLabel;

    return [waterType, bestWindow, confidenceLabel].filter(Boolean).join(" • ");
  }, [selectedFishIQAreaRead]);

  const fishAreaHasMoreSpecies =
    uniqueFishListSpots.length > visibleFishSpotsBase.length;
  const fishAreaTotalCount = uniqueFishListSpots.length;
  const expandedFish = selectedFishSpot;
  const isFishIQThinkingVisible =
    isSearchingSavedArea || (isSearchingArea && fishIQRetryMode === "viewport");

  const fishWidgetHeaderTitle = useMemo(() => {
    if (expandedFish) return "Water read field notes";
    if (fishWidgetAreaSource === "selected") return "Water Read";
    return "Fish in this area";
  }, [expandedFish, fishWidgetAreaSource, fishWidgetMode]);

  const fishWidgetHeaderSubtitle = useMemo(() => {
    if (expandedFish) {
      const speciesName = String(
        (expandedFish as any)?.species ||
          (expandedFish as any)?.name ||
          (expandedFish as any)?.fishIQSpeciesData?.commonName ||
          "species",
      ).trim();
      return `${speciesName} field notes`;
    }

    if (fishWidgetAreaSource === "selected") {
      return "Best local matches";
    }

    return "";
  }, [
    expandedFish,
    fishAreaTotalCount,
    fishWidgetAreaSource,
    fishWidgetMode,
    isFishAreaExpanded,
  ]);

  const fishWidgetIconAlt = expandedFish
    ? "Water read detail"
    : fishWidgetAreaSource === "selected"
      ? "Fish near selected spot"
      : "Fish in this area";

  useEffect(() => {
    if (!selectedFishSpot) setIsFishFullDetailOpen(false);
  }, [selectedFishSpot]);

  const expandedFishSpecies = useMemo(() => {
    if (!expandedFish) return null;
    return (expandedFish as any)?.fishIQSpeciesData || null;
  }, [expandedFish]);

  const expandedFishAreaReadForSpecies = useMemo(() => {
    const areaRead = (expandedFish as any)?.fishIQAreaRead || null;
    if (!areaRead || !expandedFishSpecies) return areaRead;

    const environment = areaRead?.environmentalContext || null;
    if (!environment) return areaRead;

    const compatibility = getFishIQEnvironmentCompatibility(
      expandedFishSpecies,
      {
        lat: areaRead?.lat,
        lon: areaRead?.lng,
        waterName: areaRead?.waterName || null,
        waterType: areaRead?.waterType || null,
        waterKind: areaRead?.waterKind || null,
        regionHint: areaRead?.regionHint || null,
        source: 'fishiq-full-detail',
      },
      environment,
    );

    const filteredEnvironment = {
      ...environment,
      current: compatibility.currentCompatible ? environment?.current : undefined,
      bathymetry: compatibility.bathymetryCompatible ? environment?.bathymetry : undefined,
    };
    const hasIncompatibleComponent = Boolean(
      (environment?.current?.available === true && !compatibility.currentCompatible) ||
        (environment?.bathymetry?.available === true && !compatibility.bathymetryCompatible),
    );

    return {
      ...areaRead,
      environmentalContext: compatibility.compatible ? filteredEnvironment : null,
      fusion: compatibility.compatible
        ? {
            ...(areaRead?.fusion || {}),
            currentAvailable:
              compatibility.currentCompatible && environment?.current?.available === true,
            depthAvailable:
              compatibility.bathymetryCompatible && environment?.bathymetry?.available === true,
          }
        : null,
      tacticalFusion: hasIncompatibleComponent ? null : areaRead?.tacticalFusion || null,
    };
  }, [expandedFish, expandedFishSpecies]);

  const normalizeSpeciesKey = useCallback((value: any) => {
    return String(value || "")
      .toLowerCase()
      .replace(/[^a-z0-9 ]/g, " ")
      .replace(/\s+/g, " ")
      .trim();
  }, []);

  const fishSpotBySpeciesKey = useMemo(() => {
    const next = new Map<string, Hotspot>();

    for (const spot of uniqueFishListSpots) {
      const rawValues = [spot?.species, spot?.name, spot?.id].filter(Boolean);
      rawValues.forEach((raw) => {
        const normalized = normalizeSpeciesKey(raw);
        if (!normalized || next.has(normalized)) return;
        next.set(normalized, spot);

        const words = normalized.split(" ").filter(Boolean);
        if (words.length >= 2) {
          const tail = words.slice(-2).join(" ");
          if (tail && !next.has(tail)) next.set(tail, spot);
        }
        const last = words[words.length - 1];
        if (last && !next.has(last)) next.set(last, spot);
      });
    }

    return next;
  }, [normalizeSpeciesKey, uniqueFishListSpots]);

  const nearbyCatchSpeciesRows = useMemo(() => {
    const anchor = fishWidgetAnchor || latestUserLatLngRef.current || null;
    if (!anchor)
      return [] as Array<{
        id: string;
        species: string;
        count: number;
        lastSeen: number;
        waterName: string;
        latestCatch: MyCatchMapEntry;
        catches: MyCatchMapEntry[];
        matchedSpot: Hotspot | null;
        badgeLabel: string;
        summaryLabel: string;
      }>;

    // Stable private-memory radius: zoom must not change whether a catch counts as local.
    const radiusMeters = 12000;

    const grouped = new Map<
      string,
      {
        id: string;
        species: string;
        count: number;
        lastSeen: number;
        waterName: string;
        latestCatch: MyCatchMapEntry;
        catches: MyCatchMapEntry[];
        matchedSpot: Hotspot | null;
        badgeLabel: string;
        summaryLabel: string;
      }
    >();

    for (const entry of normalizeCatchMapEntries(props.catchLog || [])) {
      const dist = getDistanceInMeters(
        anchor.lat,
        anchor.lng,
        entry.lat,
        entry.lng,
      );
      if (!Number.isFinite(dist) || dist > radiusMeters) continue;

      const rawSpecies = String(entry.species || "Catch").trim() || "Catch";
      const normalizedSpecies = normalizeSpeciesKey(rawSpecies);
      const tailKey = normalizedSpecies.split(" ").slice(-2).join(" ");
      const lastWordKey = normalizedSpecies.split(" ").slice(-1).join(" ");
      const matchedSpot =
        fishSpotBySpeciesKey.get(normalizedSpecies) ||
        fishSpotBySpeciesKey.get(tailKey) ||
        fishSpotBySpeciesKey.get(lastWordKey) ||
        null;

      const canonicalSpecies =
        String(
          matchedSpot?.species || matchedSpot?.name || rawSpecies,
        ).trim() || rawSpecies;
      const groupKey =
        normalizeSpeciesKey(canonicalSpecies) ||
        normalizedSpecies ||
        `catch_${entry.id}`;
      const waterName =
        String(
          matchedSpot?.waterName || entry.location || "Area memory",
        ).trim() || "Area memory";
      const summaryLabel = matchedSpot?.fishIQSpeciesData
        ? `Memory species read for ${waterName}.`
        : `Logged nearby around ${waterName}.`;
      const existing = grouped.get(groupKey);

      if (!existing) {
        grouped.set(groupKey, {
          id: groupKey,
          species: canonicalSpecies,
          count: 1,
          lastSeen: Number(entry.timestamp || 0),
          waterName,
          latestCatch: entry,
          catches: [entry],
          matchedSpot,
          badgeLabel: matchedSpot?.timeOfDay || "Memory",
          summaryLabel,
        });
        continue;
      }

      existing.count += 1;
      existing.catches.push(entry);
      if (Number(entry.timestamp || 0) > existing.lastSeen) {
        existing.lastSeen = Number(entry.timestamp || 0);
        existing.latestCatch = entry;
      }
      if (!existing.matchedSpot && matchedSpot) {
        existing.matchedSpot = matchedSpot;
        existing.badgeLabel = matchedSpot?.timeOfDay || existing.badgeLabel;
      }
      if (waterName && existing.waterName === "Area memory")
        existing.waterName = waterName;
      existing.summaryLabel = existing.matchedSpot?.fishIQSpeciesData
        ? `Memory species read for ${existing.waterName}.`
        : `Logged nearby around ${existing.waterName}.`;
    }

    return Array.from(grouped.values())
      .sort((a, b) => {
        if (b.count !== a.count) return b.count - a.count;
        return b.lastSeen - a.lastSeen;
      })
      .slice(0, fishWidgetVisibleCount);
  }, [
    fishSpotBySpeciesKey,
    fishWidgetAnchor,
    fishWidgetVisibleCount,
    normalizeSpeciesKey,
    props.catchLog,
  ]);

  const lifetimeCatchSpeciesRows = useMemo(() => {
    const grouped = new Map<
      string,
      {
        id: string;
        species: string;
        count: number;
        lastSeen: number;
        waterName: string;
        latestCatch: MyCatchMapEntry;
        catches: MyCatchMapEntry[];
        matchedSpot: Hotspot | null;
        badgeLabel: string;
        summaryLabel: string;
      }
    >();

    for (const entry of normalizeCatchMapEntries(props.catchLog || [])) {
      const rawSpecies = String(entry.species || "Catch").trim() || "Catch";
      const normalizedSpecies = normalizeSpeciesKey(rawSpecies);
      if (!normalizedSpecies) continue;

      const tailKey = normalizedSpecies.split(" ").slice(-2).join(" ");
      const lastWordKey = normalizedSpecies.split(" ").slice(-1).join(" ");
      const matchedSpot =
        fishSpotBySpeciesKey.get(normalizedSpecies) ||
        fishSpotBySpeciesKey.get(tailKey) ||
        fishSpotBySpeciesKey.get(lastWordKey) ||
        null;

      const canonicalSpecies = String(
        matchedSpot?.species || matchedSpot?.name || rawSpecies,
      ).trim() || rawSpecies;
      const groupKey =
        normalizeSpeciesKey(canonicalSpecies) || normalizedSpecies || `catch_${entry.id}`;
      const waterName = String(entry.location || "Catch history").trim() || "Catch history";
      const existing = grouped.get(groupKey);

      if (!existing) {
        grouped.set(groupKey, {
          id: groupKey,
          species: canonicalSpecies,
          count: 1,
          lastSeen: Number(entry.timestamp || 0),
          waterName,
          latestCatch: entry,
          catches: [entry],
          matchedSpot,
          badgeLabel: "Caught before",
          summaryLabel: `Caught before around ${waterName}.`,
        });
        continue;
      }

      existing.count += 1;
      existing.catches.push(entry);
      if (Number(entry.timestamp || 0) > existing.lastSeen) {
        existing.lastSeen = Number(entry.timestamp || 0);
        existing.latestCatch = entry;
        existing.waterName = waterName;
      }
      if (!existing.matchedSpot && matchedSpot) existing.matchedSpot = matchedSpot;
    }

    return [...grouped.values()].sort((a, b) => b.lastSeen - a.lastSeen);
  }, [fishSpotBySpeciesKey, normalizeSpeciesKey, props.catchLog]);

  const findCatchMemoryMatch = useCallback((
    rows: Array<{ species: string; matchedSpot?: Hotspot | null }>,
    speciesLabel: string,
    itemId?: string | null,
  ) => {
    return rows.find((row: any) => {
      if (row?.matchedSpot?.id && itemId && row.matchedSpot.id === itemId) return true;
      const rowKey = normalizeSpeciesKey(row?.species);
      const itemKey = normalizeSpeciesKey(speciesLabel);
      return !!rowKey && !!itemKey &&
        (rowKey === itemKey || rowKey.endsWith(itemKey) || itemKey.endsWith(rowKey));
    }) || null;
  }, [normalizeSpeciesKey]);

  const selectedSavedSpot = useMemo(
    () => savedSpots.find((spot) => spot.id === selectedSavedSpotId) ?? null,
    [savedSpots, selectedSavedSpotId],
  );

  const isSavedSpotDetailMode = !!selectedSavedSpot && !pendingSavedSpotLatLng;

  // Keep transient map utilities mutually exclusive. A tournament options panel
  // should never remain underneath/over another drawer, search state, or modal.
  useEffect(() => {
    if (!isTournamentMapFilterOpen) return;
    if (
      isFishWidgetOpen ||
      isFishFullDetailOpen ||
      isWeatherOpen ||
      isSavedSpotsOpen ||
      !!isOfflinePanelOpen ||
      isMapSearchFocused
    ) {
      setIsTournamentMapFilterOpen(false);
    }
  }, [
    isTournamentMapFilterOpen,
    isFishWidgetOpen,
    isFishFullDetailOpen,
    isWeatherOpen,
    isSavedSpotsOpen,
    isOfflinePanelOpen,
    isMapSearchFocused,
  ]);

  const savedSpotFilterOptions = useMemo(
    () =>
      [
        { key: "all", label: "All", emoji: "✦" },
        {
          key: "favorite",
          label: "Favorites",
          emoji: SAVED_SPOT_KIND_META.favorite.emoji,
        },
        {
          key: "fish",
          label: "Fish Spots",
          emoji: SAVED_SPOT_KIND_META.fish.emoji,
        },
        {
          key: "food",
          label: "Food Spots",
          emoji: SAVED_SPOT_KIND_META.food.emoji,
        },
        {
          key: "launch",
          label: "Launches",
          emoji: SAVED_SPOT_KIND_META.launch.emoji,
        },
        {
          key: "sunset",
          label: "Sunsets",
          emoji: SAVED_SPOT_KIND_META.sunset.emoji,
        },
      ] as Array<{ key: SavedSpotFilter; label: string; emoji: string }>,
    [],
  );
  const savedSpotCounts = useMemo(
    () => ({
      all: savedSpots.length,
      favorite: savedSpots.filter((spot) => spot.kind === "favorite").length,
      fish: savedSpots.filter((spot) => spot.kind === "fish").length,
      food: savedSpots.filter((spot) => spot.kind === "food").length,
      launch: savedSpots.filter((spot) => spot.kind === "launch").length,
      sunset: savedSpots.filter((spot) => spot.kind === "sunset").length,
    }),
    [savedSpots],
  );
  const filteredSavedSpots = useMemo(
    () =>
      savedSpotFilter === "all"
        ? savedSpots
        : savedSpots.filter((spot) => spot.kind === savedSpotFilter),
    [savedSpotFilter, savedSpots],
  );
  const visibleSavedSpotsList = useMemo(
    () =>
      selectedSavedSpotId
        ? filteredSavedSpots.filter((spot) => spot.id !== selectedSavedSpotId)
        : filteredSavedSpots,
    [filteredSavedSpots, selectedSavedSpotId],
  );

  useEffect(() => {
    savedSpotsRef.current = savedSpots;
  }, [savedSpots]);

  useEffect(() => {
    showSavedSpotsOnMapRef.current = showSavedSpotsOnMap;
  }, [showSavedSpotsOnMap]);

  useEffect(() => {
    savedSpotFilterRef.current = savedSpotFilter;
    try {
      window.localStorage.setItem(
        SAVED_SPOT_FILTER_STORAGE_KEY,
        savedSpotFilter,
      );
    } catch {}
  }, [savedSpotFilter]);

  useEffect(() => {
    selectedSavedSpotIdRef.current = selectedSavedSpotId;
  }, [selectedSavedSpotId]);

  useEffect(() => {
    const uid = String(profile?.uid || "").trim();

    hasAttemptedSavedSpotsMigrationRef.current = false;
    setSavedSpots([]);
    setHasLoadedSavedSpots(false);
    savedSpotsRef.current = [];
    pendingSavedSpotSaveRef.current = null;
    pendingSavedSpotDeleteRef.current = null;
    setSavingSavedSpotId(null);
    setDeletingSavedSpotId(null);
    setEditingSavedSpotId(null);

    if (!uid) {
      setHasLoadedSavedSpots(true);
      return;
    }

    return subscribeToSavedSpots(
      uid,
      (next) => {
        const normalized = normalizeSavedSpotsCollection(next);
        const nextSignature = getSavedSpotCollectionSignature(normalized);

        setSavedSpots((current) => {
          const currentSignature = getSavedSpotCollectionSignature(current);
          return currentSignature === nextSignature ? current : normalized;
        });
        setHasLoadedSavedSpots((current) => current || true);
      },
      () => {
        setHasLoadedSavedSpots(true);
        addToastRef.current?.("Could not load saved spots yet.", "error");
      },
    );
  }, [profile?.uid]);

  useEffect(() => {
    const uid = String(profile?.uid || "").trim();
    if (!uid || !hasLoadedSavedSpots) return;
    if (hasAttemptedSavedSpotsMigrationRef.current) return;
    if (savedSpots.length > 0 || legacyProfileSavedSpots.length === 0) return;

    hasAttemptedSavedSpotsMigrationRef.current = true;
    void migrateLegacyProfileSavedSpots(uid, legacyProfileSavedSpots).catch(
      (err) => {
        console.warn("[map] saved spot legacy migration skipped", err);
      },
    );
  }, [
    hasLoadedSavedSpots,
    legacyProfileSavedSpots,
    profile?.uid,
    savedSpots.length,
  ]);

  useEffect(() => {
    const pendingSave = pendingSavedSpotSaveRef.current;
    const confirmedSavedSpot = pendingSave
      ? savedSpots.find((spot) => spot.id === pendingSave.id) || null
      : null;
    const pendingSaveConfirmed = Boolean(
      pendingSave &&
        confirmedSavedSpot &&
        confirmedSavedSpot.name === pendingSave.expected.name &&
        confirmedSavedSpot.kind === pendingSave.expected.kind &&
        Math.abs(confirmedSavedSpot.lat - pendingSave.expected.lat) < 1e-7 &&
        Math.abs(confirmedSavedSpot.lng - pendingSave.expected.lng) < 1e-7 &&
        String(confirmedSavedSpot.note || "") === pendingSave.expected.note,
    );
    if (pendingSaveConfirmed && pendingSave) {
      pendingSavedSpotSaveRef.current = null;
      setSavingSavedSpotId(null);
      setPendingSavedSpotLatLng(null);
      setSavedSpotDraftName("");
      setSavedSpotDraftNote("");
      setSavedSpotDraftKind("favorite");
      setEditingSavedSpotId(null);
      setShowSavedSpotsOnMap(true);
      setSelectedSavedSpotId(null);
      setIsSavedSpotFilterMenuOpen(false);
      setIsSelectedSavedSpotMenuOpen(false);
      setIsSavedSpotsOpen(false);
      addToastRef.current?.(pendingSave.message, "success");
    }

    const pendingDelete = pendingSavedSpotDeleteRef.current;
    if (
      pendingDelete &&
      !savedSpots.some((spot) => spot.id === pendingDelete.id)
    ) {
      pendingSavedSpotDeleteRef.current = null;
      setDeletingSavedSpotId(null);
      if (selectedSavedSpotIdRef.current === pendingDelete.id) {
        setSelectedSavedSpotId(null);
      }
      setPendingSavedSpotLatLng(null);
      setSavedSpotDraftName("");
      setSavedSpotDraftNote("");
      setSavedSpotDraftKind("favorite");
      addToastRef.current?.(
        `${pendingDelete.name || "Saved spot"} removed`,
        "info",
      );
    }
  }, [savedSpots]);

  // Saved spots are intentionally isolated from the profile document.
  // Canonical source of truth is users/{uid}/savedSpots/{spotId}.
  // MapView never mutates the savedSpots array optimistically; the subcollection
  // listener is the only path that updates savedSpots in React state.

  useEffect(() => {
    // Do not auto-close the Saved Spots panel/composer when the array is empty.
    // That was the hidden first-save killer: opening the composer while there
    // were zero saved spots immediately triggered this effect, cleared the
    // pending lat/lng draft, and made the save look like it spazzed/failure.
    if (!savedSpots.length) {
      if (selectedSavedSpotId) setSelectedSavedSpotId(null);
      setIsSelectedSavedSpotMenuOpen(false);
      setIsSavedSpotFilterMenuOpen(false);
      return;
    }

    if (
      selectedSavedSpotId &&
      !savedSpots.some((spot) => spot.id === selectedSavedSpotId)
    ) {
      setSelectedSavedSpotId(null);
      return;
    }

    if (
      selectedSavedSpotId &&
      savedSpotFilter !== "all" &&
      !savedSpots.some(
        (spot) =>
          spot.id === selectedSavedSpotId && spot.kind === savedSpotFilter,
      )
    ) {
      setSelectedSavedSpotId(null);
    }
  }, [savedSpots, selectedSavedSpotId, savedSpotFilter]);

  const expandedFishSpeciesId = useMemo(() => {
    if (!expandedFish) return null;
    const species = (expandedFishSpecies as any) || {};
    const candidate = [
      species.id,
      species.slug,
      species.commonName,
      species.name,
      (expandedFish as any)?.species,
      expandedFish.id,
    ]
      .map((value) => String(value || "").trim())
      .find(Boolean);
    return candidate || null;
  }, [expandedFish, expandedFishSpecies]);

  useEffect(() => {
    if (!visibleFishSpots.length) {
      setIsFishWidgetOpen(false);
      setSelectedFishSpot(null);
    }
  }, [visibleFishSpots.length]);

  const quickDailyPreview = useMemo(() => getDailyRewardPreview(profile as any), [profile]);
  const quickDailyActions = quickDailyActivity?.actions || {};
  const quickDailyRows = useMemo(() => [
    { key: "search_area", icon: "🌊", title: "Scout Big Blue", done: Number(quickDailyActions.search_area || 0) > 0 },
    { key: "open_fishiq", icon: "🐟", title: "Read the water", done: Number(quickDailyActions.open_fishiq || 0) > 0 },
    { key: "log_catch", icon: "📘", title: "Log a catch", done: Number(quickDailyActions.log_catch || 0) > 0 },
    { key: "share_to_bay", icon: "🏝️", title: "Share to Baleen Bay", done: Number(quickDailyActions.share_to_bay || 0) > 0, optional: true },
  ], [quickDailyActions]);

  const quickCrewSummary = useMemo(() => {
    const uid = String(profile?.uid || "").trim();
    const accepted = quickCrewRelationships.filter((rel: any) => String(rel?.status || "") === "accepted");
    const pending = quickCrewRelationships.filter((rel: any) => String(rel?.status || "") === "pending" && String(rel?.recipientId || "") === uid);
    const onlineByUid = new Map(
      crewPresence
        .map((presence: any) => [String(presence?.uid || "").trim(), presence] as const)
        .filter(([targetUid]) => Boolean(targetUid)),
    );
    const inboxByUid = new Map(
      quickCrewDmInbox
        .map((row) => [String(row?.otherUid || "").trim(), row] as const)
        .filter(([targetUid]) => Boolean(targetUid)),
    );

    const crewmates = accepted.map((rel: any) => {
      const ids = Array.isArray(rel?.participantIds)
        ? rel.participantIds
        : [rel?.requesterId, rel?.recipientId].filter(Boolean);
      const targetUid = String(ids.find((id: any) => String(id || "").trim() && String(id) !== uid) || "").trim();
      const presence = onlineByUid.get(targetUid);
      const inbox = inboxByUid.get(targetUid);
      const user = quickCrewProfiles[targetUid] || null;
      const displayName = String(
        presence?.displayName ||
          user?.displayName ||
          user?.name ||
          user?.username ||
          user?.handle ||
          "Crewmate",
      ).trim();
      const avatarUrl = String(
        presence?.avatarUrl ||
          user?.avatar ||
          user?.avatarUrl ||
          user?.photoURL ||
          "",
      ).trim();

      return {
        rel,
        uid: targetUid,
        displayName,
        avatarUrl,
        isOnline: Boolean(presence),
        lastMessageAt: Number(inbox?.lastMessageAt || inbox?.updatedAt || 0),
      };
    });

    crewmates.sort((a, b) => {
      // Messaging recency owns the strip: the crewmate with the newest DM
      // activity moves to the front. Crewmates with no DM history stay behind
      // active conversations, with online/name only used as stable tie-breakers.
      const recencyDelta = Number(b.lastMessageAt || 0) - Number(a.lastMessageAt || 0);
      if (recencyDelta !== 0) return recencyDelta;

      const onlineDelta = Number(b.isOnline) - Number(a.isOnline);
      if (onlineDelta !== 0) return onlineDelta;

      return a.displayName.localeCompare(b.displayName);
    });

    return { accepted, pending, crewmates };
  }, [
    profile?.uid,
    quickCrewRelationships,
    quickCrewProfiles,
    crewPresence,
    quickCrewDmInbox,
  ]);

  const quickCrewDmInboxByUid = useMemo(() => {
    const map = new Map<string, CrewDMInboxRow>();
    quickCrewDmInbox.forEach((row) => {
      if (row.otherUid) map.set(row.otherUid, row);
    });
    return map;
  }, [quickCrewDmInbox]);

  const quickCrewUnreadCount = useMemo(
    () =>
      quickCrewDmInbox.reduce(
        (total, row) => total + Math.max(0, Number(row.unreadCount || (row.unread ? 1 : 0))),
        0,
      ),
    [quickCrewDmInbox],
  );

  const quickCrewDmCard = useMemo(
    () => quickCrewSummary.crewmates.find((card) => card.uid === quickCrewDmUid) || null,
    [quickCrewSummary.crewmates, quickCrewDmUid],
  );

  const quickCrewRelationshipByTarget = useMemo(() => {
    const uid = String(profile?.uid || "").trim();
    const map = new Map<string, any>();
    quickCrewRelationships.forEach((rel: any) => {
      const ids = Array.isArray(rel?.participantIds)
        ? rel.participantIds
        : [rel?.requesterId, rel?.recipientId].filter(Boolean);
      const targetUid = String(ids.find((id: any) => String(id || "").trim() && String(id) !== uid) || "").trim();
      if (targetUid) map.set(targetUid, rel);
    });
    return map;
  }, [profile?.uid, quickCrewRelationships]);

  const quickCrewSearchResults = useMemo(() => {
    const uid = String(profile?.uid || "").trim();
    const q = String(quickCrewSearch || "").trim().toLowerCase().replace(/^@/, "");
    if (!uid || q.length < 2) return [];

    const users = ((props as any)?.users || {}) as Record<string, any>;
    return Object.entries(users)
      .map(([key, user]) => {
        const targetUid = String((user as any)?.uid || key || "").trim();
        const displayName = String((user as any)?.displayName || (user as any)?.name || (user as any)?.username || (user as any)?.handle || "Angler").trim();
        const username = String((user as any)?.username || (user as any)?.handle || "").trim();
        const avatarUrl = String((user as any)?.avatar || (user as any)?.avatarUrl || (user as any)?.photoURL || "").trim();
        const haystack = [
          targetUid,
          displayName,
          username,
          (user as any)?.displayName,
          (user as any)?.usernameLower,
          (user as any)?.handleLower,
          (user as any)?.email,
        ]
          .map((value) => String(value || "").trim().toLowerCase().replace(/^@/, ""))
          .filter(Boolean)
          .join(" ");
        return { targetUid, user, displayName, username, avatarUrl, haystack };
      })
      .filter((row) => row.targetUid && row.targetUid !== uid && row.haystack.includes(q))
      .slice(0, 7);
  }, [profile?.uid, quickCrewSearch, (props as any)?.users]);

  const quickCrewSendRequest = useCallback(async (targetUid: string, displayName: string) => {
    const uid = String(profile?.uid || "").trim();
    const cleanTarget = String(targetUid || "").trim();
    if (!uid || !cleanTarget || cleanTarget === uid || quickCrewSearchBusyUid) return;

    const rel = quickCrewRelationshipByTarget.get(cleanTarget);
    if (String(rel?.status || "") === "accepted") {
      props.addToast?.(`${displayName} is already in your crew.`, "info");
      return;
    }
    if (String(rel?.status || "") === "pending") {
      props.addToast?.(String(rel?.requesterId || "") === uid ? "Crew request already sent." : "They already sent you a crew request.", "info");
      return;
    }

    try {
      setQuickCrewSearchBusyUid(cleanTarget);
      await sendFriendRequest(uid, cleanTarget);
      props.addToast?.(`Crew pass sent to ${displayName}.`, "success");
    } catch (err) {
      console.error("[MapView] quick crew request failed", err);
      props.addToast?.("Could not send crew request. Try again.", "error");
    } finally {
      setQuickCrewSearchBusyUid(null);
    }
  }, [profile?.uid, quickCrewRelationshipByTarget, quickCrewSearchBusyUid, props.addToast]);

  const quickChallengeRows = useMemo(() => {
    const normalizeKey = (value: unknown) => String(value || "").replace(/__all_time$/, "");
    const completionKeys = new Set(Object.keys(quickChallengeCompletions || {}).map(normalizeKey));
    return canonicalChallenges
      .map((challenge) => {
        const keys = [challenge.key, challenge.id, challenge.legacyId].filter(Boolean).map(normalizeKey);
        const progressDoc = keys.map((key) => quickChallengeProgress[key] || quickChallengeProgress[`${key}__all_time`]).find(Boolean);
        const completed = keys.some((key) => completionKeys.has(key)) || !!progressDoc?.completed;
        const current = Math.max(0, Number(progressDoc?.progress ?? (completed ? challenge.goal : 0)));
        const total = Math.max(1, Number(progressDoc?.target ?? challenge.goal ?? 1));
        const percent = Math.max(0, Math.min(100, Math.round((current / total) * 100)));
        return { challenge, current, total, percent, completed };
      })
      .sort((a, b) => Number(a.completed) - Number(b.completed) || b.percent - a.percent)
      .slice(0, 8);
  }, [quickChallengeCompletions, quickChallengeProgress]);

  const dismissMapTextInputs = useCallback(() => {
    try {
      const active = document.activeElement as HTMLElement | null;
      if (active && typeof active.blur === "function") active.blur();
    } catch {}
  }, []);

  const closeBigBlueQuickWindow = useCallback(() => {
    setQuickWindow(null);
    setQuickFishIQSearch("");
    setQuickCrewMode("summary");
    setQuickCrewSearch("");
    setQuickCrewDmUid("");
  }, []);

  // RoutePortal keeps Big Blue mounted/warm while other app views are active.
  // Clear only transient HUD presentation while hidden so a previously-open
  // Crew DM/quick window cannot flash for one frame when Big Blue is revealed
  // again. Crew relationships/inbox/presence data remain warm in memory.
  useEffect(() => {
    if (props.isActive) return;
    setQuickWindow(null);
    setQuickFishIQSearch("");
    setQuickCrewMode("summary");
    setQuickCrewSearch("");
    setQuickCrewDmUid("");
  }, [props.isActive]);

  const openBigBlueQuickWindow = useCallback((next: BigBlueQuickWindowId) => {
    dismissMapTextInputs();

    // HUD buttons are true toggles: clicking the already-open tool closes it.
    // This is especially important for FishIQ on desktop, where the icon remains
    // visible beside the anchored panel and should behave like Weather/Tournaments.
    if (quickWindow === next) {
      closeBigBlueQuickWindow();
      return;
    }

    setIsWeatherOpen(false);
    setMapActionAnchor(null);
    setIsFishWidgetOpen(false);
    setSelectedFishSpot(null);
    setIsFishFullDetailOpen(false);
    setIsSavedSpotsOpen(false);
    setPendingSavedSpotLatLng(null);
    setSelectedSavedSpotId(null);
    setIsSavedSpotFilterMenuOpen(false);
    setIsSelectedSavedSpotMenuOpen(false);
    setIsTournamentMapFilterOpen(false);
    if (isOfflinePanelOpen) onCloseOfflinePanel?.();
    if (next !== "fishiq") setQuickFishIQSearch("");
    setQuickWindow(next);
  }, [closeBigBlueQuickWindow, dismissMapTextInputs, isOfflinePanelOpen, onCloseOfflinePanel, quickWindow]);

  const handleTournamentMapRegistration = useCallback(async (tournament: any) => {
    const id = String(tournament?.id || "").trim();
    const uid = String((profile as any)?.uid || "").trim();
    if (!id || !uid || tournamentMapRegistrationBusyId) return;

    const participantStatus = String(myTournamentParticipation[id]?.status || "").toLowerCase();
    const activeRegistration = participantStatus === "approved" || participantStatus === "pending";
    const tournamentStatus = String(tournament?.status || "").toLowerCase();
    const closesAt = Number(tournament?.registrationClosesAt || 0);
    const registrationOpen =
      tournamentStatus === "registration_open" && (!closesAt || closesAt > Date.now());
    const canWithdraw =
      activeRegistration && (tournamentStatus === "approved" || tournamentStatus === "registration_open");

    // Duo registration has a create/join-code workflow in Tournament Dock. Keep
    // that single canonical flow instead of inventing a second Duo form on Big Blue.
    if (!activeRegistration && registrationOpen && String(tournament?.participationMode || "").toLowerCase() === "team") {
      setSelectedTournamentMapDetail(null);
      setIsTournamentMapFilterOpen(false);
      setIsWorldTournamentMapOpen(false);
      if (typeof props.onTournamentClick === "function") props.onTournamentClick(id);
      else props.onEventsClick?.({ from: "big_blue_tournament_registration" });
      return;
    }

    if (!canWithdraw && !registrationOpen) return;

    setTournamentMapRegistrationBusyId(id);
    try {
      if (canWithdraw) {
        await leaveTournamentRegistration(id);
        props.addToast?.(
          participantStatus === "pending"
            ? "Tournament registration request withdrawn."
            : "Tournament registration withdrawn.",
          "info",
        );
      } else {
        const displayName =
          String((profile as any)?.name || (profile as any)?.displayName || "Roddo Angler").trim() ||
          "Roddo Angler";
        const avatarUrl =
          String(
            (profile as any)?.avatarUrl ||
              (profile as any)?.avatar ||
              (profile as any)?.photoURL ||
              "",
          ).trim() || null;
        const status = await joinTournamentRegistration(id, displayName, avatarUrl);
        props.addToast?.(
          status === "pending"
            ? "Registration request sent for approval."
            : "You are registered for this tournament.",
          "success",
        );
      }
    } catch (error: any) {
      props.addToast?.(
        String(error?.message || (canWithdraw ? "Could not withdraw." : "Could not register.")),
        "error",
      );
    } finally {
      setTournamentMapRegistrationBusyId(null);
    }
  }, [
    myTournamentParticipation,
    profile,
    props.addToast,
    props.onEventsClick,
    props.onTournamentClick,
    tournamentMapRegistrationBusyId,
  ]);

  const openSavedSpotComposer = useCallback(
    (latlng: LatLng, kind?: SavedSpotKind) => {
      const nextKind = kind || "favorite";
      dismissMapTextInputs();
      setMapActionAnchor(null);
      setEditingSavedSpotId(null);
      setPendingSavedSpotLatLng(latlng);
      setSavedSpotDraftKind(nextKind);
      savedSpotDraftNameLiveRef.current = "";
      savedSpotDraftNoteLiveRef.current = "";
      setSavedSpotDraftName("");
      setSavedSpotDraftNote("");
      setSelectedSavedSpotId(null);
      setIsSavedSpotsOpen(true);
    },
    [dismissMapTextInputs],
  );

  const centerMapOnSavedSpot = useCallback(
    (
      spot: SavedSpotRecord,
      zoom = 15,
      opts?: { revealAbovePanel?: boolean },
    ) => {
      const map = mapInstanceRef.current;
      if (!map) return;

      const targetZoom = Math.max(Number(map.getZoom?.() ?? zoom), zoom);
      programmaticMoveRef.current = true;

      try {
        if (opts?.revealAbovePanel) {
          const size = map.getSize?.();
          const mapRect = mapRef.current?.getBoundingClientRect?.();
          const panelRect =
            savedSpotsPanelRef.current?.getBoundingClientRect?.();

          if (size && mapRect && panelRect) {
            const panelTop = Math.max(0, panelRect.top - mapRect.top);
            const targetY = Math.max(
              92,
              Math.min(panelTop - 76, size.y * 0.42),
            );
            const targetX = size.x * 0.5;
            const centerX = size.x * 0.5;
            const centerY = size.y * 0.5;
            const spotPoint = map.project([spot.lat, spot.lng], targetZoom);
            const centerPoint = (L as any).point(
              spotPoint.x - (targetX - centerX),
              spotPoint.y - (targetY - centerY),
            );
            const nextCenter = map.unproject(centerPoint, targetZoom);
            map.setView(nextCenter, targetZoom, { animate: true });
          } else {
            map.setView([spot.lat, spot.lng], targetZoom, { animate: true });
          }
        } else {
          map.setView([spot.lat, spot.lng], targetZoom, { animate: true });
        }
      } catch {
        try {
          map.setView([spot.lat, spot.lng], targetZoom, { animate: true });
        } catch {}
      }

      window.setTimeout(() => {
        programmaticMoveRef.current = false;
      }, 0);
    },
    [],
  );

  const openSavedSpotDetail = useCallback(
    (spot: SavedSpotRecord, zoom = 15) => {
      dismissMapTextInputs();
      setPendingSavedSpotLatLng(null);
      setIsSelectedSavedSpotMenuOpen(false);
      setIsSavedSpotFilterMenuOpen(false);
      setSelectedSavedSpotId(spot.id);
      setIsSavedSpotsOpen(true);

      window.setTimeout(() => {
        centerMapOnSavedSpot(spot, zoom, { revealAbovePanel: true });
      }, 80);
    },
    [centerMapOnSavedSpot, dismissMapTextInputs],
  );

  const editSavedSpot = useCallback((spot: SavedSpotRecord) => {
    dismissMapTextInputs();
    setEditingSavedSpotId(spot.id);
    setPendingSavedSpotLatLng({ lat: spot.lat, lng: spot.lng });
    savedSpotDraftNameLiveRef.current = spot.name || "";
    savedSpotDraftNoteLiveRef.current = spot.note || "";
    setSavedSpotDraftName(spot.name || "");
    setSavedSpotDraftKind(spot.kind);
    setSavedSpotDraftNote(spot.note || "");
    setSelectedSavedSpotId(null);
    setIsSelectedSavedSpotMenuOpen(false);
    setIsSavedSpotFilterMenuOpen(false);
    setIsSavedSpotsOpen(true);
  }, [dismissMapTextInputs]);

  const savePendingSavedSpot = useCallback(async () => {
    if (!pendingSavedSpotLatLng) return;

    const kind =
      normalizeSavedSpotKind(savedSpotDraftKind) !== "favorite"
        ? normalizeSavedSpotKind(savedSpotDraftKind)
        : inferSavedSpotKindFromLabel(savedSpotDraftNameLiveRef.current) || "favorite";
    const meta = SAVED_SPOT_KIND_META[kind];
    let resolvedName = savedSpotDraftNameLiveRef.current.trim();

    if (!resolvedName) {
      const geoLabel = await reverseGeocodeServer(
        pendingSavedSpotLatLng.lat,
        pendingSavedSpotLatLng.lng,
      ).catch(() => null);
      resolvedName = String(geoLabel || "").trim() || meta.label;
    }

    const existingEdit = editingSavedSpotId
      ? savedSpotsRef.current.find((item) => item.id === editingSavedSpotId) || null
      : null;
    const stableCreatedAt = Number(existingEdit?.clientCreatedAt ?? existingEdit?.createdAt ?? Date.now());
    const spot: SavedSpotRecord = {
      id: existingEdit?.id || `saved_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      name: resolvedName,
      kind,
      lat: pendingSavedSpotLatLng.lat,
      lng: pendingSavedSpotLatLng.lng,
      note: savedSpotDraftNoteLiveRef.current.trim(),
      createdAt: stableCreatedAt,
      clientCreatedAt: stableCreatedAt,
    };

    const uid = String(profile?.uid || "").trim();
    if (!uid) {
      addToastRef.current?.("Sign in to save map spots.", "error");
      return;
    }

    pendingSavedSpotSaveRef.current = {
      id: spot.id,
      message: existingEdit ? `${spot.name} updated` : `${meta.label} saved`,
      expected: {
        name: spot.name,
        kind: spot.kind,
        lat: spot.lat,
        lng: spot.lng,
        note: spot.note || "",
      },
    };
    setSavingSavedSpotId(spot.id);

    try {
      await saveSavedSpot(uid, spot as any);
    } catch (err) {
      console.error("[map] failed to save spot", err);
      if (pendingSavedSpotSaveRef.current?.id === spot.id)
        pendingSavedSpotSaveRef.current = null;
      setSavingSavedSpotId((current) => (current === spot.id ? null : current));
      addToastRef.current?.("Could not save map spot. Try again.", "error");
      return;
    }

    // The write has already completed successfully, so reconcile the local
    // Saved Spots collection immediately instead of waiting for the Firestore
    // listener round-trip. This is not an optimistic write: Firestore accepted
    // the save first. The live listener remains authoritative and will replace
    // this row if the canonical server payload differs.
    setSavedSpots((current) => {
      const existingIndex = current.findIndex((item) => item.id === spot.id);
      const next = existingIndex >= 0
        ? current.map((item, index) => (index === existingIndex ? spot : item))
        : [spot, ...current];
      savedSpotsRef.current = next;
      return next;
    });
  }, [
    pendingSavedSpotLatLng,
    profile?.uid,
    savedSpotDraftKind,
    editingSavedSpotId,
  ]);

  const removeSavedSpot = useCallback(
    async (spotId: string) => {
      if (deletingSavedSpotId) return;

      const uid = String(profile?.uid || "").trim();
      const existing = savedSpotsRef.current.find((spot) => spot.id === spotId);

      if (!uid) {
        addToastRef.current?.("Sign in to remove saved spots.", "error");
        return;
      }

      pendingSavedSpotDeleteRef.current = {
        id: spotId,
        name: existing?.name || "Saved spot",
      };
      setDeletingSavedSpotId(spotId);
      setIsSelectedSavedSpotMenuOpen(false);

      try {
        await deleteSavedSpot(uid, spotId);
      } catch (err) {
        console.error("[map] failed to delete spot", err);
        if (pendingSavedSpotDeleteRef.current?.id === spotId)
          pendingSavedSpotDeleteRef.current = null;
        setDeletingSavedSpotId((current) =>
          current === spotId ? null : current,
        );
        addToastRef.current?.(
          "Could not remove saved spot. Try again.",
          "error",
        );
      }

      // Wait for the Firestore saved-spots listener to confirm removal before
      // clearing selectedSavedSpotId. That prevents disappear/reappear flicker.
    },
    [deletingSavedSpotId, profile?.uid],
  );

  useEffect(() => {
    setIsSelectedSavedSpotMenuOpen(false);
    setIsSelectedSavedSpotExpanded(true);
    setIsSelectedSavedSpotNoteExpanded(false);
  }, [selectedSavedSpotId, isSavedSpotsOpen]);

  React.useLayoutEffect(() => {
    if (!isSavedSpotsOpen) {
      setSavedSpotsDesktopAnchor(null);
      return;
    }

    const syncSavedSpotsAnchor = () => {
      const anchor = savedSpotsAnchorRef.current;
      const shell = mapShellRef.current;
      const isDesktop = window.matchMedia?.("(min-width: 1024px)")?.matches === true;

      if (!anchor || !shell || !isDesktop) {
        setSavedSpotsDesktopAnchor(null);
        return;
      }

      const anchorRect = anchor.getBoundingClientRect();
      const shellRect = shell.getBoundingClientRect();
      const panelWidth = Math.min(420, Math.max(320, shellRect.width - 96));
      const gap = 12;
      const desiredLeft = anchorRect.right - shellRect.left + gap;
      const maxLeft = Math.max(12, shellRect.width - panelWidth - 12);
      const left = Math.max(12, Math.min(desiredLeft, maxLeft));

      // Saved Spots sits low on the left HUD rail. On desktop, aligning the
      // drawer's TOP to that low icon leaves most of the panel below the map
      // viewport. Instead, treat Saved Spots as an up-right anchored panel:
      // its bottom edge tracks the icon's bottom edge and its available height
      // grows upward inside Big Blue.
      const bottom = Math.max(12, shellRect.bottom - anchorRect.bottom);
      const availableAboveAnchor = Math.max(240, anchorRect.bottom - shellRect.top - 24);
      const height = Math.min(620, availableAboveAnchor);

      setSavedSpotsDesktopAnchor({
        left: Math.round(left),
        bottom: Math.round(bottom),
        height: Math.round(height),
      });
    };

    syncSavedSpotsAnchor();
    const raf = window.requestAnimationFrame(syncSavedSpotsAnchor);
    window.addEventListener("resize", syncSavedSpotsAnchor);
    window.visualViewport?.addEventListener("resize", syncSavedSpotsAnchor);
    window.visualViewport?.addEventListener("scroll", syncSavedSpotsAnchor);

    return () => {
      window.cancelAnimationFrame(raf);
      window.removeEventListener("resize", syncSavedSpotsAnchor);
      window.visualViewport?.removeEventListener("resize", syncSavedSpotsAnchor);
      window.visualViewport?.removeEventListener("scroll", syncSavedSpotsAnchor);
    };
  }, [isSavedSpotsOpen]);

  // Saved Spots should behave like the other Big Blue quick surfaces: clicking
  // anywhere outside the panel (or its own HUD anchor) dismisses it without
  // blocking the underlying map interaction.
  useEffect(() => {
    if (!isSavedSpotsOpen) return;

    const onOutsideSavedSpotsPointerDown = (event: PointerEvent) => {
      const target = event.target as Node | null;
      if (!target) return;
      if (savedSpotsPanelRef.current?.contains(target)) return;
      if (savedSpotsAnchorRef.current?.contains(target)) return;

      setIsSavedSpotsOpen(false);
      setPendingSavedSpotLatLng(null);
      setEditingSavedSpotId(null);
      savedSpotDraftNameLiveRef.current = "";
      savedSpotDraftNoteLiveRef.current = "";
      setSelectedSavedSpotId(null);
      setIsSavedSpotFilterMenuOpen(false);
      setIsSelectedSavedSpotMenuOpen(false);
    };

    document.addEventListener('pointerdown', onOutsideSavedSpotsPointerDown, true);
    return () => document.removeEventListener('pointerdown', onOutsideSavedSpotsPointerDown, true);
  }, [isSavedSpotsOpen]);

  const handleShareSavedSpot = useCallback(
    async (spot: SavedSpotRecord) => {
      const result = await share({
        url: buildSavedSpotShareUrl(spot),
      });

      if (result.ok && result.method === "native") {
        props.addToast?.("Shared saved spot.", "success");
        return;
      }

      if (result.ok) {
        props.addToast?.("Saved spot link copied.", "success");
        return;
      }

      props.addToast?.("Could not share saved spot.", "error");
    },
    [props],
  );

  const formatSavedSpotTimestamp = useCallback((timestamp?: number) => {
    if (!Number.isFinite(Number(timestamp))) return "Saved recently";

    try {
      return new Intl.DateTimeFormat(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric",
      }).format(new Date(Number(timestamp)));
    } catch {
      return "Saved recently";
    }
  }, []);

  // Do not auto-focus the saved-spot name field on mobile.
  // Auto-focus was popping the keyboard immediately and collapsing the map HUD
  // before users could see the Saved Spots panel. Users can tap the field when
  // they are ready to type.

  const playPreLogCatchPulse = useCallback(
    (latlng: LatLng | null, duration = 260) => {
      const map = mapInstanceRef.current;
      if (!map || !latlng) return;

      let pulseMarker: any = null;
      let timer: number | null = null;
      let cleanupTimer: number | null = null;

      try {
        const clamped = clampLatLngToSafeBox(map, latlng);
        pulseMarker = (L as any)
          .circleMarker([clamped.lat, clamped.lng], {
            pane: BIG_BLUE_MAP_PANES.markers,
            radius: 8,
            color: "#7dd3fc",
            weight: 2,
            opacity: 0.72,
            fillColor: "#dbeafe",
            fillOpacity: 0.16,
          })
          .addTo(map);

        let radius = 8;
        timer = window.setInterval(() => {
          radius += 1.35;
          try {
            pulseMarker?.setRadius?.(radius);
            pulseMarker?.setStyle?.({
              opacity: Math.max(0, 0.72 - (radius - 8) * 0.085),
              fillOpacity: Math.max(0, 0.16 - (radius - 8) * 0.022),
            });
          } catch {}
        }, 34);

        cleanupTimer = window.setTimeout(() => {
          if (timer != null) window.clearInterval(timer);
          try {
            pulseMarker?.remove?.();
          } catch {}
        }, duration);
      } catch {}

      return () => {
        if (timer != null) window.clearInterval(timer);
        if (cleanupTimer != null) window.clearTimeout(cleanupTimer);
        try {
          pulseMarker?.remove?.();
        } catch {}
      };
    },
    [],
  );

  useEffect(() => {
    return () => {
      if (justLoggedCatchPulseTimerRef.current != null) {
        window.clearInterval(justLoggedCatchPulseTimerRef.current);
        justLoggedCatchPulseTimerRef.current = null;
      }
      if (justLoggedCatchCleanupTimerRef.current != null) {
        window.clearTimeout(justLoggedCatchCleanupTimerRef.current);
        justLoggedCatchCleanupTimerRef.current = null;
      }
      try {
        justLoggedCatchMarkerRef.current?.remove?.();
      } catch {}
      justLoggedCatchMarkerRef.current = null;
    };
  }, []);

  useEffect(() => {
    const map = mapInstanceRef.current;
    const state = (props.viewState as any) || {};
    const rawCoords =
      state?.justLoggedCatchCoords ?? props.lastLoggedCatchCoords ?? null;
    const lat = Number(rawCoords?.lat);
    const lng = Number(rawCoords?.lng);
    const stamp = Number(state?.justLoggedCatchAt || 0);

    if (!map || !Number.isFinite(lat) || !Number.isFinite(lng)) return;

    const markerKey = `${lat.toFixed(6)}:${lng.toFixed(6)}:${Number.isFinite(stamp) ? stamp : 0}`;
    if (lastJustLoggedCatchKeyRef.current === markerKey) return;
    lastJustLoggedCatchKeyRef.current = markerKey;

    if (justLoggedCatchPulseTimerRef.current != null) {
      window.clearInterval(justLoggedCatchPulseTimerRef.current);
      justLoggedCatchPulseTimerRef.current = null;
    }
    if (justLoggedCatchCleanupTimerRef.current != null) {
      window.clearTimeout(justLoggedCatchCleanupTimerRef.current);
      justLoggedCatchCleanupTimerRef.current = null;
    }
    try {
      justLoggedCatchMarkerRef.current?.remove?.();
    } catch {}
    justLoggedCatchMarkerRef.current = null;

    const markerLatLng = clampLatLngToSafeBox(map, { lat, lng });
    const marker = (L as any)
      .circleMarker([markerLatLng.lat, markerLatLng.lng], {
        pane: BIG_BLUE_MAP_PANES.markers,
        radius: 11,
        color: "#34d399",
        weight: 3,
        opacity: 0.95,
        fillColor: "#7cf0c5",
        fillOpacity: 0.52,
      })
      .addTo(map);

    marker.bindPopup?.(
      '<div class="p-1 text-xs font-semibold">Catch logged here</div>',
    );
    justLoggedCatchMarkerRef.current = marker;

    try {
      const nextZoom = Math.max(Number(map.getZoom?.() ?? 13), 13);
      programmaticMoveRef.current = true;
      map.setView([markerLatLng.lat, markerLatLng.lng], nextZoom, {
        animate: true,
      });
      window.setTimeout(() => {
        programmaticMoveRef.current = false;
      }, 0);
    } catch {}

    let radius = 11;
    let growing = true;

    justLoggedCatchPulseTimerRef.current = window.setInterval(() => {
      if (!justLoggedCatchMarkerRef.current) return;
      radius += growing ? 0.8 : -0.8;
      if (radius >= 16) growing = false;
      if (radius <= 11) growing = true;
      try {
        justLoggedCatchMarkerRef.current.setRadius?.(radius);
      } catch {}
    }, 70);

    justLoggedCatchCleanupTimerRef.current = window.setTimeout(() => {
      if (justLoggedCatchPulseTimerRef.current != null) {
        window.clearInterval(justLoggedCatchPulseTimerRef.current);
        justLoggedCatchPulseTimerRef.current = null;
      }
      try {
        justLoggedCatchMarkerRef.current?.remove?.();
      } catch {}
      justLoggedCatchMarkerRef.current = null;
    }, 4200);
  }, [props.lastLoggedCatchCoords, props.viewState]);

  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!props.isActive || !map) return;

    if (!hasHandledFirstActiveSyncRef.current) {
      hasHandledFirstActiveSyncRef.current = true;
      return;
    }

    // Reactivating Big Blue must preserve the warm camera/tile layer. The old
    // multi-pass invalidate + tiny setView nudge caused visible raster flashes.
    // One deferred size check is sufficient when returning from another view.
    const timer = window.setTimeout(() => {
      const container = map.getContainer?.() as HTMLElement | undefined;
      if (!container || !isActiveRef.current) return;
      const rect = container.getBoundingClientRect();
      const width = Math.round(rect.width || 0);
      const height = Math.round(rect.height || 0);
      if (width <= 0 || height <= 0) return;
      if (
        observedMapSizeRef.current.width === width &&
        observedMapSizeRef.current.height === height
      ) return;
      observedMapSizeRef.current = { width, height };
      try {
        map.invalidateSize({ animate: false, pan: false });
      } catch {}
    }, 32);

    return () => window.clearTimeout(timer);
  }, [props.isActive]);

  useEffect(() => {
    saveFishIQPrefs(fishIQPrefs);
  }, [fishIQPrefs]);

  useEffect(() => {
    if (props.isActive) return;
    setIsSearchingArea(false);

    // Leaving Big Blue should not leave a stale FishIQ drawer waiting behind
    // the warm Leaflet map. Keep the hotspots/markers cached, but close the UI
    // state so returning from FishIQ or another tab starts clean.
    setIsFishWidgetOpen(false);
    setSelectedFishSpot(null);
    setIsFishFullDetailOpen(false);
    setIsFishAreaExpanded(false);
    setFishWidgetAreaSource("viewport");
    setFishWidgetAnchor(null);
    setFishIQAreaSignals([]);
    try {
      searchedFishAreaMarkerRef.current?.remove?.();
      searchedFishAreaMarkerRef.current = null;
    } catch {}
  }, [props.isActive]);

  useEffect(() => {
    return () => {
      if (fishHydrationWriteTimerRef.current != null) {
        window.clearTimeout(fishHydrationWriteTimerRef.current);
        fishHydrationWriteTimerRef.current = null;
      }
    };
  }, []);

  const clearInitialViewportKickTimers = () => {
    if (!initialViewportKickTimersRef.current.length) return;
    initialViewportKickTimersRef.current.forEach((timerId) =>
      window.clearTimeout(timerId),
    );
    initialViewportKickTimersRef.current = [];
  };

  const runInitialViewportKick = (map: any) => {
    try {
      if (!map || !isActiveRef.current) return;

      const center = map.getCenter?.();
      const zoom = Number(map.getZoom?.() ?? 13);

      map.invalidateSize(true);

      if (
        center &&
        Number.isFinite(center.lat) &&
        Number.isFinite(center.lng)
      ) {
        programmaticMoveRef.current = true;
        map.setView([center.lat, center.lng], zoom, { animate: false });
        window.setTimeout(() => {
          programmaticMoveRef.current = false;
        }, 0);
      }
    } catch {}
  };

  const scheduleBootViewportKicks = (map: any) => {
    clearInitialViewportKickTimers();
    [0, 80, 220, 450, 800].forEach((delay) => {
      const timerId = window.setTimeout(() => {
        runInitialViewportKick(map);
      }, delay);
      initialViewportKickTimersRef.current.push(timerId);
    });
  };

  const settleMapLayoutBeforeFirstView = async (map: any) => {
    if (didInitialLayoutSettleRef.current) return;

    didInitialLayoutSettleRef.current = true;

    await new Promise<void>((resolve) => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          try {
            map.invalidateSize(true);
          } catch {}
          resolve();
        });
      });
    });

    scheduleBootViewportKicks(map);
  };

  const clearSavedSpotLongPress = () => {
    if (savedSpotLongPressTimerRef.current != null) {
      window.clearTimeout(savedSpotLongPressTimerRef.current);
      savedSpotLongPressTimerRef.current = null;
    }
    savedSpotLongPressStartRef.current = null;
  };

  const loadOfflineRegions = async () => {
    try {
      const regions = await idb.getAll(REGION_STORE);
      setOfflineRegions(Array.isArray(regions) ? regions : []);
    } catch (err) {
      console.warn(
        "[offline-maps] Failed to load offline regions; continuing without offline map list.",
        err,
      );
      setOfflineRegions([]);
    }
  };

  const markMapVisualReady = (delayMs = 90) => {
    if (mapVisualReadyTimerRef.current != null) {
      window.clearTimeout(mapVisualReadyTimerRef.current);
      mapVisualReadyTimerRef.current = null;
    }

    mapVisualReadyTimerRef.current = window.setTimeout(() => {
      mapVisualReadyTimerRef.current = null;
      setIsMapVisualReady(true);
    }, delayMs);
  };

  const getTileUrl = () => BASEMAPS.hot.lightUrl;
  const getAttribution = () => BASEMAPS.hot.lightAttr;

  const applyTileTuning = () => {
    // Keep dark-mode map tint as one pane-level composite pass.
    // Do not filter the individual raster tiles; per-tile filters are what make
    // Leaflet's 256px tile boundaries show up as faint grid/vector seams.
    const darkFilter = "brightness(0.50) contrast(0.96) saturate(0.72)";
    const activeFilter = isDarkModeRef.current ? darkFilter : "none";

    const el = tileLayerRef.current?.getContainer?.() as
      HTMLElement | undefined;
    if (el) {
      el.classList.remove("roddo-tiles-dark");
      el.classList.remove("roddo-tiles-light");
      el.classList.add(
        isDarkModeRef.current ? "roddo-tiles-dark" : "roddo-tiles-light",
      );
      el.style.transition = "none";
      el.style.willChange = "auto";
      el.style.filter = "none";

      const imgs = Array.from(el.querySelectorAll("img")) as HTMLImageElement[];
      imgs.forEach((img: HTMLImageElement) => {
        img.style.transition = "none";
        img.style.willChange = "auto";
        img.style.filter = "none";
      });
    }

    const map = mapInstanceRef.current;
    if (!map?.getPane) return;

    const tilePane = map.getPane("tilePane") as HTMLElement | undefined;
    if (!tilePane) return;

    tilePane.style.transition = "filter 180ms ease";
    tilePane.style.willChange = isDarkModeRef.current ? "filter" : "auto";
    tilePane.style.filter = activeFilter;

    const paneImgs = Array.from(
      tilePane.querySelectorAll("img"),
    ) as HTMLImageElement[];
    paneImgs.forEach((img: HTMLImageElement) => {
      img.style.transition = "none";
      img.style.willChange = "auto";
      img.style.filter = "none";
    });
  };

  const getUserLocationIcon = (isDark: boolean, zoomOverride?: number) => {
    const avatar = userAvatarUrl || USER_AVATAR_FALLBACK;
    const mapZoom = Number.isFinite(Number(zoomOverride))
      ? Number(zoomOverride)
      : Number(mapInstanceRef.current?.getZoom?.() ?? currentMapZoom ?? 13);
    const avatarScale = getMapAvatarScaleForZoom(mapZoom);
    const { iconSize, iconAnchor } = getMapAvatarIconMetrics(avatarScale);

    return createBigBlueDivIcon({
      className: "roddo-map-avatar-icon bg-transparent",
      html: renderMapAvatarMarkerHtml({
        profile,
        profilePhotoUrl: avatar,
        isDark,
        avatarScale,
      }),
      iconSize,
      iconAnchor,
      popupAnchor: [0, -Math.round(76 * avatarScale)],
    });
  };

  const refreshCrewPresenceMarkers = useCallback((force = false) => {
    crewPresenceLayerControllerRef.current?.update({
      layer: crewPresenceLayerRef.current,
      presence: crewPresenceRef.current,
      isDarkMode: isDarkModeRef.current,
      force,
    });
  }, []);


  const isWorldwideTournament = (tournament: any): boolean => {
    const locationScope = String(tournament?.locationScope || '').trim().toLowerCase();
    const tournamentScale = String(tournament?.tournamentScale || '').trim().toLowerCase();
    const locationLabel = String(
      tournament?.locationLabel || tournament?.mapLocation?.label || '',
    ).trim().toLowerCase();

    return (
      locationScope === 'worldwide' ||
      tournamentScale === 'global' ||
      locationLabel === 'worldwide' ||
      locationLabel === 'global'
    );
  };

  const compactTournamentCountdown = (targetMs: number, nowMs = searchLureClockMs): string => {
    if (!Number.isFinite(targetMs) || targetMs <= 0) return '';
    const delta = targetMs - nowMs;
    if (delta <= 0) return 'now';
    const minutes = Math.max(1, Math.floor(delta / 60_000));
    const days = Math.floor(minutes / 1440);
    const hours = Math.floor((minutes % 1440) / 60);
    const mins = minutes % 60;
    if (days > 0) return `${days}d ${hours}h`;
    if (hours > 0) return `${hours}h ${mins}m`;
    return `${mins}m`;
  };

  const getTournamentMapPresentation = (tournament: any) => {
    const id = String(tournament?.id || '').trim();
    const status = String(tournament?.status || '').toLowerCase();
    const participant = myTournamentParticipation[id] || null;
    const participantStatus = String(participant?.status || '').toLowerCase();
    const isPending = participantStatus === 'pending';
    const isJoined = participantStatus === 'approved';
    const isLive = status === 'active' || status === 'live';
    const isFinished = status === 'finalized' || status === 'completed';
    const registrationOpen = status === 'registration_open' && Number(tournament?.registrationClosesAt || 0) > searchLureClockMs;
    const uid = String((props.userProfile as any)?.uid || '').trim();
    const resultsSeen = isFinished ? hasSeenTournamentResults(uid, id) : false;

    if (isFinished && isJoined && !resultsSeen) {
      return { badge: 'RESULTS', timer: '', tone: 'finished', priority: 5, tooltip: 'Results ready' };
    }
    if (isLive) {
      const timeLeft = compactTournamentCountdown(Number(tournament?.endDate || 0));
      return {
        badge: isJoined ? 'Live!' : 'Live Now',
        timer: timeLeft ? `Ends: ${timeLeft}` : '',
        tone: 'live',
        priority: isJoined ? 6 : 4,
        tooltip: isJoined ? 'You’re competing live' : 'Watch live tournament',
      };
    }
    if (isPending) {
      const pendingTime = registrationOpen
        ? compactTournamentCountdown(Number(tournament?.registrationClosesAt || 0))
        : compactTournamentCountdown(Number(tournament?.startDate || 0));
      return {
        badge: 'WAIT',
        timer: pendingTime ? `${registrationOpen ? 'Closes' : 'Starts'}: ${pendingTime}` : '',
        tone: 'pending',
        priority: 5,
        tooltip: 'Registration pending',
      };
    }
    if (isJoined) {
      const startsIn = compactTournamentCountdown(Number(tournament?.startDate || 0));
      return {
        badge: 'Registered',
        timer: startsIn ? `Starts: ${startsIn}` : '',
        tone: 'joined',
        priority: 5,
        tooltip: 'Registered · ready to compete',
      };
    }
    if (registrationOpen) {
      const closesIn = compactTournamentCountdown(Number(tournament?.registrationClosesAt || 0));
      return {
        badge: 'Join Now!',
        timer: closesIn ? `Closes: ${closesIn}` : '',
        tone: 'open',
        priority: 3,
        tooltip: 'Registration open · get in',
      };
    }
    if (isFinished) {
      return { badge: 'FINAL', timer: '', tone: 'finished', priority: 2, tooltip: 'Finished' };
    }
    const startsIn = compactTournamentCountdown(Number(tournament?.startDate || 0));
    return {
      badge: startsIn ? 'Soon' : '',
      timer: startsIn ? `Starts: ${startsIn}` : '',
      tone: 'quiet',
      priority: 1,
      tooltip: 'Tournament',
    };
  };


  const tournamentHubRows = useMemo(() => {
    const shellBalance = Math.max(
      0,
      Number((props.userProfile as any)?.shells ?? (props.userProfile as any)?.shellBalance ?? 0),
    );

    // Tournament discovery is geography-first on Big Blue: pinned/local events
    // nearest the angler lead the list, then expand outward, with WORLD events
    // intentionally last because they have no honest physical coordinate.
    const cached = readCachedBrowserLocation();
    const propLat = Number(props.coords?.lat);
    const propLng = Number(props.coords?.lon);
    const propsAreFinite = Number.isFinite(propLat) && Number.isFinite(propLng);
    const propsLookLikeUntouchedDefault =
      propsAreFinite &&
      Math.abs(propLat - 34.0522) < 0.0001 &&
      Math.abs(propLng + 118.2437) < 0.0001;
    const userAnchor: LatLng | null =
      latestUserLatLngRef.current ||
      (cached ? { lat: Number(cached.lat), lng: Number(cached.lng) } : null) ||
      (propsAreFinite && !propsLookLikeUntouchedDefault
        ? { lat: propLat, lng: propLng }
        : null);

    const rows = (Array.isArray(props.tournaments) ? props.tournaments : [])
      .filter((tournament: any) => {
        const status = String(tournament?.status || "").toLowerCase();
        if (["draft", "rejected", "cancelled", "archived"].includes(status)) return false;
        return tournamentPassesDiscoveryFilters(
          tournament,
          tournamentMapFilters,
          shellBalance,
          searchLureClockMs,
        );
      })
      .map((tournament: any) => {
        const worldwide = isWorldwideTournament(tournament);
        const ll = worldwide ? null : getSharedTournamentMapLatLng(tournament);
        const distance =
          userAnchor && ll
            ? getDistanceInMeters(userAnchor.lat, userAnchor.lng, ll.lat, ll.lng)
            : Number.POSITIVE_INFINITY;

        return {
          tournament,
          presentation: getTournamentMapPresentation(tournament),
          worldwide,
          distance,
        };
      })
      .sort((a: any, b: any) => {
        if (a.worldwide !== b.worldwide) return a.worldwide ? 1 : -1;

        if (!a.worldwide && !b.worldwide) {
          const aDistance = Number(a.distance);
          const bDistance = Number(b.distance);
          if (Number.isFinite(aDistance) || Number.isFinite(bDistance)) {
            if (!Number.isFinite(aDistance)) return 1;
            if (!Number.isFinite(bDistance)) return -1;
            const distanceDelta = aDistance - bDistance;
            if (Math.abs(distanceDelta) > 250) return distanceDelta;
          }
        }

        const priorityDelta = Number(b.presentation?.priority || 0) - Number(a.presentation?.priority || 0);
        if (priorityDelta) return priorityDelta;
        const discoveryDelta = getTournamentDiscoveryPriority(b.tournament) - getTournamentDiscoveryPriority(a.tournament);
        if (discoveryDelta) return discoveryDelta;
        return Number(a.tournament?.startDate || Number.MAX_SAFE_INTEGER) - Number(b.tournament?.startDate || Number.MAX_SAFE_INTEGER);
      });

    return rows.slice(0, 12);
  }, [
    props.tournaments,
    props.userProfile,
    props.coords?.lat,
    props.coords?.lon,
    tournamentMapFilters,
    myTournamentParticipation,
    tournamentResultsSeenRevision,
    searchLureClockMs,
  ]);

  const worldTournamentRows = useMemo(
    () => tournamentHubRows.filter(({ tournament }: any) => isWorldwideTournament(tournament)),
    [tournamentHubRows],
  );

  // Geographic trophy pins below remain map-truth only. Worldwide events get
  // their own viewport-fixed WORLD control so Roddo never invents coordinates.

  const getTournamentScopeBadge = (tournament: any) => {
    const scale = String(tournament?.tournamentScale || '').trim().toLowerCase();
    const visibility = String(tournament?.visibility || '').trim().toLowerCase();

    if (isWorldwideTournament(tournament) || scale === 'open_global') {
      return { emoji: '🌎', label: 'Global tournament' };
    }

    if (['friend_group', 'local_crew', 'fishing_club', 'boat_crew'].includes(scale)) {
      return { emoji: '👥', label: 'Social tournament' };
    }

    if (['city', 'province', 'island', 'country'].includes(scale)) {
      return { emoji: '📍', label: 'Geographic tournament' };
    }

    if (visibility === 'private' || visibility === 'crew') {
      return { emoji: '👥', label: 'Social tournament' };
    }

    return { emoji: '📍', label: 'Geographic tournament' };
  };

  const updateTournamentMarkers = useCallback(() => {
    const map = mapInstanceRef.current;
    const layer = tournamentLayerRef.current;
    const controller = tournamentLayerControllerRef.current;
    if (!map || !layer || !controller) return;

    const localRows = tournamentHubRows.filter(
      ({ tournament }: any) => !isWorldwideTournament(tournament),
    );

    controller.sync({
      map,
      layer,
      localRows,
      worldRows: worldTournamentRows,
      hideAll: hideAllTournamentMapMarkers,
      isDarkMode: !!isDarkModeRef.current,
      nowMs: searchLureClockMs,
      userLatLng: latestUserLatLngRef.current,
      participation: myTournamentParticipation,
      resultsSeenRevision: tournamentResultsSeenRevision,
      getTournamentLatLng: getSharedTournamentMapLatLng,
      getPresentation: getTournamentMapPresentation,
      getScopeBadge: getTournamentScopeBadge,
      getOccupiedMapPoints: (activeMap) =>
        getUnifiedOccupiedMapPoints(activeMap, ["tournament"]),
      clampPointToSafeBox,
      resolveMapMarkerCollision,
      getSafeInsetsPx,
      focusMapCardNearMarker,
      onBeforeLocalMarkerOpen: () => {
        setSelectedFishSpot(null);
        setIsFishWidgetOpen(false);
      },
      onBeforeWorldMarkerOpen: () => {
        setIsTournamentMapFilterOpen(false);
        setQuickWindow(null);
        dismissMapTextInputs();
        setIsWeatherOpen(false);
        setIsFishWidgetOpen(false);
        setSelectedFishSpot(null);
        setIsFishFullDetailOpen(false);
        setIsSavedSpotsOpen(false);
        setPendingSavedSpotLatLng(null);
        setSelectedSavedSpotId(null);
        setIsSavedSpotFilterMenuOpen(false);
        setIsSelectedSavedSpotMenuOpen(false);
        if (isOfflinePanelOpen) onCloseOfflinePanel?.();
      },
      onOpenTournament: (tournamentId) => {
        const previewTournament = (props.tournaments || []).find?.(
          (entry: any) => String(entry?.id || "") === String(tournamentId),
        );
        if (previewTournament) setSelectedTournamentMapDetail(previewTournament);
        else if (typeof props.onTournamentClick === "function") props.onTournamentClick(tournamentId);
        else props.onEventsClick?.();
      },
      onOpenTournamentDock: () => {
        props.onEventsClick?.({ from: "big_blue_world_tournaments" });
      },
      onMarkResultsSeen: (tournamentId) => {
        markTournamentResultsSeen(
          String((props.userProfile as any)?.uid || ""),
          tournamentId,
        );
        setTournamentResultsSeenRevision((value) => value + 1);
      },
    });
  }, [
    tournamentHubRows,
    worldTournamentRows,
    hideAllTournamentMapMarkers,
    myTournamentParticipation,
    tournamentResultsSeenRevision,
    searchLureClockMs,
    dismissMapTextInputs,
    isOfflinePanelOpen,
    onCloseOfflinePanel,
    props.tournaments,
    props.onTournamentClick,
    props.onEventsClick,
    props.userProfile,
  ]);

  useEffect(() => {
    // Leaflet map listeners are installed once. Keep them pointed at the
    // latest tournament renderer instead of capturing a stale React closure.
    updateTournamentMarkersRef.current = updateTournamentMarkers;
  }, [updateTournamentMarkers]);


  useEffect(() => {
    if (!props.isActive) return;
    if (!mapInstanceRef.current || !tournamentLayerRef.current) return;

    updateTournamentMarkersRef.current();
  }, [
    props.isActive,
    props.tournaments,
    isDarkMode,
    hideAllTournamentMapMarkers,
    tournamentHubRows.length,
  ]);

  const escapeHtml = (value: string) =>
    String(value || "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");


  const getSavedSpotPopupHtml = (spot: SavedSpotRecord, expanded = false) => {
    const kind = resolveSavedSpotKind(spot);
    const meta = SAVED_SPOT_KIND_META[kind] || SAVED_SPOT_KIND_META.favorite;
    const safeName = escapeHtml(String(spot.name || meta.label));
    const safeKind = escapeHtml(String(meta.label || "Saved spot"));
    const safeNote = escapeHtml(String(spot.note || "No description yet."));
    const dark = !!isDarkModeRef.current;
    const savedOn = escapeHtml(formatSavedSpotTimestamp(spot.createdAt));
    const coords = `${Number(spot.lat).toFixed(4)}, ${Number(spot.lng).toFixed(4)}`;
    const actionBg = dark ? "rgba(255,255,255,.07)" : "rgba(255,255,255,.84)";
    const actionBorder = dark ? "rgba(255,255,255,.10)" : "rgba(91,67,49,.10)";
    return `
      <div style="width:min(270px,calc(100vw - 36px));padding:2px 1px 1px;color:${dark ? '#F3F6FB' : '#2D3957'};font-family:inherit;">
        <div style="display:flex;align-items:flex-start;gap:8px;">
          <div style="width:28px;height:28px;border-radius:9px;display:grid;place-items:center;background:${dark ? 'rgba(255,224,130,.10)' : 'rgba(47,128,237,.08)'};font-size:15px;flex:none;">${escapeHtml(String(meta.emoji || '★'))}</div>
          <div style="min-width:0;flex:1;">
            <div style="font-size:13px;font-weight:700;line-height:1.2;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">${safeName}</div>
            <div style="margin-top:2px;font-size:9px;font-weight:600;letter-spacing:.08em;text-transform:uppercase;color:${dark ? '#95A4B8' : '#8A7E72'};">${safeKind}</div>
          </div>
          <button type="button" data-saved-spot-action="toggle" aria-label="${expanded ? 'Collapse' : 'Expand'} saved spot details" style="width:28px;height:28px;border-radius:999px;border:1px solid ${actionBorder};background:${actionBg};color:${dark ? '#F3F6FB' : '#2D3957'};font:600 17px/1 system-ui;display:grid;place-items:center;cursor:pointer;">${expanded ? '−' : '+'}</button>
        </div>
        <div style="margin-top:7px;font-size:11px;font-weight:500;line-height:1.45;color:${dark ? '#C4CEDC' : '#665E56'};white-space:normal;word-break:break-word;">${safeNote}</div>
        ${expanded ? `
          <div style="margin-top:9px;padding-top:9px;border-top:1px solid ${dark ? 'rgba(132,151,190,.10)' : 'rgba(91,67,49,.08)'};">
            <div style="display:grid;gap:4px;font-size:9.5px;font-weight:500;color:${dark ? '#95A4B8' : '#7A7067'};">
              <div>Saved ${savedOn}</div>
              <div>${coords}</div>
            </div>
            <div style="display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:6px;margin-top:9px;">
              <button type="button" data-saved-spot-action="edit" style="height:30px;border-radius:10px;border:1px solid ${actionBorder};background:${actionBg};color:${dark ? '#DCE8FF' : '#245FAD'};font:600 10px/1 system-ui;cursor:pointer;">Edit</button>
              <button type="button" data-saved-spot-action="share" style="height:30px;border-radius:10px;border:1px solid ${actionBorder};background:${actionBg};color:${dark ? '#F3F6FB' : '#4F463D'};font:600 10px/1 system-ui;cursor:pointer;">Share</button>
              <button type="button" data-saved-spot-action="delete" style="height:30px;border-radius:10px;border:1px solid ${dark ? 'rgba(255,141,120,.20)' : 'rgba(196,80,61,.16)'};background:${dark ? 'rgba(255,141,120,.08)' : 'rgba(255,239,235,.92)'};color:${dark ? '#FF9B88' : '#B94D3D'};font:600 10px/1 system-ui;cursor:pointer;">Delete</button>
            </div>
          </div>` : ''}
      </div>
    `;
  };

  const getSavedSpotClusterPopupHtml = (spots: SavedSpotRecord[]) => {
    const dark = !!isDarkModeRef.current;
    const rows = spots.slice(0, 6).map((spot) => {
      const meta = SAVED_SPOT_KIND_META[resolveSavedSpotKind(spot)] || SAVED_SPOT_KIND_META.favorite;
      const safeName = escapeHtml(String(spot.name || meta.label));
      return `<div style="display:flex;align-items:center;gap:7px;padding:5px 0;border-top:1px solid ${dark ? 'rgba(132,151,190,.10)' : 'rgba(91,67,49,.08)'};"><span style="font-size:13px;">${escapeHtml(String(meta.emoji || '★'))}</span><span style="min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;font-size:11px;font-weight:600;">${safeName}</span></div>`;
    }).join('');
    const more = spots.length > 6 ? `<div style="padding-top:5px;font-size:9px;font-weight:600;color:${dark ? '#95A4B8' : '#8A7E72'};">+${spots.length - 6} more</div>` : '';
    return `<div style="min-width:190px;max-width:250px;color:${dark ? '#F3F6FB' : '#2D3957'};font-family:inherit;"><div style="font-size:12px;font-weight:700;padding-bottom:4px;">${spots.length} saved spots here</div>${rows}${more}</div>`;
  };

  const createSavedSpotIcon = (spot: SavedSpotRecord, selected = false) => {
    const kind = resolveSavedSpotKind(spot);
    const meta = SAVED_SPOT_KIND_META[kind] || SAVED_SPOT_KIND_META.favorite;
    const safeName = escapeHtml(String(spot.name || meta.label));

    const palettes: Record<SavedSpotKind, { shell: string; deep: string; glyph: string }> = isDarkModeRef.current
      ? {
          favorite: { shell: '#493F70', deep: '#27223F', glyph: '#FFE7A8' },
          fish: { shell: '#1F4C63', deep: '#122D3D', glyph: '#A8EAFF' },
          food: { shell: '#694131', deep: '#3A261F', glyph: '#FFC8A8' },
          launch: { shell: '#23545A', deep: '#15353B', glyph: '#A8EEF2' },
          sunset: { shell: '#5C3D54', deep: '#352536', glyph: '#FFC0BB' },
        }
      : {
          favorite: { shell: '#F4BE32', deep: '#C88912', glyph: '#E7A900' },
          fish: { shell: '#2F80ED', deep: '#0B5ED1', glyph: '#1767D8' },
          food: { shell: '#F58A43', deep: '#C85D20', glyph: '#E66D26' },
          launch: { shell: '#43C7D9', deep: '#168CA2', glyph: '#159BB3' },
          sunset: { shell: '#F08B78', deep: '#C85F51', glyph: '#DB6E5D' },
        };
    const palette = palettes[kind] || palettes.favorite;
    const size = selected ? 44 : 38;
    const pinH = size + 10;
    const innerInset = selected ? 4 : 4;
    const glyphSize = selected ? 21 : 18;
    const labelHtml = selected
      ? `<div style="position:absolute;left:50%;top:-13px;transform:translateX(-50%);z-index:4;max-width:170px;padding:4px 7px;border-radius:999px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;font-size:9px;font-weight:950;line-height:1;color:${isDarkModeRef.current ? '#FFFFFF' : '#243441'};background:${isDarkModeRef.current ? 'rgba(18,26,44,.97)' : 'rgba(255,253,248,.98)'};border:1px solid ${isDarkModeRef.current ? 'rgba(255,255,255,.12)' : 'rgba(95,72,49,.14)'};box-shadow:0 4px 10px rgba(15,23,42,.16);">${safeName}</div>`
      : '';

    return createBigBlueDivIcon({
      className: 'roddo-saved-spot-icon',
      iconSize: [size, pinH],
      iconAnchor: [Math.round(size / 2), pinH - 2],
      popupAnchor: [0, -Math.round(size * 0.8)],
      html: `
        <div class="roddo-saved-spot-bounce" style="position:relative;width:${size}px;height:${pinH}px;overflow:visible;">
          ${labelHtml}
          <div style="position:absolute;left:50%;top:1px;width:${size - 3}px;height:${size - 3}px;transform:translateX(-50%) rotate(-45deg);border-radius:50% 50% 50% 10%;background:linear-gradient(145deg,${palette.shell} 0%,${palette.deep} 84%);border:2px solid ${isDarkModeRef.current ? 'rgba(194,210,244,.56)' : 'rgba(255,255,255,.98)'};box-shadow:${selected ? `0 7px 16px rgba(0,0,0,.30),0 0 0 2px ${palette.shell}38` : (isDarkModeRef.current ? '0 5px 11px rgba(0,0,0,.26)' : '0 5px 10px rgba(15,23,42,.20)')};">
            <div style="position:absolute;inset:${innerInset}px;border-radius:999px;background:${isDarkModeRef.current ? '#0D1627' : '#FFFDF8'};display:grid;place-items:center;transform:rotate(45deg);box-shadow:inset 0 0 0 1px ${isDarkModeRef.current ? 'rgba(184,204,245,.12)' : 'rgba(255,255,255,.65)'};overflow:hidden;">
              <span aria-hidden="true" style="display:block;font-size:${glyphSize}px;line-height:1;transform:translateY(-1px);filter:drop-shadow(0 1px 2px rgba(15,23,42,.16));">${meta.emoji}</span>
            </div>
          </div>
        </div>
      `,
    });
  };

  const createSavedSpotClusterIcon = (
    count: number,
    kinds: SavedSpotKind[],
  ) => {
    const preferredKind = normalizeSavedSpotKind(kinds[0]);
    const meta = SAVED_SPOT_KIND_META[preferredKind] || SAVED_SPOT_KIND_META.favorite;
    const palettes: Record<SavedSpotKind, { shell: string; deep: string }> = isDarkModeRef.current
      ? {
          favorite: { shell: '#493F70', deep: '#27223F' },
          fish: { shell: '#1F4C63', deep: '#122D3D' },
          food: { shell: '#694131', deep: '#3A261F' },
          launch: { shell: '#23545A', deep: '#15353B' },
          sunset: { shell: '#5C3D54', deep: '#352536' },
        }
      : {
          favorite: { shell: '#F4BE32', deep: '#C88912' },
          fish: { shell: '#2F80ED', deep: '#0B5ED1' },
          food: { shell: '#F58A43', deep: '#C85D20' },
          launch: { shell: '#43C7D9', deep: '#168CA2' },
          sunset: { shell: '#F08B78', deep: '#C85F51' },
        };
    const palette = palettes[preferredKind] || palettes.favorite;
    const size = count >= 10 ? 46 : count >= 5 ? 43 : 40;
    const pinH = size + 10;
    const countText = count > 99 ? '99+' : String(Math.max(2, count));

    return createBigBlueDivIcon({
      className: 'roddo-saved-spot-cluster-icon',
      iconSize: [size, pinH],
      iconAnchor: [Math.round(size / 2), pinH - 2],
      html: `
        <div class="roddo-saved-spot-cluster-bounce" style="position:relative;width:${size}px;height:${pinH}px;overflow:visible;">
          <div style="position:absolute;left:50%;top:1px;width:${size - 3}px;height:${size - 3}px;transform:translateX(-50%) rotate(-45deg);border-radius:50% 50% 50% 10%;background:linear-gradient(145deg,${palette.shell} 0%,${palette.deep} 84%);border:2px solid ${isDarkModeRef.current ? 'rgba(194,210,244,.56)' : 'rgba(255,255,255,.98)'};box-shadow:${isDarkModeRef.current ? '0 6px 14px rgba(0,0,0,.30),0 0 0 1px rgba(126,145,196,.10)' : '0 6px 12px rgba(15,23,42,.22)'};">
            <div style="position:absolute;inset:4px;border-radius:999px;background:${isDarkModeRef.current ? '#0D1627' : '#FFFDF8'};display:grid;place-items:center;transform:rotate(45deg);overflow:hidden;">
              <span aria-hidden="true" style="font-size:${Math.max(17, size - 24)}px;line-height:1;transform:translateY(-1px);">${meta.emoji}</span>
            </div>
          </div>
          <div style="position:absolute;right:-2px;top:-3px;z-index:3;min-width:17px;height:17px;padding:0 4px;border-radius:999px;display:grid;place-items:center;background:${isDarkModeRef.current ? '#101A2D' : '#1F5FD6'};color:${isDarkModeRef.current ? '#DCE8FF' : '#fff'};border:2px solid ${isDarkModeRef.current ? '#6E78B8' : '#fffaf2'};font:950 8px/1 system-ui,-apple-system,sans-serif;box-shadow:0 2px 5px rgba(15,23,42,.2);">${countText}</div>
        </div>
      `,
    });
  };

  const scheduleSavedSpotMarkerUpdate = useCallback(
    (force = false) => {
      const map = mapInstanceRef.current;
      const layer = savedSpotLayerRef.current;
      if (!map || !layer) return;

      savedSpotsLayerControllerRef.current?.schedule(
        {
          map,
          layer,
          spots: savedSpotsRef.current as any,
          visible: showSavedSpotsOnMapRef.current,
          activeFilter: savedSpotFilterRef.current,
          selectedSpotId: selectedSavedSpotIdRef.current,
          dark: !!isDarkModeRef.current,
          createSpotIcon: (spot, selected) => createSavedSpotIcon(spot as SavedSpotRecord, selected),
          createClusterIcon: (count, kinds) => createSavedSpotClusterIcon(count, kinds as SavedSpotKind[]),
          getSpotPopupHtml: (spot, expanded) => getSavedSpotPopupHtml(spot as SavedSpotRecord, expanded),
          getClusterPopupHtml: (spots) => getSavedSpotClusterPopupHtml(spots as SavedSpotRecord[]),
          focusPopup: focusMapCardNearMarker,
          onBeforeMarkerOpen: (opts) => {
            dismissMapTextInputs();
            setPendingSavedSpotLatLng(null);
            if (opts?.clearEditing) setEditingSavedSpotId(null);
            setSelectedSavedSpotId(null);
            setIsSavedSpotFilterMenuOpen(false);
            setIsSelectedSavedSpotMenuOpen(false);
            setIsSavedSpotsOpen(false);
          },
          onEdit: (spot) => editSavedSpot(spot as SavedSpotRecord),
          onShare: (spot) => handleShareSavedSpot(spot as SavedSpotRecord),
          onDelete: (spot) => setSavedSpotDeleteConfirm(spot as SavedSpotRecord),
          closeTournamentTooltips: () => {
            tournamentLayerControllerRef.current?.forEachMarker((marker) => {
              try { marker?.closeTooltip?.(); } catch {}
            });
          },
        },
        force,
      );
    },
    [dismissMapTextInputs, editSavedSpot, handleShareSavedSpot],
  );

  const clearFishMarkers = useCallback(
    (opts?: { resetSignature?: boolean; invalidateVersion?: boolean }) => {
      fishIQLayerControllerRef.current?.clear(
        mapInstanceRef.current,
        fishLayerRef.current,
        opts,
      );
    },
    [],
  );

  const refreshFishHubMarkerIcons = useCallback(() => {
    fishIQLayerControllerRef.current?.refreshSelection({
      open: isFishWidgetOpen,
      mode: fishWidgetMode,
      hasSelectedFish: !!selectedFishSpot,
    });
  }, [fishWidgetMode, isFishWidgetOpen, selectedFishSpot]);

  const scheduleFishMarkerUpdate = useCallback(
    (
      spots?: Hotspot[] | null,
      force = false,
      opts?: { invalidateVersion?: boolean },
    ) => {
      const map = mapInstanceRef.current;
      const layer = fishLayerRef.current;
      if (!map || !layer) return;

      const nextSpots = Array.isArray(spots)
        ? [...spots]
        : Array.isArray(fishMarkerHotspotsRef.current)
          ? [...fishMarkerHotspotsRef.current]
          : [];

      fishIQLayerControllerRef.current?.schedule(
        {
          map,
          layer,
          spots: nextSpots,
          dark: !!isDarkModeRef.current,
          fishWidgetAnchor: fishWidgetAnchorRef.current,
          latestUserLatLng: latestUserLatLngRef.current,
          mapActionAnchor: mapActionAnchorRef.current,
          promotedMapActionAnchor: promotedMapActionFishIQAnchorRef.current,
          uniqueFishCount: uniqueFishListSpots.length,
          getOccupiedPoints: () => getUnifiedOccupiedMapPoints(map, ["fish"]),
          resolveCollision: resolveMapMarkerCollision,
          hashString,
          onHubClick: (kind) => {
            setFishWidgetMode((prev) => (prev === kind ? prev : kind));
            setIsFishWidgetOpen((prev) => (prev ? prev : true));
            setSelectedFishSpot((prev) => (prev ? null : prev));
          },
        },
        force,
        opts,
      );
    },
    [uniqueFishListSpots.length],
  );

  // Keep the marker layer synchronized after POI -> FishIQ promotion.
  // The search path schedules a marker render in the same tick that it updates
  // fishWidgetAnchor, so rerun once after promotion state commits. The layer
  // controller then suppresses a duplicate area hub when the POI owns FishIQ.
  useEffect(() => {
    const nextSpots = Array.isArray(fishMarkerHotspotsRef.current)
      ? fishMarkerHotspotsRef.current
      : [];
    if (!nextSpots.length) return;
    scheduleFishMarkerUpdate(nextSpots, true);
  }, [mapActionHasFishIQ, scheduleFishMarkerUpdate]);

  const rerenderFishMarkersNow = useCallback(
    (spots?: Hotspot[] | null, opts?: { settlePasses?: boolean }) => {
      clearFishMarkers({ resetSignature: true, invalidateVersion: true });

      const nextSpots = Array.isArray(spots)
        ? [...spots]
        : Array.isArray(fishMarkerHotspotsRef.current)
          ? [...fishMarkerHotspotsRef.current]
          : [];

      if (!nextSpots.length) return;

      scheduleFishMarkerUpdate(nextSpots, true, { invalidateVersion: false });

      if (opts?.settlePasses) {
        [0, 90, 220].forEach((delay) => {
          window.setTimeout(() => {
            if (!mapInstanceRef.current || !isActiveRef.current) return;
            scheduleFishMarkerUpdate(nextSpots, true, {
              invalidateVersion: false,
            });
          }, delay);
        });
      }
    },
    [clearFishMarkers, scheduleFishMarkerUpdate],
  );

  const persistFishHydration = useCallback(
    (spots?: Hotspot[] | null) => {
      const uid = String(profile?.uid || "").trim();
      const map = mapInstanceRef.current;
      const nextSpots = Array.isArray(spots)
        ? spots
        : fishMarkerHotspotsRef.current;

      if (!uid || !map || !Array.isArray(nextSpots) || !nextSpots.length) return;

      const centerRaw = map.getCenter?.();
      const center =
        centerRaw &&
        Number.isFinite(Number(centerRaw.lat)) &&
        Number.isFinite(Number(centerRaw.lng))
          ? { lat: Number(centerRaw.lat), lng: Number(centerRaw.lng) }
          : lastRefreshViewportRef.current?.center || null;

      if (!center) return;

      const anchorCandidate =
        fishWidgetAnchor ||
        lastRefreshViewportRef.current?.center ||
        center;

      const anchor = isFiniteLatLng(anchorCandidate)
        ? {
            lat: Number(anchorCandidate.lat),
            lng: Number(anchorCandidate.lng),
          }
        : null;

      writeFishHydrationCache({
        version: FISH_HYDRATION_CACHE_VERSION,
        savedAt: Date.now(),
        uid,
        center,
        zoom: Number(map.getZoom?.() ?? currentMapZoom ?? 13),
        anchor,
        hotspots: [...nextSpots],
      });
    },
    [currentMapZoom, fishWidgetAnchor, profile?.uid],
  );

  // Persist the latest successful fish set after base or enhanced FishIQ data
  // lands. Debouncing keeps localStorage quiet while a search upgrades in place.
  useEffect(() => {
    if (!hotspots.length || !profile?.uid) return;

    if (fishHydrationWriteTimerRef.current != null) {
      window.clearTimeout(fishHydrationWriteTimerRef.current);
    }

    fishHydrationWriteTimerRef.current = window.setTimeout(() => {
      fishHydrationWriteTimerRef.current = null;
      persistFishHydration(hotspots);
    }, 120);

    return () => {
      if (fishHydrationWriteTimerRef.current != null) {
        window.clearTimeout(fishHydrationWriteTimerRef.current);
        fishHydrationWriteTimerRef.current = null;
      }
    };
  }, [hotspots, persistFishHydration, profile?.uid]);

  const recordFishIQValidationSnapshot = useCallback(
    (
      spots: Hotspot[],
      anchor: LatLng,
      source: "big_blue_initial" | "big_blue_manual" | "big_blue_saved_area",
    ) => {
      if (!profile?.uid || !Array.isArray(spots) || !spots.length) return;

      const areaRead = spots
        .map((spot) => (spot as any)?.fishIQAreaRead as FishIQAreaRead | null | undefined)
        .find(
          (read) =>
            read &&
            !read.readId &&
            Array.isArray(read.species) &&
            read.species.some((prediction) => Boolean(prediction?.canonicalSpeciesId)),
        );

      if (!areaRead) return;
      const generatedAt = Number(areaRead.generatedAt || 0);

      void recordFishIQPredictionRead({
        source,
        anchor,
        areaRead,
      })
        .then((result) => {
          if (!result?.readId) return;

          setHotspots((current) => {
            let changed = false;
            const next = current.map((spot) => {
              const read = (spot as any)?.fishIQAreaRead as FishIQAreaRead | null | undefined;
              if (!read || read.readId || Number(read.generatedAt || 0) !== generatedAt) return spot;
              changed = true;
              return {
                ...spot,
                fishIQAreaRead: {
                  ...read,
                  readId: result.readId,
                  predictedAtMs: result.predictedAtMs,
                  validationSchemaVersion: 1,
                },
              } as Hotspot;
            });

            if (!changed) return current;
            fishMarkerHotspotsRef.current = [...next];
            return next;
          });
        })
        .catch((err) => {
          // D.8 is calibration-only and must never block or degrade FishIQ reads.
          console.warn("[FishIQ D.8] prediction snapshot skipped", err);
        });
    },
    [profile?.uid],
  );

  const restoreHydratedFish = useCallback(
    (opts?: { restoreViewport?: boolean; settlePasses?: boolean }) => {
      const map = mapInstanceRef.current;
      if (!map || !isActiveRef.current || !fishIQPrefs.enabled) return false;

      // If React/ref state already has fish, simply repaint them. This is the
      // common route-tab case and avoids any storage work.
      const warmSpots = Array.isArray(fishMarkerHotspotsRef.current)
        ? fishMarkerHotspotsRef.current
        : [];

      if (warmSpots.length) {
        rerenderFishMarkersNow(warmSpots, {
          settlePasses: opts?.settlePasses !== false,
        });
        return true;
      }

      const uid = String(profile?.uid || "").trim();
      const cached = readFishHydrationCache(uid);
      if (!cached) return false;

      fishHydrationRestoreSeqRef.current += 1;
      hotspotRequestSeqRef.current += 1;
      fishMarkerHotspotsRef.current = [...cached.hotspots];
      setHotspots(cached.hotspots);
      setSearchError(null);
      setIsSearchingArea(false);
      setFishWidgetAreaSource("viewport");
      setFishWidgetAnchor(cached.anchor);
      lastRefreshViewportRef.current = {
        center: { ...cached.center },
        zoom: cached.zoom,
      };

      // On a real remount, restore the searched viewport as well so the paid
      // fish are not hydrated somewhere off-screen at the user's GPS/default.
      if (opts?.restoreViewport !== false) {
        try {
          const liveCenter = map.getCenter?.();
          const liveZoom = Number(map.getZoom?.() ?? cached.zoom);
          const farFromCached =
            !liveCenter ||
            getDistanceInMeters(
              Number(liveCenter.lat),
              Number(liveCenter.lng),
              cached.center.lat,
              cached.center.lng,
            ) > 500;

          if (farFromCached || Math.abs(liveZoom - cached.zoom) > 0.25) {
            programmaticMoveRef.current = true;
            map.setView([cached.center.lat, cached.center.lng], cached.zoom, {
              animate: false,
            });
            window.setTimeout(() => {
              programmaticMoveRef.current = false;
            }, 0);
          }
        } catch {}
      }

      rerenderFishMarkersNow(cached.hotspots, {
        settlePasses: opts?.settlePasses !== false,
      });

      return true;
    },
    [fishIQPrefs.enabled, profile?.uid, rerenderFishMarkersNow],
  );

  // Keep the latest restore implementation behind a stable ref. The callback
  // depends on marker-render helpers whose identities can legitimately change
  // after FishIQ state hydrates. Putting that callback directly in an effect
  // dependency array creates a restore -> setState -> new callback -> restore
  // loop. The ref breaks that feedback cycle without using stale logic.
  restoreHydratedFishRef.current = restoreHydratedFish;

  // FishIQ -> Log Catch -> Big Blue return bridge. Restore the exact fishing
  // session the angler left: camera, active FishIQ read, drawer, selected
  // species, and full-detail state. This is presentation-only and never
  // triggers a new FishIQ read or Search Lure spend.
  useEffect(() => {
    if (!props.isActive) return;

    const session = (props.viewState as any)?.restoreFishIQSession as
      | BigBlueFishIQReturnSession
      | null
      | undefined;
    const restoreKey = String(session?.key || "").trim();
    if (!session || !restoreKey) return;
    if (lastFishIQReturnRestoreKeyRef.current === restoreKey) return;

    let cancelled = false;
    const timers: number[] = [];

    const findSelectedSpot = () => {
      const candidates = Array.isArray(fishMarkerHotspotsRef.current)
        ? fishMarkerHotspotsRef.current
        : [];

      if (session.selectedFishId) {
        const byId = candidates.find(
          (spot: any) => String(spot?.id || "") === String(session.selectedFishId),
        );
        if (byId) return byId;
      }

      if (session.selectedSpeciesKey) {
        return (
          candidates.find((spot: any) => {
            const values = [
              spot?.fishIQSpeciesData?.scientificName,
              spot?.fishIQSpeciesData?.commonName,
              spot?.fishIQSpeciesData?.name,
              spot?.species,
              spot?.commonName,
              spot?.name,
            ];
            return values.some(
              (value) =>
                value && normalizeSpeciesKey(String(value)) === session.selectedSpeciesKey,
            );
          }) || null
        );
      }

      return null;
    };

    const applyRestore = () => {
      if (cancelled) return;
      const map = mapInstanceRef.current;
      if (!map) return;

      // Make sure the existing FishIQ result is repainted before reopening its UI.
      restoreHydratedFishRef.current?.({
        restoreViewport: false,
        settlePasses: false,
      });

      try {
        programmaticMoveRef.current = true;
        map.setView(
          [Number(session.center.lat), Number(session.center.lng)],
          Math.max(MAP_MIN_ZOOM, Math.min(MAP_MAX_ZOOM, Number(session.zoom || 13))),
          { animate: false },
        );
        window.setTimeout(() => {
          programmaticMoveRef.current = false;
        }, 0);
      } catch {}

      // FishIQ return owns the visible map surface. Clear transient Saved
      // Spots UI so a previously-open drawer/composer cannot cover the restored
      // FishIQ session after a catch save.
      setIsSavedSpotsOpen(false);
      setPendingSavedSpotLatLng(null);
      setSelectedSavedSpotId(null);
      setIsSavedSpotFilterMenuOpen(false);
      setIsSelectedSavedSpotMenuOpen(false);

      setFishWidgetMode(session.fishWidgetMode === "history" ? "history" : "area");
      setFishWidgetAreaSource(
        session.fishWidgetAreaSource === "selected" ? "selected" : "viewport",
      );
      setFishWidgetAnchor(
        session.anchor &&
          Number.isFinite(Number(session.anchor.lat)) &&
          Number.isFinite(Number(session.anchor.lng))
          ? { lat: Number(session.anchor.lat), lng: Number(session.anchor.lng) }
          : null,
      );
      setIsFishAreaExpanded(session.fishAreaExpanded === true);

      const restoredSpot = findSelectedSpot();
      const needsSelectedSpot = Boolean(
        session.selectedFishId || session.selectedSpeciesKey,
      );

      if (needsSelectedSpot && !restoredSpot) return;

      setSelectedFishSpot(restoredSpot as Hotspot | null);
      setIsFishWidgetOpen(session.fishWidgetOpen === true);
      setIsFishFullDetailOpen(
        session.fullDetailOpen === true && Boolean(restoredSpot),
      );
      setSearchError(null);

      lastFishIQReturnRestoreKeyRef.current = restoreKey;
      timers.forEach((timer) => window.clearTimeout(timer));
    };

    [0, 80, 180, 360, 700].forEach((delay) => {
      timers.push(window.setTimeout(applyRestore, delay));
    });

    return () => {
      cancelled = true;
      timers.forEach((timer) => window.clearTimeout(timer));
    };
  }, [
    props.isActive,
    (props.viewState as any)?.restoreFishIQSession?.key,
  ]);

  // Hydrate at most once successfully per Big Blue activation. Multiple short
  // retries are only for the case where React has mounted before Leaflet is
  // ready. As soon as one restore succeeds, every later retry becomes a no-op.
  useEffect(() => {
    if (!props.isActive || !fishIQPrefs.enabled) return;

    let cancelled = false;
    let restored = false;
    const seq = ++fishHydrationRestoreSeqRef.current;
    const timers: number[] = [];

    const tryRestore = () => {
      if (cancelled || restored || seq !== fishHydrationRestoreSeqRef.current) {
        return;
      }

      const fn = restoreHydratedFishRef.current;
      if (!fn) return;

      restored = fn({
        // Cached fish can repaint under the loader, but the first visible camera
        // belongs to the angler's resolved location, never a prior searched area.
        restoreViewport: false,
        // One deterministic repaint is enough. Marker settling already happens
        // through the normal map render path after state restoration.
        settlePasses: false,
      });

      if (restored) {
        timers.forEach((timer) => window.clearTimeout(timer));
      }
    };

    [0, 90, 220, 450].forEach((delay) => {
      timers.push(window.setTimeout(tryRestore, delay));
    });

    return () => {
      cancelled = true;
      timers.forEach((timer) => window.clearTimeout(timer));
    };
  }, [props.isActive, profile?.uid, fishIQPrefs.enabled]);

  // Save once more when leaving Big Blue. The drawer can close, but the actual
  // fish result remains hydrated for the return trip.
  useEffect(() => {
    if (props.isActive) return;
    if (fishMarkerHotspotsRef.current.length) {
      persistFishHydration(fishMarkerHotspotsRef.current);
    }
  }, [props.isActive, persistFishHydration]);

  const scheduleMapUiSync = useCallback(
    (opts?: {
      refreshSavedSpots?: boolean;
      refreshTournaments?: boolean;
      refreshCrew?: boolean;
      refreshFish?: boolean;
      syncZoom?: boolean;
    }) => {
      if (pendingMapUiSyncRafRef.current != null) return;

      pendingMapUiSyncRafRef.current = window.requestAnimationFrame(() => {
        pendingMapUiSyncRafRef.current = null;

        const map = mapInstanceRef.current;
        if (!map) return;

        if (opts?.syncZoom) {
          const nextZoom = Number(map.getZoom?.() ?? 13);
          setCurrentMapZoom((prev) => (prev === nextZoom ? prev : nextZoom));
        }

        if (opts?.refreshSavedSpots !== false) {
          scheduleSavedSpotMarkerUpdate();
        }

        // Settle dynamic marker families in one deterministic frame. Tournament
        // pins/hub establish occupancy first, crew resolves around them next,
        // and the legacy viewport FishIQ bubble resolves last. Exact geographic
        // FishIQ anchors never need a collision pass. This avoids the old
        // tournament-vs-crew-vs-fish RAF race after zoom.
        if (opts?.refreshTournaments) {
          updateTournamentMarkersRef.current();
        }

        if (opts?.refreshCrew) {
          refreshCrewPresenceMarkers();
        }

        if (opts?.refreshFish) {
          // Exact FishIQ anchors are geographic truth and need no pan-time
          // collision pass. The fallback viewport bubble still may.
          const hasExactFishAnchor = !!fishWidgetAnchorRef.current;
          if (!hasExactFishAnchor) {
            scheduleFishMarkerUpdate(fishMarkerHotspotsRef.current, false, {
              invalidateVersion: false,
            });
          }
        }
      });
    },
    [
      scheduleSavedSpotMarkerUpdate,
      refreshCrewPresenceMarkers,
      scheduleFishMarkerUpdate,
    ],
  );



  const getHeaderHeightPx = () => {
    try {
      const raw = getComputedStyle(document.documentElement)
        .getPropertyValue("--roddo-header-h")
        ?.trim();
      const n = Number(String(raw || "").replace("px", ""));
      return Number.isFinite(n) && n > 0 ? n : 96;
    } catch {
      return 96;
    }
  };

  const getSafeInsetsPx = () => getBigBlueSafeInsets(getHeaderHeightPx());

  const clampPointToSafeBox = (pt: { x: number; y: number }, size: any) =>
    clampMapPointToSafeBox(pt, size, getSafeInsetsPx());

  // MapView owns which marker families participate; reusable screen-space math
  // lives in mapCollision.ts. Canonical geographic coordinates never move here.
  const resolveMapMarkerCollision = (
    desired: { x: number; y: number },
    occupied: Array<{ x: number; y: number }>,
    size: any,
    opts?: { minDistance?: number; seed?: number; maxRadius?: number },
  ) =>
    resolveScreenMarkerCollision(
      desired,
      occupied,
      size,
      getSafeInsetsPx(),
      opts,
    );

  type BigBlueMarkerKind =
    | "user"
    | "fish"
    | "tournament"
    | "crew"
    | "saved"
    | "bazaar";

  // One occupancy collector for the dynamic marker families on Big Blue.
  // Each renderer excludes itself, then adds its own newly-resolved points as
  // it lays markers out. This avoids category A knowing every detail of B/C/D.
  const getUnifiedOccupiedMapPoints = (
    map: any,
    exclude: BigBlueMarkerKind[] = [],
  ): Array<{ x: number; y: number }> => {
    const excluded = new Set<BigBlueMarkerKind>(exclude);
    const out: Array<{ x: number; y: number }> = [];

    const pushLatLng = (latLng: any) => {
      if (!latLng) return;
      const lat = Number(latLng?.lat);
      const lng = Number(latLng?.lng ?? latLng?.lon);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
      try {
        const point = map.latLngToContainerPoint([lat, lng]);
        if (Number.isFinite(point?.x) && Number.isFinite(point?.y)) {
          out.push({ x: Number(point.x), y: Number(point.y) });
        }
      } catch {}
    };

    if (!excluded.has("user")) pushLatLng(latestUserLatLngRef.current);

    if (!excluded.has("fish") && mapLayerVisibilityRef.current.fishiq) {
      fishIQLayerControllerRef.current?.forEachMarker((marker) => {
        try {
          pushLatLng(marker?.getLatLng?.());
        } catch {}
      });
    }

    if (!excluded.has("tournament") && mapLayerVisibilityRef.current.tournaments) {
      tournamentLayerControllerRef.current?.forEachMarker((marker) => {
        try {
          pushLatLng(marker?.getLatLng?.());
        } catch {}
      });
    }

    if (!excluded.has("crew")) {
      crewPresenceLayerControllerRef.current?.forEachMarker((marker) => {
        try {
          pushLatLng(marker?.getLatLng?.());
        } catch {}
      });
    }

    if (!excluded.has("saved") && mapLayerVisibilityRef.current.savedSpots) {
      savedSpotsLayerControllerRef.current?.forEachMarker((marker) => {
        try {
          pushLatLng(marker?.getLatLng?.());
        } catch {}
      });
    }

    if (!excluded.has("bazaar") && mapLayerVisibilityRef.current.marketplace) {
      bazaarLayerControllerRef.current?.getMarkers().forEach((marker) => {
        try {
          pushLatLng(marker?.getLatLng?.());
        } catch {}
      });
    }

    return out;
  };

  const pointIsInsideSafeBox = (pt: { x: number; y: number }, size: any) =>
    isMapPointInsideSafeBox(pt, size, getSafeInsetsPx());

  // Keep transient feature cards readable while preserving canonical marker
  // coordinates. Shared screen-space camera math lives in mapPopupPositioning.
  const focusMapCardNearMarker = (
    map: any,
    marker: any,
    cardEl: HTMLElement | null,
    opts?: { preferredGap?: number; focusBias?: number },
  ) =>
    focusMapCardNearMarkerInViewport(map, marker, cardEl, {
      ...opts,
      safeInsets: getSafeInsetsPx(),
      onProgrammaticMoveChange: (active) => {
        programmaticMoveRef.current = active;
      },
    });

  const metersToLatDegrees = (meters: number) => meters / 111320;

  const metersToLngDegrees = (meters: number, atLat: number) => {
    const cosLat = Math.max(0.2, Math.cos((atLat * Math.PI) / 180));
    return meters / (111320 * cosLat);
  };

  const clampLatLngToSafeBox = (map: any, pos: LatLng): LatLng => {
    try {
      const size = map.getSize?.();
      if (!size) return pos;
      const pt = map.latLngToContainerPoint([pos.lat, pos.lng]);
      const clampedPt = clampPointToSafeBox({ x: pt.x, y: pt.y }, size);
      const ll = map.containerPointToLatLng(clampedPt);
      return { lat: ll.lat, lng: ll.lng };
    } catch {
      return pos;
    }
  };

  const getViewportAnchorLatLng = (
    map: any,
    fallback?: LatLng | null,
  ): LatLng | null => {
    try {
      const size = map.getSize?.();
      if (!size) return fallback ?? null;
      const inset = getSafeInsetsPx();
      const anchorPt = {
        x: inset.left + (size.x - inset.left - inset.right) * 0.5,
        y: inset.top + (size.y - inset.top - inset.bottom) * 0.5,
      };
      const ll = map.containerPointToLatLng(anchorPt);
      if (ll && Number.isFinite(ll.lat) && Number.isFinite(ll.lng)) {
        return { lat: ll.lat, lng: ll.lng };
      }
    } catch {}

    try {
      const center = map.getCenter?.();
      if (
        center &&
        Number.isFinite(center.lat) &&
        Number.isFinite(center.lng)
      ) {
        return { lat: center.lat, lng: center.lng };
      }
    } catch {}

    return fallback ?? null;
  };

  const buildUserAnchoredPoints = (
    map: any,
    anchor: LatLng,
    count: number,
  ): LatLng[] => {
    const targetCount = Math.max(4, count);
    const rand = makeSeededRandom(
      anchor.lat.toFixed(4),
      anchor.lng.toFixed(4),
      targetCount,
    );
    const size = map.getSize?.();
    const userPt = map.latLngToContainerPoint?.([anchor.lat, anchor.lng]);
    const nearbyRadiusMeters = computeNearbyRadiusMeters(map);

    if (!size || !userPt) {
      const maxRadiusMeters = Math.max(
        220,
        Math.min(nearbyRadiusMeters * 0.18, 1200),
      );
      const minRadiusMeters = Math.max(
        100,
        Math.min(maxRadiusMeters * 0.44, 320),
      );
      const minSeparationMeters = Math.max(
        150,
        Math.min(maxRadiusMeters * 0.5, 340),
      );
      const pts: LatLng[] = [];
      let attempts = 0;
      const maxAttempts = targetCount * 20;

      while (pts.length < targetCount && attempts < maxAttempts) {
        const idx = pts.length;
        const baseAngle = Math.PI * 2 * (idx / targetCount) + attempts * 0.43;
        const angle = baseAngle + (rand() - 0.5) * 0.95;
        const ringMix = 0.44 + 0.48 * rand();
        const radiusMeters =
          minRadiusMeters + (maxRadiusMeters - minRadiusMeters) * ringMix;
        const eastMeters = Math.cos(angle) * radiusMeters;
        const northMeters = Math.sin(angle) * radiusMeters;
        const candidate = clampLatLngToSafeBox(map, {
          lat: anchor.lat + metersToLatDegrees(northMeters),
          lng: anchor.lng + metersToLngDegrees(eastMeters, anchor.lat),
        });

        const tooClose = pts.some(
          (pt) => distanceMeters(pt, candidate) < minSeparationMeters,
        );
        attempts += 1;
        if (tooClose) continue;
        pts.push(candidate);
      }

      while (pts.length < targetCount) {
        const idx = pts.length;
        const angle = (Math.PI * 2 * idx) / targetCount;
        const radiusMeters =
          minRadiusMeters + (maxRadiusMeters - minRadiusMeters) * 0.45;
        pts.push(
          clampLatLngToSafeBox(map, {
            lat:
              anchor.lat + metersToLatDegrees(Math.sin(angle) * radiusMeters),
            lng:
              anchor.lng +
              metersToLngDegrees(Math.cos(angle) * radiusMeters, anchor.lat),
          }),
        );
      }

      return pts;
    }

    const inset = getSafeInsetsPx();
    const safeWidth = Math.max(180, size.x - inset.left - inset.right);
    const safeHeight = Math.max(220, size.y - inset.top - inset.bottom);
    // Keep the user-anchored FishIQ hub visually connected to the angler.
    // The old ring could push markers toward ~31% of the usable short side,
    // which looked detached at wider/zoomed-out Big Blue views.
    const shortSide = Math.min(safeWidth, safeHeight);
    const minScreenRadiusPx = Math.max(
      54,
      Math.min(76, shortSide * 0.13),
    );
    const maxScreenRadiusPx = Math.max(
      86,
      Math.min(112, shortSide * 0.24),
    );
    const minSeparationPx = Math.max(
      48,
      Math.min(64, shortSide * 0.105),
    );

    const pts: LatLng[] = [];
    let attempts = 0;
    const maxAttempts = targetCount * 28;

    while (pts.length < targetCount && attempts < maxAttempts) {
      const idx = pts.length;
      const golden = 2.399963229728653;
      const angle = idx * golden + (rand() - 0.5) * 0.85;
      const ringMix = 0.42 + 0.5 * rand();
      const radiusPx =
        minScreenRadiusPx + (maxScreenRadiusPx - minScreenRadiusPx) * ringMix;
      const candidatePt = clampPointToSafeBox(
        {
          x: userPt.x + Math.cos(angle) * radiusPx,
          y: userPt.y + Math.sin(angle) * radiusPx,
        },
        size,
      );

      const tooClose = pts.some((pt) => {
        try {
          const placedPt = map.latLngToContainerPoint([pt.lat, pt.lng]);
          const dx = placedPt.x - candidatePt.x;
          const dy = placedPt.y - candidatePt.y;
          return Math.sqrt(dx * dx + dy * dy) < minSeparationPx;
        } catch {
          return false;
        }
      });

      attempts += 1;
      if (tooClose) continue;

      const ll = map.containerPointToLatLng(candidatePt);
      pts.push({ lat: ll.lat, lng: ll.lng });
    }

    while (pts.length < targetCount) {
      const idx = pts.length;
      const angle = (Math.PI * 2 * idx) / targetCount;
      const radiusPx =
        minScreenRadiusPx + (maxScreenRadiusPx - minScreenRadiusPx) * 0.58;
      const candidatePt = clampPointToSafeBox(
        {
          x: userPt.x + Math.cos(angle) * radiusPx,
          y: userPt.y + Math.sin(angle) * radiusPx,
        },
        size,
      );
      const ll = map.containerPointToLatLng(candidatePt);
      pts.push({ lat: ll.lat, lng: ll.lng });
    }

    return pts;
  };

  const keepIfStillOnScreen = (map: any, original: LatLng, snapped: LatLng) => {
    try {
      const size = map.getSize?.();
      if (!size) return original;
      const pt = map.latLngToContainerPoint([snapped.lat, snapped.lng]);
      if (pointIsInsideSafeBox({ x: pt.x, y: pt.y }, size)) return snapped;
      return original;
    } catch {
      return original;
    }
  };

  const addSnapJitter = (pos: LatLng, idx: number): LatLng => {
    const jitterScale = 0.00022;
    const rand = makeSeededRandom(pos.lat.toFixed(4), pos.lng.toFixed(4), idx);
    const latNoise = (rand() - 0.5) * jitterScale;
    const lngNoise = (rand() - 0.5) * jitterScale;
    return {
      lat: pos.lat + latNoise,
      lng: pos.lng + lngNoise,
    };
  };

  const computeNearbyRadiusMeters = (map: any) => {
    try {
      const b = map.getBounds?.();
      if (!b) return 15 * 1609.344;
      const sw = b.getSouthWest();
      const ne = b.getNorthEast();
      const diagM = distanceMeters(
        { lat: sw.lat, lng: sw.lng },
        { lat: ne.lat, lng: ne.lng },
      );
      const halfDiagM = diagM * 0.5;
      const miles = halfDiagM / 1609.344;
      const clampedMiles = Math.max(5, Math.min(50, miles));
      return clampedMiles * 1609.344;
    } catch {
      return 15 * 1609.344;
    }
  };

  const finalizeDistributedHotspots = (
    map: any,
    base: Hotspot[],
    snapped: any[],
  ): Hotspot[] => {
    const size = map.getSize?.();
    const minSeparationMeters = Math.max(
      170,
      Math.min(computeNearbyRadiusMeters(map) * 0.03, 420),
    );
    const minSeparationPx = size
      ? Math.max(56, Math.min(size.x, size.y) * 0.12)
      : 60;
    const placed: LatLng[] = [];

    const tooCloseToPlaced = (candidate: LatLng) => {
      return placed.some((pt) => {
        const geoClose = distanceMeters(pt, candidate) < minSeparationMeters;
        if (!geoClose) return false;
        if (!size) return true;
        try {
          const a = map.latLngToContainerPoint([pt.lat, pt.lng]);
          const b = map.latLngToContainerPoint([candidate.lat, candidate.lng]);
          const dx = a.x - b.x;
          const dy = a.y - b.y;
          return Math.sqrt(dx * dx + dy * dy) < minSeparationPx;
        } catch {
          return true;
        }
      });
    };

    return snapped.map((h: any, i: number) => {
      const original = base[i]?.position
        ? { lat: base[i].position.lat, lng: base[i].position.lng }
        : h?.position;
      const snappedPos = h?.position
        ? { lat: h.position.lat, lng: h.position.lng }
        : original;

      let next =
        original && snappedPos
          ? keepIfStillOnScreen(map, original, snappedPos)
          : snappedPos;
      next = clampLatLngToSafeBox(map, next);

      const candidates: LatLng[] = [];
      if (next)
        candidates.push(clampLatLngToSafeBox(map, addSnapJitter(next, i)));
      if (original)
        candidates.push(
          clampLatLngToSafeBox(map, addSnapJitter(original, i + 1)),
        );

      if (size && original) {
        try {
          const originPt = map.latLngToContainerPoint([
            original.lat,
            original.lng,
          ]);
          const repelRadiusPx = Math.max(48, minSeparationPx * 0.92);
          for (let step = 0; step < 5; step += 1) {
            const angle =
              ((i + 1) / Math.max(1, snapped.length)) * Math.PI * 2 +
              step * 0.9;
            const pt = clampPointToSafeBox(
              {
                x: originPt.x + Math.cos(angle) * repelRadiusPx,
                y: originPt.y + Math.sin(angle) * repelRadiusPx,
              },
              size,
            );
            const ll = map.containerPointToLatLng(pt);
            candidates.push(
              clampLatLngToSafeBox(
                map,
                addSnapJitter({ lat: ll.lat, lng: ll.lng }, i + step + 2),
              ),
            );
          }
        } catch {}
      }

      const chosen =
        candidates.find(
          (candidate) => candidate && !tooCloseToPlaced(candidate),
        ) ||
        candidates[candidates.length - 1] ||
        next;
      placed.push(chosen);
      return { ...h, position: chosen } as any;
    }) as Hotspot[];
  };

  const buildBaseFishHotspotsAroundUser = (
    map: any,
    anchor: LatLng,
    count: number,
    opts?: { locationName?: string | null; environment?: any | null },
  ): Hotspot[] => {
    const pts = buildUserAnchoredPoints(map, anchor, Math.max(4, count));
    const slots = pts.slice(0, Math.max(4, count));
    const coarseGeo = inferGeoProfile(anchor, opts?.locationName ?? null);
    const environmentContext = applyFishIQEnvironmentWaterContext(
      buildFishIQLocationContext({
        lat: anchor.lat,
        lon: anchor.lng,
        waterName: opts?.locationName || null,
        waterType:
          coarseGeo.waterKind === "saltwater"
            ? "inshore"
            : coarseGeo.waterKind === "river"
              ? "river"
              : coarseGeo.waterKind === "lake"
                ? "lake"
                : "freshwater",
        waterKind: coarseGeo.waterKind,
        regionHint: coarseGeo.region,
        source: "big-blue-base-pool",
      }),
      opts?.environment || null,
    );
    const geo = {
      ...coarseGeo,
      waterKind:
        environmentContext.waterKind === "saltwater"
          ? ("saltwater" as const)
          : coarseGeo.waterKind,
    };
    const pool = getExpandedSpeciesPoolForProfile(geo);
    const rand = makeSeededRandom(
      anchor.lat.toFixed(3),
      anchor.lng.toFixed(3),
      geo.region,
      geo.waterKind,
      String(opts?.locationName || "").toLowerCase(),
    );
    const used = new Set<string>();

    const mk = (
      id: string,
      pos: LatLng,
      species: string,
      idx: number,
    ): Hotspot =>
      ({
        id,
        name: `${species} (Area Ping)`,
        species: species as any,
        timeOfDay:
          idx % 3 === 0 ? "Morning" : idx % 3 === 1 ? "Evening" : "Day",
        position: { lat: pos.lat, lng: pos.lng },
        tactic:
          geo.waterKind === "saltwater"
            ? "Coastal profile — surf, bay, and inshore species are prioritized for this stretch."
            : geo.waterKind === "river"
              ? "Moving-water signal — prioritizing species that fit current and channel structure."
              : "Freshwater signal — refining toward the strongest local match.",
        funFact:
          geo.waterKind === "saltwater"
            ? "Roddo is leaning toward saltwater species because this area reads coastal."
            : geo.waterKind === "river"
              ? "Roddo is leaning toward river-friendly species because this area reads as moving water."
              : "Roddo is leaning toward inland freshwater species for this area.",
        sourceURL: "",
        sourceTitle: "",
        waterName:
          geo.waterKind === "saltwater"
            ? `${geo.region.replace(/_/g, " ")} coastal water`
            : geo.waterKind === "river"
              ? `${geo.region.replace(/_/g, " ")} moving water`
              : geo.waterKind === "lake"
                ? `${geo.region.replace(/_/g, " ")} lake / reservoir water`
                : `${geo.region.replace(/_/g, " ")} freshwater profile`,
        waterKind:
          geo.waterKind === "saltwater"
            ? "inshore"
            : geo.waterKind === "river"
              ? "river"
              : geo.waterKind === "lake"
                ? "lake"
                : "lake",
        waterType:
          geo.waterKind === "saltwater"
            ? "inshore"
            : geo.waterKind === "river"
              ? "river"
              : geo.waterKind === "lake"
                ? "lake"
                : "lake",
        regionHint: geo.region,
      }) as any;

    return slots.map((p, i) =>
      mk(`fish_${i}`, p, pickFromPool(pool, rand, used), i),
    );
  };

  const getFishIQAlignedLocationName = (anchor: LatLng): string | null => {
    const userAnchor = latestUserLatLngRef.current;
    const currentLocationName = String(props.locationName || "").trim();

    // props.locationName follows the app's current GPS/weather position. Reusing
    // it after the angler pans somewhere else can poison a remote FishIQ read
    // with the wrong city/water hint (for example, an inland name on the coast).
    // Only reuse it when the searched anchor is genuinely local to that position.
    if (userAnchor && currentLocationName) {
      const distance = getDistanceInMeters(
        userAnchor.lat,
        userAnchor.lng,
        anchor.lat,
        anchor.lng,
      );
      if (Number.isFinite(distance) && distance <= 12000) return currentLocationName;
    }

    return null;
  };

  const getFishIQAreaContextForAnchor = (anchor: LatLng) => {
    const alignedLocationName = getFishIQAlignedLocationName(anchor);

    // Exact Water Intel bathymetry is authoritative marine evidence for the
    // searched coordinate. This matters for remote/open-ocean reads where the
    // app intentionally does not reuse the user's inland GPS location name.
    //
    // Keep this narrow: only an exact cached bathymetry hit may promote the
    // coarse fallback to marine here. Nearby OFS/current cells alone are not
    // enough, so coastal rivers/lakes keep their explicit freshwater authority.
    const cachedWaterIntel = getCachedWaterIntelRead({
      lat: anchor.lat,
      lng: anchor.lng,
      requestedCapabilities: [
        "basic_current",
        "basic_depth",
        "structure_analysis",
      ] as any,
    });
    const cachedDepthMeters = Number(
      (cachedWaterIntel as any)?.bathymetry?.depthMeters,
    );
    const hasExactMarineBottom = Boolean(
      (cachedWaterIntel as any)?.bathymetry?.available === true &&
        Number.isFinite(cachedDepthMeters) &&
        cachedDepthMeters > 0,
    );
    const marineLocationHint = hasExactMarineBottom
      ? cachedDepthMeters >= 100
        ? "ocean"
        : "coastal water"
      : null;

    const geo = inferGeoProfile(
      anchor,
      alignedLocationName || marineLocationHint,
    );
    const waterType =
      geo.waterKind === "saltwater"
        ? hasExactMarineBottom && cachedDepthMeters >= 100
          ? "ocean"
          : "inshore"
        : geo.waterKind === "river"
          ? "river"
          : geo.waterKind === "lake"
            ? "lake"
            : "unknown";

    const waterName =
      alignedLocationName ||
      (geo.waterKind === "saltwater"
        ? hasExactMarineBottom && cachedDepthMeters >= 100
          ? `${geo.region.replace(/_/g, " ")} ocean water`
          : `${geo.region.replace(/_/g, " ")} coastal water`
        : geo.waterKind === "river"
          ? `${geo.region.replace(/_/g, " ")} moving water`
          : geo.waterKind === "lake"
            ? `${geo.region.replace(/_/g, " ")} lake / reservoir water`
            : `${geo.region.replace(/_/g, " ")} water area`);

    return {
      alignedLocationName,
      geo,
      waterType,
      waterName,
      waterTruthConfidence: hasExactMarineBottom
        ? "exact-bathymetry"
        : "coarse-area-context",
    };
  };

  // D.9 waterbody authority: marker placement is decorative, but an Overpass
  // snap gives us concrete waterbody metadata. Preserve the exact search anchor
  // for geography/privacy while letting the snapped water feature own the realm.
  // This prevents a river/creek near the coast from being reclassified by a
  // coarse regional fallback after snapping already proved what water it is.
  const getFishIQWaterAuthorityForSpot = (spot: any, anchor: LatLng) => {
    const areaContext = getFishIQAreaContextForAnchor(anchor);
    const snapDistanceMeters = Number(spot?.snapDistanceMeters);
    const snappedWaterName = String(spot?.waterName || "").trim();
    const snappedWaterKind = String(spot?.waterKind || "").trim();
    const snappedWaterType = String(spot?.waterType || "").trim();
    const hasSnappedWaterAuthority =
      Number.isFinite(snapDistanceMeters) &&
      Boolean(snappedWaterName || snappedWaterKind || snappedWaterType);

    return {
      areaContext,
      waterName: hasSnappedWaterAuthority
        ? snappedWaterName || areaContext.waterName
        : areaContext.waterName,
      waterType: hasSnappedWaterAuthority
        ? snappedWaterType || snappedWaterKind || areaContext.waterType
        : areaContext.waterType,
      waterKind: hasSnappedWaterAuthority
        ? snappedWaterKind || snappedWaterType || areaContext.waterType
        : areaContext.waterType,
      regionHint: String(spot?.regionHint || areaContext.geo?.region || "").trim() || null,
      source: hasSnappedWaterAuthority ? "big-blue-map+snapped-water" : "big-blue-map",
      waterTruthConfidence: hasSnappedWaterAuthority
        ? String(spot?.waterTruthConfidence || "nearby-water-feature").trim()
        : "coarse-area-context",
    };
  };

  const getFishIQAlignedWeatherData = (anchor: LatLng): WeatherData | null => {
    const weatherCoords = (weatherData as any)?.coords;
    const weatherLat = Number(weatherCoords?.lat);
    const weatherLon = Number(weatherCoords?.lon ?? weatherCoords?.lng);

    if (Number.isFinite(weatherLat) && Number.isFinite(weatherLon)) {
      const distance = getDistanceInMeters(
        weatherLat,
        weatherLon,
        anchor.lat,
        anchor.lng,
      );
      // Weather is useful context only when it actually belongs to this read.
      // Remote map exploration should prefer "weather unavailable" over silently
      // importing conditions from the angler's physical location.
      return Number.isFinite(distance) && distance <= 25000 ? weatherData : null;
    }

    const userAnchor = latestUserLatLngRef.current;
    if (!userAnchor) return null;
    const userDistance = getDistanceInMeters(
      userAnchor.lat,
      userAnchor.lng,
      anchor.lat,
      anchor.lng,
    );
    return Number.isFinite(userDistance) && userDistance <= 12000 ? weatherData : null;
  };

  const debugFishIQAlignment = (stage: string, payload: Record<string, any>) => {
    if (!import.meta.env.DEV) return;
    console.debug(`[FishIQ alignment] ${stage}`, payload);
  };

  const rankFishHotspotsForMapArea = (
    spots: Hotspot[],
    anchor: LatLng,
    opts?: { signals?: any[] | null; environment?: any | null },
  ): Hotspot[] => {
    if (!Array.isArray(spots) || !spots.length) return [];

    const rankSignals = Array.isArray(opts?.signals)
      ? opts?.signals || []
      : fishIQAreaSignals;

    return spots.map((spot, index) => {
      const position = (spot as any)?.position || {};
      const markerLat = Number(position.lat ?? anchor.lat);
      const markerLon = Number(position.lng ?? position.lon ?? anchor.lng);

      // Marker positions may be spread/snapped for map legibility. FishIQ's read,
      // water classification, ranking and validation context must not drift with
      // those decorative/per-species positions. The search anchor is authoritative.
      const readLat = Number(anchor.lat);
      const readLon = Number(anchor.lng);
      const waterAuthority = getFishIQWaterAuthorityForSpot(spot, anchor);
      const areaContext = waterAuthority.areaContext;
      const geo = areaContext.geo;
      const waterName = waterAuthority.waterName;
      const inferredWaterType = waterAuthority.waterType;
      const inferredWaterKind = waterAuthority.waterKind;

      const baseContext = buildFishIQLocationContext({
        lat: readLat,
        lon: readLon,
        waterName,
        waterType: inferredWaterType,
        waterKind: inferredWaterKind,
        regionHint: waterAuthority.regionHint,
        source: waterAuthority.source,
      });
      const context = applyFishIQEnvironmentWaterContext(
        baseContext,
        opts?.environment || null,
      );

      const areaSpeciesRaw = [
        ...((spot as any)?.fishIQAreaSpeciesData &&
        Array.isArray((spot as any).fishIQAreaSpeciesData)
          ? (spot as any).fishIQAreaSpeciesData
          : []),
        ...((spot as any)?.fishIQSpeciesData
          ? [(spot as any).fishIQSpeciesData]
          : []),
      ];

      const seen = new Set<string>();
      const areaSpecies = areaSpeciesRaw.filter((item: any) => {
        const key = getFishIQPresentationIdentityKey(item);
        if (!key || seen.has(key)) return false;
        seen.add(key);
        return true;
      });

      if (!areaSpecies.length) return spot;

      const rankedSpecies = rankFishIQSpeciesForLocation(areaSpecies, context, {
        catchLog: props.catchLog || [],
        signals: rankSignals,
        waterFeatures: [spot],
        environment: opts?.environment || null,
      });

      const areaRead = buildFishIQAreaRead({
        lat: readLat,
        lng: readLon,
        waterName,
        waterType: context.waterType || inferredWaterType,
        waterKind: context.waterKind || inferredWaterKind,
        regionHint: context.regionHint || waterAuthority.regionHint || geo?.region || null,
        source: waterAuthority.source === "big-blue-map+snapped-water"
          ? "map-area-hotspot+snapped-water"
          : "map-area-hotspot",
        species: areaSpecies,
        catchLog: props.catchLog || [],
        signals: rankSignals,
        waterFeatures: [spot],
        environment: opts?.environment || null,
      });

      const currentSpotSpeciesKey = getFishIQPresentationIdentityKey(
        (spot as any)?.fishIQSpeciesData || {
          id: (spot as any)?.id,
          commonName: (spot as any)?.species || (spot as any)?.name,
        },
      );

      const preservedSpotSpecies =
        rankedSpecies.find((candidate: any) => {
          const key = getFishIQPresentationIdentityKey(candidate);
          return Boolean(
            key && currentSpotSpeciesKey && key === currentSpotSpeciesKey,
          );
        }) || null;

      // Preserve the unique species already assigned by decorateHotspotsWithUniqueSpecies.
      // The old code always used rankedSpecies[0], so every Area Ping could be rewritten
      // to the same top fish and the UI dedupe collapsed "fish in this area" to 1 item.
      const primary =
        preservedSpotSpecies ||
        rankedSpecies[index % Math.max(1, rankedSpecies.length)] ||
        rankedSpecies[0] ||
        areaSpecies[index % Math.max(1, areaSpecies.length)] ||
        areaSpecies[0] ||
        null;
      if (!primary) return spot;

      const rank = (primary as any)?.__fishiq?.locationRank;
      const bestTechnique =
        (primary as any)?.bestTechniques?.[0] ||
        (primary as any)?.tactics?.bestTechniques?.[0] ||
        (spot as any)?.tactic ||
        "Work the strongest local cover and forage naturally.";
      const bestBait =
        (primary as any)?.bestBaits?.[0] ||
        (primary as any)?.tactics?.bestBaits?.[0] ||
        "";
      const bestTime =
        (primary as any)?.bestTimesOfDay?.[0] ||
        (primary as any)?.tactics?.bestTimesOfDay?.[0] ||
        (spot as any)?.timeOfDay ||
        "Day";

      return {
        ...spot,
        waterName: context.waterName || waterName || (spot as any)?.waterName,
        waterKind: context.waterKind || inferredWaterKind || (spot as any)?.waterKind,
        waterType: context.waterType || inferredWaterType || (spot as any)?.waterType,
        regionHint: context.regionHint || waterAuthority.regionHint || (spot as any)?.regionHint,
        waterTruthConfidence: waterAuthority.waterTruthConfidence,
        fishIQReadAnchor: { lat: readLat, lng: readLon },
        fishIQMarkerPosition: Number.isFinite(markerLat) && Number.isFinite(markerLon)
          ? { lat: markerLat, lng: markerLon }
          : null,
        name: `${(primary as any).commonName || (primary as any).name || spot.species || "Fish"} (Area Ping)`,
        species: ((primary as any).commonName ||
          (primary as any).name ||
          spot.species) as any,
        timeOfDay: String(bestTime).toLowerCase().includes("dawn")
          ? "Morning"
          : String(bestTime).toLowerCase().includes("dusk") ||
              String(bestTime).toLowerCase().includes("night")
            ? "Evening"
            : (spot as any).timeOfDay,
        tactic: bestBait
          ? `${bestTechnique} Try ${bestBait} around ${waterName || rank?.waterLabel || "this water"}.`
          : bestTechnique,
        funFact:
          (primary as any)?.__fishiq?.localReadBody ||
          (rank?.reasons?.length
            ? `${rank.label}: ${rank.reasons.slice(0, 2).join(" • ")}.`
            : (primary as any)?.summary || (spot as any)?.funFact),
        fishIQSpeciesData: primary,
        fishIQAreaSpeciesData: rankedSpecies,
        fishIQAreaRead: areaRead,
      } as any;
    });
  };

  const buildFishHotspotsAroundUser = async (
    map: any,
    anchor: LatLng,
    count: number,
    opts?: { signals?: any[] | null; environment?: any | null },
  ): Promise<Hotspot[]> => {
    const base = buildBaseFishHotspotsAroundUser(map, anchor, count, {
      locationName: getFishIQAreaContextForAnchor(anchor).waterName,
      environment: opts?.environment || null,
    });

    try {
      const bounds = map.getBounds?.();
      const zoom = map.getZoom?.() ?? 13;
      if (bounds) {
        // Water snapping is background enrichment. The base FishIQ read is already
        // visible before this runs, so let waterOverpass own its endpoint timeout,
        // retry, cache, and last-good fallback policy instead of abandoning it
        // after an arbitrary 1.8s MapView deadline.
        const features = (await getWaterFeaturesCached(bounds, zoom, {
          ttlMs: 45 * 60 * 1000,
          allowLastGoodFallback: true,
        })) as any[] | null | undefined;

        if (features?.length) {
          const snapped = snapHotspotsToWaterSpread(
            base as any,
            features as any,
            {
              maxSnapMeters: Math.max(
                180,
                Math.min(760, computeNearbyRadiusMeters(map) * 0.065),
              ),
              attachWaterName: true,
              minSeparationMeters: 240,
            },
          ) as any[];

          const decorated = decorateHotspotsWithUniqueSpecies(
            finalizeDistributedHotspots(map, base, snapped) as any,
            Math.max(count, 8),
          ) as any;
          return rankFishHotspotsForMapArea(
            decorated as Hotspot[],
            anchor,
            opts,
          ) as any;
        }
      }
    } catch (err) {
      // Overpass is optional enrichment. Unexpected failures must never block or
      // downgrade the already-rendered deterministic FishIQ read. Keep this at
      // debug level so a transient public Overpass outage is not presented as
      // an application failure.
      if (import.meta.env.DEV) {
        console.debug("[map] water snap enrichment unavailable; using base hotspots", err);
      }
    }

    const decorated = decorateHotspotsWithUniqueSpecies(
      base as any,
      Math.max(count, 8),
    ) as any;
    return rankFishHotspotsForMapArea(decorated as Hotspot[], anchor, opts) as any;
  };

  const fishIQWaterPayloadForLatLng = useCallback(
    (latlng: LatLng, source = "map area") => {
      const areaContext = getFishIQAreaContextForAnchor(latlng);

      return {
        source,
        coords: {
          lat: latlng.lat,
          lon: latlng.lng,
        },
        waterType: areaContext.waterType,
        waterKind: areaContext.waterType,
        waterName: areaContext.waterName,
        regionHint: areaContext.geo.region,
      };
    },
    [props.locationName],
  );

  const fetchFishIQSignalsForAnchor = useCallback(
    async (anchor: LatLng): Promise<any[]> => {
      const seq = ++fishIQAreaSignalRequestSeqRef.current;
      const payload = fishIQWaterPayloadForLatLng(anchor, "map area signals");

      try {
        debugFishIQAlignment("signal-request", {
          anchor,
          waterType: payload.waterType,
          waterName: payload.waterName,
          regionHint: payload.regionHint,
        });
        const res = await fishIQGetSignalsNearMe({
          lat: payload.coords.lat,
          lon: payload.coords.lon,
          waterType: payload.waterType,
          waterKind: payload.waterKind,
          waterName: payload.waterName,
          regionHint: payload.regionHint,
        });
        const signals = Array.isArray((res as any)?.data?.signals)
          ? ((res as any).data.signals as any[])
          : [];
        if (fishIQAreaSignalRequestSeqRef.current === seq) {
          setFishIQAreaSignals(signals);
        }
        return signals;
      } catch (err) {
        console.warn("[map] FishIQ live signals skipped", err);
        if (fishIQAreaSignalRequestSeqRef.current === seq) {
          setFishIQAreaSignals([]);
        }
        return [];
      }
    },
    [fishIQWaterPayloadForLatLng],
  );

  const fetchFishIQEnvironmentForAnchor = useCallback(
    async (
      anchor: LatLng,
      opts?: { cacheOnly?: boolean },
    ): Promise<any | null> => {
      const seq = ++fishIQEnvironmentRequestSeqRef.current;
      const request = {
        lat: anchor.lat,
        lng: anchor.lng,
        requestedCapabilities: [
          "basic_current",
          "basic_depth",
          "structure_analysis",
        ] as any,
      };

      // Passive first-read/boot hydration must stay instant. Reuse a prior exact
      // Water Intel read when one exists, otherwise fuse nearby weather only.
      // Manual/selected area reads still request live current + bathymetry.
      const alignedWeatherData = getFishIQAlignedWeatherData(anchor);

      if (opts?.cacheOnly) {
        const cachedRead = getCachedWaterIntelRead(request);
        return buildWaterIntelEnvironment(alignedWeatherData, anchor, cachedRead);
      }

      const waterIntelRead = await getWaterIntelRead(request, {
        timeoutMs: 10000,
        quiet: true,
      });
      const environment = buildWaterIntelEnvironment(
        alignedWeatherData,
        anchor,
        waterIntelRead,
      );
      if (fishIQEnvironmentRequestSeqRef.current !== seq) return null;
      return environment;
    },
    [weatherData],
  );

  const showSearchedFishAreaMarker = useCallback((_latlng: LatLng | null) => {
    // The FishIQ area hub itself is now map-locked to fishWidgetAnchor. Remove
    // the old extra circle marker so users see one clear "FishIQ read" bubble
    // instead of two different markers for the same search.
    try {
      searchedFishAreaMarkerRef.current?.remove?.();
      searchedFishAreaMarkerRef.current = null;
    } catch {}
  }, []);

  const openFishAreaForLatLng = useCallback(
    async (latlng: LatLng, _source = "selected map point", spendSearchLure = false) => {
      const map = mapInstanceRef.current;
      if (!map) return;
      if (spendSearchLure && isSearchingSavedArea) return;

      // Canonicalize Leaflet world-copy coordinates once so the same point is
      // used for charging, FishIQ ranking, live signals, markers, and caching.
      const targetLatLng = normalizeSearchLureLatLng(latlng);
      if (!targetLatLng) {
        console.warn('[map] Search Lure blocked: invalid selected coordinates', latlng);
        setSearchError('Could not read this map point. Try selecting the spot again.');
        props.addToast?.('Could not read this map point. Try selecting the spot again.', 'warning');
        if (spendSearchLure) trackRoddoEvent('search_lure_search_failed', { source: 'big_blue', reason_code: 'invalid_target' });
        setIsSearchingSavedArea(false);
        return;
      }

      let searchLureReusedArea = false;
      if (spendSearchLure) {
        // Open the FishIQ surface immediately so a paid/manual read never feels
        // like a dead tap while Search Lure verification and the first read run.
        fishWidgetAnchorRef.current = targetLatLng;
        setFishWidgetAnchor(targetLatLng);
        setFishWidgetMode("area");
        setFishWidgetAreaSource("selected");
        setIsFishAreaExpanded(false);
        setSelectedFishSpot(null);
        setIsFishFullDetailOpen(false);
        setFishIQThinkingError(null);
        setFishIQRetryAnchor(targetLatLng);
        setFishIQRetryMode("selected");
        setIsFishWidgetOpen(true);
        trackRoddoEvent('search_lure_search_started', { source: 'big_blue' });
        setIsSearchingSavedArea(true);

        let pendingLureRequest: PendingSearchLureRequest | null = null;
        try {
          pendingLureRequest = getOrCreatePendingSearchLureRequest(
            profile?.uid,
            targetLatLng.lat,
            targetLatLng.lng,
            'saved-area',
          );
          const lureResult = await consumeSearchLure({
            lat: targetLatLng.lat,
            lng: targetLatLng.lng,
            requestId: pendingLureRequest.requestId,
          });
          clearPendingSearchLureRequest(profile?.uid, pendingLureRequest.requestId);
          searchLureReusedArea = lureResult.reusedArea === true;
          setLocalSearchLureBalance(
            Math.max(0, Math.floor(Number(lureResult.searchLureBalance || 0))),
          );
          setSearchLureClockMs(Date.now());

          if (lureResult.reusedArea) {
            props.addToast?.('Same water — no Search Lure used.', 'info');
          } else if ((lureResult.passiveSearchLuresGranted || 0) > 0) {
            props.addToast?.(`+${lureResult.passiveSearchLuresGranted} Search Lure${lureResult.passiveSearchLuresGranted === 1 ? '' : 's'} refilled.`, 'success');
          }
        } catch (err: any) {
          const code = String(err?.code || '').toLowerCase();
          const message = String(err?.message || 'Search Lure check failed.');
          if (pendingLureRequest && isDefinitiveSearchLureFailure(err)) {
            clearPendingSearchLureRequest(profile?.uid, pendingLureRequest.requestId);
          }
          trackRoddoEvent('search_lure_search_failed', { source: 'big_blue', reason_code: code.includes('resource-exhausted') || /no search lures/i.test(message) ? 'no_lures' : 'verification_failed' });
          if (code.includes('resource-exhausted') || /no search lures/i.test(message)) {
            setLocalSearchLureBalance(0);
            setSearchError('No Search Lures left. Refill in The Hold.');
            setFishIQThinkingError('No Search Lures left. Refill in The Hold, then try this water again.');
            props.addToast?.('No Search Lures left — refill in The Hold.', 'info');
          } else {
            setSearchError('Could not verify a Search Lure. Try again.');
            setFishIQThinkingError('Roddo could not verify this water read. Check your connection and try again.');
            props.addToast?.('Could not verify a Search Lure. Try again.', 'warning');
          }
          setIsFishWidgetOpen(true);
          setIsSearchingSavedArea(false);
          return;
        }
      }

      // Promote an existing working POI synchronously before the first FishIQ
      // marker render. React state commits on the next render, but marker work
      // can be scheduled in this tick; the ref prevents a duplicate area hub.
      const workingAnchor = mapActionAnchorRef.current;
      if (workingAnchor) {
        const workingDistance = getDistanceInMeters(
          workingAnchor.lat,
          workingAnchor.lng,
          targetLatLng.lat,
          targetLatLng.lng,
        );
        if (Number.isFinite(workingDistance) && workingDistance <= 80) {
          promotedMapActionFishIQAnchorRef.current = targetLatLng;
          fishIQLayerControllerRef.current?.removeAreaHub(
            mapInstanceRef.current,
            fishLayerRef.current,
          );
        }
      }

      fishWidgetAnchorRef.current = targetLatLng;
      setFishWidgetMode("area");
      setFishWidgetAreaSource("selected");
      setFishWidgetAnchor(targetLatLng);
      setIsFishAreaExpanded(false);
      setIsFishWidgetOpen(true);
      setSelectedFishSpot(null);
      setIsFishFullDetailOpen(false);
      setSearchError(null);
      setIsSavedSpotsOpen(false);
      setPendingSavedSpotLatLng(null);
      setSelectedSavedSpotId(null);
      setSavedSpotDraftName("");
      setSavedSpotDraftNote("");
      setSavedSpotDraftKind("favorite");
      showSearchedFishAreaMarker(targetLatLng);
      playPreLogCatchPulse(targetLatLng, 360);

      const baseHotspots = buildBaseFishHotspotsAroundUser(
        map,
        targetLatLng,
        FISH_AREA_EXPANDED_LIST_LIMIT,
        {
          locationName: getFishIQAreaContextForAnchor(targetLatLng).waterName,
        },
      );
      const decoratedBase = decorateHotspotsWithUniqueSpecies(
        baseHotspots as any,
        FISH_AREA_EXPANDED_LIST_LIMIT,
      ) as Hotspot[];
      const baseRanked = rankFishHotspotsForMapArea(decoratedBase, targetLatLng, {
        signals: [],
      }).slice(0, FISH_AREA_EXPANDED_LIST_LIMIT);

      fishMarkerHotspotsRef.current = [...baseRanked];
      setHotspots(baseRanked);
      scheduleFishMarkerUpdate(baseRanked, true);
      recordFishIQValidationSnapshot(baseRanked, targetLatLng, "big_blue_manual");
      if (spendSearchLure) trackRoddoEvent('search_lure_search_succeeded', { source: 'big_blue', reused_area: searchLureReusedArea, result_count: baseRanked.length });
      if (baseRanked.length) setIsFishWidgetOpen(true);
      // The deterministic base read is useful immediately. Stop the visible
      // thinking state here; local signals + Water Intel continue enriching it.
      if (spendSearchLure) setIsSearchingSavedArea(false);
      setFishIQThinkingError(null);
      setFishIQRetryAnchor(null);
      setFishIQRetryMode(null);

      const [liveSignals, fusionEnvironment] = await Promise.all([
        fetchFishIQSignalsForAnchor(targetLatLng),
        fetchFishIQEnvironmentForAnchor(targetLatLng),
      ]);
      if (!isActiveRef.current) return;

      if (liveSignals.length || fusionEnvironment) {
        const fusionBaseHotspots = buildBaseFishHotspotsAroundUser(
          map,
          targetLatLng,
          FISH_AREA_EXPANDED_LIST_LIMIT,
          {
            locationName: getFishIQAreaContextForAnchor(targetLatLng).waterName,
            environment: fusionEnvironment,
          },
        );
        const fusionDecoratedBase = decorateHotspotsWithUniqueSpecies(
          fusionBaseHotspots as any,
          FISH_AREA_EXPANDED_LIST_LIMIT,
        ) as Hotspot[];
        const liveRanked = rankFishHotspotsForMapArea(fusionDecoratedBase, targetLatLng, {
          signals: liveSignals,
          environment: fusionEnvironment,
        }).slice(0, FISH_AREA_EXPANDED_LIST_LIMIT);
        fishMarkerHotspotsRef.current = [...liveRanked];
        setHotspots(liveRanked);
        scheduleFishMarkerUpdate(liveRanked, true);
      }

      buildFishHotspotsAroundUser(
        map,
        targetLatLng,
        FISH_AREA_EXPANDED_LIST_LIMIT,
        { signals: liveSignals, environment: fusionEnvironment },
      )
        .then((nextHotspots) => {
          if (!isActiveRef.current) return;
          const ranked = rankFishHotspotsForMapArea(nextHotspots, targetLatLng, {
            signals: liveSignals,
            environment: fusionEnvironment,
          }).slice(0, FISH_AREA_EXPANDED_LIST_LIMIT);
          fishMarkerHotspotsRef.current = [...ranked];
          setHotspots(ranked);
          scheduleFishMarkerUpdate(ranked, true);
        })
        .catch((err) => {
          console.warn("[map] selected-area FishIQ enhancement skipped", err);
        });

      void completeDailyTaskAction("search_area")
        .then((result) => {
          console.log("[DailyTask] selected-area search_area result", result);

          if (result?.completed && !result?.alreadyCompleted) {
            const shells = Math.max(
              0,
              Math.floor(Number(result.awardedShells || 0)),
            );
            const title = String(
              result.dailyTask?.title || "Today's Cast task",
            );

            props.addToast?.(
              `${title} complete — +${shells} Shell${shells === 1 ? "" : "s"}`,
              "success",
            );
          }
        })
        .catch((err) => {
          console.warn("[DailyTask] selected-area search_area failed", err);
        });

      // Launch quiet pass: selected-area FishIQ results update visually.
      if (spendSearchLure) setIsSearchingSavedArea(false);
    },
    [
      fetchFishIQSignalsForAnchor,
      fetchFishIQEnvironmentForAnchor,
      isSearchingSavedArea,
      playPreLogCatchPulse,
      props.addToast,
      props.locationName,
      rankFishHotspotsForMapArea,
      recordFishIQValidationSnapshot,
      scheduleFishMarkerUpdate,
      showSearchedFishAreaMarker,
    ],
  );

  useEffect(() => {
    loadOfflineRegions();
    initOfflineTileLayer();

    if (!mapRef.current || mapInstanceRef.current) return;

    const cachedInitial = readCachedBrowserLocation();
    const defaultInitial = props.coords ?? { lat: 34.0522, lon: -118.2437 };
    const shouldUseCachedInitial =
      !props.coords ||
      (Math.abs(Number(defaultInitial?.lat ?? 0) - 34.0522) < 0.0001 &&
        Math.abs(Number(defaultInitial?.lon ?? 0) + 118.2437) < 0.0001);

    const initial =
      shouldUseCachedInitial && cachedInitial
        ? { lat: cachedInitial.lat, lon: cachedInitial.lng }
        : defaultInitial;

    const map = (L as any).map(mapRef.current, {
      center: [initial.lat, initial.lon],
      zoom: 11,
      zoomControl: false,
      minZoom: MAP_MIN_ZOOM,
      maxZoom: MAP_MAX_ZOOM,
      doubleClickZoom: false,
    });

    map.on("movestart", () => {
      if (programmaticMoveRef.current) return;
      pendingUserMoveRef.current = true;
    });
    map.on("dragstart", () => {
      if (programmaticMoveRef.current) return;
      hasUserMovedMapRef.current = true;
      pendingUserMoveRef.current = true;
    });
    map.on("zoomstart", () => {
      if (programmaticMoveRef.current) return;
      hasUserMovedMapRef.current = true;
      pendingUserMoveRef.current = true;
    });

    ensureBigBlueMapPanes(map);
    mapInstanceRef.current = map;
    if (!mapBootReadyRef.current) {
      setMapBootProgress(26);
      setMapBootDetail("Finding your location…");
    }
    setCurrentMapZoom(Number(map.getZoom?.() ?? 13));

    // Restore the signed-in angler marker whenever Big Blue is remounted.
    // Prefer the last real browser GPS cache. If that cache is unavailable,
    // fall back to App's persisted coords state (but never manufacture a marker
    // from the untouched Los Angeles default). This is marker-only hydration:
    // no GPS prompt, no recenter, no FishIQ run, no crew-presence write, and no
    // popup. A later real locationfound event simply moves/re-skins this marker.
    const restorePersistedUserMarker = () => {
      const cached = readCachedBrowserLocation();
      const propLat = Number(props.coords?.lat);
      const propLng = Number(props.coords?.lon);
      const propsAreFinite = Number.isFinite(propLat) && Number.isFinite(propLng);
      const propsLookLikeUntouchedDefault =
        propsAreFinite &&
        Math.abs(propLat - 34.0522) < 0.0001 &&
        Math.abs(propLng + 118.2437) < 0.0001;

      const lat = cached
        ? Number(cached.lat)
        : propsAreFinite && !propsLookLikeUntouchedDefault
          ? propLat
          : NaN;
      const lng = cached
        ? Number(cached.lng)
        : propsAreFinite && !propsLookLikeUntouchedDefault
          ? propLng
          : NaN;

      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return false;

      latestUserLatLngRef.current = { lat, lng };

      // Leaflet can briefly report a logical map instance before its pane DOM is
      // attached during a warm/remounted Big Blue boot. Adding a marker in that
      // window makes Leaflet call appendChild on an unavailable pane. Treat that
      // as "not ready yet" and let the already-scheduled restore passes retry.
      const mapContainer = map.getContainer?.() as HTMLElement | undefined;
      const markerPane = getBigBlueMapPane(map, BIG_BLUE_MAP_PANES.markers);
      if (
        !mapContainer ||
        !mapContainer.isConnected ||
        !markerPane ||
        !isBigBlueMapPaneConnected(map, BIG_BLUE_MAP_PANES.markers)
      ) {
        return false;
      }

      try {
        const icon = getUserLocationIcon(
          isDarkModeRef.current,
          Number(map.getZoom?.() ?? 13),
        );
        if (!icon) return false;

        if (userMarkerRef.current) {
          userMarkerRef.current.setLatLng([lat, lng]);
          userMarkerRef.current.setIcon?.(icon);
          window.requestAnimationFrame(() => updateTournamentMarkersRef.current());
          return true;
        }

        userMarkerRef.current = (L as any)
          .marker([lat, lng], {
            icon,
            pane: BIG_BLUE_MAP_PANES.markers,
            keyboard: false,
            riseOnHover: true,
            zIndexOffset: 1800,
          })
          .addTo(map)
          .bindPopup(t("map.userLocationPopup"), { offset: [0, -10] });

        window.requestAnimationFrame(() => updateTournamentMarkersRef.current());
        return true;
      } catch (err) {
        // If the map was detached between the readiness check and marker add,
        // allow a later restore pass to handle it without polluting the console.
        const liveContainer = map.getContainer?.() as HTMLElement | undefined;
        const liveMarkerPane = getBigBlueMapPane(map, BIG_BLUE_MAP_PANES.markers);
        if (
          !liveContainer?.isConnected ||
          !liveMarkerPane?.isConnected
        ) {
          return false;
        }

        console.warn("[geo] persisted user marker restore failed", err);
        return false;
      }
    };

    restorePersistedUserMarker();
    window.requestAnimationFrame(() => restorePersistedUserMarker());
    window.setTimeout(() => restorePersistedUserMarker(), 120);

    const mapContainer = map.getContainer?.() as HTMLElement | undefined;
    const onNativeMapDblClick = (event: MouseEvent) => {
      try {
        event.preventDefault();
        event.stopPropagation();
      } catch {}

      if (!mapContainer) return;

      try {
        const rect = mapContainer.getBoundingClientRect();
        const point = L.point(
          event.clientX - rect.left,
          event.clientY - rect.top,
        );
        const latlng = map.containerPointToLatLng(point);
        const lat = Number(latlng?.lat);
        const lng = Number(latlng?.lng);
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
        dismissMapTextInputs();
        setIsSavedSpotsOpen(false);
        setPendingSavedSpotLatLng(null);
        setSelectedSavedSpotId(null);
        setIsSavedSpotFilterMenuOpen(false);
        setIsSelectedSavedSpotMenuOpen(false);
        setIsWeatherOpen(false);
        const nextAnchor = { lat, lng };
        setMapActionAnchor(nextAnchor);
        setIsMapActionCardOpen(true);
        focusMapActionAnchor(map, nextAnchor);

        // Double-click selects water first; the angler chooses Search FishIQ,
        // Log Catch, or Save Spot from one compact action card.
      } catch (err) {
        console.warn("[map] native dblclick save failed", err);
      }
    };

    const onNativeMapClick = (event: MouseEvent) => {
      if (!isActiveRef.current) return;

      const target = event.target as HTMLElement | null;
      if (
        target?.closest(
          "button, input, textarea, select, a, .leaflet-control, .leaflet-popup, .roddo-map-hud, .roddo-bottom-nav, .roddo-fish-entry, .roddo-fish-hub-entry, .roddo-shop-entry, .roddo-saved-spot-bounce, .roddo-saved-spot-cluster-bounce, .roddo-map-action-pin",
        )
      ) {
        return;
      }

      if (mapActionAnchor && isMapActionCardOpen) {
        // A normal map tap dismisses only the action strip. The working POI
        // remains pinned until the angler explicitly clears it with the ×.
        setIsMapActionCardOpen(false);
      }

      // Plain map taps should dismiss the normal viewport FishIQ drawer.
      // A selected-spot FishIQ read is intentional and stays open until the
      // user closes it, searches elsewhere, or leaves MapView.
      if (
        isFishWidgetOpenRef.current &&
        fishWidgetAreaSourceRef.current !== "selected"
      ) {
        setIsFishWidgetOpen(false);
        setSelectedFishSpot(null);
        setIsFishFullDetailOpen(false);
        setIsFishAreaExpanded(false);
      }
    };

    try {
      map.doubleClickZoom?.disable?.();
      mapContainer?.addEventListener("dblclick", onNativeMapDblClick, true);
      mapContainer?.addEventListener("click", onNativeMapClick, true);
    } catch {}

    try {
      const url = getTileUrl();

      tileLayerRef.current = OfflineTileLayer
        ? new OfflineTileLayer(url, {
            maxZoom: MAP_MAX_ZOOM,
            attribution: getAttribution(),
            basemapId,
            isDarkMode: isDarkModeRef.current,
            errorTileUrl: "",
            // Keep surrounding/previous tiles warm so aggressive pan/zoom does
            // not expose the bare map while replacement tiles are arriving.
            keepBuffer: 4,
            updateWhenIdle: false,
            updateWhenZooming: true,
          })
        : (L as any).tileLayer(url, {
            maxZoom: MAP_MAX_ZOOM,
            attribution: getAttribution(),
            keepBuffer: 4,
            updateWhenIdle: false,
            updateWhenZooming: true,
          });

      tileLayerRef.current.addTo(map);
      try {
        tileLayerRef.current.on?.("tileload", applyTileTuning);
        tileLayerRef.current.on?.("tileload", () => markMapVisualReady(70));
        tileLayerRef.current.on?.("load", applyTileTuning);
        tileLayerRef.current.on?.("load", () => markMapVisualReady(70));
      } catch {}
      applyTileTuning();
      requestAnimationFrame(() => applyTileTuning());
      window.setTimeout(() => applyTileTuning(), 80);
      window.setTimeout(() => markMapVisualReady(0), 1100);
    } catch (e) {
      console.warn("[map] failed to add tile layer", e);
    }

    const syncMapSizeIfChanged = () => {
      if (!isActiveRef.current) return false;
      const container =
        (map.getContainer?.() as HTMLElement | undefined) ||
        mapRef.current ||
        mapShellRef.current;
      if (!container) return false;

      const rect = container.getBoundingClientRect();
      const width = Math.round(rect.width || 0);
      const height = Math.round(rect.height || 0);
      if (width <= 0 || height <= 0) return false;

      if (
        observedMapSizeRef.current.width === width &&
        observedMapSizeRef.current.height === height
      ) {
        return false;
      }

      observedMapSizeRef.current = { width, height };
      try {
        // Size-only correction: never animate or re-center the warm map.
        map.invalidateSize({ animate: false, pan: false });
      } catch {}
      return true;
    };

    const onResize = () => {
      try {
        if (!isActiveRef.current) return;
        if (resizeInvalidateRafRef.current != null) return;
        resizeInvalidateRafRef.current = window.requestAnimationFrame(() => {
          resizeInvalidateRafRef.current = null;
          syncMapSizeIfChanged();
        });
      } catch {}
    };

    const recenterAfterContainerSettle = () => {
      // Historical name kept to minimize churn. This is now a size-only settle:
      // no setView nudge, no camera reset, and no tile-pane flash.
      syncMapSizeIfChanged();
    };

    let settleRafA: number | null = null;
    let settleRafB: number | null = null;
    let settleTimeout: number | null = null;

    const clearSettleTimers = () => {
      if (settleRafA != null) window.cancelAnimationFrame(settleRafA);
      if (settleRafB != null) window.cancelAnimationFrame(settleRafB);
      if (settleTimeout != null) window.clearTimeout(settleTimeout);
      settleRafA = null;
      settleRafB = null;
      settleTimeout = null;
    };

    const scheduleInitialResizeSettle = () => {
      const container =
        (map.getContainer?.() as HTMLElement | undefined) ||
        mapRef.current ||
        mapShellRef.current;
      if (!container) return;

      const rect = container.getBoundingClientRect();
      const width = Math.round(rect.width || 0);
      const height = Math.round(rect.height || 0);
      if (width <= 0 || height <= 0) return;

      if (
        observedMapSizeRef.current.width === width &&
        observedMapSizeRef.current.height === height
      ) {
        return;
      }

      observedMapSizeRef.current = { width, height };
      clearSettleTimers();

      settleRafA = window.requestAnimationFrame(() => {
        settleRafB = window.requestAnimationFrame(() => {
          recenterAfterContainerSettle();
          window.setTimeout(recenterAfterContainerSettle, 120);
        });
      });

      settleTimeout = window.setTimeout(() => {
        recenterAfterContainerSettle();
      }, 220);
    };

    const onVis = () => {
      if (document.visibilityState !== "visible" || !isActiveRef.current) return;
      // Returning to a visible tab should not repaint tiles unless the viewport
      // actually changed size while hidden. Marker/presence listeners update
      // independently.
      syncMapSizeIfChanged();
    };

    let resizeObserver: ResizeObserver | null = null;
    if (typeof ResizeObserver !== "undefined") {
      try {
        resizeObserver = new ResizeObserver(() => {
          onResize();
          scheduleInitialResizeSettle();
        });

        if (mapShellRef.current) resizeObserver.observe(mapShellRef.current);
        const mapContainerEl = map.getContainer?.() as HTMLElement | undefined;
        if (mapContainerEl) resizeObserver.observe(mapContainerEl);
      } catch {}
    }

    const onViewportKick = () => {
      // Browser chrome/visualViewport events can fire for focus, drawers and
      // keyboard movement even when the Leaflet box is unchanged. Only perform
      // a size settle; never kick/re-center an already-warm map.
      onResize();
      scheduleInitialResizeSettle();
    };

    const scheduleShellLayoutSync = () => {
      if (appShellSyncTimerRef.current != null) {
        window.clearTimeout(appShellSyncTimerRef.current);
      }
      appShellSyncTimerRef.current = window.setTimeout(() => {
        appShellSyncTimerRef.current = null;
        onViewportKick();
      }, 24);
    };

    const onAppShellTransitionEnd = (event: Event) => {
      const target = event.target as HTMLElement | null;
      const propertyName = String(
        (event as TransitionEvent).propertyName || "",
      );
      if (target?.id !== "roddo-app-shell") return;
      if (propertyName && propertyName !== "opacity") return;
      scheduleShellLayoutSync();
    };

    const repaintCrewPresenceLayer = () => {
      window.requestAnimationFrame(() => {
        try {
          refreshCrewPresenceMarkers(true);
        } catch {}
      });
    };

    const onWindowFocus = () => {
      syncMapSizeIfChanged();
      repaintCrewPresenceLayer();
    };

    const onRoddoAppResume = () => {
      if (!isActiveRef.current) return;
      syncMapSizeIfChanged();
      window.requestAnimationFrame(() => {
        try {
          updateTournamentMarkersRef.current();
        } catch {}
        repaintCrewPresenceLayer();
      });
    };

    const fontsApi = (document as any).fonts;
    if (fontsApi?.ready && typeof fontsApi.ready.then === "function") {
      fontsApi.ready
        .then(() => {
          if (!isActiveRef.current) return;
          if (fontsReadySyncTimerRef.current != null) {
            window.clearTimeout(fontsReadySyncTimerRef.current);
          }
          fontsReadySyncTimerRef.current = window.setTimeout(() => {
            fontsReadySyncTimerRef.current = null;
            scheduleShellLayoutSync();
          }, 0);
        })
        .catch(() => {});
    }

    window.addEventListener("resize", onResize);
    window.addEventListener("pageshow", onViewportKick);
    window.addEventListener("load", onViewportKick);
    window.addEventListener("focus", onWindowFocus);
    window.addEventListener("roddo:app:resume", onRoddoAppResume as EventListener);
    document.addEventListener("visibilitychange", onVis);
    document.addEventListener("transitionend", onAppShellTransitionEnd, true);

    if (window.visualViewport) {
      window.visualViewport.addEventListener("resize", onViewportKick);
      window.visualViewport.addEventListener("scroll", onViewportKick);
    }

    scheduleInitialResizeSettle();
    scheduleBootViewportKicks(map);

    // Safari first-paint guard: App shell/header/nav can finish sizing one
    // tick after Leaflet boots. Size-check only; avoid redundant tile repaints.
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        onViewportKick();
      });
    });
    window.setTimeout(scheduleInitialResizeSettle, 80);
    window.setTimeout(scheduleInitialResizeSettle, 220);
    window.setTimeout(onViewportKick, 360);

    fishLayerRef.current = (L as any).layerGroup([], { pane: BIG_BLUE_MAP_PANES.markers });
    if (mapLayerVisibilityRef.current.fishiq) fishLayerRef.current.addTo(map);
    bazaarLayerControllerRef.current?.resetForLayerRebuild();
    shopLayerRef.current = (L as any).layerGroup([], { pane: BIG_BLUE_MAP_PANES.markers });
    if (mapLayerVisibilityRef.current.marketplace) shopLayerRef.current.addTo(map);
    setBazaarMapLayerReady((value) => value + 1);
    savedSpotLayerRef.current = (L as any).layerGroup([], { pane: BIG_BLUE_MAP_PANES.markers });
    if (mapLayerVisibilityRef.current.savedSpots) savedSpotLayerRef.current.addTo(map);
    // A Leaflet map/layer can be recreated while React refs survive (notably in
    // development StrictMode and native/webview resume paths). Reset the
    // controller so no cached marker can point at the previous detached layer.
    crewPresenceLayerControllerRef.current?.resetForLayerRebuild();
    crewPresenceLayerRef.current = (L as any)
      .layerGroup([], { pane: BIG_BLUE_MAP_PANES.markers })
      .addTo(map);
    tournamentLayerControllerRef.current?.resetForLayerRebuild();
    tournamentLayerRef.current = (L as any).layerGroup([], { pane: BIG_BLUE_MAP_PANES.markers });
    if (mapLayerVisibilityRef.current.tournaments) tournamentLayerRef.current.addTo(map);
    restrictedLayerRef.current = (L as any)
      .layerGroup([], { pane: BIG_BLUE_MAP_PANES.restricted })
      .addTo(map);

    window.requestAnimationFrame(() => {
      refreshCrewPresenceMarkers(true);
    });

    map.on("contextmenu", (e: any) => {
      const lat = Number(e?.latlng?.lat);
      const lng = Number(e?.latlng?.lng);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
      openSavedSpotComposer({ lat, lng });
    });

    const mapTouchContainer = map.getContainer?.() as HTMLElement | undefined;
    const startSavedSpotLongPress = (touch: Touch) => {
      clearSavedSpotLongPress();
      savedSpotLongPressStartRef.current = {
        x: touch.clientX,
        y: touch.clientY,
      };
      savedSpotLongPressTimerRef.current = window.setTimeout(() => {
        const rect = mapTouchContainer?.getBoundingClientRect?.();
        if (!rect) return;
        const point = L.point(
          touch.clientX - rect.left,
          touch.clientY - rect.top,
        );
        const latlng = map.containerPointToLatLng(point);
        const lat = Number(latlng?.lat);
        const lng = Number(latlng?.lng);
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
        openSavedSpotComposer({ lat, lng });
        // Launch quiet pass: saved spot composer opening is enough feedback.
        clearSavedSpotLongPress();
      }, 560);
    };

    const onMapTouchStart = (ev: TouchEvent) => {
      if (!isActiveRef.current) return;
      if (ev.touches.length !== 1) {
        clearSavedSpotLongPress();
        return;
      }

      const target = ev.target as HTMLElement | null;
      if (
        target?.closest(
          "button, input, textarea, select, a, .leaflet-control, .leaflet-popup",
        )
      ) {
        clearSavedSpotLongPress();
        return;
      }

      startSavedSpotLongPress(ev.touches[0]);
    };

    const onMapTouchMove = (ev: TouchEvent) => {
      if (!savedSpotLongPressStartRef.current || ev.touches.length !== 1) {
        clearSavedSpotLongPress();
        return;
      }

      const touch = ev.touches[0];
      const dx = touch.clientX - savedSpotLongPressStartRef.current.x;
      const dy = touch.clientY - savedSpotLongPressStartRef.current.y;
      if (Math.hypot(dx, dy) > 14) clearSavedSpotLongPress();
    };

    const onMapTouchEnd = () => {
      clearSavedSpotLongPress();
    };

    mapTouchContainer?.addEventListener("touchstart", onMapTouchStart, {
      passive: true,
    });
    mapTouchContainer?.addEventListener("touchmove", onMapTouchMove, {
      passive: true,
    });
    mapTouchContainer?.addEventListener("touchend", onMapTouchEnd, {
      passive: true,
    });
    mapTouchContainer?.addEventListener("touchcancel", onMapTouchEnd, {
      passive: true,
    });

    restrictedZones.forEach((zone: any) => {
      (L as any)
        .circle(zone.position, {
          pane: BIG_BLUE_MAP_PANES.restricted,
          radius: zone.radiusMeters,
          color: "#ef4444",
          fillColor: "#ef4444",
          fillOpacity: 0.18,
          weight: 2,
        })
        .bindPopup(`<b>${zone.name}</b><br>${zone.reason}`)
        .addTo(restrictedLayerRef.current);
    });

    map.on("locationfound", async (e: any) => {
      const lat = e?.latlng?.lat ?? e?.latitude;
      const lon = e?.latlng?.lng ?? e?.longitude;

      if (typeof lat !== "number" || typeof lon !== "number") return;

      latestUserLatLngRef.current = { lat, lng: lon };
      if (!mapBootReadyRef.current) {
        setMapBootProgress(52);
        setMapBootDetail("Location ready · reading the water…");
      }

      // Worldwide/global tournaments are rendered relative to the current
      // angler. Refresh immediately when GPS/cache resolves so their marker
      // appears without waiting for tournament data or another React render.
      window.requestAnimationFrame(() => {
        updateTournamentMarkersRef.current();
      });
      if (
        isCrewPresenceOptedIn((profile as any)?.notificationSettings) &&
        (profile as any)?.uid
      ) {
        void updateOwnCrewPresence({
          uid: String((profile as any).uid),
          displayName: String(
            (profile as any)?.displayName ||
              (profile as any)?.name ||
              (profile as any)?.username ||
              "Angler",
          ),
          avatarUrl: String(getProfileAvatarBaseUrl(profile as any) || ""),
          lat,
          lng: lon,
          shareWithCrew: true,
          isOnline: true,
          reason: "locationfound",
        }).catch((err) =>
          console.warn("[map] crew presence location write failed", err),
        );
      }
      writeCachedBrowserLocation(lat, lon, Number(e?.accuracy));

      if (typeof props.setCoords === "function") props.setCoords({ lat, lon });

      if (!isActiveRef.current) {
        if (userMarkerRef.current) {
          userMarkerRef.current.setLatLng([lat, lon]);
        }
        return;
      }

      const queueUserAnchoredFishRefresh = () => {
        const shouldRunInitialFishRead =
          !initialUserAnchoredSearchDoneRef.current &&
          !initialUserAnchoredSearchPendingRef.current;
        const shouldRunRecenterFishRead =
          pendingRecenterFishRefreshRef.current;

        if (!shouldRunInitialFishRead && !shouldRunRecenterFishRead) return;

        // Do not mark the initial read DONE here. React StrictMode can tear down
        // the consuming effect after this location event but before FishIQ runs.
        // "done" is only set after the free boot read actually completes.
        if (shouldRunInitialFishRead) {
          initialUserAnchoredSearchPendingRef.current = true;
          initialOverlayDoneRef.current = true;
        }
        pendingRecenterFishRefreshRef.current = false;

        const requestId = ++userAnchoredFishSearchRequestIdRef.current;
        if (shouldRunInitialFishRead && !mapBootReadyRef.current) {
          setMapBootProgress(66);
          setMapBootDetail("Building your local fish read…");
        }
        setUserAnchoredFishSearchRequest({
          id: requestId,
          isInitial: shouldRunInitialFishRead,
        });
      };

      const locationSource = String(e?.source || "gps");
      if (
        locationSource === "cache" &&
        !hasGeoErrorToastRef.current &&
        typeof props.addToast === "function"
      ) {
        hasGeoErrorToastRef.current = true;
        props.addToast(
          "Using your last saved location while live GPS is unavailable.",
          "info",
        );
      }

      if (!userMarkerRef.current) {
        const icon = getUserLocationIcon(isDarkMode, map.getZoom?.());
        if (icon) {
          userMarkerRef.current = (L as any)
            .marker([lat, lon], { icon, pane: BIG_BLUE_MAP_PANES.markers })
            .addTo(map)
            .bindPopup(t("map.userLocationPopup"), { offset: [0, -10] });

          const hasShownPopup = localStorage.getItem(
            "roddo_youAreHerePopupShown",
          );
          if (!hasShownPopup) {
            userMarkerRef.current.openPopup();
            localStorage.setItem("roddo_youAreHerePopupShown", "true");
            setTimeout(() => mapInstanceRef.current?.closePopup(), 3000);
          }
        }

        programmaticMoveRef.current = true;

        void (async () => {
          await settleMapLayoutBeforeFirstView(map);

          try {
            map.setView([lat, lon], 13, { animate: false });
            setUserAnchorBootReady(true);
          } finally {
            window.setTimeout(() => {
              programmaticMoveRef.current = false;
            }, 0);
          }

          // First boot search must happen after the map is centered on the
          // resolved user anchor. This keeps fish/shop placement aligned with
          // the actual visible viewport.
          queueUserAnchoredFishRefresh();
        })();
      } else {
        userMarkerRef.current.setLatLng([lat, lon]);

        if (!userAnchorBootReadyRef.current && !mapBootReadyRef.current) {
          programmaticMoveRef.current = true;
          try {
            map.setView([lat, lon], 13, { animate: false });
            setUserAnchorBootReady(true);
          } finally {
            window.setTimeout(() => {
              programmaticMoveRef.current = false;
            }, 0);
          }
        }

        // Recenter refreshes use requestLocation({ setView: true }); the
        // zero-delay queue lets that setView finish before FishIQ recalculates.
        queueUserAnchoredFishRefresh();
      }

      restrictedZones.forEach((zone: any) => {
        const distance = getDistanceInMeters(
          lat,
          lon,
          zone.position.lat,
          zone.position.lng,
        );
        if (distance <= zone.radiusMeters && onEnterRestrictedZone)
          onEnterRestrictedZone(zone);
      });
    });

    map.on("locationerror", (e: any) => {
      pendingRecenterFishRefreshRef.current = false;
      if (!isActiveRef.current) return;

      const reason = String(e?.message || e || "").toLowerCase();
      console.warn("[geo] locationerror", e?.message || e);

      // A location error is not a completed initial area search.
      // Leave initialUserAnchoredSearchDoneRef false so the first later
      // successful GPS/cache resolution can still populate Big Blue.
      if (!initialOverlayDoneRef.current) {
        initialOverlayDoneRef.current = true;
      }

      if (
        typeof props.addToast === "function" &&
        !hasGeoErrorToastRef.current
      ) {
        hasGeoErrorToastRef.current = true;

        if (reason.includes("interactive_required")) {
          if (!isLikelyIOSSafari()) {
            props.addToast(
              "Tap the recenter button to request live GPS. iPhone Safari will not grant location on passive page load until you interact.",
              "info",
            );
          }
        } else if (reason.includes("permission_denied")) {
          props.addToast(
            Capacitor.isNativePlatform()
              ? "Location access is off. Enable Location Services for Roddo or use the recenter button after granting access."
              : "Location access is off. Enable location access for this site, then use the recenter button again.",
            "info",
          );
        } else if (reason.includes("insecure_context")) {
          props.addToast(
            "Live GPS needs a secure HTTPS page. This browser session is not exposing geolocation.",
            "warning",
          );
        } else if (reason.includes("timeout")) {
          props.addToast(
            "GPS timed out. We will keep trying and use your last saved area when available.",
            "info",
          );
        } else if (reason.includes("position_unavailable")) {
          props.addToast(
            "GPS is temporarily unavailable on this device/browser. Tap recenter again in a few seconds and keep the page open.",
            "info",
          );
        } else if (reason.includes("unsupported")) {
          props.addToast(
            "This browser does not expose geolocation. Live GPS is unavailable here.",
            "warning",
          );
        } else {
          props.addToast(
            "Could not get live GPS yet. Tap recenter again and keep the page open for a few seconds.",
            "info",
          );
        }
      }
    });

    map.on("moveend", () => {
      // Leaflet already translates geographic markers during a pan. Keep
      // ordinary moveend free of marker RAF work; only the redo-search state
      // below needs to react to a completed drag.
      if (!isActiveRef.current) return;
      if (programmaticMoveRef.current) return;

      const center = map.getCenter?.();
      const zoom = map.getZoom?.() ?? 13;
      const lastRefresh = lastRefreshViewportRef.current;
      const userActuallyMoved =
        pendingUserMoveRef.current || hasUserMovedMapRef.current;

      pendingUserMoveRef.current = false;

      if (!center) return;
      if (!userActuallyMoved) return;

      if (!lastRefresh) {
        return;
      }

      const movedMeters = getDistanceInMeters(
        center.lat,
        center.lng,
        lastRefresh.center.lat,
        lastRefresh.center.lng,
      );
      const zoomChanged = Math.abs(zoom - lastRefresh.zoom) >= 1;
      const shouldShowRedo = movedMeters >= 140 || zoomChanged;

      if (!shouldShowRedo) return;

      hasUserMovedMapRef.current = true;
    });

    map.on("zoomend", () => {
      const zoom = Number(map.getZoom?.() ?? 13);
      setCurrentMapZoom((prev) => (prev === zoom ? prev : zoom));

      // The avatar marker is rendered as Leaflet divIcon HTML, so React CSS
      // state alone will not resize it. Rebuild the icon on every zoomend so
      // both the inner CSS scale and Leaflet iconSize/iconAnchor stay synced.
      if (userMarkerRef.current) {
        const icon = getUserLocationIcon(isDarkModeRef.current, zoom);
        if (icon) userMarkerRef.current.setIcon(icon);
      }

      scheduleMapUiSync({
        // Saved spots and exact catch/FishIQ markers are canonical geographic
        // positions, so zooming does not require rebuilding them.
        refreshSavedSpots: false,
        refreshTournaments: true,
        refreshCrew: true,
        refreshFish: true,
        syncZoom: false,
      });

      // Locked behavior: zooming all the way out should only reveal the
      // Big Blue Globe button. It must not auto-open the globe view.
    });

    return () => {
      clearSavedSpotLongPress();
      try {
        mapContainer?.removeEventListener(
          "dblclick",
          onNativeMapDblClick,
          true,
        );
        mapContainer?.removeEventListener("click", onNativeMapClick, true);
        mapTouchContainer?.removeEventListener(
          "touchstart",
          onMapTouchStart as any,
        );
        mapTouchContainer?.removeEventListener(
          "touchmove",
          onMapTouchMove as any,
        );
        mapTouchContainer?.removeEventListener(
          "touchend",
          onMapTouchEnd as any,
        );
        mapTouchContainer?.removeEventListener(
          "touchcancel",
          onMapTouchEnd as any,
        );
      } catch {}
      try {
        clearInitialViewportKickTimers();
        window.removeEventListener("resize", onResize);
        window.removeEventListener("pageshow", onViewportKick);
        window.removeEventListener("load", onViewportKick);
          window.removeEventListener("roddo:app:resume", onRoddoAppResume as EventListener);
        document.removeEventListener(
          "transitionend",
          onAppShellTransitionEnd,
          true,
        );
        if (window.visualViewport) {
          window.visualViewport.removeEventListener("resize", onViewportKick);
          window.visualViewport.removeEventListener("scroll", onViewportKick);
        }
        document.removeEventListener("visibilitychange", onVis);
      } catch {}
      try {
        resizeObserver?.disconnect();
      } catch {}
      clearSettleTimers();
      if (appShellSyncTimerRef.current != null) {
        window.clearTimeout(appShellSyncTimerRef.current);
        appShellSyncTimerRef.current = null;
      }
      if (fontsReadySyncTimerRef.current != null) {
        window.clearTimeout(fontsReadySyncTimerRef.current);
        fontsReadySyncTimerRef.current = null;
      }
      if (mapVisualReadyTimerRef.current != null) {
        window.clearTimeout(mapVisualReadyTimerRef.current);
        mapVisualReadyTimerRef.current = null;
      }

      try {
        if (browserGeoWatchId != null && window.navigator?.geolocation) {
          window.navigator.geolocation.clearWatch(browserGeoWatchId);
        }
      } catch {}
      browserGeoWatchId = null;
      browserGeoWatchStartedAt = 0;

      initialUserAnchoredSearchPendingRef.current = false;

      clearFishMarkers();
      if (pendingMapUiSyncRafRef.current != null) {
        window.cancelAnimationFrame(pendingMapUiSyncRafRef.current);
        pendingMapUiSyncRafRef.current = null;
      }
      if (resizeInvalidateRafRef.current != null) {
        window.cancelAnimationFrame(resizeInvalidateRafRef.current);
        resizeInvalidateRafRef.current = null;
      }
      try {
        fishLayerRef.current = null;
        bazaarLayerControllerRef.current?.dispose(shopLayerRef.current);
        shopLayerRef.current = null;
        savedSpotsLayerControllerRef.current?.dispose(savedSpotLayerRef.current);
        savedSpotLayerRef.current = null;
        crewPresenceLayerControllerRef.current?.dispose(crewPresenceLayerRef.current);
        crewPresenceLayerRef.current = null;
        tournamentLayerControllerRef.current?.dispose(tournamentLayerRef.current);
        tournamentLayerRef.current = null;
        restrictedLayerRef.current = null;
      } catch {}

      try {
        // IMPORTANT: Leaflet removes the marker DOM when the map is destroyed,
        // but the React ref would otherwise keep pointing at that detached marker.
        // Clear it before removing the map so the next Big Blue mount creates a
        // fresh current-user avatar marker on the new Leaflet instance.
        userMarkerRef.current = null;

        if (mapInstanceRef.current) {
          mapInstanceRef.current.off();
          mapInstanceRef.current.remove();
        }
      } catch {
      } finally {
        userMarkerRef.current = null;
        mapInstanceRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map) return;

    if (viewState?.targetId) {
      const zone = restrictedZones.find(
        (z: any) => z.id === viewState.targetId,
      );
      if (zone) map.setView(zone.position, 15);
    }

    const globeTarget = (viewState as any)?.globeTarget;
    if (globeTarget) {
      const lat = Number(globeTarget.lat);
      const lng = Number(globeTarget.lng ?? globeTarget.lon);
      const zoom = Number(globeTarget.zoom ?? 10);
      if (Number.isFinite(lat) && Number.isFinite(lng)) {
        programmaticMoveRef.current = true;
        map.setView([lat, lng], Number.isFinite(zoom) ? zoom : 10, {
          animate: true,
        });
        window.setTimeout(() => {
          programmaticMoveRef.current = false;
        }, 850);
      }
    }
  }, [viewState]);

  useEffect(() => {
    if (!tileLayerRef.current) return;

    // Basemap/theme-only work. Never redraw raster tiles because an avatar,
    // cosmetic, FishIQ callback, Crew update, or other React state changed.
    const newUrl = getTileUrl();
    const currentUrl = String(tileLayerRef.current?._url || "");
    if (typeof tileLayerRef.current.setUrl === "function" && currentUrl !== newUrl) {
      setIsMapVisualReady(false);
      tileLayerRef.current.setUrl(newUrl);
      window.setTimeout(() => markMapVisualReady(0), 850);
    }

    try {
      if (tileLayerRef.current?.options) {
        tileLayerRef.current.options.basemapId = basemapId;
        tileLayerRef.current.options.isDarkMode = isDarkModeRef.current;
      }
    } catch {}

    applyTileTuning();
    requestAnimationFrame(() => applyTileTuning());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDarkMode, basemapId]);

  useEffect(() => {
    const map = mapInstanceRef.current;
    const layer = shopLayerRef.current;
    const controller = bazaarLayerControllerRef.current;
    if (!map || !layer || !controller) return;

    return controller.sync({
      map,
      layer,
      marketplaceItems: props.marketplaceItems,
      isDarkMode: isDarkModeRef.current,
      userLatLng: latestUserLatLngRef.current,
      clampPointToSafeBox,
      getOccupiedMapPoints: (activeMap) =>
        getUnifiedOccupiedMapPoints(activeMap, ["bazaar"]),
      resolveMapMarkerCollision,
      onSelectListing: (selection) => setSelectedBazaarMapListing(selection),
      onOpenBazaar: (payload) => {
        setSelectedBazaarMapListing(null);
        onBazaarClickRef.current?.(payload);
      },
    });
    // Shared collision/layout helpers intentionally stay owned by MapView.
    // Re-run only on the same Bazaar inputs as the pre-extraction implementation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.marketplaceItems, bazaarMapLayerReady, currentMapZoom]);

  useEffect(() => {
    // Marker-only visual refresh. This must never touch the raster tile layer or
    // invalidate Leaflet's viewport.
    if (userMarkerRef.current) {
      const icon = getUserLocationIcon(
        isDarkMode,
        mapInstanceRef.current?.getZoom?.(),
      );
      if (icon) userMarkerRef.current.setIcon(icon);
    }

    bazaarLayerControllerRef.current?.refreshIcons(isDarkMode);

    rerenderFishMarkersNow(undefined, { settlePasses: false });
    scheduleSavedSpotMarkerUpdate(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isDarkMode,
    userAvatarSignature,
    (profile as any)?.avatarBase,
    userCosmeticSignature,
  ]);

  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map) return;

    const setLayerVisible = (layer: any, visible: boolean) => {
      if (!layer) return;
      let isVisible = false;
      try { isVisible = !!map.hasLayer?.(layer); } catch {}
      if (visible && !isVisible) {
        try { layer.addTo?.(map); } catch {}
      } else if (!visible && isVisible) {
        try { map.removeLayer?.(layer); } catch {}
      }
    };

    setLayerVisible(fishLayerRef.current, mapLayerVisibility.fishiq);
    setLayerVisible(shopLayerRef.current, mapLayerVisibility.marketplace);
    setLayerVisible(tournamentLayerRef.current, mapLayerVisibility.tournaments);
    setLayerVisible(savedSpotLayerRef.current, mapLayerVisibility.savedSpots);

    if (!mapLayerVisibility.fishiq) {
      setSelectedFishSpot(null);
      setIsFishWidgetOpen(false);
      setQuickWindow((current) => (current === "fishiq" ? null : current));
    }

    if (!mapLayerVisibility.marketplace) {
      bazaarLayerControllerRef.current?.closePopup();
      setSelectedBazaarMapListing(null);
    }

    if (!mapLayerVisibility.tournaments) {
      tournamentLayerControllerRef.current?.closePopups();
      setIsTournamentMapFilterOpen(false);
      setIsWorldTournamentMapOpen(false);
      setSelectedTournamentMapDetail(null);
    }

    if (!mapLayerVisibility.savedSpots) {
      setSelectedSavedSpotId(null);
      setIsSelectedSavedSpotMenuOpen(false);
      setIsSelectedSavedSpotExpanded(false);
    }

    const previous = previousMapLayerVisibilityRef.current;
    const reenabled =
      (!previous.fishiq && mapLayerVisibility.fishiq) ||
      (!previous.marketplace && mapLayerVisibility.marketplace) ||
      (!previous.tournaments && mapLayerVisibility.tournaments) ||
      (!previous.savedSpots && mapLayerVisibility.savedSpots);

    previousMapLayerVisibilityRef.current = mapLayerVisibility;

    if (reenabled) {
      window.requestAnimationFrame(() => {
        if (mapInstanceRef.current !== map) return;
        updateTournamentMarkersRef.current();
        refreshCrewPresenceMarkers(true);
        if (mapLayerVisibility.fishiq) {
          const warmFish = Array.isArray(fishMarkerHotspotsRef.current)
            ? [...fishMarkerHotspotsRef.current]
            : [];
          if (warmFish.length) scheduleFishMarkerUpdate(warmFish, true);
        }
      });
    }
  }, [mapLayerVisibility, scheduleFishMarkerUpdate]);

  useEffect(() => {
    scheduleSavedSpotMarkerUpdate(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    savedSpots,
    showSavedSpotsOnMap,
    savedSpotFilter,
    selectedSavedSpotId,
    isDarkMode,
    scheduleSavedSpotMarkerUpdate,
  ]);

  useEffect(() => {
    scheduleSavedSpotMarkerUpdate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hotspots, scheduleSavedSpotMarkerUpdate]);

  useEffect(() => {
    fishMarkerHotspotsRef.current = Array.isArray(hotspots)
      ? [...hotspots]
      : [];
    scheduleFishMarkerUpdate(fishMarkerHotspotsRef.current, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hotspots]);

  useEffect(() => {
    // Keep FishIQ hub clicks smooth: selection changes should only swap icon styling,
    // not clear/recreate the Leaflet markers and replay their entry animation.
    refreshFishHubMarkerIcons();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshFishHubMarkerIcons]);

  useEffect(() => {
    // Catch history is data-driven: as soon as the signed-in angler logs the
    // first catch near the current FishIQ anchor, the history hub can appear.
    // Removing the last nearby catch removes it again.
    scheduleFishMarkerUpdate(fishMarkerHotspotsRef.current, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    props.catchLog,
    isDarkMode,
    nearbyCatchSpeciesRows.length,
  ]);

  useEffect(() => {
    if (fishWidgetMode === "history" && nearbyCatchSpeciesRows.length === 0) {
      setFishWidgetMode("area");
    }
  }, [fishWidgetMode, nearbyCatchSpeciesRows.length]);

  useEffect(() => {
    refreshCrewPresenceMarkers();
  }, [crewPresence, isDarkMode, refreshCrewPresenceMarkers]);

  useEffect(() => {
    setQuickCrewPresenceOptIn(
      isCrewPresenceOptedIn(((profile as any)?.notificationSettings || {}) as Record<string, any>),
    );
  }, [
    (profile as any)?.notificationSettings?.shareCrewMapPresence,
    (profile as any)?.notificationSettings?.crewMapPresence,
    (profile as any)?.notificationSettings?.crewMapPresenceEnabled,
  ]);

  const toggleQuickCrewPresence = useCallback(async () => {
    const uid = String((profile as any)?.uid || "").trim();
    if (!uid || quickCrewPresenceSaving) return;

    const next = !quickCrewPresenceOptIn;
    const previous = quickCrewPresenceOptIn;
    const currentSettings = (((profile as any)?.notificationSettings || {}) as Record<string, any>);

    try {
      setQuickCrewPresenceSaving(true);
      setQuickCrewPresenceOptIn(next);

      await updateUserProfile(uid, {
        notificationSettings: {
          ...currentSettings,
          shareCrewMapPresence: next,
          crewMapPresence: next,
          crewMapPresenceEnabled: next,
        },
        updatedAt: Date.now(),
      } as any);

      window.dispatchEvent(
        new CustomEvent("roddo:crew-presence-setting-changed", {
          detail: { uid, enabled: next, requestedAt: Date.now() },
        }),
      );

      props.addToast?.(
        next ? "Crew map presence is on." : "Crew map presence is off.",
        "success",
      );
    } catch (err) {
      console.error("[map] quick Crew presence toggle failed", err);
      setQuickCrewPresenceOptIn(previous);
      props.addToast?.("Could not update crew map presence.", "error");
    } finally {
      setQuickCrewPresenceSaving(false);
    }
  }, [
    profile,
    props.addToast,
    quickCrewPresenceOptIn,
    quickCrewPresenceSaving,
  ]);

  useEffect(() => {
    const uid = String((profile as any)?.uid || "").trim();
    setCrewPresenceBootReady(false);

    if (!uid) {
      setCrewPresence([]);
      setCrewPresenceBootReady(true);
      return;
    }

    const unsub = subscribeToAcceptedCrewPresence(
      uid,
      (next) => {
        setCrewPresence(next || []);
        setCrewPresenceBootReady(true);
      },
      (err) => {
        console.warn("[map] crew presence listener failed", err);
        setCrewPresence([]);
        setCrewPresenceBootReady(true);
      },
    );
    return () => unsub();
  }, [profile?.uid]);

  useEffect(() => {
    const uid = String((profile as any)?.uid || "").trim();
    const presenceSettings = ((profile as any)?.notificationSettings ||
      {}) as Record<string, any>;
    const shareWithCrew = isCrewPresenceOptedIn(presenceSettings);
    const displayName = String(
      (profile as any)?.displayName ||
        (profile as any)?.name ||
        (profile as any)?.username ||
        "Angler",
    );
    const avatarUrl = String(
      (profile as any)?.avatar ||
        (profile as any)?.avatarUrl ||
        (profile as any)?.photoURL ||
        "",
    );

    if (!uid) {
      crewPresenceSessionPulseUidRef.current = null;
      return;
    }

    const claimPresenceTransition = (online: boolean, dedupeMs = 1600) => {
      const now = Date.now();
      const previous = crewPresenceTransitionRef.current;
      if (
        previous?.uid === uid &&
        previous.online === online &&
        now - previous.at < dedupeMs
      ) {
        return false;
      }
      crewPresenceTransitionRef.current = { uid, online, at: now };
      return true;
    };

    const writePresence = (options?: {
      force?: boolean;
      refreshVisibility?: boolean;
      forceShareWithCrew?: boolean;
      reason?: string;
    }) => {
      const propLat = Number(props.coords?.lat);
      const propLng = Number(props.coords?.lon);
      const propLocation =
        Number.isFinite(propLat) && Number.isFinite(propLng)
          ? { lat: propLat, lng: propLng }
          : null;
      const loc = latestUserLatLngRef.current || propLocation;
      const shouldShare = options?.forceShareWithCrew === true || shareWithCrew;

      if (!shouldShare) {
        void markOwnCrewPresenceOffline(uid, {
          force: options?.force,
          reason: options?.reason || "opted_out",
        }).catch((err) =>
          console.warn("[map] crew presence offline write failed", err),
        );
        return;
      }

      // Opted-in users must not be forced offline merely because MapView has
      // mounted before GPS/cache hydration. Keep the last valid presence alive
      // until a real location arrives or the user explicitly opts out/hides app.
      if (!loc) {
        console.info("[map] crew presence waiting for location", { uid });
        return;
      }

      void updateOwnCrewPresence({
        uid,
        displayName,
        avatarUrl,
        lat: loc.lat,
        lng: loc.lng,
        shareWithCrew: true,
        isOnline: true,
        force: options?.force,
        refreshVisibility: options?.refreshVisibility,
        reason: options?.reason || "presence_pulse",
      }).catch((err) => console.warn("[map] crew presence write failed", err));
    };

    const onPresenceSettingChanged = (event: Event) => {
      const detail = (event as CustomEvent)?.detail || {};
      if (String(detail?.uid || "").trim() !== uid) return;

      if (detail?.enabled === true) {
        if (claimPresenceTransition(true)) {
          console.info("[map] crew presence enabled; publishing now", { uid });
          writePresence({
            force: true,
            refreshVisibility: true,
            forceShareWithCrew: true,
            reason: "toggle_on",
          });
        }

        // If MapView has not received a coordinate yet, start a fresh location
        // request. Its locationfound handler performs the canonical online write.
        if (!latestUserLatLngRef.current && mapInstanceRef.current) {
          void requestLocation(mapInstanceRef.current, {
            setView: false,
            warmWatch: true,
            forceFresh: false,
            allowLivePrompt: true,
          }).catch((err) =>
            console.warn("[map] crew presence location refresh failed", err),
          );
        }
      } else if (detail?.enabled === false) {
        if (!claimPresenceTransition(false)) return;
        void markOwnCrewPresenceOffline(uid, {
          force: true,
          reason: "toggle_off",
        }).catch((err) =>
          console.warn("[map] crew presence event offline write failed", err),
        );
      }
    };

    window.addEventListener(
      "roddo:crew-presence-setting-changed",
      onPresenceSettingChanged as EventListener,
    );

    // Crew lifecycle presence:
    // - publish once when an opted-in signed-in session has a usable location
    // - mark offline when the app/document is hidden
    // - publish again when the app/document returns to the foreground
    // - no recurring heartbeat: readers locally expire stale sessions
    const propLatForPulse = Number(props.coords?.lat);
    const propLngForPulse = Number(props.coords?.lon);
    const hasPulseLocation =
      !!latestUserLatLngRef.current ||
      (Number.isFinite(propLatForPulse) && Number.isFinite(propLngForPulse));

    if (
      shareWithCrew &&
      hasPulseLocation &&
      crewPresenceSessionPulseUidRef.current !== uid
    ) {
      crewPresenceSessionPulseUidRef.current = uid;
      if (claimPresenceTransition(true)) {
        writePresence({
          refreshVisibility: true,
          reason: "session_pulse",
        });
      }
    }

    let lifecycleActive = document.visibilityState !== "hidden";

    const publishForegroundPresence = (reason: string, force = false) => {
      if (!shareWithCrew || !lifecycleActive) return;
      if (!claimPresenceTransition(true)) return;
      writePresence({
        force,
        refreshVisibility: force,
        reason,
      });
    };


    const publishLifecycleOffline = (reason: string) => {
      if (!shareWithCrew) return;
      lifecycleActive = false;
      if (!claimPresenceTransition(false)) return;
      void markOwnCrewPresenceOffline(uid, {
        force: true,
        reason,
      }).catch((err) =>
        console.warn("[map] crew presence lifecycle offline write failed", err),
      );
    };

    const onPresenceVisibilityChange = () => {
      if (!shareWithCrew) return;

      lifecycleActive = document.visibilityState !== "hidden";
      if (!lifecycleActive) {
        publishLifecycleOffline("visibility_hidden");
        return;
      }

      publishForegroundPresence("visibility_visible", true);
    };

    document.addEventListener("visibilitychange", onPresenceVisibilityChange);

    // Browser pagehide and Capacitor appStateChange cover the lifecycle edges
    // that document.visibilitychange can miss on iOS/Android. These are real
    // state transitions only; there is still no recurring presence heartbeat.
    let nativeAppStateHandle: { remove: () => Promise<void> } | null = null;

    const onPageHide = () => publishLifecycleOffline("pagehide");
    const onPageShow = () => {
      lifecycleActive = document.visibilityState !== "hidden";
      publishForegroundPresence("pageshow", true);
    };
    window.addEventListener("pagehide", onPageHide);
    window.addEventListener("pageshow", onPageShow);

    if (Capacitor.isNativePlatform()) {
      void CapacitorApp.addListener("appStateChange", ({ isActive }) => {
        if (!shareWithCrew) return;
        lifecycleActive = isActive;
        if (!isActive) {
          publishLifecycleOffline("native_background");
          return;
        }

        publishForegroundPresence("native_foreground", true);
      })
        .then((handle) => {
          nativeAppStateHandle = handle;
        })
        .catch((err) =>
          console.warn("[map] crew presence native lifecycle listener failed", err),
        );
    }

    return () => {
      window.removeEventListener(
        "roddo:crew-presence-setting-changed",
        onPresenceSettingChanged as EventListener,
      );
      document.removeEventListener("visibilitychange", onPresenceVisibilityChange);
      window.removeEventListener("pagehide", onPageHide);
      window.removeEventListener("pageshow", onPageShow);
      if (nativeAppStateHandle) void nativeAppStateHandle.remove();
    };
  }, [
    profile?.uid,
    (profile as any)?.notificationSettings?.shareCrewMapPresence,
    (profile as any)?.notificationSettings?.crewMapPresence,
    (profile as any)?.notificationSettings?.crewMapPresenceEnabled,
    (profile as any)?.displayName,
    (profile as any)?.name,
    (profile as any)?.username,
    (profile as any)?.avatar,
    (profile as any)?.avatarUrl,
    (profile as any)?.photoURL,
    props.coords?.lat,
    props.coords?.lon,
  ]);

  const areHotspotListsEquivalent = useCallback(
    (nextList: Hotspot[], prevList: Hotspot[]) => {
      if (nextList === prevList) return true;
      if (nextList.length !== prevList.length) return false;

      for (let i = 0; i < nextList.length; i += 1) {
        const nextSpot = nextList[i] as any;
        const prevSpot = prevList[i] as any;
        if (!nextSpot || !prevSpot) return false;
        if (String(nextSpot.id || "") !== String(prevSpot.id || ""))
          return false;
        if (
          Math.abs(
            Number(nextSpot?.position?.lat || 0) -
              Number(prevSpot?.position?.lat || 0),
          ) >= 0.000001
        )
          return false;
        if (
          Math.abs(
            Number(nextSpot?.position?.lng || 0) -
              Number(prevSpot?.position?.lng || 0),
          ) >= 0.000001
        )
          return false;
        if (
          String(nextSpot?.waterName || "") !==
          String(prevSpot?.waterName || "")
        )
          return false;
      }

      return true;
    },
    [],
  );

  const handleRedoSearch = useCallback(
    async (options?: { isInitial?: boolean; spendSearchLure?: boolean }) => {
      const isInitial = options?.isInitial === true;
      const spendSearchLure = options?.spendSearchLure === true;
      const map = mapInstanceRef.current;
      if (!map || !isActiveRef.current) return;

      if (!fishIQPrefs.enabled) {
        hotspotRequestSeqRef.current += 1;
        fishMarkerHotspotsRef.current = [];
        setHotspots([]);
        scheduleFishMarkerUpdate([], true);
        setIsSearchingArea(false);
        return;
      }
      setIsSearchingArea(true);
      setSearchError(null);

      const zoom = map.getZoom?.() ?? 13;

      if (zoom < 10) {
        const msg = t("map.zoomInError");
        setSearchError(msg);
        props.addToast?.(msg, "info");
        fishMarkerHotspotsRef.current = [];
        setHotspots([]);
        scheduleFishMarkerUpdate([], true);
        setIsSearchingArea(false);
        return;
      }

      const center = map.getCenter?.();
      const user =
        latestUserLatLngRef.current ??
        (center ? { lat: center.lat, lng: center.lng } : null);

      // Search-this-area accuracy contract:
      // - automatic/initial reads remain GPS-first
      // - a manual Search this area read belongs to the exact geographic center
      //   the angler is looking at after pan/zoom
      // - this same canonical anchor is then reused for lure charging, signals,
      //   ranking, markers, cache identity, and D.8 validation
      const rawAnchor = isInitial
        ? (latestUserLatLngRef.current ??
          user ??
          getViewportAnchorLatLng(
            map,
            center ? { lat: center.lat, lng: center.lng } : null,
          ))
        : (center ? { lat: center.lat, lng: center.lng } : getViewportAnchorLatLng(map, user));
      const anchor = normalizeSearchLureLatLng(rawAnchor);

      if (anchor) {
        const areaContext = getFishIQAreaContextForAnchor(anchor);
        debugFishIQAlignment(isInitial ? "initial-search" : "manual-search", {
          anchor,
          viewportCenter: center ? { lat: center.lat, lng: center.lng } : null,
          waterType: areaContext.waterType,
          waterName: areaContext.waterName,
          regionHint: areaContext.geo.region,
          alignedLocationName: areaContext.alignedLocationName,
          alignedWeather: Boolean(getFishIQAlignedWeatherData(anchor)),
        });
      }

      if (!anchor || !center) {
        if (rawAnchor) {
          console.warn('[map] FishIQ search blocked: invalid viewport coordinates', rawAnchor);
        }
        setIsSearchingArea(false);
        return;
      }

      if (spendSearchLure && !isInitial) {
        fishWidgetAnchorRef.current = anchor;
        setFishWidgetAnchor(anchor);
        setFishWidgetMode("area");
        setFishWidgetAreaSource("viewport");
        setIsFishAreaExpanded(false);
        setSelectedFishSpot(null);
        setIsFishFullDetailOpen(false);
        setFishIQThinkingError(null);
        setFishIQRetryAnchor(anchor);
        setFishIQRetryMode("viewport");
        setIsFishWidgetOpen(true);
      }

      // FAIL-SAFE SEARCH LURE RULE:
      // Automatic boot/GPS/recenter/hydration reads are always free.
      // A lure can only be spent when a user action explicitly passes spendSearchLure: true.
      if (spendSearchLure) {
        let pendingLureRequest: PendingSearchLureRequest | null = null;
        try {
          pendingLureRequest = getOrCreatePendingSearchLureRequest(
            profile?.uid,
            anchor.lat,
            anchor.lng,
            'area',
          );
          const lureResult = await consumeSearchLure({
            lat: anchor.lat,
            lng: anchor.lng,
            requestId: pendingLureRequest.requestId,
          });
          clearPendingSearchLureRequest(profile?.uid, pendingLureRequest.requestId);

          setLocalSearchLureBalance(
            Math.max(0, Math.floor(Number(lureResult.searchLureBalance || 0))),
          );
          setSearchLureClockMs(Date.now());

          if (lureResult.reusedArea) {
            props.addToast?.('Same water — no Search Lure used.', 'info');
          }
        } catch (err: any) {
          const code = String(err?.code || '').toLowerCase();
          const message = String(err?.message || 'Search Lure check failed.');
          if (pendingLureRequest && isDefinitiveSearchLureFailure(err)) {
            clearPendingSearchLureRequest(profile?.uid, pendingLureRequest.requestId);
          }
          if (code.includes('resource-exhausted') || /no search lures/i.test(message)) {
            setLocalSearchLureBalance(0);
            setSearchError('No Search Lures left. Refill in The Hold.');
            if (!isInitial) setFishIQThinkingError('No Search Lures left. Refill in The Hold, then try this water again.');
            props.addToast?.('No Search Lures left — refill in The Hold.', 'info');
          } else {
            setSearchError('Could not verify a Search Lure. Try again.');
            if (!isInitial) setFishIQThinkingError('Roddo could not verify this water read. Check your connection and try again.');
            props.addToast?.('Could not verify a Search Lure. Try again.', 'warning');
          }
          if (!isInitial) setIsFishWidgetOpen(true);
          setIsSearchingArea(false);
          return;
        }
      }

      const requestSeq = ++hotspotRequestSeqRef.current;
      lastRefreshViewportRef.current = {
        center: { lat: center.lat, lng: center.lng },
        zoom,
      };

      try {
        setFishWidgetAreaSource("viewport");
        setIsFishAreaExpanded(false);
        // Keep the imperative ref and React state in lockstep. Marker rendering
        // can run before the state commit, so a stale ref here made some manual
        // reads appear offset from the area that was actually searched.
        fishWidgetAnchorRef.current = anchor;
        setFishWidgetAnchor(anchor);
        try {
          searchedFishAreaMarkerRef.current?.remove?.();
          searchedFishAreaMarkerRef.current = null;
        } catch {}
        // Release hardening: once a manual Search Lure charge succeeds, show
        // the deterministic local FishIQ read immediately. Live-signal and
        // Overpass work are enrichment only and must never hold the paid result
        // behind another network request.
        const baseHotspots = buildBaseFishHotspotsAroundUser(map, anchor, 8, {
          locationName: getFishIQAreaContextForAnchor(anchor).waterName,
        });
        const decoratedBaseHotspots = decorateHotspotsWithUniqueSpecies(
          baseHotspots as any,
          8,
        ) as Hotspot[];
        const baseTrimmed = rankFishHotspotsForMapArea(
          decoratedBaseHotspots.slice(0, 8),
          anchor,
          { signals: [] },
        );

        if (
          !areHotspotListsEquivalent(baseTrimmed, fishMarkerHotspotsRef.current)
        ) {
          fishMarkerHotspotsRef.current = [...baseTrimmed];
          setHotspots(baseTrimmed);
        } else {
          fishMarkerHotspotsRef.current = [...baseTrimmed];
        }
        setSelectedFishSpot((prev) => {
          if (!prev) return null;
          const refreshed = baseTrimmed.find((spot) => spot.id === prev.id);
          return refreshed ?? null;
        });
        if (!baseTrimmed.length) setIsFishWidgetOpen(false);
        scheduleFishMarkerUpdate(baseTrimmed);
        recordFishIQValidationSnapshot(
          baseTrimmed,
          anchor,
          isInitial ? "big_blue_initial" : "big_blue_manual",
        );


        setIsSearchingArea(false);
        if (!isInitial) {
          setFishIQThinkingError(null);
          setFishIQRetryAnchor(null);
          setFishIQRetryMode(null);
        }

        // A successful Big Blue FishIQ scout counts as scouting activity whether the
// read was the free first read, a paid Search Lure read, or a reused area.
// completeDailyTaskAction is server-authoritative/idempotent for the daily task,
// so cached hydration alone cannot award progress because it never reaches here.
void completeDailyTaskAction("search_area")
  .then((result) => {
    if (result?.completed && !result?.alreadyCompleted) {
      const shells = Math.max(
        0,
        Math.floor(Number(result.awardedShells || 0)),
      );
      const title = String(
        result.dailyTask?.title || "Today's Cast task",
      );

      props.addToast?.(
        `${title} complete — +${shells} Shell${shells === 1 ? "" : "s"}`,
        "success",
      );
    }
  })
  .catch((err) =>
    console.warn("[DailyTask] search_area skipped", err),
  );

        // Live observations + water snapping now enhance the already-visible
        // base read in the background. Sequence guards prevent a slow prior
        // request from repainting over a newer search.
        const fusionEnvironmentPromise = fetchFishIQEnvironmentForAnchor(
          anchor,
          { cacheOnly: isInitial },
        );
        void fetchFishIQSignalsForAnchor(anchor)
          .then(async (liveSignals) => {
            const fusionEnvironment = await fusionEnvironmentPromise;
            if (!isActiveRef.current) return;
            if (hotspotRequestSeqRef.current !== requestSeq) return;

            const fusionBaseHotspots = buildBaseFishHotspotsAroundUser(
              map,
              anchor,
              8,
              {
                locationName: getFishIQAreaContextForAnchor(anchor).waterName,
                environment: fusionEnvironment,
              },
            );
            const fusionDecoratedHotspots = decorateHotspotsWithUniqueSpecies(
              fusionBaseHotspots as any,
              8,
            ) as Hotspot[];
            const signalTrimmed = rankFishHotspotsForMapArea(
              fusionDecoratedHotspots.slice(0, 8),
              anchor,
              { signals: liveSignals, environment: fusionEnvironment },
            );

            if (
              !areHotspotListsEquivalent(signalTrimmed, fishMarkerHotspotsRef.current)
            ) {
              fishMarkerHotspotsRef.current = [...signalTrimmed];
              setHotspots(signalTrimmed);
              scheduleFishMarkerUpdate(signalTrimmed);
            } else {
              fishMarkerHotspotsRef.current = [...signalTrimmed];
            }

            try {
              const nextHotspots = await buildFishHotspotsAroundUser(
                map,
                anchor,
                8,
                { signals: liveSignals, environment: fusionEnvironment },
              );
              if (!isActiveRef.current) return;
              if (hotspotRequestSeqRef.current !== requestSeq) return;

              const trimmed = rankFishHotspotsForMapArea(nextHotspots, anchor, {
                signals: liveSignals,
                environment: fusionEnvironment,
              }).slice(0, 8);
              const samePositions = areHotspotListsEquivalent(
                trimmed,
                signalTrimmed,
              );

              if (samePositions) return;

              if (
                !areHotspotListsEquivalent(trimmed, fishMarkerHotspotsRef.current)
              ) {
                fishMarkerHotspotsRef.current = [...trimmed];
                setHotspots(trimmed);
              } else {
                fishMarkerHotspotsRef.current = [...trimmed];
              }
              setSelectedFishSpot((prev) => {
                if (!prev) return null;
                const refreshed = trimmed.find((spot) => spot.id === prev.id);
                return refreshed ?? prev;
              });
              if (!trimmed.length) setIsFishWidgetOpen(false);
              scheduleFishMarkerUpdate(trimmed);

            } catch (err) {
              console.warn("[map] enhanced hotspot refresh skipped", err);
            }
          })
          .catch((err) => {
            console.warn("[map] FishIQ live-signal enhancement skipped", err);
          });
      } catch (err) {
        console.error("handleRedoSearch failed:", err);
        const msg = t("map.hotspotError");
        setSearchError(msg);
        props.addToast?.(msg, "info");
        setIsSearchingArea(false);
      }
    },
    [
      areHotspotListsEquivalent,
      fishIQPrefs.enabled,
      fetchFishIQSignalsForAnchor,
      fetchFishIQEnvironmentForAnchor,
      props.addToast,
      recordFishIQValidationSnapshot,
      scheduleFishMarkerUpdate,
      t,
    ],
  );

  // Expose the current callback to the Leaflet locationfound handler without
  // rebuilding the map listener every time React dependencies change.
  handleUserAnchoredFishRefreshRef.current = handleRedoSearch;

  // First-read safety net.
  //
  // The normal locationfound handler queues the initial free FishIQ read after
  // the map is centered on the user's resolved anchor. During first-run flows,
  // however, location can resolve while Big Blue is still inactive (legal /
  // onboarding / shell transition). In that case locationfound intentionally
  // returns before queueing FishIQ, and the old one-shot boot path may never
  // replay it once Big Blue becomes active.
  //
  // userAnchorBootReady is only set after an active Big Blue has centered on a
  // usable GPS/cache anchor. At that exact point, guarantee the initial read if
  // it has not already completed or been queued. This remains FREE: the
  // consumer below always calls handleRedoSearch with spendSearchLure:false.
  useEffect(() => {
    if (!props.isActive || !fishIQPrefs.enabled) return;
    if (!userAnchorBootReady) return;
    if (!latestUserLatLngRef.current) return;
    if (
      initialUserAnchoredSearchDoneRef.current ||
      initialUserAnchoredSearchPendingRef.current
    ) {
      return;
    }

    initialUserAnchoredSearchPendingRef.current = true;
    initialOverlayDoneRef.current = true;

    const requestId = ++userAnchoredFishSearchRequestIdRef.current;
    if (!mapBootReadyRef.current) {
      setMapBootProgress(66);
      setMapBootDetail("Building your local fish read…");
    }

    console.log("[map] safety-net queued initial free FishIQ read", {
      requestId,
      anchor: latestUserLatLngRef.current,
    });

    setUserAnchoredFishSearchRequest({
      id: requestId,
      isInitial: true,
    });
  }, [props.isActive, userAnchorBootReady, fishIQPrefs.enabled]);

  // Deterministic GPS -> FishIQ bridge.
  // Each queued request id is consumed exactly once. Use the mutable callback
  // ref so normal React renders cannot replay the same location search.
  useEffect(() => {
    if (!userAnchoredFishSearchRequest) return;
    if (!props.isActive) return;

    const request = userAnchoredFishSearchRequest;

    // IMPORTANT:
    // Do not mark this request processed until it is actually about to execute.
    // React StrictMode intentionally mounts/cleans/re-runs effects in development.
    // The old flow marked the request processed BEFORE the two-frame settle, then
    // the StrictMode cleanup cancelled it. The second effect run saw it as already
    // processed, so the initial free FishIQ read never ran and no fish appeared.
    let cancelled = false;

    const run = async () => {
      // Let locationfound -> setView/layout settle before reading the map anchor.
      await new Promise<void>((resolve) => {
        window.requestAnimationFrame(() => {
          window.requestAnimationFrame(() => resolve());
        });
      });

      if (cancelled || !isActiveRef.current) return;

      // Another live run may already have completed this request while we waited.
      if (
        processedUserAnchoredFishSearchRequestIdRef.current >= request.id
      ) {
        return;
      }

      // Claim the request only at the execution boundary.
      processedUserAnchoredFishSearchRequestIdRef.current = request.id;

      const refresh = handleUserAnchoredFishRefreshRef.current;
      if (!refresh) {
        console.warn("[map] queued user-anchored FishIQ search skipped: callback unavailable", {
          requestId: request.id,
        });
        return;
      }

      try {
        console.log("[map] running queued user-anchored FishIQ search", {
          requestId: request.id,
          anchor: latestUserLatLngRef.current,
        });
        if (request.isInitial && !mapBootReadyRef.current) {
          setMapBootProgress(76);
          setMapBootDetail("Reading local water…");
        }
        await refresh({
          isInitial: request.isInitial,
          spendSearchLure: false,
        });

        if (request.isInitial) {
          initialUserAnchoredSearchDoneRef.current = true;
          initialUserAnchoredSearchPendingRef.current = false;
        }

        // Initial boot is free, but it still needs a deterministic marker repaint
        // after the map settles. This does not perform another FishIQ search and
        // cannot consume a Search Lure.
        if (request.isInitial && fishMarkerHotspotsRef.current.length) {
          scheduleFishMarkerUpdate([...fishMarkerHotspotsRef.current], true);
        }

        if (request.isInitial && !mapBootReadyRef.current) {
          setMapBootProgress(82);
          setMapBootDetail("Water read ready · syncing Crew and map data…");
        }

        // Give the marker render queue two frames to commit before marking FishIQ
        // settled. The final boot coordinator below still waits on the rest of
        // Big Blue's first-use data before revealing the map.
        await new Promise<void>((resolve) => {
          window.requestAnimationFrame(() => {
            window.requestAnimationFrame(() => resolve());
          });
        });
        if (!cancelled && request.isInitial) {
          setInitialFishBootSettled(true);
        }
      } catch (err) {
        console.warn("[map] queued user-anchored FishIQ search failed", err);
        if (request.isInitial) {
          initialUserAnchoredSearchPendingRef.current = false;
        }
        if (!cancelled && request.isInitial) {
          // FishIQ is soft-fail during boot. The map should not hang forever if
          // the service is temporarily unavailable.
          setInitialFishBootSettled(true);
        }
      } finally {
        // Consume only after this request has actually had its chance to run.
        // The processed-id ref above prevents normal renders from replaying it.
        if (!cancelled) {
          setUserAnchoredFishSearchRequest((current) =>
            current?.id === request.id ? null : current,
          );
        }
      }
    };

    void run();

    return () => {
      cancelled = true;

      if (
        request.isInitial &&
        processedUserAnchoredFishSearchRequestIdRef.current < request.id
      ) {
        initialUserAnchoredSearchPendingRef.current = false;
      }
    };
  }, [userAnchoredFishSearchRequest, props.isActive]);

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    const rawQuery = searchQuery.trim();
    if (!rawQuery || !mapInstanceRef.current) return;

    const normalizedFishQuery = normalizeSpeciesKey(rawQuery);
    const localFishMatch =
      fishSpotBySpeciesKey.get(normalizedFishQuery) ||
      Array.from(fishSpotBySpeciesKey.entries()).find(
        ([key]) =>
          key.includes(normalizedFishQuery) ||
          normalizedFishQuery.includes(key),
      )?.[1] ||
      null;

    if (localFishMatch) {
      setSearchError(null);
      setFishWidgetMode("area");
      setFishWidgetAreaSource("viewport");
      setIsFishAreaExpanded(false);
      setIsFishWidgetOpen(true);
      setSelectedFishSpot(localFishMatch);
      setIsFishFullDetailOpen(false);
      closeMapSearch();
      return;
    }
    setSearchError(null);

    try {
      const results = await forwardGeocodeServer(rawQuery, 5);

      if (results.length > 0) {
        const { lat, lon } = results[0];
        const nextAnchor = { lat: Number(lat), lng: Number(lon) };
        mapInstanceRef.current.setView([nextAnchor.lat, nextAnchor.lng], 13);
        // The old floating "Search this area" CTA is gone. A place search now
        // lands directly on the unified Selected Water working point so Search,
        // Log, and Save all continue from the same exact coordinate.
        setMapActionAnchor(nextAnchor);
        setIsMapActionCardOpen(true);
        window.setTimeout(() => {
          const map = mapInstanceRef.current;
          if (map) focusMapActionAnchor(map, nextAnchor);
        }, 180);
        closeMapSearch();
      } else if (typeof props.onFishIQClick === "function") {
        const liveMapCenter = mapInstanceRef.current?.getCenter?.();
        const anchorForFishIQ =
          fishWidgetAnchor ??
          latestUserLatLngRef.current ??
          (liveMapCenter
            ? { lat: liveMapCenter.lat, lng: liveMapCenter.lng }
            : null);
        const geo = anchorForFishIQ
          ? inferGeoProfile(anchorForFishIQ, props.locationName ?? null)
          : null;

        closeMapSearch();
        props.onFishIQClick({
          initialQuery: rawQuery,
          mapContext:
            geo && anchorForFishIQ
              ? {
                  source: "map search",
                  coords: {
                    lat: anchorForFishIQ.lat,
                    lon: anchorForFishIQ.lng,
                  },
                  waterType:
                    geo.waterKind === "saltwater"
                      ? "inshore"
                      : geo.waterKind === "river"
                        ? "river"
                        : geo.waterKind === "lake"
                          ? "lake"
                          : "freshwater",
                  waterKind:
                    geo.waterKind === "saltwater"
                      ? "inshore"
                      : geo.waterKind === "river"
                        ? "river"
                        : geo.waterKind === "lake"
                          ? "lake"
                          : "freshwater",
                  waterName:
                    props.locationName ||
                    (geo.waterKind === "saltwater"
                      ? "Coastal water"
                      : geo.waterKind === "river"
                        ? "Moving water"
                        : geo.waterKind === "lake"
                          ? "Lake / reservoir water"
                          : "Freshwater profile"),
                  regionHint: geo.region,
                }
              : null,
        });
      } else {
        setSearchError(t("map.searchError"));
      }
    } catch (error) {
      console.error(error);
      setSearchError(t("map.searchFailed"));
    } finally {
    }
  };

  const handleRecenter = () => {
    const map = mapInstanceRef.current;
    if (!map) return;

    hasGeoErrorToastRef.current = false;
    hasTriggeredSafariFirstTapGpsRef.current = true;
    pendingRecenterFishRefreshRef.current = true;
    setSearchError(null);

    void requestLocation(map, {
      setView: true,
      warmWatch: false,
      forceFresh: true,
      allowLivePrompt: true,
    });
  };

  useEffect(() => {
    // iPhone Safari should only request live GPS from the explicit recenter action.
    // The earlier shell-wide first-interaction upgrader was too eager and could
    // consume the first tap with a soft denial before the user intentionally hit recenter.
    hasArmedSafariFirstTapGpsRef.current = false;

    return () => {
      hasArmedSafariFirstTapGpsRef.current = false;
      hasTriggeredSafariFirstTapGpsRef.current = false;
    };
  }, [props.isActive]);

  const weatherBootReady = props.bootDependenciesReady?.weather !== false;
  const tournamentsBootReady = props.bootDependenciesReady?.tournaments !== false;

  useEffect(() => {
    if (!props.isActive || mapBootReady) return;

    const checks = [
      { ready: userAnchorBootReady, progress: 34, detail: "Finding your location…" },
      { ready: isMapVisualReady, progress: 46, detail: "Painting the map…" },
      { ready: initialFishBootSettled, progress: 72, detail: "Reading local water…" },
      { ready: hasLoadedSavedSpots, progress: 84, detail: "Loading saved spots…" },
      { ready: crewRelationshipsBootReady, progress: 87, detail: "Syncing Crew…" },
      { ready: crewProfilesBootReady, progress: 89, detail: "Loading crewmates…" },
      { ready: crewGroupsBootReady, progress: 91, detail: "Loading private crews…" },
      { ready: crewInboxBootReady, progress: 93, detail: "Syncing Crew messages…" },
      { ready: crewPresenceBootReady, progress: 94, detail: "Checking Crew presence…" },
      { ready: dailyBootReady, progress: 95, detail: "Loading today's Bite Plan…" },
      { ready: challengesBootReady, progress: 96, detail: "Loading challenges…" },
      { ready: tournamentsBootReady, progress: 97, detail: "Loading tournaments…" },
      { ready: weatherBootReady, progress: 98, detail: "Reading local weather…" },
    ];

    const firstPending = checks.find((check) => !check.ready);
    if (firstPending) {
      setMapBootProgress((current) => Math.max(current, firstPending.progress));
      setMapBootDetail(firstPending.detail);
      return;
    }

    let cancelled = false;
    const reveal = async () => {
      // Let all marker/UI state commit before the loading screen fades away.
      await new Promise<void>((resolve) => {
        window.requestAnimationFrame(() => {
          window.requestAnimationFrame(() => resolve());
        });
      });
      if (cancelled || mapBootReadyRef.current) return;

      setMapBootProgress(100);
      setMapBootDetail("Big Blue ready");
      setMapBootReady(true);
    };

    void reveal();
    return () => {
      cancelled = true;
    };
  }, [
    props.isActive,
    mapBootReady,
    userAnchorBootReady,
    isMapVisualReady,
    initialFishBootSettled,
    hasLoadedSavedSpots,
    crewRelationshipsBootReady,
    crewProfilesBootReady,
    crewGroupsBootReady,
    crewInboxBootReady,
    crewPresenceBootReady,
    dailyBootReady,
    challengesBootReady,
    tournamentsBootReady,
    weatherBootReady,
  ]);

  useEffect(() => {
    if (!props.isActive || mapBootReady) return;

    // Hard safety release. Normal boots should satisfy the coordinator first.
    // This only prevents a dead screen when GPS/provider/listener data is
    // unavailable or a platform (notably Safari) requires a user gesture.
    const timer = window.setTimeout(() => {
      if (mapBootReadyRef.current) return;
      setInitialFishBootSettled(true);
      setMapBootProgress(100);
      setMapBootDetail(
        userAnchorBootReadyRef.current
          ? "Big Blue ready"
          : "Big Blue ready · tap recenter for live GPS",
      );
      setMapBootReady(true);
    }, 8500);

    return () => window.clearTimeout(timer);
  }, [props.isActive, mapBootReady]);

  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!props.isActive || !map) return;
    if (bootRequestStartedRef.current) return;

    bootRequestStartedRef.current = true;
    let cancelled = false;

    const boot = async () => {
      try {
        console.log("[map] initial boot -> resolve user location + automatic area search");

        await requestLocation(map, {
          setView: false,
          warmWatch: false,
          forceFresh: false,
          allowLivePrompt: false,
        });
      } catch (e) {
        console.warn("[map] initial location boot failed", e);
      }

      window.setTimeout(() => {
        if (cancelled || !isActiveRef.current) return;
        if (
          initialUserAnchoredSearchDoneRef.current ||
          initialOverlayDoneRef.current
        )
          return;

        console.log(
          "[map] initial FishIQ area read is waiting for a resolved user location",
        );
        initialOverlayDoneRef.current = true;
      }, 700);
    };

    const t = window.setTimeout(() => {
      if (!cancelled) void boot();
    }, 320);

    return () => {
      cancelled = true;
      window.clearTimeout(t);
    };
  }, [props.isActive]);

  const toggleMapLayerVisibility = useCallback(
    (key: keyof BigBlueMapLayerVisibility) => {
      setMapLayerVisibility((current) => {
        const next = { ...current, [key]: !current[key] };
        if (key === "savedSpots") setShowSavedSpotsOnMap(next.savedSpots);
        return next;
      });
    },
    [],
  );



  const handleStartDownload = async () => {
    if (!mapInstanceRef.current || typeof L === "undefined") return;

    const regionName = window.prompt(
      t("map.enterRegionName"),
      t("map.defaultRegionName"),
    );
    if (!regionName) return;

    const bounds = mapInstanceRef.current.getBounds();
    const minZoom = mapInstanceRef.current.getZoom();
    const maxZoom = Math.min(minZoom + 2, 16);

    const tilesToDownload: { z: number; x: number; y: number }[] = [];
    for (let z = minZoom; z <= maxZoom; z++) {
      const nw = mapInstanceRef.current
        .project(bounds.getNorthWest(), z)
        .divideBy(256)
        .floor();
      const se = mapInstanceRef.current
        .project(bounds.getSouthEast(), z)
        .divideBy(256)
        .floor();
      for (let x = nw.x; x <= se.x; x++) {
        for (let y = nw.y; y <= se.y; y++) tilesToDownload.push({ z, x, y });
      }
    }

    setDownloadState({
      active: true,
      name: regionName,
      progress: 0,
      total: tilesToDownload.length,
    });

    const tileUrlTemplate = BASEMAPS.hot.lightUrl;

    for (let i = 0; i < tilesToDownload.length; i++) {
      const tile = tilesToDownload[i];
      const tileUrl = (L as any).Util.template(tileUrlTemplate, {
        ...tile,
        s: "a",
      });

      const tileKey = `tile-${basemapId}-${tile.z}-${tile.x}-${tile.y}`;

      try {
        const response = await fetch(tileUrl);
        if (response.ok) {
          const blob = await response.blob();
          await idb.set(TILE_STORE, tileKey, blob);
        }
        setDownloadState((prev) => ({ ...prev, progress: i + 1 }));
      } catch (e) {
        console.warn(`Failed to download tile ${tileKey}`, e);
      }
    }

    try {
      await idb.set(REGION_STORE, regionName, {
        name: regionName,
        bounds: bounds.toBBoxString(),
        minZoom,
        maxZoom,
        timestamp: Date.now(),
        tileCount: tilesToDownload.length,
        basemapId,
        theme: isDarkModeRef.current ? "dark" : "light",
      });
    } catch (err) {
      console.warn(
        "[offline-maps] Failed to save offline region metadata.",
        err,
      );
    }

    setDownloadState({ active: false, name: "", progress: 0, total: 0 });
    void loadOfflineRegions();
  };

  const HUD_TOP_GAP_PX = 14;
  const HUD_TOP_TUNE_PX = 0;
  const HUD_REDO_OFFSET_PX = 76;
  const HUD_BOTTOM_GAP_PX = 12;
  const HUD_RIGHT_BOTTOM_GAP_PX = 44;

  // MapView already lives below <Header/> inside App.tsx.
  // Do NOT add --roddo-header-h again here.
  const headerTopCss = `${HUD_TOP_GAP_PX + HUD_TOP_TUNE_PX}px`;
  const redoTopCss = `${HUD_TOP_GAP_PX + HUD_REDO_OFFSET_PX + HUD_TOP_TUNE_PX}px`;
  // Keep the FishIQ redo button in the normal search lane. Roddo Radio now
  // lives in the lower media dock so the search area stays clean.
  const redoBelowRadioTopCss = redoTopCss;
  const searchErrorBelowRedoTopCss = `calc(${redoBelowRadioTopCss} + 44px)`;

  // BottomNav overlays the map. Clear the actual nav height, then add
  // only a small visual gap so the bottom HUD rests cleanly above the dock.
  const bottomHudCss =
    `calc(var(--roddo-map-bottom-clearance, var(--roddo-bottom-nav-h, 84px)) + ${HUD_BOTTOM_GAP_PX}px)`;
  const rightBottomHudCss =
    `calc(var(--roddo-map-bottom-clearance, var(--roddo-bottom-nav-h, 84px)) + ${HUD_RIGHT_BOTTOM_GAP_PX}px)`;

  if (!profile) return <div className="h-full w-full bg-[#090B13]" />;

  const actionButtonStyle =
    "roddo-pressable roddo-hud-orb group relative w-[44px] h-[44px] rounded-[14px] flex items-center justify-center overflow-hidden " +
    "border border-[#6f573c]/14 dark:border-[rgba(132,151,190,0.18)] " +
    "bg-[rgba(255,249,239,0.96)] dark:bg-[#101827] " +
    "shadow-[0_4px_12px_rgba(89,65,37,0.10)] dark:shadow-[0_4px_12px_rgba(0,0,0,0.18)] " +
    "transition-[transform,box-shadow,filter] duration-150 ease-out " +
    "hover:-translate-y-[1px] hover:brightness-[1.02] active:translate-y-0 active:scale-[0.97]";

  return (
    // ✅ Hard clamp MapView to the available viewport space under the header.
    // This guarantees: NO document scrollbars on desktop, consistent resize behavior.
    <div
      ref={mapShellRef}
      data-roddo-tour="big-blue-map"
      data-roddo-dark={isDarkMode ? "true" : "false"}
      className="absolute inset-0 h-full w-full overflow-hidden"
      style={{
        minHeight: 0,
        height: "100%",
        width: "100%",
        contain: "layout size style",
      }}
    >
      <div
        className="absolute inset-0"
        aria-hidden="true"
        style={{
          background: isDarkMode
            ? "linear-gradient(180deg, #0A1120 0%, #070B15 100%)"
            : "linear-gradient(180deg, #dff5ff 0%, #cfe9f2 100%)",
        }}
      />
      <div
        ref={mapRef}
        id="map"
        className="absolute inset-0 h-full w-full transition-opacity duration-300 ease-out"
        style={{
          height: "100%",
          width: "100%",
          minHeight: 0,
          contain: "layout size style",
          opacity: isMapVisualReady ? 1 : 0,
        }}
      />
      {!isMapVisualReady ? (
        <div className="pointer-events-none absolute inset-0 z-[2] flex items-center justify-center">
          <div
            className="rounded-full border px-4 py-2 text-[12px] font-black shadow-sm backdrop-blur-xl"
            style={{
              background: isDarkMode
                ? "rgba(18,27,50,0.54)"
                : "rgba(255,252,246,0.62)",
              borderColor: isDarkMode
                ? "rgba(255,255,255,0.10)"
                : "rgba(91,67,49,0.10)",
              color: isDarkMode ? "rgba(238,244,255,0.78)" : "#5f6f7a",
            }}
          >
            Charting Big Blue…
          </div>
        </div>
      ) : null}
      <div className="roddo-map-ambient" aria-hidden="true" />
      <style>{`
        /* Desktop Big Blue uses a left nav rail, not a bottom dock. Keep the
           mobile clearance as the default, then release it only for web
           desktop so HUD controls and drawers can use the full map height. */
        @media (min-width: 1024px) {
          html[data-roddo-platform="web"] [data-roddo-tour="big-blue-map"] {
            --roddo-map-bottom-clearance: 0px;
          }
        }

        /* Reserve a clean attribution lane directly above the bottom-nav/safe-area
           boundary. The shared bottom HUD baseline is lifted above this lane, so
           provider credits stay readable without weaving around Roddo controls. */
        [data-roddo-tour="big-blue-map"] .leaflet-control-attribution {
          margin-right: 8px !important;
          margin-bottom: calc(var(--roddo-map-bottom-clearance, var(--roddo-bottom-nav-h, 84px)) + 2px) !important;
          max-width: min(72vw, 520px);
          padding: 3px 7px !important;
          border: 1px solid rgba(91, 67, 49, 0.12);
          border-radius: 7px;
          background: rgba(255, 252, 246, 0.92) !important;
          color: #4f5d66 !important;
          box-shadow: 0 2px 8px rgba(39, 27, 14, 0.10);
          backdrop-filter: blur(8px);
          -webkit-backdrop-filter: blur(8px);
          font-size: 10px !important;
          font-weight: 650;
          line-height: 1.25 !important;
          white-space: normal;
          text-align: right;
          z-index: 1000;
        }
        [data-roddo-tour="big-blue-map"] .leaflet-control-attribution a {
          color: #236bb0 !important;
          font-weight: 800;
          text-decoration: none;
        }
        [data-roddo-tour="big-blue-map"] .leaflet-control-attribution a:hover,
        [data-roddo-tour="big-blue-map"] .leaflet-control-attribution a:focus-visible {
          text-decoration: underline;
        }
        [data-roddo-tour="big-blue-map"][data-roddo-dark="true"] .leaflet-control-attribution {
          border-color: rgba(142, 156, 211, 0.20);
          background: rgba(10, 18, 33, 0.90) !important;
          color: #c7d2e2 !important;
          box-shadow: 0 3px 12px rgba(0, 0, 0, 0.28);
        }
        [data-roddo-tour="big-blue-map"][data-roddo-dark="true"] .leaflet-control-attribution a {
          color: #8fc8ff !important;
        }
        @media (max-width: 640px) {
          [data-roddo-tour="big-blue-map"] .leaflet-control-attribution {
            margin-right: 6px !important;
            margin-bottom: calc(var(--roddo-map-bottom-clearance, var(--roddo-bottom-nav-h, 84px)) + 2px) !important;
            max-width: calc(100vw - 24px);
            padding: 2px 6px !important;
            font-size: 9px !important;
          }
        }
        @keyframes roddo-fish-enter {
          0% { opacity: 0; transform: translateY(8px) scale(0.84); }
          70% { opacity: 1; transform: translateY(-2px) scale(1.03); }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes roddo-bob-drift {
          0%, 100% { transform: translate3d(0, 0, 0) scale(1); }
          25% { transform: translate3d(var(--roddo-drift-x, 1px), calc(var(--roddo-drift-y, 2px) * -0.35), 0) scale(1.01); }
          50% { transform: translate3d(calc(var(--roddo-drift-x, 1px) * -0.45), var(--roddo-drift-y, 2px), 0) scale(1.02); }
          75% { transform: translate3d(calc(var(--roddo-drift-x, 1px) * 0.65), calc(var(--roddo-drift-y, 2px) * 0.4), 0) scale(1.005); }
        }
        @keyframes roddo-selected-bob {
          0%,100%{ transform: translateY(0) scale(1.03) }
          50%{ transform: translateY(-6px) scale(1.07) }
        }
        @keyframes roddo-shop-float {
          0%,100%{ transform: translateY(0) scale(1) rotate(0deg) }
          50%{ transform: translateY(-4px) scale(1.02) rotate(-1deg) }
        }
        @keyframes roddo-sonar-ring {
          0% { transform: translate(-50%, -50%) scale(0.42); opacity: 0.92; }
          70% { opacity: 0.34; }
          100% { transform: translate(-50%, -50%) scale(1.62); opacity: 0; }
        }
        .roddo-fish-sonar {
          position:absolute;
          left:50%;
          top:50%;
          transform:translate(-50%,-50%);
          border-radius:999px;
          border:3px solid var(--roddo-sonar, rgba(95,170,255,0.92));
          background: radial-gradient(circle, rgba(255,255,255,0.12) 0%, rgba(255,255,255,0.04) 32%, rgba(255,255,255,0) 62%);
          box-shadow:0 0 24px var(--roddo-glow-outer, rgba(110,185,255,0.52)), inset 0 0 16px rgba(255,255,255,0.08);
          pointer-events:none;
          animation: roddo-sonar-ring 2.1s ease-out infinite;
          will-change: transform, opacity;
          mix-blend-mode: screen;
        }
        .roddo-fish-sonar-a { animation-delay: calc(var(--roddo-delay, 0s) + 0s); }
        .roddo-fish-sonar-b { animation-delay: calc(var(--roddo-delay, 0s) + 0.78s); }
        .roddo-fish-sonar-c { animation-delay: calc(var(--roddo-delay, 0s) + 1.56s); }
        .roddo-fish-entry {
          animation: roddo-fish-enter 320ms cubic-bezier(.2,.75,.28,1.12) both;
          animation-delay: var(--roddo-delay, 0s);
          will-change: transform, opacity;
        }
        .roddo-fishiQ-bounce {
          animation: roddo-bob-drift 7.8s ease-in-out infinite;
          animation-delay: var(--roddo-delay, 0s);
          transform-origin:center;
          will-change: transform;
        }
        .roddo-shop-entry {
          animation: roddo-fish-enter 360ms cubic-bezier(.2,.75,.28,1.12) both;
        }
        .roddo-shop-bounce {
          animation: roddo-shop-float 4.2s ease-in-out infinite;
          will-change: transform;
        }
        .roddo-fish-selected {
          animation: roddo-selected-bob 3.6s ease-in-out infinite;
          transform-origin:center;
          will-change: transform;
        }
        .roddo-map-fish-icon,
        .roddo-map-fish-icon * {
          overflow: visible !important;
        }
        .roddo-map-fish-icon {
          background: transparent !important;
          border: 0 !important;
        }
        .roddo-map-fish-shell {
          isolation: isolate;
        }
        .roddo-fish-core-glow {
          animation: roddo-glow-pulse 2.6s ease-in-out infinite;
          will-change: transform, opacity;
        }
        .roddo-fish-inner-ring {
          animation: roddo-glow-pulse 2.2s ease-in-out infinite;
          will-change: transform, opacity;
        }
        .roddo-fish-visibility-bubble {
          mix-blend-mode: normal;
        }
        .roddo-map-fish-icon:hover {
          filter: brightness(1.14);
        }
        @keyframes roddo-fish-bowl-swim {
          0% { transform: translate3d(-9px, 1px, 0) rotate(-2deg) scaleX(0.96) scaleY(0.98); }
          10% { transform: translate3d(-6px, -1px, 0) rotate(-1deg) scaleX(0.99) scaleY(1); }
          22% { transform: translate3d(1px, -2px, 0) rotate(1deg) scaleX(1.04) scaleY(1.02); }
          34% { transform: translate3d(8px, -1px, 0) rotate(2deg) scaleX(1.08) scaleY(1.03); }
          46% { transform: translate3d(10px, 1px, 0) rotate(1deg) scaleX(1.02) scaleY(1); }
          58% { transform: translate3d(4px, 2px, 0) rotate(-1deg) scaleX(0.98) scaleY(0.99); }
          70% { transform: translate3d(-3px, 1px, 0) rotate(-2deg) scaleX(0.94) scaleY(0.98); }
          84% { transform: translate3d(-10px, -1px, 0) rotate(-3deg) scaleX(0.91) scaleY(0.97); }
          100% { transform: translate3d(-9px, 1px, 0) rotate(-2deg) scaleX(0.96) scaleY(0.98); }
        }
        @keyframes roddo-fish-tail-flick {
          0%, 100% { transform: rotate(0deg) skewX(0deg) scaleX(1); }
          14% { transform: rotate(1.4deg) skewX(2.8deg) scaleX(0.99); }
          28% { transform: rotate(-1.8deg) skewX(-3.6deg) scaleX(1.012); }
          42% { transform: rotate(1.6deg) skewX(3.2deg) scaleX(0.992); }
          58% { transform: rotate(-1.5deg) skewX(-3deg) scaleX(1.01); }
          74% { transform: rotate(1.1deg) skewX(2.2deg) scaleX(0.996); }
          88% { transform: rotate(-0.8deg) skewX(-1.8deg) scaleX(1.004); }
        }
        .roddo-fish-swim-bowl {
          transform-origin:center;
          will-change: transform;
        }
        .roddo-fish-swim-sprite {
          transform-origin: 72% 50%;
          will-change: transform;
          backface-visibility:hidden;
        }
        .roddo-map-fish-icon:hover .roddo-fish-swim-bowl,
        .roddo-fish-entry:hover .roddo-fish-swim-bowl,
        .leaflet-interactive:hover .roddo-fish-swim-bowl,
        .roddo-fish-hub-entry:hover .roddo-fish-swim-bowl {
          animation: roddo-fish-bowl-swim 2.35s ease-in-out infinite;
        }
        .roddo-map-fish-icon:hover .roddo-fish-swim-sprite,
        .roddo-fish-entry:hover .roddo-fish-swim-sprite,
        .leaflet-interactive:hover .roddo-fish-swim-sprite,
        .roddo-fish-hub-entry:hover .roddo-fish-swim-sprite {
          animation: roddo-fish-tail-flick 1.85s ease-in-out infinite;
        }
        @keyframes roddo-fish-hub-hover-sonar {
          0% { transform: translate(-50%, -50%) scale(0.72); opacity: 0; }
          18% { opacity: 0.36; }
          72% { opacity: 0.14; }
          100% { transform: translate(-50%, -50%) scale(1.34); opacity: 0; }
        }
        .roddo-fish-hub-entry {
          isolation:isolate;
          cursor:pointer;
        }
        .roddo-fish-hub-entry::after {
          content:"";
          position:absolute;
          left:50%;
          top:50%;
          width:62px;
          height:62px;
          transform:translate(-50%,-50%) scale(0.72);
          border-radius:999px;
          border:1.5px solid var(--hub-ripple, rgba(144,220,255,0.72));
          box-shadow:0 0 18px color-mix(in srgb, var(--hub-ripple, rgba(144,220,255,0.72)) 52%, transparent), inset 0 0 10px rgba(255,255,255,0.06);
          opacity:0;
          pointer-events:none;
        }
        .leaflet-interactive:hover .roddo-fish-hub-entry::after,
        .roddo-fish-hub-entry:hover::after {
          animation: roddo-fish-hub-hover-sonar 2.9s ease-out infinite;
        }
        @keyframes roddo-saved-spot-float {
          0%,100% { transform: translate(-50%,-50%) scale(1); }
          50% { transform: translate(-50%, calc(-50% - 4px)) scale(1.05); }
        }
        @keyframes roddo-saved-spot-cluster-float {
          0%,100% { transform: translateY(0) scale(1); }
          50% { transform: translateY(-4px) scale(1.03); }
        }
        .roddo-saved-spot-bounce {
          animation: roddo-saved-spot-idle 5.8s ease-in-out infinite;
          transform-origin: 50% 78%;
          will-change: transform, filter;
        }
        .roddo-saved-spot-cluster-bounce {
          animation: roddo-saved-spot-cluster-idle 5.1s ease-in-out infinite;
          transform-origin: 50% 78%;
          will-change: transform, filter;
        }
        @keyframes roddo-saved-spot-idle {
          0%, 100% { transform: translateY(0) scale(1); filter: brightness(1); }
          46% { transform: translateY(-1.5px) scale(1.018); filter: brightness(1.04); }
          54% { transform: translateY(-1px) scale(1.012); filter: brightness(1.025); }
        }
        @keyframes roddo-saved-spot-cluster-idle {
          0%, 100% { transform: translateY(0) scale(1); filter: brightness(1); }
          50% { transform: translateY(-1.5px) scale(1.02); filter: brightness(1.045); }
        }
        @keyframes roddo-fish-hub-idle {
          0%, 100% { transform: translateY(0) scale(1); filter: brightness(1); }
          50% { transform: translateY(-1.5px) scale(1.018); filter: brightness(1.045); }
        }
        .roddo-fish-hub-entry {
          animation: roddo-fish-hub-idle 5.4s ease-in-out infinite;
          transform-origin: 50% 78%;
          will-change: transform, filter;
        }
        @keyframes roddo-world-tournament-idle {
          0%, 100% { transform: translateY(0) scale(1); filter: brightness(1); }
          45% { transform: translateY(-2px) scale(1.025); filter: brightness(1.06); }
          55% { transform: translateY(-1px) scale(1.018); filter: brightness(1.035); }
        }
        @keyframes roddo-world-tournament-glow {
          0%, 100% { opacity: .34; transform: translate(-50%,-50%) scale(.90); }
          50% { opacity: .72; transform: translate(-50%,-50%) scale(1.12); }
        }
        .roddo-world-tournament-hub-entry {
          animation: roddo-world-tournament-idle 4.6s ease-in-out infinite;
          transform-origin: 50% 80%;
          will-change: transform, filter;
        }
        .roddo-world-tournament-hub-glow {
          animation: roddo-world-tournament-glow 4.6s ease-in-out infinite;
          will-change: opacity, transform;
        }

        @keyframes roddo-pop { 0%,100%{ transform: translateY(0) scale(1) } 50%{ transform: translateY(-4px) scale(1.03) } }
        .roddo-tourney-pop { animation: roddo-pop 1.4s ease-in-out infinite; }

        @media (prefers-reduced-motion: reduce) {
          .roddo-saved-spot-bounce,
          .roddo-saved-spot-cluster-bounce,
          .roddo-fish-hub-entry,
          .roddo-world-tournament-hub-entry,
          .roddo-world-tournament-hub-glow {
            animation: none !important;
          }
        }

        /* Quiet discovery cue for Crew + Tournaments.
           The button itself stays still; only the halo/icon breathe so taps/hover
           keep their normal tactile transform behavior. */
        @keyframes roddo-hud-attention-halo {
          0%, 100% {
            opacity: 0;
            transform: scale(0.88);
          }
          20% {
            opacity: 0.20;
            transform: scale(0.96);
          }
          34% {
            opacity: 0.62;
            transform: scale(1.08);
          }
          48% {
            opacity: 0.10;
            transform: scale(1.18);
          }
          62% {
            opacity: 0.48;
            transform: scale(1.05);
          }
          78% {
            opacity: 0;
            transform: scale(1.24);
          }
        }
        @keyframes roddo-hud-attention-icon {
          0%, 100% {
            transform: translateY(0) scale(1);
            filter: drop-shadow(0 0 0 var(--roddo-attention-glow));
          }
          18% {
            transform: translateY(-1px) scale(1.075);
            filter: drop-shadow(0 0 5px var(--roddo-attention-glow));
          }
          30% {
            transform: translateY(-2px) scale(1.135);
            filter: drop-shadow(0 0 10px var(--roddo-attention-glow));
          }
          42% {
            transform: translateY(0) scale(1.035);
            filter: drop-shadow(0 0 4px var(--roddo-attention-glow));
          }
          56% {
            transform: translateY(-1px) scale(1.105);
            filter: drop-shadow(0 0 8px var(--roddo-attention-glow));
          }
          70% {
            transform: translateY(0) scale(1.02);
            filter: drop-shadow(0 0 3px var(--roddo-attention-glow));
          }
          84% {
            transform: translateY(-0.5px) scale(1.055);
            filter: drop-shadow(0 0 5px var(--roddo-attention-glow));
          }
        }
        .roddo-hud-attention {
          isolation: isolate;
        }
        .roddo-hud-attention-halo {
          position: absolute;
          inset: -5px;
          z-index: -1;
          border-radius: 20px;
          border: 2px solid var(--roddo-attention-ring);
          box-shadow:
            0 0 14px var(--roddo-attention-glow),
            0 0 24px var(--roddo-attention-soft);
          opacity: 0;
          pointer-events: none;
          animation: roddo-hud-attention-halo 3.2s cubic-bezier(.2,.72,.28,1) infinite;
          animation-delay: var(--roddo-attention-delay, 0s);
          transform-origin: center;
          will-change: transform, opacity;
        }
        .roddo-hud-attention-icon {
          display: flex;
          align-items: center;
          justify-content: center;
          animation: roddo-hud-attention-icon 1.85s cubic-bezier(.22,.78,.32,1) infinite;
          animation-delay: var(--roddo-attention-delay, 0s);
          transform-origin: center;
          will-change: transform, filter;
        }
        @media (prefers-reduced-motion: reduce) {
          .roddo-hud-attention-halo,
          .roddo-hud-attention-icon {
            animation: none !important;
          }
        }

        div[class*="pointer-events-none absolute inset-0 z-50 flex justify-between"] {
          display: none !important;
          pointer-events: none !important;
        }

        .roddo-weather-pill button * { line-height: 1.05; }
        .roddo-map-theme-light #roddo-map-search::placeholder { color: #7b8392; opacity: 1; }
        .roddo-map-theme-dark #roddo-map-search::placeholder { color: rgba(255,247,232,0.55); opacity: 1; }
        .roddo-weather-pill button .text-xs { font-weight: 900; }
        html:not(.dark) .roddo-weather-pill button {
          background: rgba(255,255,255,0.98) !important;
          color: #474f5f !important;
          border: 2px solid rgba(82,74,64,0.12) !important;
          box-shadow: 0 10px 24px rgba(89,65,37,0.10), inset 0 1px 0 rgba(255,255,255,0.94) !important;
        }
        html:not(.dark) .roddo-weather-pill button svg,
        html:not(.dark) .roddo-weather-pill button span,
        html:not(.dark) .roddo-weather-pill button div,
        html:not(.dark) .roddo-weather-pill button p,
        html:not(.dark) .roddo-weather-pill button strong,
        html:not(.dark) .roddo-weather-pill button small { color: #474f5f !important; fill: #474f5f !important; }
        .leaflet-popup { z-index: 2400 !important; }
        .roddo-tournament-inline-tooltip {
          z-index: 2390 !important;
          border: 0 !important;
          border-radius: 18px !important;
          padding: 10px 12px !important;
          box-shadow: 0 14px 34px rgba(0,0,0,0.24) !important;
          white-space: normal !important;
        }
        .roddo-tournament-inline-tooltip::before { display: none !important; }
        html.dark .roddo-tournament-inline-tooltip {
          background: rgba(28,33,58,0.94) !important;
          color: #fff7e8 !important;
        }
        html:not(.dark) .roddo-tournament-inline-tooltip {
          background: rgba(255,251,244,0.97) !important;
          color: #2d3142 !important;
        }
        .roddo-tournament-inline-popup .leaflet-popup-content-wrapper {
          border-radius: 18px !important;
          box-shadow: 0 14px 34px rgba(0,0,0,0.24) !important;
        }
        .roddo-tournament-inline-popup .leaflet-popup-content {
          margin: 10px 12px !important;
        }
        .leaflet-popup-content-wrapper { border-radius: 18px; box-shadow: 0 14px 34px rgba(0,0,0,0.24); }
        .leaflet-popup-content { margin: 10px 12px; }
        html.dark .leaflet-popup-content-wrapper,
        html.dark .leaflet-popup-tip {
          background: rgba(28,33,58,0.94);
          color: #fff7e8;
        }
        html:not(.dark) .leaflet-popup-content-wrapper,
        html:not(.dark) .leaflet-popup-tip {
          background: rgba(255,251,244,0.97);
          color: #2d3142;
        }
      `}</style>

      {/* --- HUD OVERLAY --- */}
      {props.isActive ? (
      <div
        data-roddo-map-hud="true"
        className="absolute inset-0 pointer-events-none z-[1000]"
      >
        <BigBlueMapHud
          isDarkMode={isDarkMode}
          headerTopCss={headerTopCss}
          weatherData={weatherData}
          isLoadingWeather={isLoadingWeather}
          tempUnit={tempUnit}
          locationName={props.locationName ?? null}
          coords={props.coords ?? null}
          isWeatherOpen={isWeatherOpen}
          onWeatherExpandedChange={(open) => {
            setIsWeatherOpen(open);
            if (open) {
              setQuickWindow(null);
              setIsTournamentMapFilterOpen(false);
            }
          }}
          isMapSearchOpen={isMapSearchOpen}
          onOpenMapSearch={() => {
            dismissMapTextInputs();
            setIsWeatherOpen(false);
            setIsTournamentMapFilterOpen(false);
            setIsSavedSpotsOpen(false);
            setIsMapSearchOpen(true);
          }}
          mapSearchInputRef={mapSearchInputRef}
          searchQuery={searchQuery}
          onSearchQueryChange={setSearchQuery}
          isMapSearchFocused={isMapSearchFocused}
          onMapSearchFocus={() => setIsMapSearchFocused(true)}
          onMapSearchBlur={() =>
            window.setTimeout(() => setIsMapSearchFocused(false), 120)
          }
          onSearchEscape={() => closeMapSearch()}
          onSearchSubmit={handleSearch}
          onCloseMapSearch={(clear) =>
            closeMapSearch(clear ? { clear: true } : undefined)
          }
          onOpenDaily={() => openBigBlueQuickWindow("daily")}
          displayedSearchLureBalance={displayedSearchLureBalance}
          compactSearchLureRefillLabel={compactSearchLureRefillLabel}
          onTackleBoxClick={props.onTackleBoxClick}
          onRecenter={handleRecenter}
          mapLayerVisibility={mapLayerVisibility}
          onToggleMapLayerVisibility={toggleMapLayerVisibility}
          onBeforeOpenLayerControl={() => {
            closeMapSearch();
            setIsWeatherOpen(false);
            setIsTournamentMapFilterOpen(false);
            setIsWorldTournamentMapOpen(false);
            setIsSavedSpotsOpen(false);
            setQuickWindow(null);
          }}
          onOpenCrew={() => openBigBlueQuickWindow("crew")}
          showCrewUnread={Boolean(
            props.isActive && crewInboxBootReady && quickCrewUnreadCount > 0
          )}
          crewUnreadCount={quickCrewUnreadCount}
          showGlobe={Boolean(
            currentMapZoom <= MAP_MIN_ZOOM &&
            typeof props.onOpenGlobe === "function"
          )}
          onOpenGlobe={() => {
            const map = mapInstanceRef.current;
            const center = map?.getCenter?.();
            const lat = Number(
              center?.lat ??
                latestUserLatLngRef.current?.lat ??
                props.coords?.lat,
            );
            const lng = Number(
              center?.lng ??
                latestUserLatLngRef.current?.lng ??
                props.coords?.lon,
            );
            const zoom = Number(map?.getZoom?.() ?? currentMapZoom);

            if (Number.isFinite(lat) && Number.isFinite(lng)) {
              onOpenGlobeRef.current?.({ lat, lng, zoom });
            } else {
              onOpenGlobeRef.current?.();
            }
          }}
          searchError={searchError}
          searchErrorTopCss={searchErrorBelowRedoTopCss}
          searchLureRefillLabel={searchLureRefillLabel}
        />

        <BigBlueWaterTools
          isDarkMode={isDarkMode}
          isActive={!!props.isActive}
          bottomHudCss={bottomHudCss}
          actionButtonStyle={actionButtonStyle}
          suppressWaterIntelLegend={Boolean(
            isFishWidgetOpen ||
            isFishFullDetailOpen ||
            isSavedSpotsOpen ||
            quickWindow ||
            isWeatherOpen ||
            isTournamentMapFilterOpen ||
            isOfflinePanelOpen ||
            isMapSearchOpen ||
            isMapActionCardOpen
          )}
          getMap={() => mapInstanceRef.current}
          weatherData={weatherData}
          savedSpotsAnchorRef={savedSpotsAnchorRef}
          isSavedSpotsOpen={isSavedSpotsOpen}
          onWaterIntelLogCatch={(target) => {
            setIsSavedSpotsOpen(false);
            setPendingSavedSpotLatLng(null);
            setSelectedSavedSpotId(null);
            setIsSavedSpotFilterMenuOpen(false);
            setIsSelectedSavedSpotMenuOpen(false);
            setIsFishWidgetOpen(false);
            setSelectedFishSpot(null);
            setIsFishFullDetailOpen(false);
            playPreLogCatchPulse(target, 280);
            props.onLogCatchClick?.({
              coords: { lat: target.lat, lon: target.lng },
              prefilledData: { source: "water_intel", coordinateLocked: true },
            });
          }}
          onWaterIntelSearchFishIQ={(target) => {
            setIsSavedSpotsOpen(false);
            setPendingSavedSpotLatLng(null);
            setSelectedSavedSpotId(null);
            setIsSavedSpotFilterMenuOpen(false);
            setIsSelectedSavedSpotMenuOpen(false);
            void openFishAreaForLatLng(target, "water-intel selected water", true);
          }}
          onOpenFishIQ={() => openBigBlueQuickWindow("fishiq")}
          onToggleSavedSpots={() => {
            dismissMapTextInputs();
            setPendingSavedSpotLatLng(null);
            setEditingSavedSpotId(null);
            savedSpotDraftNameLiveRef.current = "";
            savedSpotDraftNoteLiveRef.current = "";
            setSavedSpotDraftName("");
            setSavedSpotDraftNote("");
            setSavedSpotDraftKind("favorite");
            setSelectedSavedSpotId(null);
            setIsSelectedSavedSpotMenuOpen(false);
            setIsSavedSpotFilterMenuOpen(false);
            setQuickWindow(null);
            setIsTournamentMapFilterOpen(false);
            setIsSavedSpotsOpen((prev) => !prev);
          }}
          mapActionAnchor={mapActionAnchor}
          isMapActionCardOpen={isMapActionCardOpen}
          mapActionWaterRead={mapActionWaterRead}
          mapActionWaterLoading={mapActionWaterLoading}
          mapActionHasFishIQ={mapActionHasFishIQ}
          mapActionFishIQCount={mapActionFishIQCount}
          isSearchingSavedArea={isSearchingSavedArea}
          onCloseMapAction={() => {
            setIsMapActionCardOpen(false);
            setMapActionAnchor(null);
          }}
          onMapActionFishIQ={(target, hasExistingRead) => {
            setIsMapActionCardOpen(false);
            if (hasExistingRead) {
              setFishWidgetMode("area");
              setFishWidgetAreaSource("selected");
              setSelectedFishSpot(null);
              setIsFishFullDetailOpen(false);
              setIsFishWidgetOpen(true);
              return;
            }
            void openFishAreaForLatLng(target, "double-click selected water", true);
          }}
          onMapActionLog={(target) => {
            setIsMapActionCardOpen(false);
            setIsSavedSpotsOpen(false);
            setPendingSavedSpotLatLng(null);
            setSelectedSavedSpotId(null);
            playPreLogCatchPulse(target, 280);
            props.onLogCatchClick?.({
              coords: { lat: target.lat, lon: target.lng },
              prefilledData: { source: "map_poi", coordinateLocked: true },
            });
          }}
          onMapActionSave={(target) => {
            setIsMapActionCardOpen(false);
            openSavedSpotComposer(target, "favorite");
          }}
        />

        {isSavedSpotsOpen ? (
          <SavedSpotsMapOverlay
            savedSpotsDesktopAnchor={savedSpotsDesktopAnchor}
            headerTopCss={headerTopCss}
            isSavedSpotDetailMode={isSavedSpotDetailMode}
            savedSpotsPanelRef={savedSpotsPanelRef}
            isDarkMode={isDarkMode}
            pendingSavedSpotLatLng={pendingSavedSpotLatLng}
            selectedSavedSpot={selectedSavedSpot}
            savedSpots={savedSpots}
            savedSpotFilter={savedSpotFilter}
            savedSpotFilterOptions={savedSpotFilterOptions}
            savedSpotCounts={savedSpotCounts}
            isSavedSpotFilterMenuOpen={isSavedSpotFilterMenuOpen}
            showSavedSpotsOnMap={showSavedSpotsOnMap}
            filteredSavedSpots={filteredSavedSpots}
            visibleSavedSpotsList={visibleSavedSpotsList}
            savedSpotDraftKind={savedSpotDraftKind}
            savedSpotDraftName={savedSpotDraftName}
            savedSpotDraftNote={savedSpotDraftNote}
            savedSpotDraftNameLiveRef={savedSpotDraftNameLiveRef}
            savedSpotDraftNoteLiveRef={savedSpotDraftNoteLiveRef}
            savedSpotNameInputRef={savedSpotNameInputRef}
            editingSavedSpotId={editingSavedSpotId}
            savingSavedSpotId={savingSavedSpotId}
            deletingSavedSpotId={deletingSavedSpotId}
            isSelectedSavedSpotExpanded={isSelectedSavedSpotExpanded}
            isSelectedSavedSpotNoteExpanded={isSelectedSavedSpotNoteExpanded}
            isSelectedSavedSpotMenuOpen={isSelectedSavedSpotMenuOpen}
            SAVED_SPOT_KIND_META={SAVED_SPOT_KIND_META}
            getSavedSpotDefaultName={getSavedSpotDefaultName}
            formatSavedSpotTimestamp={formatSavedSpotTimestamp}
            centerMapOnSavedSpot={centerMapOnSavedSpot}
            openSavedSpotDetail={openSavedSpotDetail}
            handleShareSavedSpot={handleShareSavedSpot}
            savePendingSavedSpot={savePendingSavedSpot}
            setEditingSavedSpotId={setEditingSavedSpotId}
            setIsSavedSpotFilterMenuOpen={setIsSavedSpotFilterMenuOpen}
            setIsSavedSpotsOpen={setIsSavedSpotsOpen}
            setIsSelectedSavedSpotExpanded={setIsSelectedSavedSpotExpanded}
            setIsSelectedSavedSpotMenuOpen={setIsSelectedSavedSpotMenuOpen}
            setIsSelectedSavedSpotNoteExpanded={setIsSelectedSavedSpotNoteExpanded}
            setPendingSavedSpotLatLng={setPendingSavedSpotLatLng}
            setSavedSpotDeleteConfirm={setSavedSpotDeleteConfirm}
            setSavedSpotDraftKind={setSavedSpotDraftKind}
            setSavedSpotDraftName={setSavedSpotDraftName}
            setSavedSpotDraftNote={setSavedSpotDraftNote}
            setSavedSpotFilter={setSavedSpotFilter}
            setSelectedSavedSpotId={setSelectedSavedSpotId}
            setShowSavedSpotsOnMap={setShowSavedSpotsOnMap}
          />
        ) : null}

        {isFishWidgetOpen && (visibleFishSpots.length || isFishIQThinkingVisible || fishIQThinkingError) ? (
          <div
            className="absolute inset-0 z-[6200] overflow-hidden pointer-events-auto"
            onClick={() => {
              setIsFishWidgetOpen(false);
              setSelectedFishSpot(null);
              setFishIQThinkingError(null);
              setFishIQRetryAnchor(null);
              setFishIQRetryMode(null);
            }}
          >
            <div
              className="absolute inset-x-0"
              style={{
                top: `calc(${headerTopCss} + 68px)`,
                bottom:
                  "calc(var(--roddo-map-bottom-clearance, var(--roddo-nav-h, 84px)) + env(safe-area-inset-bottom) + 10px)",
              }}
            >
              <div className="mx-auto flex h-full w-[min(94vw,440px)] min-h-0 flex-col items-end justify-end px-2 pb-1 sm:px-3">
                <div
                  className="pointer-events-auto relative z-[1000] flex min-h-0 w-full flex-col overflow-hidden rounded-[22px] border"
                  onClick={(e) => e.stopPropagation()}
                  style={{
                    background: isDarkMode
                      ? "#0A1120"
                      : "linear-gradient(180deg, rgba(250,244,238,0.96), rgba(244,236,228,0.96))",
                    borderColor: isDarkMode
                      ? "rgba(132,151,190,0.16)"
                      : "rgba(116,86,54,0.14)",
                    boxShadow: isDarkMode
                      ? "0 8px 24px rgba(0,0,0,0.26)"
                      : "0 18px 42px rgba(89,65,37,0.16), inset 0 1px 0 rgba(255,255,255,0.62)",
                    backdropFilter: "blur(10px)",
                    WebkitBackdropFilter: "blur(10px)",
                    maxHeight: expandedFish
                      ? "min(calc(var(--roddo-visual-vh, 100dvh) - var(--roddo-header-h, 88px) - var(--roddo-map-bottom-clearance, var(--roddo-nav-h, 84px)) - 104px), 680px)"
                      : "min(calc(var(--roddo-visual-vh, 100dvh) - var(--roddo-header-h, 88px) - var(--roddo-map-bottom-clearance, var(--roddo-nav-h, 84px)) - 104px), 600px)",
                  }}
                >
                  <div className="relative z-[2] flex shrink-0 justify-center pt-2.5">
                    <div
                      className="h-1 w-10 rounded-full sm:w-12"
                      style={{
                        background: isDarkMode
                          ? "rgba(255,255,255,0.20)"
                          : "rgba(80,62,45,0.16)",
                      }}
                    />
                  </div>

                  <div
                    className="pointer-events-none absolute inset-0"
                    style={{
                      background: isDarkMode
                        ? "transparent"
                        : "radial-gradient(120% 100% at 20% 0%, rgba(255,255,255,0.52), transparent 58%)",
                    }}
                  />

                  <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden px-3 pb-3 pt-2.5 sm:px-4 sm:pb-4 sm:pt-3">
                    <div
                      className="flex shrink-0 items-center gap-2.5 rounded-[18px] border px-3 py-2 sm:px-3.5 sm:py-2.5"
                      style={{
                        background: isDarkMode
                          ? "#101827"
                          : "linear-gradient(135deg, rgba(255,255,255,0.82), rgba(239,247,255,0.62))",
                        borderColor: isDarkMode
                          ? "rgba(132,151,190,0.14)"
                          : "rgba(104,84,64,0.09)",
                        boxShadow: isDarkMode
                          ? "inset 0 1px 0 rgba(255,255,255,0.05)"
                          : "inset 0 1px 0 rgba(255,255,255,0.86), 0 5px 14px rgba(89,65,37,0.045)",
                      }}
                    >
                      <div
                        className="flex h-11 w-11 shrink-0 items-center justify-center rounded-[15px] sm:h-12 sm:w-12 sm:rounded-2xl"
                        style={{
                          background: isDarkMode
                            ? "rgba(103,216,232,0.10)"
                            : "linear-gradient(180deg, rgba(124,199,255,0.26), rgba(95,101,255,0.10))",
                          border: isDarkMode
                            ? "1px solid rgba(255,255,255,0.10)"
                            : "1px solid rgba(255,255,255,0.72)",
                        }}
                      >
                        <img
                          src={roddoFish}
                          alt={fishWidgetIconAlt}
                          className="h-7 w-7 object-contain drop-shadow-[0_6px_12px_rgba(15,23,42,0.18)] sm:h-8 sm:w-8"
                          draggable={false}
                        />
                      </div>

                      <div className="min-w-0 flex-1">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <div className="flex min-w-0 items-center gap-2">
                              <div
                                className="truncate text-[16px] font-black leading-tight sm:text-[17px]"
                                style={{
                                  color: isDarkMode ? "#F8FAFF" : "#2f3d45",
                                }}
                              >
                                {fishWidgetHeaderTitle}
                              </div>

                              {!expandedFish && fishWidgetMode !== "history" && !isFishIQThinkingVisible && !fishIQThinkingError ? (
                                <span
                                  className="shrink-0 rounded-full px-2 py-1 text-[9px] font-black uppercase tracking-[0.08em]"
                                  style={{
                                    background: isDarkMode
                                      ? "rgba(158,235,255,0.11)"
                                      : "rgba(61,154,207,0.10)",
                                    color: isDarkMode
                                      ? "rgba(221,246,255,0.88)"
                                      : "#2F7EAF",
                                    border: isDarkMode
                                      ? "1px solid rgba(158,235,255,0.15)"
                                      : "1px solid rgba(61,154,207,0.13)",
                                  }}
                                >
                                  {visibleFishSpots.length} fish
                                </span>
                              ) : null}
                            </div>

                            {fishWidgetHeaderSubtitle ? (
                              <div
                                className="mt-1 truncate text-[10px] font-black uppercase tracking-[0.14em]"
                                style={{
                                  color: isDarkMode
                                    ? "rgba(216,225,255,0.70)"
                                    : "rgba(107,98,88,0.76)",
                                }}
                              >
                                {fishWidgetHeaderSubtitle}
                              </div>
                            ) : null}
                          </div>

                          <button
                            type="button"
                            onClick={() => {
                              setIsFishWidgetOpen(false);
                              setSelectedFishSpot(null);
                              setFishIQThinkingError(null);
                              setFishIQRetryAnchor(null);
                              setFishIQRetryMode(null);
                              // Closing the drawer does not erase the map-locked FishIQ
                              // area bubble. The bubble remains as the user's last-read context
                              // until another search/read replaces it.
                            }}
                            className="grid h-8 w-8 shrink-0 place-items-center rounded-full transition-transform active:scale-95"
                            style={{
                              background: isDarkMode
                                ? "rgba(255,255,255,0.08)"
                                : "rgba(255,255,255,0.52)",
                              color: isDarkMode ? "#F8FAFF" : "#2b2b2b",
                              border: isDarkMode
                                ? "1px solid rgba(255,255,255,0.10)"
                                : "1px solid rgba(60,40,20,0.10)",
                            }}
                            aria-label="Close fish info"
                          >
                            <XIcon className="h-4 w-4" />
                          </button>
                        </div>


                      </div>
                    </div>

                    {!expandedFish ? (
                      isFishIQThinkingVisible || fishIQThinkingError ? (
                        <FishIQThinkingState
                          isDarkMode={isDarkMode}
                          error={fishIQThinkingError}
                          onRetry={
                            fishIQRetryMode === "selected" && fishIQRetryAnchor
                              ? () => {
                                  void openFishAreaForLatLng(
                                    fishIQRetryAnchor,
                                    "Retry water read",
                                    true,
                                  );
                                }
                              : fishIQRetryMode === "viewport"
                                ? () => {
                                    void handleRedoSearch({ spendSearchLure: true });
                                  }
                                : null
                          }
                        />
                      ) : (
                      <div
                      className="mt-2.5 min-h-0 flex-1 overflow-y-auto overscroll-contain pr-0.5 [-webkit-overflow-scrolling:touch]"
                      style={{
                        scrollbarWidth: "thin",
                        scrollbarColor: isDarkMode
                          ? "rgba(226,232,255,0.28) transparent"
                          : "rgba(91,67,49,0.24) transparent",
                      }}
                    >
                        <div className="grid gap-1.5 pb-1">
                          {selectedFishIQAreaRead ? (
                            <div
                              className="rounded-[20px] border px-3.5 py-3"
                              style={{
                                background: isDarkMode
                                  ? "#0F1828"
                                  : "linear-gradient(180deg, rgba(231,249,249,0.88), rgba(255,250,238,0.92))",
                                borderColor: isDarkMode
                                  ? "rgba(102,217,232,0.16)"
                                  : "rgba(72,147,161,0.18)",
                                boxShadow: isDarkMode
                                  ? "inset 0 1px 0 rgba(255,255,255,0.08)"
                                  : "inset 0 1px 0 rgba(255,255,255,0.74)",
                              }}
                            >
                              <div className="flex items-center justify-between gap-3">
                                <div
                                  className="text-[10px] font-black uppercase tracking-[0.18em]"
                                  style={{
                                    color: isDarkMode
                                      ? "rgba(216,245,255,0.72)"
                                      : "rgba(47,100,112,0.78)",
                                  }}
                                >
                                  Water Read
                                </div>
                                <div
                                  className="rounded-full px-2 py-1 text-[9px] font-black uppercase tracking-[0.12em]"
                                  style={{
                                    background: isDarkMode
                                      ? "rgba(255,255,255,0.08)"
                                      : "rgba(255,255,255,0.64)",
                                    color: isDarkMode
                                      ? "rgba(234,248,255,0.78)"
                                      : "rgba(60,88,96,0.78)",
                                    border: isDarkMode
                                      ? "1px solid rgba(255,255,255,0.08)"
                                      : "1px solid rgba(47,100,112,0.12)",
                                  }}
                                >
                                  Movement read
                                </div>
                              </div>

                              <div
                                className="mt-1.5 text-[12px] font-extrabold leading-4"
                                style={{
                                  color: isDarkMode ? "#F2FBFF" : "#2f4b52",
                                }}
                              >
                                {fishIQWaterReadLabel}
                              </div>

                              {selectedFishIQAreaRead.movementTags?.length ? (
                                <div className="mt-2 flex flex-wrap gap-1.5">
                                  {selectedFishIQAreaRead.movementTags
                                    .slice(0, 3)
                                    .map((tag) => (
                                      <span
                                        key={`movement-${tag}`}
                                        className="rounded-full px-2.5 py-1 text-[10px] font-bold leading-none"
                                        style={{
                                          background: isDarkMode
                                            ? "rgba(158,235,255,0.12)"
                                            : "rgba(96,176,187,0.12)",
                                          color: isDarkMode
                                            ? "rgba(231,249,255,0.86)"
                                            : "rgba(42,91,101,0.86)",
                                        }}
                                      >
                                        {String(tag)}
                                      </span>
                                    ))}
                                </div>
                              ) : null}
                            </div>
                          ) : null}

                          {visibleFishSpots.length ? (
                            <>
                              {visibleFishSpots.map((item: any, index: number) => {
                                const isHistoryRow = false;
                                const spot = isHistoryRow
                                  ? item.matchedSpot
                                  : item;
                                const speciesData =
                                  (spot as any)?.fishIQSpeciesData || null;
                                const speciesLabel = isHistoryRow
                                  ? String(
                                      item.species ||
                                        item.matchedSpot?.species ||
                                        item.matchedSpot?.name ||
                                        "Catch",
                                    ).trim()
                                  : String(
                                      item.species ||
                                        item.name ||
                                        speciesData?.commonName ||
                                        "Area species",
                                    ).trim();
                                const localMemoryMatch = findCatchMemoryMatch(nearbyCatchSpeciesRows, speciesLabel, item?.id);
                                const lifetimeMemoryMatch = localMemoryMatch
                                  ? null
                                  : findCatchMemoryMatch(lifetimeCatchSpeciesRows, speciesLabel, item?.id);
                                const memoryMatch = localMemoryMatch || lifetimeMemoryMatch;
                                const waterLabel = isHistoryRow
                                  ? String(
                                      item.waterName ||
                                        item.matchedSpot?.waterName ||
                                        "Area memory",
                                    ).trim()
                                  : String(
                                      item.waterName ||
                                        speciesData?.waterName ||
                                        "Area signal",
                                    ).trim();
                                const locationRank =
                                  speciesData?.__fishiq?.locationRank || null;
                                const primaryBadgeLabel = isHistoryRow
                                  ? String(
                                      item.badgeLabel ||
                                        item.matchedSpot?.timeOfDay ||
                                        "Memory",
                                    )
                                      .trim()
                                      .toUpperCase()
                                  : String(
                                      locationRank?.shortLabel ||
                                        getAreaActivityBadgeLabel(index),
                                    )
                                      .trim()
                                      .toUpperCase();
                                const catchCountLabel = localMemoryMatch
                                  ? `✓ · ${localMemoryMatch.count}`
                                  : lifetimeMemoryMatch
                                    ? "✓"
                                    : getFishRulesBadgeLabel(
                                      speciesLabel,
                                      speciesData,
                                    )?.toUpperCase() || null;
                                const canOpenDetail =
                                  isHistoryRow || !!speciesData;
                                const whyHereParts = [
                                  waterLabel,
                                  speciesData?.habitats?.[0],
                                  speciesData?.structureTags?.[0],
                                  speciesData?.behaviorTags?.[0],
                                ]
                                  .filter(Boolean)
                                  .map((value: any) => String(value).trim())
                                  .filter(Boolean)
                                  .slice(0, 3);
                                const summaryLabel = isHistoryRow
                                  ? String(
                                      item.summaryLabel ||
                                        `Logged nearby around ${waterLabel}.`,
                                    ).trim()
                                  : String(
                                      speciesData?.__fishiq?.localReadBody ||
                                        (locationRank?.reasons?.length
                                          ? `${locationRank.label}: ${locationRank.reasons.slice(0, 3).join(" • ")}.`
                                          : `Area profile: ${whyHereParts.length ? whyHereParts.join(" • ") : "matched to this water profile"}.`),
                                    ).trim();
                                const fallbackHistorySpecies =
                                  isHistoryRow && !speciesData
                                    ? {
                                        id: `memory-${item.id}`,
                                        commonName: speciesLabel,
                                        name: speciesLabel,
                                        summary:
                                          item.latestCatch?.note ||
                                          summaryLabel,
                                        description:
                                          item.latestCatch?.note ||
                                          summaryLabel,
                                        habitats: ["Catch memory"],
                                        waterTypes: [waterLabel].filter(
                                          Boolean,
                                        ),
                                        bestTimesOfDay: [
                                          item.badgeLabel || "recent memory",
                                        ].filter(Boolean),
                                        baits: [
                                          item.latestCatch?.weight
                                            ? `Logged weight: ${item.latestCatch.weight}`
                                            : "Logged catch",
                                        ].filter(Boolean),
                                        tactics: {
                                          bestBaits: [
                                            item.latestCatch?.weight
                                              ? `Logged weight: ${item.latestCatch.weight}`
                                              : "Logged catch",
                                          ].filter(Boolean),
                                          bestTimesOfDay: [
                                            item.badgeLabel || "recent memory",
                                          ].filter(Boolean),
                                          bestTechniques: [
                                            "Review your saved catch notes",
                                          ],
                                        },
                                        waterContext: {
                                          summary: `Memory species around ${waterLabel}.`,
                                          habitats: [waterLabel].filter(
                                            Boolean,
                                          ),
                                        },
                                        __fishiq: {
                                          localReadBody: summaryLabel,
                                          source: "catch_memory",
                                        },
                                      }
                                    : null;
                                const detailSpot = isHistoryRow
                                  ? {
                                      ...(spot || {}),
                                      id: spot?.id || `memory-${item.id}`,
                                      species: speciesLabel,
                                      name: speciesLabel,
                                      waterName: waterLabel,
                                      timeOfDay:
                                        item.badgeLabel ||
                                        spot?.timeOfDay ||
                                        "Memory",
                                      position:
                                        spot?.position ||
                                        (item.latestCatch
                                          ? {
                                              lat: item.latestCatch.lat,
                                              lng: item.latestCatch.lng,
                                            }
                                          : undefined),
                                      fishIQSpeciesData:
                                        speciesData || fallbackHistorySpecies,
                                    }
                                  : {
                                      ...(spot || {}),
                                      __roddoCatches: memoryMatch?.catches || [],
                                    };

                                return (
                                  <FishSpeciesRow
                                    key={`area-${item.id}`}
                                    isDarkMode={isDarkMode}
                                    speciesLabel={speciesLabel}
                                    speciesData={speciesData || fallbackHistorySpecies}
                                    waterLabel={waterLabel}
                                    primaryBadgeLabel={primaryBadgeLabel}
                                    catchCountLabel={catchCountLabel}
                                    summaryLabel={summaryLabel}
                                    canOpenDetail={canOpenDetail}
                                    icon={getFishAreaEmoji(
                                      speciesLabel,
                                      waterLabel,
                                    )}
                                    onClick={() => {
                                      if (!canOpenDetail) return;
                                      setSelectedFishSpot(detailSpot);
                                      setIsFishFullDetailOpen(false);
                                    }}
                                  />
                                );
                              })}

                              {fishWidgetMode !== "history" &&
                              ((fishWidgetAreaSource === "selected" &&
                                !isFishAreaExpanded &&
                                fishAreaHasMoreSpecies) ||
                                (fishWidgetAreaSource !== "selected" &&
                                  fishAreaHasMoreSpecies)) ? (
                                <button
                                  type="button"
                                  onClick={() => {
                                    if (fishWidgetAreaSource === "selected") {
                                      setIsFishAreaExpanded(true);
                                      return;
                                    }

                                    const liveMapCenter =
                                      mapInstanceRef.current?.getCenter?.();
                                    const anchorForSearch =
                                      fishWidgetAnchor ??
                                      (liveMapCenter
                                        ? {
                                            lat: liveMapCenter.lat,
                                            lng: liveMapCenter.lng,
                                          }
                                        : latestUserLatLngRef.current);

                                    if (!anchorForSearch) return;

                                    void openFishAreaForLatLng(
                                      anchorForSearch,
                                      "fish area search more",
                                      true,
                                    );
                                  }}
                                  className="mt-1 w-full rounded-[16px] px-4 py-2.5 text-[11px] font-black uppercase tracking-[0.12em] transition-transform active:scale-[0.985]"
                                  style={{
                                    background: isDarkMode
                                      ? "#102234"
                                      : "linear-gradient(180deg, rgba(210,242,255,0.88), rgba(231,244,255,0.76))",
                                    color: isDarkMode ? "#DDF6FF" : "#22638A",
                                    border: isDarkMode
                                      ? "1px solid rgba(158,235,255,0.22)"
                                      : "1px solid rgba(86,174,220,0.28)",
                                    boxShadow: isDarkMode
                                      ? "0 4px 12px rgba(0,0,0,0.14)"
                                      : "0 10px 22px rgba(76,142,180,0.14)",
                                  }}
                                >
                                  {fishWidgetAreaSource === "selected"
                                    ? `Show more (${Math.max(0, Math.min(FISH_AREA_EXPANDED_LIST_LIMIT, fishAreaTotalCount) - visibleFishSpots.length)} more)`
                                    : "🔎 Search more"}
                                </button>
                              ) : null}

                              {fishWidgetMode !== "history" &&
                              fishWidgetAreaSource === "selected" &&
                              isFishAreaExpanded &&
                              fishAreaTotalCount >
                                FISH_AREA_DEFAULT_LIST_LIMIT ? (
                                <div
                                  className="px-2 pb-1 pt-0.5 text-center text-[11px] font-bold leading-4"
                                  style={{
                                    color: isDarkMode
                                      ? "rgba(232,236,255,0.66)"
                                      : "rgba(95,83,70,0.68)",
                                  }}
                                >
                                  Showing broader nearby matches. Top species
                                  stay ranked first.
                                </div>
                              ) : null}
                            </>
                          ) : (
                            <div
                              className="rounded-[20px] border px-4 py-4 text-left"
                              style={{
                                background: isDarkMode
                                  ? "#0F1828"
                                  : "linear-gradient(180deg, rgba(255,255,255,0.88), rgba(245,238,231,0.96))",
                                borderColor: isDarkMode
                                  ? "rgba(255,255,255,0.10)"
                                  : "rgba(91,67,49,0.10)",
                                color: isDarkMode ? "#F8FAFF" : "#2f3d45",
                              }}
                            >
                              <div className="text-[15px] font-black">
                                No area species yet
                              </div>
                              <div
                                className="mt-2 text-[13px] leading-5"
                                style={{
                                  color: isDarkMode
                                    ? "rgba(232,236,255,0.72)"
                                    : "#6f6559",
                                }}
                              >
                                Search this area again or move the map to refresh this water read for this water.
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                      )
                    ) : (
                      <div className="mt-3 min-h-0 flex-1 overflow-hidden rounded-[20px]">
                        <MapFishQuickCard
                          species={expandedFishSpecies as any}
                          spot={expandedFish as any}
                          isDarkMode={isDarkMode}
                          isActive={!!props.isActive}
                          onBack={() => {
                            setSelectedFishSpot(null);
                            setIsFishFullDetailOpen(false);
                          }}
                          onOpenFull={() => setIsFishFullDetailOpen(true)}
                          onOpenCatchHistory={
                            typeof props.onOpenCatchHistory === "function"
                              ? (speciesName: string) => {
                                  props.onOpenCatchHistory?.({
                                    source: "all",
                                    species: speciesName,
                                  });
                                }
                              : undefined
                          }
                          onLogCatch={
                            typeof props.onLogCatchClick === "function"
                              ? () => {
                                  // A FishIQ quick-log belongs to the searched/read
                                  // location, not the decorative per-species hotspot
                                  // offset. Prefer the exact active FishIQ anchor so the
                                  // Log Catch map starts where the angler searched.
                                  const nextCoords = fishWidgetAnchor
                                    ? {
                                        lat: Number(fishWidgetAnchor.lat),
                                        lon: Number(fishWidgetAnchor.lng),
                                      }
                                    : getHotspotLogCatchCoords(
                                        expandedFish,
                                        props.coords ?? null,
                                      );
                                  const speciesName = String(
                                    (expandedFishSpecies as any)?.commonName ||
                                      (expandedFishSpecies as any)?.name ||
                                      (expandedFish as any)?.species ||
                                      (expandedFish as any)?.commonName ||
                                      (expandedFish as any)?.name ||
                                      "",
                                  ).trim();

                                  const bigBlueReturnSession = buildFishIQReturnSession();
                                  setIsFishWidgetOpen(false);
                                  // Logging from FishIQ should never carry an unrelated
                                  // Saved Spots drawer/composer into the return state.
                                  setIsSavedSpotsOpen(false);
                                  setPendingSavedSpotLatLng(null);
                                  setSelectedSavedSpotId(null);
                                  setIsSavedSpotFilterMenuOpen(false);
                                  setIsSelectedSavedSpotMenuOpen(false);

                                  props.onLogCatchClick?.({
                                    coords: nextCoords,
                                    bigBlueReturnSession,
                                    ...(speciesName
                                      ? {
                                          species: speciesName,
                                          prefilledData: {
                                            species: speciesName,
                                            source: "fishiq",
                                            quickLog: true,
                                            coordinateLocked: true,
                                            returnTo: "big_blue",
                                          },
                                        }
                                      : {}),
                                  });
                                }
                              : undefined
                          }
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : null}
        {props.isActive && isFishFullDetailOpen && expandedFish
          ? ReactDOM.createPortal(
              <div
                data-roddo-map-portal="true"
                className="fixed inset-0 z-[7600] overflow-hidden bg-[rgba(24,18,18,0.58)] p-0 backdrop-blur-md"
                onClick={() => setIsFishFullDetailOpen(false)}
              >
                <div className="flex h-full w-full items-stretch justify-center">
                  <div
                    className="flex h-full w-full max-w-[960px] min-h-0 flex-col px-3 pb-[max(12px,env(safe-area-inset-bottom))] pt-[max(12px,env(safe-area-inset-top))] sm:px-5 sm:pt-5"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="mb-3 shrink-0 px-1">
                      <div className="text-[10px] font-black uppercase tracking-[0.24em] text-white/70">
                        Water Read field guide
                      </div>
                      <div className="mt-1 text-sm font-semibold text-white/90">
                        Local read → species detail
                      </div>
                    </div>

                    <div className="min-h-0 flex-1 overflow-hidden rounded-[34px]">
                      <FishIQDetailView
                        isOpen={!!expandedFish}
                        kind="species"
                        id={expandedFishSpeciesId}
                        onClose={() => setIsFishFullDetailOpen(false)}
                        variant="modal"
                        maxBodyHeightClassName="max-h-full"
                        catchLog={props.catchLog || []}
                        species={expandedFishSpecies as any}
                        viewerUserId={profile?.uid || null}
                        isDarkMode={isDarkMode}
                        mapContext={{
                          waterName: (expandedFish as any)?.waterName || (expandedFish as any)?.fishIQAreaRead?.waterName || props.locationName || null,
                          areaRead: expandedFishAreaReadForSpecies || null,
                          anchor: fishWidgetAnchor
                            ? { lat: Number(fishWidgetAnchor.lat), lon: Number(fishWidgetAnchor.lng) }
                            : getHotspotLogCatchCoords(expandedFish, props.coords ?? null),
                        }}
                        onSelectSpecies={(speciesName, payload) => {
                          // Keep the catch tied to the FishIQ read/search anchor.
                          // Species markers can be visually offset from that anchor and
                          // must not become the logged catch position.
                          const nextCoords = fishWidgetAnchor
                            ? {
                                lat: Number(fishWidgetAnchor.lat),
                                lon: Number(fishWidgetAnchor.lng),
                              }
                            : getHotspotLogCatchCoords(
                                expandedFish,
                                props.coords ?? null,
                              );
                          const bigBlueReturnSession = buildFishIQReturnSession();
                          setIsFishFullDetailOpen(false);
                          setIsFishWidgetOpen(false);
                          setIsSavedSpotsOpen(false);
                          setPendingSavedSpotLatLng(null);
                          setSelectedSavedSpotId(null);
                          setIsSavedSpotFilterMenuOpen(false);
                          setIsSelectedSavedSpotMenuOpen(false);
                          props.onLogCatchClick?.({
                            ...(payload || {}),
                            bigBlueReturnSession,
                            species: speciesName,
                            coords: nextCoords,
                            prefilledData: {
                              ...((payload as any)?.prefilledData || {}),
                              species: speciesName,
                              source: "fishiq",
                              quickLog: true,
                              coordinateLocked: true,
                              returnTo: "big_blue",
                            },
                          } as any);
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>,
              document.body,
            )
          : null}
        <BigBlueTournamentOverlays
          ctx={{
          getTournamentMapPresentation,
          getTournamentScopeBadge,
          handleTournamentMapRegistration,
          isDarkMode,
          isTournamentMapFilterOpen,
          isWorldTournamentMapOpen,
          isWorldwideTournament,
          markTournamentResultsSeen,
          myTournamentParticipation,
          props,
          selectedTournamentMapDetail,
          setIsTournamentMapFilterOpen,
          setIsWorldTournamentMapOpen,
          setSelectedTournamentMapDetail,
          setTournamentResultsSeenRevision,
          tournamentHubRows,
          tournamentMapRegistrationBusyId,
          worldTournamentRows,
          }}
        />

        {/* Bottom-right: Tournaments / Challenges. Keep this stack lifted above
            the attribution lane while the left media/search dock stays low. */}
        <div
          className="absolute right-2 sm:right-4 pointer-events-auto flex flex-col space-y-2 z-[1000]"
          style={{ bottom: rightBottomHudCss }}
        >
          <button
            type="button"
            data-roddo-tour="map-tournaments"
            data-roddo-quick-anchor="tournaments"
            className={`${actionButtonStyle} roddo-hud-attention overflow-visible`}
            onClick={() => {
              const nextOpen = !isTournamentMapFilterOpen;
              if (nextOpen) {
                setIsWorldTournamentMapOpen(false);
                setQuickWindow(null);
                dismissMapTextInputs();
                setIsWeatherOpen(false);
                setIsFishWidgetOpen(false);
                setSelectedFishSpot(null);
                setIsFishFullDetailOpen(false);
                setIsSavedSpotsOpen(false);
                setPendingSavedSpotLatLng(null);
                setSelectedSavedSpotId(null);
                setIsSavedSpotFilterMenuOpen(false);
                setIsSelectedSavedSpotMenuOpen(false);
                if (isOfflinePanelOpen) onCloseOfflinePanel?.();
              }
              setIsTournamentMapFilterOpen(nextOpen);
            }}
            aria-label="Tournaments"
            title="Tournaments"
            style={{
              "--roddo-attention-ring": isDarkMode
                ? "rgba(255,224,130,0.64)"
                : "rgba(197,138,18,0.52)",
              "--roddo-attention-glow": isDarkMode
                ? "rgba(255,205,84,0.46)"
                : "rgba(197,138,18,0.30)",
              "--roddo-attention-soft": isDarkMode
                ? "rgba(112,87,232,0.20)"
                : "rgba(246,185,54,0.20)",
              "--roddo-attention-delay": "0.95s",
            } as React.CSSProperties}
          >
            <span className="roddo-hud-attention-halo" aria-hidden="true" />
            {tournamentHubRows.length > 0 ? (
              <span
                className="absolute -right-1 -top-1 z-20 grid min-w-[18px] h-[18px] place-items-center rounded-full border px-1 text-[8px] font-black"
                style={{
                  background: isDarkMode ? "#171E35" : "#FFFFFF",
                  borderColor: isDarkMode ? "#7B6FB4" : "#D6A52B",
                  color: isDarkMode ? "#FFE0A0" : "#9B680A",
                }}
              >
                {tournamentHubRows.length > 99 ? "99+" : tournamentHubRows.length}
              </span>
            ) : null}
            <span className="roddo-hud-attention-icon">
              <HudTrophyIcon
                className="w-[22px] h-[22px] transition-transform duration-200 ease-out group-hover:scale-[1.10] group-active:scale-[0.92]"
                style={{
                  color: isDarkMode ? "#E8C97A" : "#C58A12",
                  filter: isDarkMode
                    ? "drop-shadow(0 0 9px rgba(151,128,214,0.26))"
                    : "drop-shadow(0 0 7px rgba(197,138,18,0.24))",
                }}
              />
            </span>
          </button>

          <button
            type="button"
            data-roddo-quick-anchor="challenges"
            className={`${actionButtonStyle}`}
            onClick={() => openBigBlueQuickWindow("challenges")}
            aria-label="Challenges"
            title="Challenges"
          >
            <HudFlagIcon
              className="w-[22px] h-[22px] transition-transform duration-200 ease-out group-hover:scale-[1.10] group-active:scale-[0.92]"
              style={{
                color: isDarkMode ? "#9CF7BF" : "#2F9E58",
                filter: isDarkMode
                  ? "drop-shadow(0 0 10px rgba(156,247,191,0.32))"
                  : "drop-shadow(0 0 7px rgba(47,158,88,0.22))",
              }}
            />
          </button>
        </div>
      </div>
      ) : null}

      <BigBlueQuickOverlays
        ctx={{
        closeBigBlueQuickWindow,
        crewPresence,
        findCatchMemoryMatch,
        fishWidgetAnchor,
        inferGeoProfile,
        isDarkMode,
        latestUserLatLngRef,
        lifetimeCatchSpeciesRows,
        mapInstanceRef,
        nearbyCatchSpeciesRows,
        profile,
        props,
        quickChallengeCompletions,
        quickChallengeRows,
        quickCrewDmCard,
        quickCrewDmInboxByUid,
        quickCrewMode,
        quickCrewPresenceOptIn,
        quickCrewPresenceSaving,
        quickCrewRelationshipByTarget,
        quickCrewSearch,
        quickCrewSearchBusyUid,
        quickCrewSearchResults,
        quickCrewSendRequest,
        quickCrewSummary,
        quickCrewUnreadCount,
        quickDailyPreview,
        quickDailyRows,
        quickFishIQSearch,
        quickGroupCrews,
        quickWindow,
        setFishWidgetAreaSource,
        setFishWidgetMode,
        setIsFishWidgetOpen,
        setQuickCrewDmUid,
        setQuickCrewMode,
        setQuickCrewSearch,
        setQuickFishIQSearch,
        setQuickWindow,
        setSelectedFishSpot,
        toggleQuickCrewPresence,
        visibleFishSpots,
        }}
      />

      {/* Offline Panel */}
      {isOfflinePanelOpen && (
        <div
          className="absolute inset-0 bg-black/60 z-[6200]"
          onClick={onCloseOfflinePanel || (() => {})}
        >
          <div
            className="absolute bottom-32 left-4 w-[calc(100%-2rem)] max-w-sm bg-gradient-to-br from-[#fdf8e2]/80 to-[#fdf8e2]/50 dark:from-[#504e76]/80 dark:to-[#504e76]/50 backdrop-blur-xl rounded-3xl shadow-lg border border-white/20 p-5 space-y-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center border-b border-black/10 pb-2">
              <h3 className="text-xl font-black text-[#504e76] dark:text-[#fdf8e2]">
                {t("map.offlineMaps")}
              </h3>
              <button
                onClick={onCloseOfflinePanel || (() => {})}
                className="p-2 rounded-xl bg-red-100/50 border border-red-200 hover:bg-red-200/50"
                aria-label="Close offline maps panel"
                type="button"
              >
                <XIcon className="w-6 h-6 text-black" />
              </button>
            </div>

            {downloadState.active ? (
              <div className="p-4 bg-white/50 dark:bg-black/20 rounded-xl border border-black/10">
                <p className="font-bold text-sm mb-2">
                  {t("map.downloading", { name: downloadState.name })}
                </p>
                <div className="w-full bg-slate-200 rounded-full h-4 border border-black/10 overflow-hidden">
                  <div
                    className="bg-[#f1642e] h-full"
                    style={{
                      width: `${(downloadState.progress / downloadState.total) * 100}%`,
                    }}
                  />
                </div>
                <p className="text-xs text-right mt-2 font-mono font-bold">
                  {downloadState.progress} / {downloadState.total}{" "}
                  {t("map.tiles")}
                </p>
              </div>
            ) : (
              <button
                onClick={handleStartDownload}
                className="w-full p-4 text-base font-black bg-gradient-to-br from-[#c3df96]/80 to-[#c3df96]/50 text-[#504e76] rounded-xl border border-white/30 shadow-sm hover:scale-105 active:scale-95 transition-all backdrop-blur-sm"
                type="button"
              >
                <DownloadCloudIcon className="w-6 h-6 inline-block -mt-1 mr-2" />
                {t("map.downloadCurrent")}
              </button>
            )}

            <div>
              <h4 className="font-bold text-sm mb-3 uppercase tracking-wider text-[#504e76]/70 dark:text-[#fdf8e2]/70">
                {t("map.myRegions")}
              </h4>
              <div className="space-y-3 max-h-48 overflow-y-auto pr-2">
                {offlineRegions.length > 0 ? (
                  offlineRegions.map((region) => (
                    <div
                      key={region.name}
                      className="flex items-center justify-between p-3 bg-white/50 dark:bg-black/20 rounded-xl border border-white/20 backdrop-blur-sm"
                    >
                      <div>
                        <p className="font-bold text-base text-[#504e76] dark:text-[#fdf8e2]">
                          {region.name}
                        </p>
                        <p className="text-xs font-semibold text-[#504e76]/60 dark:text-[#fdf8e2]/60">
                          {t("map.tilesSaved", {
                            count:
                              region.tileCount?.toLocaleString?.() ??
                              region.tileCount,
                          })}
                        </p>
                      </div>
                      <button
                        onClick={() => setRegionToDelete(region.name)}
                        className="p-2 rounded-lg bg-red-100/50 border border-red-500/50 hover:bg-red-200/50"
                        aria-label={`Delete ${region.name} offline map`}
                        type="button"
                      >
                        <TrashIcon className="w-5 h-5 text-red-600" />
                      </button>
                    </div>
                  ))
                ) : (
                  <div className="text-center p-4 border border-dashed border-[#504e76]/30 rounded-xl">
                    <p className="text-sm font-bold text-[#504e76]/50 dark:text-[#fdf8e2]/50">
                      {t("map.noOffline")}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {props.isActive && selectedBazaarMapListing ? (
        <div
          className="absolute inset-0 z-[1450] grid place-items-center bg-black/45 p-4 backdrop-blur-[2px]"
          role="dialog"
          aria-modal="true"
          aria-label="Bazaar listing preview"
          onClick={() => setSelectedBazaarMapListing(null)}
        >
          {(() => {
            const item = selectedBazaarMapListing.item || {};
            const image = String(item?.imageUrls?.[0] || item?.images?.[0] || item?.imageUrl || "").trim();
            const title = String(item?.title || item?.name || "Bazaar listing").trim();
            const description = String(item?.description || "").trim();
            const location = String(item?.locationLabel || [item?.city, item?.region].filter(Boolean).join(", ") || "Seller-selected area").trim();
            const condition = String(item?.condition || "used").replace(/_/g, " ");
            const seller = String(item?.sellerUsername || item?.sellerName || "Roddo angler").trim();
            const priceCents = Number(item?.priceCents || 0);
            const currency = String(item?.currency || "USD").toUpperCase();
            let price = "View price";
            try {
              if (Number.isFinite(priceCents) && priceCents > 0) {
                price = new Intl.NumberFormat(undefined, { style: "currency", currency }).format(priceCents / 100);
              }
            } catch {}
            return (
              <div
                className="w-full max-w-[430px] overflow-hidden rounded-[22px] border shadow-[0_28px_80px_rgba(0,0,0,.45)]"
                style={{
                  background: isDarkMode ? "#0E1625" : "#FFFDF8",
                  borderColor: isDarkMode ? "rgba(255,255,255,.12)" : "rgba(65,53,43,.13)",
                  color: isDarkMode ? "#F3F6FB" : "#30271E",
                }}
                onClick={(event) => event.stopPropagation()}
              >
                <div className="flex items-center justify-between border-b px-4 py-3" style={{ borderColor: isDarkMode ? "rgba(255,255,255,.08)" : "rgba(65,53,43,.10)" }}>
                  <div>
                    <div className="text-[10px] font-black uppercase tracking-[.15em] opacity-55">Bazaar listing</div>
                    <div className="mt-0.5 max-w-[310px] truncate text-[15px] font-black">{title}</div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setSelectedBazaarMapListing(null)}
                    className="grid h-9 w-9 place-items-center rounded-full border text-sm font-black"
                    style={{ borderColor: isDarkMode ? "rgba(255,255,255,.12)" : "rgba(65,53,43,.12)" }}
                    aria-label="Close listing preview"
                  >
                    ×
                  </button>
                </div>

                {image ? <img src={image} alt="" className="h-52 w-full object-cover" /> : null}

                <div className="p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-xl font-black">{price}</div>
                      <div className="mt-1 text-[11px] font-bold capitalize opacity-65">{condition} · {location}</div>
                    </div>
                    <div className="shrink-0 rounded-full px-2.5 py-1 text-[10px] font-black" style={{ background: isDarkMode ? "rgba(118,87,232,.18)" : "rgba(78,165,233,.12)", color: isDarkMode ? "#C8B9FF" : "#246FAD" }}>
                      Nearby
                    </div>
                  </div>

                  {description ? <p className="mt-3 line-clamp-4 text-[12px] font-semibold leading-5 opacity-80">{description}</p> : null}
                  <div className="mt-3 text-[11px] font-bold opacity-60">Seller · {seller}</div>

                  <button
                    type="button"
                    onClick={() => {
                      const itemId = selectedBazaarMapListing.id;
                      setSelectedBazaarMapListing(null);
                      onBazaarClickRef.current?.({ itemId, from: "big_blue_bazaar_detail" });
                    }}
                    className="mt-4 w-full rounded-[13px] px-4 py-3 text-sm font-black text-white transition active:scale-[.99]"
                    style={{ background: isDarkMode ? "#7657E8" : "#4EA5E9" }}
                  >
                    Open in Bazaar
                  </button>
                </div>
              </div>
            );
          })()}
        </div>
      ) : null}

      <ConfirmationModal
        isOpen={props.isActive === true && !!savedSpotDeleteConfirm}
        onClose={() => setSavedSpotDeleteConfirm(null)}
        onConfirm={async () => {
          const spot = savedSpotDeleteConfirm;
          if (!spot) return;
          setSavedSpotDeleteConfirm(null);
          await removeSavedSpot(spot.id);
        }}
        title="Delete Saved Spot"
        message={savedSpotDeleteConfirm ? `Remove ${savedSpotDeleteConfirm.name || "this saved spot"} from Big Blue?` : "Remove this saved spot?"}
        confirmText="Delete"
      />

      <ConfirmationModal
        isOpen={props.isActive === true && !!regionToDelete}
        onClose={() => setRegionToDelete(null)}
        onConfirm={async () => {
          if (regionToDelete) {
            try {
              await idb.delete(REGION_STORE, regionToDelete);
            } catch (err) {
              console.warn(
                "[offline-maps] Failed to delete offline region metadata.",
                err,
              );
            }
            void loadOfflineRegions();
            setRegionToDelete(null);
          }
        }}
        title="Delete Offline Map"
        message={t("map.deleteRegionConfirm", { name: regionToDelete })}
        confirmText={t("common.delete")}
      />
    </div>
  );
};

export default MapView;
